/**
 * OmniCheck Design Studio - Advanced Check Builder v3.0
 * Full-featured check designer with all premium features
 */

import React, { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import { fabric } from 'fabric';
import { bankSpecificTemplates } from '../data/bankTemplates';
import { TEMPLATE_DESIGN, TEMPLATE_BORDER, STYLE_PRESETS, STYLE_TEMPLATE_ROUTES, resolveTemplateStyle, micrVariantFor, templateWordmarkInk, templateWeaveInk } from '../data/designPresets';
import { resolveBackVariant, buildBackEngineConfig } from '../data/backVariants';
import { CheckBackEngine } from '../engines/CheckBackEngine';
import { SignatureExtractorEngine } from '../engines/SignatureExtractorEngine';
import { SignatureCaptureEngine } from '../engines/SignatureCaptureEngine';
import { BorderlineEngine, BorderConfig } from './BorderlineEngine';
import UltimateCustomizationPanel from './UltimateCustomizationPanel';
import { processAiPrompt } from '../engines/AiDesignEngine';
import { useLicense } from '../contexts/LicenseContext';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../services/apiClient';
import { MICRComplianceEngine } from '../engines/MICRComplianceEngine';
import { convertToWords } from '../utils/numberToWords';
import { checkSheetLayout, drawCheckSheetPage } from '../utils/checkPdfSheet';
import { isAuthFailureStatus, notifyAuthExpired } from '../utils/authExpiry';
import { processLogoImage } from '../utils/backgroundRemoval';
import { BACK_DEFAULT_KEY, parseBackDefault, normalizeBackDefault, serializeBackDefault, mergeArrangementPositions, stripArrangementPositions, stripLegacyBackObjectPositions, normalizeBackTextDirections, specimenAngleFor, mobileDepositBlockEntryFromLegacy, MOBILE_DEPOSIT_BLOCK_ID, MOBILE_CHECKBOX_ID, MOBILE_INSTRUCTION_ID, BackDefault } from '../utils/backDefault';
import { defaultLogoPlacement, logoZoneForLayout, logoSuppressesBankHeader, clampLogoScaleToZone } from '../utils/logoPlacement';
import { buildFactoryCheckSnapshot, buildDesignOnlyResetSnapshot, factoryCheckData, factoryBorderConfig, FACTORY_SECURITY_FEATURES as SECURITY_FEATURES, FACTORY_SEAL_COLOR, FACTORY_SEAL_FINISH, isBlankCheckSnapshot } from '../utils/factoryCheckSnapshot';
import { sealGradientStops, sealStroke, SEAL_COLOR_PRESETS, type SealFinish } from '../utils/sealDesign';
import { extractDesignPattern, DEFAULT_CROP, patternTileSize, patternTilePlacements, PATTERN_TILE_ALPHA, deepTone, lightTone, type CropRegion } from '../utils/designPatternImport';
import { pointInQuad } from '../utils/selectionGeometry';
import { LongPressTracker, LONG_PRESS_MS, LONG_PRESS_TOLERANCE_PX } from '../utils/longPress';
import { attachPinchZoom, lockStudioViewport, pinchCssTransform, clampPinchPan, pinchViewportFrame, anchorZoom, DOUBLE_TAP_ZOOM, IDENTITY_PINCH_VIEW, type PinchView, type PinchPoint, type PinchZoomOptions } from '../utils/pinchZoom';
import { resolveTouchDragAction, touchFindTolerance, resolveEmptyAreaGesture, doubleTapMatch, isSubThresholdJitter, STILL_FINGER_TOLERANCE_PX, type TapStamp } from '../utils/touchDrag';
import { InteractFlags } from '../utils/interactionFade';
import { editTargetForObject, objectMenuLabel } from '../utils/objectEditTargets';
import { lineCount, bankBlockShiftPx } from '../utils/addressBlocks';
import { parseMoney, roundMoney } from '../utils/money';
import { saveBlobAsDownload } from '../utils/downloadFile';
import { NEW_TEXTURE_ENTRIES, isNewBackgroundDesign, drawBackgroundDesign } from '../utils/backgroundDesigns';
import { isNewBorderStyle, drawCanvasBorder } from '../utils/canvasBorder';
import { borderInnerExtents } from '../utils/borderExtents';
import { applyTemplateSecuritySet } from '../utils/templateSecurity';
import { type AssetEntry, ASSET_LIBRARY_CAPS, loadAssetLibrary, saveAssetLibrary, makeAssetEntry, addAssetEntry, removeAssetEntry, migrateLegacyAsset } from '../utils/assetLibrary';
import { DESIGN_WRITEUP_TEXT, designBankBlock, captureCanvasWithoutGhosts } from '../utils/ghostWriteups';
import { useHistorySnapshots } from '../hooks/useHistorySnapshots';
import { publishStudioStartOffer, onStudioStartAction, type StudioStartAction } from '../utils/studioStartBridge';
import HistoryBar, { dispatchOpenMyDesigns } from './HistoryBar';

// ============== CONSTANTS ==============
// Standard Business Check: 8.25" x 3.00" -> 825px x 300px (100px/inch base)
// Personal Check: 6.00" x 2.75" -> 600px x 275px
const DEFAULT_BUSINESS_WIDTH = 850; // 8.5" at 100 DPI
const DEFAULT_BUSINESS_HEIGHT = 350; // 3.5" at 100 DPI
const DEFAULT_PERSONAL_WIDTH = 600;
const DEFAULT_PERSONAL_HEIGHT = 275; // 6.00" x 2.75" at 100 DPI

const RAINBOW_COLORS = [
    '#FF0000', '#FF3300', '#FF6600', '#FF9900', '#FFCC00', '#FFFF00',
    '#CCFF00', '#99FF00', '#66FF00', '#33FF00', '#00FF00', '#00FF33',
    '#00FF66', '#00FF99', '#00FFCC', '#00FFFF', '#00CCFF', '#0099FF',
    '#0066FF', '#0033FF', '#0000FF', '#3300FF', '#6600FF', '#9900FF',
    '#CC00FF', '#FF00FF', '#FF00CC', '#FF0099', '#FF0066', '#FF0033'
];

/**
 * Export capture multiplier for every DELIVERABLE path (GENERATE
 * FINAL CHECK PNG, composite PDF front + back, Direct Print). The
 * canvas is 100 units per inch (850 x 350 = 8.5" x 3.5"), so the
 * multiplier IS the DPI in hundreds: 6 = 600 DPI, the check-printing
 * standard — 5100 x 2100 px for a business check. Raised from 4
 * (400 DPI; the flagship GENERATE button was still on 2 = 200 DPI)
 * on the owner's quality bar (2026-11-04): his original build's PDF
 * ran ~22.7 MB and stayed clear at any zoom. The PDF page size is
 * set in inches independently, so the exact physical size never
 * changes — only the raster density inside it. Preview/thumbnail
 * captures keep their small multipliers; they are not deliverables.
 */
export const EXPORT_MULTIPLIER = 6;

// ============== PRESET DATA ==============
const CHECK_STYLES = [
    { id: 'cashier', name: "Cashier's Check (Secure)", icon: '🏦' },
    { id: 'personal', name: 'Personal Check', icon: '👤' },
    { id: 'business', name: 'Business Check (Professional)', icon: '💼' },
    { id: 'payroll', name: 'Payroll Check (Industrial)', icon: '💰' },
    { id: 'payroll_classic', name: 'Payroll Check (Classic)', icon: '🎁' },
    { id: 'payroll_karen', name: 'Karen Art Studio Payroll', icon: '🎨' },
    { id: 'echeck', name: 'eCheck Digital', icon: '💳' },
    { id: 'money_order', name: 'Money Order Style', icon: '📜' },
    { id: 'certified', name: 'Certified Check', icon: '✅' },
    { id: 'counter', name: 'Counter Check', icon: '🏪' },
    { id: 'bofa_classic', name: 'Bank of America Classic', icon: '🏦' },
    { id: 'bofa_v2', name: 'Bank of America Business (v2)', icon: '💼' },
    { id: 'greenfi', name: 'GreenFi', icon: '🌱' },
    { id: 'anb', name: 'ANB Bank', icon: '☁️' },
    { id: 'usaa', name: 'USAA (Federal Savings Bank)', icon: '⭐' },
    { id: 'us_treasury', name: 'U.S. Treasury Official', icon: '🏛️' }
];

const FONTS = [
    { id: 'times', name: 'Times (Classic)', family: 'Times New Roman, serif' },
    { id: 'arial', name: 'Arial (Modern)', family: 'Arial, sans-serif' },
    { id: 'courier', name: 'Courier (Typewriter)', family: 'Courier New, monospace' },
    { id: 'garamond', name: 'Garamond (Elegant)', family: 'Garamond, serif' },
    { id: 'georgia', name: 'Georgia (Readable)', family: 'Georgia, serif' },
    { id: 'helvetica', name: 'Helvetica (Clean)', family: 'Helvetica, Arial, sans-serif' }
];

const MICR_FONTS = [
    { id: 'e13b', name: 'Standard E-13B (US/UK)', family: '"GnuMICR", "MICR E13B Match", "MICR E-13B", monospace' },
    { id: 'cmc7', name: 'CMC-7 (European)', family: '"Glegoo", "Courier New", monospace' },
    { id: 'ocra', name: 'OCR-A (Digital Pro)', family: '"OCR A Extended", "OCR-A", monospace' },
    { id: 'modern', name: 'Modern Sans MICR', family: 'Arial, sans-serif' }
];

const TEXTURES = [
    { id: 'linen', name: 'Linen Weave', icon: '🧵' },
    { id: 'parchment', name: 'Parchment', icon: '📜' },
    { id: 'marble', name: 'Marble Swirl', icon: '🪨' },
    { id: 'security', name: 'Security Crosshatch', icon: '🔒' },
    { id: 'plain', name: 'Plain Paper', icon: '📄' },
    { id: 'cotton', name: 'Cotton Fiber', icon: '🌾' },
    { id: 'particles', name: 'Premium Particles', icon: '✨' },
    { id: 'rough', name: 'Rough Paper', icon: '📃' },
    { id: 'guilloche_bg', name: 'Guilloché Wave', icon: '🌀' },
    { id: 'blue_wave', name: 'Blue Wave (Safety)', icon: '🌊' },
    { id: 'prismatic', name: 'Prismatic Security', icon: '🌈' },
    { id: 'laser_security', name: 'Laser Security Moiré', icon: '🛰️' },
    { id: 'fluorescent_fibers', name: 'Fluorescent Fibers', icon: '🧬' },
    { id: 'toner_adhesion', name: 'Toner Adhesion Grain', icon: '🎞️' },
    { id: 'nano_optical', name: 'Nano-Optical Screen', icon: '💠' },
    { id: 'interference', name: 'Interference Pattern', icon: '📡' },
    { id: 'multi_tone', name: 'Multi-Tone Depth', icon: '🏔️' },
    { id: 'fiber_optic', name: 'Fiber Optic Security', icon: '✨' },
    { id: 'micro_embossed', name: 'Micro-Embossed Vellum', icon: '🎞️' },
    { id: 'geometric_diamond', name: 'Geometric Diamond', icon: '💎' },
    { id: 'circuit_board', name: 'FinTech Circuitry', icon: '🔌' },
    { id: 'royal_damask', name: 'Royal Damask', icon: '⚜️' },
    { id: 'digital_hive', name: 'Digital Hive', icon: '🐝' },
    { id: 'cloud_marble', name: 'Classic Cloud Marble', icon: '☁️' },
    { id: 'cloud_band', name: 'Sky Cloud Band', icon: '🌤️' },
    // Design-library expansion (2026-11-04): geometric patterns and
    // paper textures from utils/backgroundDesigns.ts — same picker,
    // same snapshot persistence, drawn by the same render pass.
    ...NEW_TEXTURE_ENTRIES
];

const PAPER_COLORS = [
    { id: 'cream', name: 'Classic Cream', color: '#f8f4ec' },
    { id: 'white', name: 'Bright White', color: '#ffffff' },
    { id: 'ivory', name: 'Ivory', color: '#fffff0' },
    { id: 'light_blue', name: 'Safety Blue', color: '#e8f4fc' },
    { id: 'light_green', name: 'Sage Green', color: '#f0f8f0' },
    { id: 'light_pink', name: 'Blush Pink', color: '#fff0f5' },
    { id: 'antique', name: 'Antique Gold', color: '#faebd7' },
    { id: 'lavender', name: 'Lavender Mist', color: '#f5f0ff' },
    { id: 'peach', name: 'Peach Glow', color: '#fff5ee' },
    { id: 'treasury_green', name: 'Treasury Green', color: '#e8f0e0' }
];

// The default security-feature set lives in utils/factoryCheckSnapshot
// (imported above as SECURITY_FEATURES): it is part of the factory
// state a brand-new visitor gets, of the design slice Default Clean
// Style resets to, and of the blank state "Start a blank check"
// clears to - all derived from the one definition, so they can
// never drift apart.

// SOPHISTICATED NON-BANK TEMPLATES (Premium depositable designs)
const PREMIUM_TEMPLATES = [
    { id: 'classic_elegant', name: 'Classic Elegant', colors: { primary: '#2c3e50', secondary: '#34495e', accent: '#c9a227', background: '#fdfbf7' } },
    { id: 'executive_gold', name: 'Executive Gold', colors: { primary: '#8b6914', secondary: '#5d4e37', accent: '#d4af37', background: '#fffef8' } },
    { id: 'royal_blue', name: 'Royal Blue', colors: { primary: '#1a3a6e', secondary: '#4169e1', accent: '#ffd700', background: '#f8faff' } },
    { id: 'corporate_grey', name: 'Corporate Grey', colors: { primary: '#2f4f4f', secondary: '#708090', accent: '#b8860b', background: '#f5f5f5' } },
    { id: 'emerald_prestige', name: 'Emerald Prestige', colors: { primary: '#006400', secondary: '#228b22', accent: '#ffd700', background: '#f8fff8' } },
    { id: 'burgundy_classic', name: 'Burgundy Classic', colors: { primary: '#722f37', secondary: '#8b0000', accent: '#daa520', background: '#fffaf5' } },
    { id: 'midnight_premium', name: 'Midnight Premium', colors: { primary: '#191970', secondary: '#000080', accent: '#c0c0c0', background: '#f5f5ff' } },
    { id: 'bronze_heritage', name: 'Bronze Heritage', colors: { primary: '#8b4513', secondary: '#a0522d', accent: '#cd7f32', background: '#faf5f0' } },
    { id: 'ultra_obsidian', name: 'Ultra Prestige Obsidian', colors: { primary: '#000000', secondary: '#434343', accent: '#ff0000', background: '#f9f9f9' } },
    { id: 'ultra_sapphire', name: 'Ultra Prestige Sapphire', colors: { primary: '#0f0c29', secondary: '#302b63', accent: '#24243e', background: '#fbfcff' } },
    { id: 'ultra_amethyst', name: 'Ultra Prestige Amethyst', colors: { primary: '#4b1248', secondary: '#f0c27b', accent: '#e8cbc0', background: '#fffafa' } },
    { id: 'ultra_crimson', name: 'Ultra Prestige Crimson', colors: { primary: '#870000', secondary: '#190a05', accent: '#ff4b2b', background: '#fffcfc' } },
    { id: 'ultra_diamond', name: 'Ultra Prestige Diamond', colors: { primary: '#202020', secondary: '#777777', accent: '#00d2ff', background: '#ffffff' } },
    { id: 'corporate_safety', name: 'Corporate Safety (Blue)', colors: { primary: '#0055a4', secondary: '#003366', accent: '#00a0e9', background: '#f0f8ff' } },
    { id: 'us_treasury', name: 'U.S. Treasury / Stimulus', colors: { primary: '#1e3932', secondary: '#2d5a27', accent: '#d4af37', background: '#f4f8f0' } },
    { id: 'chime_pro', name: 'Chime / Digital Pro', colors: { primary: '#24c25f', secondary: '#000000', accent: '#24c25f', background: '#ffffff' } },
    { id: 'credit_union_classic', name: 'Credit Union Classic', colors: { primary: '#003b71', secondary: '#00254b', accent: '#fdb913', background: '#fbfcff' } },
    { id: 'varo_style', name: 'Varo / Modern Neo', colors: { primary: '#5c2d91', secondary: '#411e64', accent: '#ff00ff', background: '#fcf8ff' } },
    { id: 'prepaid_gold', name: 'Prepaid Gold Card', colors: { primary: '#111111', secondary: '#333333', accent: '#ffd700', background: '#fffef0' } }
];

const BANK_TEMPLATES = [
    ...PREMIUM_TEMPLATES,
    // Every bank template in the library is pickable — the old
    // slice(0, 15) silently hid 33 of the 48 templates from the UI
    // (they were only reachable by Ask AI prompt or not at all).
    ...Object.entries(bankSpecificTemplates).map(([id, template]) => ({
        id,
        name: template.name,
        colors: template.specs.colors
    }))
];

const BACK_SIDE_STYLES = [
    { id: 'standard', name: 'Standard (Personal)', icon: '📄' },
    { id: 'mobile', name: 'Mobile Deposit', icon: '📱' },
    { id: 'atm', name: 'ATM / Branch Deposit', icon: '🏦' },
    { id: 'business', name: 'Corporate / Business', icon: '🏢' }
];

const HANDWRITING_STYLES = [
    { id: 'none', name: 'Standard (Typed)', icon: '⌨️' },
    { id: 'ballpoint', name: 'Ballpoint Pen', icon: '🖊️' },
    { id: 'fountain', name: 'Fountain Pen', icon: '🖋️' },
    { id: 'felt_tip', name: 'Felt Tip', icon: '🖍️' },
    { id: 'cursive', name: 'Cursive Script', icon: '✒️' },
    { id: 'stephanie_lacey', name: 'Stephanie Lacey', icon: '👑' }
];

// --- STYLES (Professional Studio — Light Sidebar + Dark Canvas) ---

const containerStyle: React.CSSProperties = {
    display: 'flex',
    height: '100vh',
    width: '100%',
    backgroundColor: '#07080d',
    overflow: 'hidden',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
};

const sidebarStyle: React.CSSProperties = {
    width: '290px',
    minWidth: '290px',
    background: 'linear-gradient(180deg, #12141d 0%, #0b0d14 100%)',
    borderRight: '1px solid rgba(255,255,255,0.08)',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '2px 0 24px rgba(0,0,0,0.45)',
    zIndex: 10
};

const headerStyle: React.CSSProperties = {
    padding: '16px 18px',
    borderBottom: '1px solid #E2E8F0',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    background: 'linear-gradient(135deg, #F97316 0%, #EA580C 55%, #9A3412 100%)',
    color: 'white'
};

const sectionStyle: React.CSSProperties = {
    borderBottom: '1px solid rgba(255,255,255,0.06)'
};

const sectionHeaderStyle: React.CSSProperties = {
    padding: '11px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    backgroundColor: 'rgba(255,255,255,0.02)',
    fontWeight: '600',
    fontSize: '12px',
    color: '#E2E8F0',
    transition: 'all 0.2s ease',
    letterSpacing: '0.2px'
};

const inputGroupStyle: React.CSSProperties = {
    marginBottom: '12px'
};

const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '10px',
    fontWeight: '700',
    color: '#FB923C',
    marginBottom: '5px',
    textTransform: 'uppercase',
    letterSpacing: '0.8px'
};

const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    borderRadius: '10px',
    border: '1px solid rgba(255,255,255,0.10)',
    fontSize: '12px',
    outline: 'none',
    backgroundColor: 'rgba(255,255,255,0.04)',
    color: '#F1F5F9',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    fontFamily: "'Inter', sans-serif"
};

const checkboxStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    fontSize: '12px',
    color: '#94A3B8',
    padding: '5px 0',
    transition: 'color 0.2s'
};

const actionBtnStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '12px',
    border: '1px solid rgba(255,255,255,0.10)',
    background: 'rgba(255,255,255,0.04)',
    color: '#E2E8F0',
    fontWeight: '600',
    fontSize: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'all 0.2s ease',
    marginBottom: '8px',
    boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
    letterSpacing: '0.2px'
};

const generateBtnStyle: React.CSSProperties = {
    ...actionBtnStyle,
    background: 'linear-gradient(135deg, #F97316 0%, #EA580C 55%, #9A3412 100%)',
    color: '#FFFFFF',
    border: 'none',
    marginTop: '12px',
    boxShadow: '0 4px 18px rgba(249,115,22,0.40)',
    fontSize: '13px',
    fontWeight: '700',
    letterSpacing: '0.5px',
    textTransform: 'uppercase'
};

const previewContainerStyle: React.CSSProperties = {
    minWidth: 0,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#07080d',
    overflow: 'hidden'
};

const previewHeaderStyle: React.CSSProperties = {
    padding: '8px 20px',
    background: 'rgba(255,255,255,0.02)',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
};

const toolbarStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'rgba(255,255,255,0.02)',
    padding: '6px 16px',
    color: '#CBD5E1',
    borderBottom: '1px solid rgba(255,255,255,0.06)'
};

const toolBtnStyle: React.CSSProperties = {
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.09)',
    color: '#CBD5E1',
    fontSize: '13px',
    cursor: 'pointer',
    padding: '5px 8px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease'
};

const zoomSelectStyle: React.CSSProperties = {
    backgroundColor: 'rgba(255,255,255,0.06)',
    color: '#CBD5E1',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '4px',
    padding: '4px 8px',
    fontSize: '11px',
    outline: 'none',
    fontFamily: "'Inter', sans-serif"
};

const canvasWrapperStyle: React.CSSProperties = {
    flex: 1,
    padding: '20px 28px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    overflowY: 'auto',
    overflowX: 'auto',
    gap: '30px',
    background: '#1a1d23',
    justifyContent: 'flex-start'
};

const pageInputStyle: React.CSSProperties = {
    width: '32px',
    padding: '3px 5px',
    textAlign: 'center',
    borderRadius: '5px',
    border: '1px solid #CBD5E1',
    backgroundColor: '#FFFFFF',
    color: '#334155',
    outline: 'none',
    fontSize: '12px',
    fontFamily: "'Inter', sans-serif"
};

const dropdownItemStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 16px',
    textAlign: 'left',
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '13px',
    color: '#475569',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    fontFamily: "'Inter', sans-serif"
};

// Segmented option groups (Background: Remove/Keep, Content: Full
// block/Logo only, and peers): the studio's option-group idiom - a
// small uppercase group label over a row of equal choices, the active
// choice carrying the orange selection treatment used across the panel.
const segGroupLabelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '9px',
    fontWeight: '700',
    color: '#94A3B8',
    textTransform: 'uppercase',
    letterSpacing: '0.7px',
    marginBottom: '4px'
};

const segRowStyle: React.CSSProperties = {
    display: 'flex',
    gap: '6px'
};

const segBtnStyle = (active: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '7px 8px',
    fontSize: '11px',
    fontWeight: active ? '700' : '500',
    color: active ? '#FDBA74' : '#CBD5E1',
    backgroundColor: active ? 'rgba(249,115,22,0.12)' : 'rgba(255,255,255,0.03)',
    border: active ? '1px solid #F97316' : '1px solid rgba(255,255,255,0.12)',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    fontFamily: "'Inter', sans-serif"
});

// Tool section titles in workflow order - shown in the mobile sheet
// header so the slide-in tools panel names the section being edited.
const SECTION_TITLES: Record<string, string> = {
    check_info: 'Check Information & Bank Details',
    payroll_info: 'Payroll & Corporate Details',
    bank_style: 'Bank Styles & Templates',
    paper_texture: 'Background & Texture Design',
    custom_assets: 'Custom Assets (Logos & Signs)',
    typography: 'Handwriting & Typography',
    security: 'Security Features',
    micr_config: 'MICR Line Engineering',
    borders: 'Borders & Secure Frames'
};

const getHandwritingFont = (style: string, fallback: string = 'Arial, sans-serif') => {
    switch (style) {
        case 'ballpoint': return "'Dancing Script', cursive";
        case 'fountain': return "'Alex Brush', cursive";
        case 'felt_tip': return "'Pinyon Script', cursive";
        case 'cursive': return "'Great Vibes', cursive";
        default: return fallback;
    }
};

// --- Touch selection helpers (mobile multi-select + selection bar) ---
// On phones there is no Delete key and no shift-click, so selection
// needs its own controls. These helpers are shared by the front and
// back canvases. Deletion goes through the SAME deleted-ids flow the
// keyboard Delete uses, so history snapshots (and Undo) see exactly
// the same deletions.

/**
 * The absolute position of a child inside an ActiveSelection, in
 * canvas coordinates, computed the way fabric itself decomposes a
 * group on discard (Group._restoreObjectsState ->
 * util.addTransformToObject): compose the selection's OWN matrix
 * with the child's OWN matrix and let applyTransformToObject do the
 * decompose + centre-to-origin conversion on a throwaway probe
 * object carrying the child's geometry, so nothing live is mutated.
 *
 * The previous code composed calcTransformMatrix() values instead
 * (those already carry each object's origin translation) and stored
 * qrDecompose's translate as left/top. Against real fabric that
 * double-applies the selection centre: a group drag wrote entries
 * like (1661, 250) for an object sitting at (820, 200), poisoning
 * the positions map - the next rebuild (a delete, an undo, any edit)
 * threw the group members off the check. That is the mechanism
 * behind the owner's repeated "deleting still scatters designs"
 * (2026-11-04): the delete was only the rebuild that exposed it.
 * Probe-verified exact against real fabric 5.5.2 for plain, rotated,
 * centre-origin and group-scaled children.
 */
function absoluteSelectionChildPosition(child: any, selection: any): { left: number; top: number; scaleX: number; scaleY: number; angle: number } | null {
    try {
        if (!child || !selection) return null;
        const util = (fabric as any).util;
        if (!util || typeof util.multiplyTransformMatrices !== 'function' || typeof util.applyTransformToObject !== 'function') return null;
        if (typeof selection.calcOwnMatrix !== 'function' || typeof child.calcOwnMatrix !== 'function') return null;
        if (typeof (fabric as any).Object !== 'function') return null;
        const m = util.multiplyTransformMatrices(selection.calcOwnMatrix(), child.calcOwnMatrix());
        const probe = new (fabric as any).Object({
            width: child.width, height: child.height,
            strokeWidth: child.strokeWidth,
            originX: child.originX, originY: child.originY
        });
        util.applyTransformToObject(probe, m);
        if (typeof probe.left !== 'number' || typeof probe.top !== 'number') return null;
        return {
            left: probe.left, top: probe.top,
            scaleX: probe.scaleX ?? 1, scaleY: probe.scaleY ?? 1, angle: probe.angle ?? 0
        };
    } catch { return null; }
}

/**
 * Harvest the canvas's LIVE arrangement into the positions map
 * (owner defect, 2026-11-04: "when I selected something and delete
 * it, it scatter all other edits I made"). The renderer rebuilds a
 * canvas from the recorded positions map, and a deletion always
 * triggers that rebuild — but the map only learns moves that fire
 * object:modified. fabric silently rewrites object transforms
 * around selection changes (ActiveSelection create/discard
 * decomposes children back to absolute coordinates without any
 * event), so by the time a delete lands, the live canvas and the
 * recorded map can disagree — and the rebuild snaps every
 * silently-moved survivor back to its recorded (stale) spot. The
 * live canvas is the arrangement the user actually made (the same
 * principle the back's "My Default Back" capture already follows),
 * so every delete route harvests it into the map FIRST, copy-on-
 * write, and only then removes anything. Ghost placeholders are
 * render-time derivatives and are never harvested.
 */
function harvestLivePositions(canvas: any, write: (entries: Record<string, { left: number; top: number; scaleX: number; scaleY: number; angle: number }>) => void): void {
    if (!canvas || (canvas as any).__isDisposed) return;
    const entries: Record<string, { left: number; top: number; scaleX: number; scaleY: number; angle: number }> = {};
    const put = (id: any, t: any) => {
        if (typeof id !== 'string' || !id || id.startsWith('ghost:')) return; // legacy ghost-prefixed ids (front ghosts abolished 2026-11-04) never enter the positions map
        entries[id] = {
            left: t.left ?? 0, top: t.top ?? 0,
            scaleX: t.scaleX ?? 1, scaleY: t.scaleY ?? 1, angle: t.angle ?? 0
        };
    };
    const decompose = (sel: any) => {
        try {
            const children = sel.getObjects ? sel.getObjects() : (sel._objects || []);
            children.forEach((child: any) => {
                if (!child?.id) return;
                // A grouped child's own left/top are group-relative;
                // only the canonical decompose yields its canvas
                // position. When it cannot run (test doubles), fall
                // back to the child's stored transform rather than
                // writing nothing.
                put(child.id, absoluteSelectionChildPosition(child, sel) ?? child);
            });
        } catch { /* a selection that cannot be decomposed contributes nothing */ }
    };
    (canvas.getObjects ? canvas.getObjects() : []).forEach((obj: any) => {
        if (!obj) return;
        if (obj.type === 'activeSelection') { decompose(obj); return; }
        put(obj.id, obj);
    });
    const active = canvas.getActiveObject ? canvas.getActiveObject() : null;
    if (active && active.type === 'activeSelection') decompose(active);
    if (Object.keys(entries).length) write(entries);
}

/** Delete the canvas's active object(s); returns how many were removed. */
function deleteCanvasSelection(canvas: any, addDeletedIds: (ids: string[]) => void, harvest?: (canvas: any) => void): number {
    if (!canvas || (canvas as any).__isDisposed) return 0;
    // Before anything is removed: pin the live arrangement into the
    // positions map, so the rebuild this deletion triggers repaints
    // the survivors exactly where they are (see harvestLivePositions).
    if (harvest) harvest(canvas);
    const active = canvas.getActiveObject();
    if (!active || (active as any).isEditing) return 0;
    let removed = 0;
    if ((active as any).type === 'activeSelection') {
        const objs = active.getObjects ? [...active.getObjects()] : [...((active as any)._objects || [])];
        const ids = objs.map((o: any) => o.id).filter(Boolean);
        if (ids.length) addDeletedIds(ids);
        objs.forEach((o: any) => { canvas.remove(o); removed++; });
        canvas.discardActiveObject();
    } else {
        if ((active as any).id) addDeletedIds([(active as any).id]);
        canvas.remove(active);
        removed = 1;
    }
    canvas.requestRenderAll();
    return removed;
}

/**
 * Saved-arrangement lookup for a back object id, with the legacy
 * fallback for the grouped panel: arrangements saved before the
 * security panel (and the eCheck instructions box in its place)
 * became single fabric Groups keyed the panel's FRAME on its own —
 * and the frame is exactly what a user drags — so the frame's entry
 * now positions the whole group until the group earns its own entry
 * under its group id. Legacy per-part entries for the panel's other
 * parts are simply never looked up again (ignored gracefully).
 */
function backPositionFor(positions: Record<string, any>, id: string, backHeight?: number): any {
    if (!positions) return undefined;
    if (positions[id]) return positions[id];
    if (id === 'security-panel') return positions['security-box-frame'];
    if (id === 'echeck-instructions-panel') return positions['echeck-instructions-frame'];
    // The mobile-deposit block became one fabric Group (2026-11-04):
    // arrangements saved while its tick box and label were siblings
    // hold per-piece entries and no group entry — synthesize the
    // group's entry from them (the box's entry anchors; see
    // mobileDepositBlockEntryFromLegacy) so a saved arrangement
    // lands the unified block where the owner left the pieces.
    if (id === MOBILE_DEPOSIT_BLOCK_ID) return mobileDepositBlockEntryFromLegacy(positions, backHeight);
    return undefined;
}

/** The object's four absolute corners under the given (or its own) transform. */
function objectQuadCorners(obj: any, matrix?: any): { x: number; y: number }[] {
    const m = matrix || obj.calcTransformMatrix();
    const w = obj.width ?? 0;
    const h = obj.height ?? 0;
    return [
        { x: -w / 2, y: -h / 2 }, { x: w / 2, y: -h / 2 },
        { x: w / 2, y: h / 2 }, { x: -w / 2, y: h / 2 }
    ].map(p => {
        const t = fabric.util.transformPoint(new fabric.Point(p.x, p.y), m);
        return { x: t.x, y: t.y };
    });
}

/** Which member of an ActiveSelection a canvas point falls on (if any). */
function childOfSelectionAtPoint(selection: any, point: { x: number; y: number }): any | null {
    const selMatrix = selection.calcTransformMatrix();
    const children = selection.getObjects ? selection.getObjects() : (selection._objects || []);
    for (let i = children.length - 1; i >= 0; i--) {
        const child = children[i];
        if (!child) continue;
        const m = fabric.util.multiplyTransformMatrices(selMatrix, child.calcTransformMatrix());
        if (pointInQuad(point, objectQuadCorners(child, m))) return child;
    }
    return null;
}

/**
 * Topmost selectable, id-carrying canvas object under a point (or the
 * ActiveSelection member under it). Id-less furniture is skipped: it
 * cannot be position-tracked or deleted through the ids flow.
 */
function hitTestCanvasObject(canvas: any, point: { x: number; y: number }): any | null {
    const objects = canvas.getObjects ? canvas.getObjects() : [];
    for (let i = objects.length - 1; i >= 0; i--) {
        const obj = objects[i];
        if (!obj || obj.selectable === false || obj.evented === false) continue;
        if ((obj as any).type === 'activeSelection') {
            const child = childOfSelectionAtPoint(obj, point);
            if (child) return child;
            continue;
        }
        if (!(obj as any).id) continue;
        if (pointInQuad(point, objectQuadCorners(obj))) return obj;
    }
    return null;
}

/**
 * Wire a canvas for the mobile selection UX:
 * - selection counts are reported via onSelectionChange (drives the
 *   floating selection action bar);
 * - in multi-select mode, a TAP (pointer down/up within 8px) toggles
 *   the tapped object in/out of the selection. The hit test runs on
 *   capture-phase DOM listeners, BEFORE fabric's own press handling
 *   can change the selection, so the pre-tap selection is known
 *   exactly. Drags (moved > 8px) are left entirely to fabric, so a
 *   built-up group still drags as a group.
 * Returns a detach function.
 */
function attachTouchSelection(canvas: any, opts: {
    isMulti: () => boolean;
    onSelectionChange: (count: number) => void;
    /**
     * Context actions (long-press on touch, right-click on desktop):
     * fired with the object under the press once it is selected.
     * The browser's own context menu is always suppressed on the
     * canvas; the menu itself is the caller's UI.
     */
    onContextAction?: (info: { object: any; clientX: number; clientY: number; via: 'longpress' | 'contextmenu' }) => void;
    /**
     * Shared camera (pinch view) control for the touch gesture
     * map: empty-area pan while zoomed + double-tap zoom toggle.
     * Optional - a canvas without it keeps selection-only wiring.
     */
    camera?: {
        getView: () => PinchView;
        getOrigin: () => PinchPoint;
        getClampBox?: () => { boxW: number; boxH: number; visualW: number; visualH: number } | null;
        applyView: (view: PinchView) => void;
        commitView: (view: PinchView) => void;
    };
}): () => void {
    const sync = () => {
        try { opts.onSelectionChange(canvas.getActiveObjects ? canvas.getActiveObjects().length : 0); }
        catch { /* canvas mid-dispose */ }
    };
    canvas.on('selection:created', sync);
    canvas.on('selection:updated', sync);
    canvas.on('selection:cleared', sync);

    // fabric 5.5's interaction element is `upperCanvasEl` — there is
    // no `upperCanvas` property (that misnomer silently disabled this
    // wiring in real browsers, and its unguarded twin in the pinch
    // wiring crashed the app on mount, 2026-11-04).
    const el: HTMLElement | null = canvas.upperCanvasEl || null;

    // --- Touch move-first precedence (utils/touchDrag) ---
    // fabric 5.5 only lets a press grab a control when the target
    // was ALREADY selected, and on a tiny object (the memo line is
    // the everyday case) the corner touch zones cover the whole
    // body - so the natural phone flow (tap to select, then touch
    // again to drag the thing you just selected) STRETCHES it
    // instead of moving it. For TOUCH presses only: widen fabric's
    // invisible hit tolerance while the press lands (a finger is
    // blunter than these objects are small; visible padding stays
    // 0), and if fabric still set the gesture up as a scale/rotate
    // on a body touch of a tiny target, rewrite the live transform
    // into the exact drag setup fabric itself uses for presses on
    // unselected targets. Mouse presses never set touchPressLive,
    // so desktop handle behaviour is untouched. Long-press, tap
    // toggles and the selection pill read the same press stream
    // and are unaffected: they key off movement distance, and a
    // rewritten drag is still a drag to them.
    let touchPressLive = false;
    const displayScale = () => {
        if (!el || !canvas.getWidth) return 1;
        const w = el.getBoundingClientRect().width;
        const logical = canvas.getWidth();
        return logical > 0 && w > 0 ? w / logical : 1;
    };
    const tdDown = (ev: any) => {
        if (ev.pointerType !== 'touch') return;
        touchPressLive = true;
        try { canvas.targetFindTolerance = touchFindTolerance(displayScale()); } catch { /* stub canvases */ }
    };
    const tdUp = () => {
        if (!touchPressLive) return;
        touchPressLive = false;
        try { canvas.targetFindTolerance = 0; } catch { /* stub canvases */ }
    };
    const onFabricDown = (opt: any) => {
        if (!touchPressLive) return;
        const t = opt && opt.transform;
        if (!t || !t.target || t.target.isEditing) return;
        const scale = displayScale();
        let inBody = false;
        try { inBody = !!opt.pointer && pointInQuad(opt.pointer, objectQuadCorners(t.target)); } catch { inBody = false; }
        const decided = resolveTouchDragAction({
            action: t.action,
            targetScreenW: (t.target.getScaledWidth ? t.target.getScaledWidth() : 0) * scale,
            targetScreenH: (t.target.getScaledHeight ? t.target.getScaledHeight() : 0) * scale,
            pointerInBody: inBody
        });
        if (decided === 'drag') {
            t.action = 'drag';
            t.actionHandler = (fabric as any).controlsUtils.dragHandler;
        }
    };
    canvas.on('mouse:down', onFabricDown);

    // --- Camera gestures + held-finger stability (touch only) ---
    // The rest of Princewill's gesture map, wired on the same
    // press stream (utils/touchDrag holds the pure decisions):
    // - STILL FINGER: a held touch that has not travelled past
    //   the jitter threshold is inert - its early moves are
    //   swallowed before fabric can start a drag, so nothing
    //   moves, scales or churns until the press becomes a real
    //   drag, a long-press (~500ms, the tracker's call), or a tap.
    // - EMPTY-AREA PAN: while the camera is zoomed in, a drag
    //   starting on empty canvas pans the camera (fabric's
    //   marquee is suppressed for that gesture via
    //   canvas.selection; a tap still clears the selection -
    //   fabric's clear path does not consult the flag). At fit
    //   zoom the marquee rules, exactly as before.
    // - DOUBLE-TAP: toggles the camera between fit and an
    //   anchored 2.5x zoom at the tap point. Disambiguation from
    //   tap-to-select: the FIRST tap selects as always; only when
    //   a second tap completes the pair does the camera move -
    //   and that release's multi-select toggle is suppressed
    //   through the same flag the long-press menu uses, so the
    //   pair never also toggles an object out of the selection.
    let tapStart: { at: number; x: number; y: number; pointer: { x: number; y: number } } | null = null;
    let lastTap: TapStamp | null = null;
    let jitter: { x: number; y: number; passed: boolean } | null = null;
    let pan: { startX: number; startY: number; view: PinchView; engaged: boolean } | null = null;
    const clampCameraView = (v: PinchView): PinchView => {
        const cam = opts.camera;
        const box = cam?.getClampBox?.();
        if (!cam || !box) return v;
        const origin = cam.getOrigin();
        const frame = el ? pinchViewportFrame(el, origin) : null;
        return frame
            ? clampPinchPan(v, frame.w, frame.h, box.visualW, box.visualH, 48, frame.offsetX, frame.offsetY)
            : clampPinchPan(v, box.boxW, box.boxH, box.visualW, box.visualH);
    };
    const toggleCameraZoom = (clientX: number, clientY: number) => {
        const cam = opts.camera;
        if (!cam) return;
        const view = cam.getView();
        const next = view.k > 1.05
            ? { ...IDENTITY_PINCH_VIEW }
            : clampCameraView(anchorZoom(view, cam.getOrigin(), { x: clientX, y: clientY }, DOUBLE_TAP_ZOOM));
        cam.applyView(next);
        cam.commitView(next);
    };
    const dtDown = (ev: any) => {
        if (ev.pointerType !== 'touch' || ev.isPrimary === false || pinchLive) return;
        tapStart = { at: Date.now(), x: ev.clientX ?? 0, y: ev.clientY ?? 0, pointer: canvas.getPointer(ev) };
        jitter = { x: ev.clientX ?? 0, y: ev.clientY ?? 0, passed: false };
    };
    const dtUp = (ev: any) => {
        if (ev.pointerType !== 'touch') return;
        const start = tapStart;
        tapStart = null;
        jitter = null;
        // A pinch release, or a press the long-press menu consumed,
        // is not a tap - and it breaks any half-built pair.
        if (!start || pinchLive || suppressTapToggle) { lastTap = null; return; }
        const pointer = canvas.getPointer(ev);
        const moved = Math.hypot(pointer.x - start.pointer.x, pointer.y - start.pointer.y);
        if (moved > LONG_PRESS_TOLERANCE_PX || Date.now() - start.at >= LONG_PRESS_MS) { lastTap = null; return; }
        const stamp: TapStamp = { at: start.at, x: start.x, y: start.y };
        if (opts.camera && doubleTapMatch(lastTap, stamp)) {
            lastTap = null;
            // Consume this release for the camera: the selection
            // toggle below must not ALSO fire for it (onUp checks
            // this flag first; dtUp is registered before onUp).
            suppressTapToggle = true;
            toggleCameraZoom(start.x, start.y);
        } else {
            lastTap = stamp;
        }
    };
    const panDown = (ev: any) => {
        pan = null;
        if (!opts.camera || ev.pointerType !== 'touch' || ev.isPrimary === false || pinchLive) return;
        const pointer = canvas.getPointer(ev);
        const hit = hitTestCanvasObject(canvas, pointer);
        let fabricTarget: any = null;
        try { fabricTarget = canvas.findTarget ? canvas.findTarget(ev) : null; } catch { fabricTarget = null; }
        if (resolveEmptyAreaGesture(opts.camera.getView().k, !!(hit || fabricTarget)) !== 'pan') return;
        pan = { startX: ev.clientX ?? 0, startY: ev.clientY ?? 0, view: opts.camera.getView(), engaged: false };
        // No marquee for this gesture; fabric's tap-to-clear path
        // does not consult this flag, so a tap still deselects.
        try { canvas.selection = false; } catch { /* stub canvases */ }
    };
    const panMove = (ev: any) => {
        if (!pan || ev.pointerType !== 'touch') return;
        const dx = (ev.clientX ?? 0) - pan.startX;
        const dy = (ev.clientY ?? 0) - pan.startY;
        if (!pan.engaged && Math.hypot(dx, dy) <= STILL_FINGER_TOLERANCE_PX) return;
        pan.engaged = true;
        ev.preventDefault();
        ev.stopPropagation();
        const cam = opts.camera;
        if (!cam) return;
        cam.applyView(clampCameraView({ k: pan.view.k, tx: pan.view.tx + dx, ty: pan.view.ty + dy }));
    };
    const panUp = () => {
        if (!pan) return;
        const wasEngaged = pan.engaged;
        pan = null;
        try { canvas.selection = true; } catch { /* stub canvases */ }
        if (wasEngaged && opts.camera) opts.camera.commitView(opts.camera.getView());
    };
    // Held-finger stability: swallow the touchmove stream while
    // the press is inside the jitter threshold, so fabric never
    // sees a tremor as the start of a drag. (fabric drives touch
    // drags from touchmove; the pinch wiring's capture handler on
    // this same element proves capture+stopPropagation reaches
    // fabric first.) Past the threshold the press is a real drag
    // and every move flows.
    const guardTouchMove = (ev: TouchEvent) => {
        if (!jitter || jitter.passed || pinchLive) return;
        const t = ev.touches && ev.touches[0];
        if (!t) return;
        if (isSubThresholdJitter(t.clientX - jitter.x, t.clientY - jitter.y)) {
            ev.preventDefault();
            ev.stopPropagation();
        } else {
            jitter.passed = true;
        }
    };

    let press: { x: number; y: number; hit: any; pre: any[] } | null = null;
    const onDown = (ev: any) => {
        if (pinchLive || ev.isPrimary === false) { press = null; return; }
        if (!opts.isMulti()) { press = null; return; }
        const pointer = canvas.getPointer(ev);
        press = {
            x: pointer.x, y: pointer.y,
            hit: hitTestCanvasObject(canvas, pointer),
            pre: canvas.getActiveObjects ? [...canvas.getActiveObjects()] : []
        };
    };
    const onUp = (ev: any) => {
        const p = press;
        press = null;
        // Releases that belong to a pinch gesture are never taps.
        if (pinchLive) return;
        // A long-press that just opened the menu must not ALSO read
        // as a multi-select tap on release (it would toggle the very
        // object the menu is acting on straight back out).
        if (suppressTapToggle) { suppressTapToggle = false; return; }
        if (!p || !opts.isMulti() || !p.hit) return;
        const pointer = canvas.getPointer(ev);
        if (Math.hypot(pointer.x - p.x, pointer.y - p.y) > 8) return; // a drag, not a tap
        if ((p.hit as any).isEditing) return;
        const set = [...p.pre];
        const idx = set.indexOf(p.hit);
        if (idx >= 0) set.splice(idx, 1); else set.push(p.hit);
        canvas.discardActiveObject();
        if (set.length === 1) canvas.setActiveObject(set[0]);
        else if (set.length > 1) canvas.setActiveObject(new fabric.ActiveSelection(set, { canvas }));
        canvas.requestRenderAll();
        sync();
    };
    // --- Long-press + right-click context menu ---
    // A near-stationary press held LONG_PRESS_MS selects the object
    // under it and asks the caller to open the menu; any drift past
    // the tolerance is a drag and cancels it (see LongPressTracker).
    const tracker = new LongPressTracker();
    let lpTimer: ReturnType<typeof setTimeout> | null = null;
    let lpHit: any = null;
    let lpClient = { x: 0, y: 0 };
    let suppressTapToggle = false;
    const clearLpTimer = () => { if (lpTimer) { clearTimeout(lpTimer); lpTimer = null; } };
    const selectForMenu = (hit: any) => {
        const actives = canvas.getActiveObjects ? canvas.getActiveObjects() : [];
        if (!actives.includes(hit)) {
            canvas.discardActiveObject();
            canvas.setActiveObject(hit);
        }
        canvas.requestRenderAll();
        sync();
    };
    const lpDown = (ev: any) => {
        if (!opts.onContextAction) return;
        if (pinchLive || ev.isPrimary === false) return; // a pinch's second finger is not a press
        if (typeof ev.button === 'number' && ev.button !== 0) return; // right button: contextmenu path
        const pointer = canvas.getPointer(ev);
        lpHit = hitTestCanvasObject(canvas, pointer);
        lpClient = { x: ev.clientX ?? 0, y: ev.clientY ?? 0 };
        suppressTapToggle = false;
        tracker.begin(pointer.x, pointer.y, Date.now());
        clearLpTimer();
        if (!lpHit) return;
        lpTimer = setTimeout(() => {
            lpTimer = null;
            if (!tracker.isDue(Date.now()) || !lpHit) return;
            suppressTapToggle = true;
            lastTap = null; // a menu interaction breaks any tap pair
            selectForMenu(lpHit);
            opts.onContextAction?.({ object: lpHit, clientX: lpClient.x, clientY: lpClient.y, via: 'longpress' });
        }, LONG_PRESS_MS);
    };
    const lpMove = (ev: any) => {
        if (!tracker.isActive) return;
        const pointer = canvas.getPointer(ev);
        if (tracker.move(pointer.x, pointer.y)) clearLpTimer();
    };
    const lpUp = () => { clearLpTimer(); tracker.reset(); lpHit = null; };
    const onCtxMenu = (ev: any) => {
        // The browser's own menu never appears on the canvases.
        ev.preventDefault();
        if (!opts.onContextAction) return;
        const pointer = canvas.getPointer(ev);
        const hit = hitTestCanvasObject(canvas, pointer);
        if (!hit) return;
        selectForMenu(hit);
        opts.onContextAction({ object: hit, clientX: ev.clientX ?? 0, clientY: ev.clientY ?? 0, via: 'contextmenu' });
    };

    const hasPointer = typeof window !== 'undefined' && typeof (window as any).PointerEvent === 'function';
    const downEvt = hasPointer ? 'pointerdown' : 'mousedown';
    const upEvt = hasPointer ? 'pointerup' : 'mouseup';
    const moveEvt = hasPointer ? 'pointermove' : 'mousemove';
    // --- Pinch coordination ---
    // A pinch starts with a SECOND finger, whose pointerdown looks
    // like a fresh press to the trackers above (it overwrites `press`
    // and arms a long-press). The pinch wiring (utils/pinchZoom)
    // announces itself on this element the moment the gesture is
    // recognized: abandon all press tracking, and ignore releases
    // until the gesture fully ends - none of them are taps.
    let pinchLive = false;
    const onPinchStart = () => {
        pinchLive = true;
        press = null;
        clearLpTimer();
        tracker.reset();
        lpHit = null;
        // Camera-gesture state dies with the pinch too: no tap
        // pair, no jitter tracking, and an armed empty-area pan
        // hands the view over to the pinch (restoring marquee).
        tapStart = null;
        lastTap = null;
        jitter = null;
        if (pan) {
            pan = null;
            try { canvas.selection = true; } catch { /* stub canvases */ }
        }
    };
    const onPinchEnd = () => { pinchLive = false; };
    if (el) {
        // Camera-gesture listeners register FIRST: dtUp must run
        // before onUp so a recognized double-tap can consume the
        // release (via suppressTapToggle) before the multi-select
        // toggle sees it.
        el.addEventListener(downEvt, dtDown, true);
        el.addEventListener(upEvt, dtUp, true);
        el.addEventListener(downEvt, panDown, true);
        el.addEventListener(moveEvt, panMove, true);
        el.addEventListener(upEvt, panUp, true);
        el.addEventListener('pointercancel', panUp, true);
        el.addEventListener('touchmove', guardTouchMove, { capture: true, passive: false });
        el.addEventListener(downEvt, onDown, true);
        el.addEventListener(upEvt, onUp, true);
        el.addEventListener(downEvt, tdDown, true);
        el.addEventListener(upEvt, tdUp, true);
        el.addEventListener('pointercancel', tdUp, true);
        el.addEventListener(downEvt, lpDown, true);
        el.addEventListener(moveEvt, lpMove, true);
        el.addEventListener(upEvt, lpUp, true);
        el.addEventListener('pointercancel', lpUp, true);
        el.addEventListener('contextmenu', onCtxMenu, true);
        el.addEventListener('oc:pinchstart', onPinchStart);
        el.addEventListener('oc:pinchend', onPinchEnd);
    }
    return () => {
        canvas.off('selection:created', sync);
        canvas.off('selection:updated', sync);
        canvas.off('selection:cleared', sync);
        canvas.off('mouse:down', onFabricDown);
        tdUp();
        clearLpTimer();
        if (el) {
            el.removeEventListener(downEvt, dtDown, true);
            el.removeEventListener(upEvt, dtUp, true);
            el.removeEventListener(downEvt, panDown, true);
            el.removeEventListener(moveEvt, panMove, true);
            el.removeEventListener(upEvt, panUp, true);
            el.removeEventListener('pointercancel', panUp, true);
            el.removeEventListener('touchmove', guardTouchMove, { capture: true } as EventListenerOptions);
            el.removeEventListener(downEvt, onDown, true);
            el.removeEventListener(upEvt, onUp, true);
            el.removeEventListener(downEvt, tdDown, true);
            el.removeEventListener(upEvt, tdUp, true);
            el.removeEventListener('pointercancel', tdUp, true);
            el.removeEventListener(downEvt, lpDown, true);
            el.removeEventListener(moveEvt, lpMove, true);
            el.removeEventListener(upEvt, lpUp, true);
            el.removeEventListener('pointercancel', lpUp, true);
            el.removeEventListener('contextmenu', onCtxMenu, true);
            el.removeEventListener('oc:pinchstart', onPinchStart);
            el.removeEventListener('oc:pinchend', onPinchEnd);
        }
    };
}

// --- HELPER COMPONENT ---
const CheckBack = forwardRef<{ getDataURL: () => string | null; captureForExport: () => Promise<string | null>; deleteSelection: () => number; clearSelection: () => void; getArrangement: () => Record<string, { left: number; top: number; scaleX: number; scaleY: number; angle: number }> | null }, {
    securityFeatures: any[],
    paperColor: string,
    selectedBank: string,
    checkStyle: string,
    width: number,
    height: number,
    checkData: any,
    handwritingColor: string,
    signatureStyle: string,
    backSideStyle: string,
    objectPositions: Record<string, any>,
    setObjectPositions: React.Dispatch<React.SetStateAction<Record<string, any>>>,
    signatureImage: string | null,
    deletedIds: string[],
    setDeletedIds: React.Dispatch<React.SetStateAction<string[]>>,
    multiSelect?: boolean,
    onSelectionChange?: (count: number) => void,
    onContextAction?: (info: { object: any; clientX: number; clientY: number; via: 'longpress' | 'contextmenu' }) => void,
    /** Called once per canvas init with the live fabric canvas; the
     *  parent attaches the shared pinch-zoom wiring here and the
     *  returned function detaches it. */
    onCanvasInit?: (canvas: any) => () => void,
    /** Drag start/end on this canvas, so the parent can fade the
     *  studio chrome out of the way mid-edit. */
    onInteractChange?: (active: boolean) => void,
    cameraOps?: {
        getView: () => PinchView;
        getOrigin: () => PinchPoint;
        getClampBox?: () => { boxW: number; boxH: number; visualW: number; visualH: number } | null;
        applyView: (view: PinchView) => void;
        commitView: (view: PinchView) => void;
    }
}>(({ securityFeatures, paperColor, selectedBank, checkStyle, width, height, checkData, handwritingColor, signatureStyle, backSideStyle, signatureImage, objectPositions, setObjectPositions, deletedIds, setDeletedIds, multiSelect, onSelectionChange, onContextAction, onCanvasInit, onInteractChange, cameraOps }, ref) => {
    const backCanvasRef = useRef<HTMLCanvasElement>(null);
    const fabRef = useRef<fabric.Canvas | null>(null);
    const backPositionsRef = useRef(objectPositions);
    // Pin the back canvas's live arrangement into the positions map
    // before a deletion rebuilds from it (see harvestLivePositions).
    const harvestBackPositions = (canvas: any) => {
        harvestLivePositions(canvas, (entries) => {
            backPositionsRef.current = { ...backPositionsRef.current, ...entries };
            setObjectPositions((prev: any) => ({ ...prev, ...entries }));
        });
    };
    // Touch-selection wiring reads these through refs so the canvas
    // init effect never has to re-run when they change.
    const multiRef = useRef(false);
    useEffect(() => { multiRef.current = !!multiSelect; }, [multiSelect]);
    const onSelChangeRef = useRef(onSelectionChange);
    useEffect(() => { onSelChangeRef.current = onSelectionChange; });
    const onCtxRef = useRef(onContextAction);
    useEffect(() => { onCtxRef.current = onContextAction; });
    const onCanvasInitRef = useRef(onCanvasInit);
    useEffect(() => { onCanvasInitRef.current = onCanvasInit; });
    const onInteractRef = useRef(onInteractChange);
    useEffect(() => { onInteractRef.current = onInteractChange; });
    const cameraOpsRef = useRef(cameraOps);
    useEffect(() => { cameraOpsRef.current = cameraOps; });

    // Sync Ref when prop changes (from external updates)
    useEffect(() => {
        backPositionsRef.current = objectPositions;
    }, [objectPositions]);

    useImperativeHandle(ref, () => ({
        getDataURL: () => {
            if (fabRef.current) {
                return fabRef.current.toDataURL({ format: 'png', multiplier: 2 });
            }
            return null;
        },
        captureForExport: async () => {
            const canvas = fabRef.current;
            if (!canvas) return null;
            // Force a full paint before capturing: on phones the back canvas
            // can hold its objects without ever having painted (Page 2 not
            // yet viewed), and a plain capture then returns a blank sheet -
            // which is how front-only PDFs happened.
            canvas.renderAll();
            await new Promise(r => requestAnimationFrame(() => setTimeout(r, 60)));
            canvas.renderAll();
            return canvas.toDataURL({ format: 'png', multiplier: EXPORT_MULTIPLIER });
        },
        // Mobile selection bar: delete the current back selection
        // through the same deleted-ids flow as the keyboard handler.
        deleteSelection: () => {
            const removed = deleteCanvasSelection(fabRef.current, (ids) => setDeletedIds(prev => [...prev, ...ids]), harvestBackPositions);
            onSelChangeRef.current?.(0);
            return removed;
        },
        clearSelection: () => {
            const canvas = fabRef.current;
            if (canvas) {
                canvas.discardActiveObject();
                canvas.requestRenderAll();
            }
            onSelChangeRef.current?.(0);
        },
        // The back arrangement AS ARRANGED on the live canvas (object
        // id -> transform) - what "save this back as my default"
        // captures. Authoritative over the positions state, which can
        // lag a drag that has not committed yet.
        getArrangement: () => {
            const canvas = fabRef.current;
            if (!canvas) return null;
            const map: Record<string, { left: number; top: number; scaleX: number; scaleY: number; angle: number }> = {};
            canvas.getObjects().forEach((obj: any) => {
                if (obj.id) {
                    // A child of a live ActiveSelection reports
                    // GROUP-RELATIVE transforms; a saved default
                    // must hold its ABSOLUTE canvas transform (the
                    // canonical decompose — the same rule as
                    // harvestLivePositions), or the arrangement
                    // poisons the next load exactly the way the
                    // position-application effect once did.
                    const abs = (obj as any).group
                        ? absoluteSelectionChildPosition(obj, (obj as any).group)
                        : null;
                    const t = abs ?? obj;
                    map[obj.id] = {
                        left: t.left ?? 0,
                        top: t.top ?? 0,
                        scaleX: t.scaleX ?? 1,
                        scaleY: t.scaleY ?? 1,
                        angle: t.angle ?? 0
                    };
                }
            });
            return map;
        }
    }));

    const getCurrentPaperColor = () => {
        // A raw hex is a colour in its own right (the design
        // import sets the sampled paper colour directly); preset
        // ids resolve through PAPER_COLORS as before.
        if (typeof paperColor === 'string' && paperColor.startsWith('#')) return paperColor;
        const colorObj = PAPER_COLORS.find(c => c.id === paperColor);
        return colorObj ? colorObj.color : '#f8f4ec';
    };

    useEffect(() => {
        if (!backCanvasRef.current) return;

        // Initialize canvas
        const canvas = new fabric.Canvas(backCanvasRef.current, {
            width,
            height,
            backgroundColor: getCurrentPaperColor(),
            selection: true,
            interactive: true,
            preserveObjectStacking: true
        });
        fabRef.current = canvas;

        const updatePosition = (obj: any) => {
            // Multi-selection (ActiveSelection): record every child
            // at its absolute canvas position (canonical decompose —
            // see absoluteSelectionChildPosition). Without this branch
            // a group drag on the back wrote NOTHING (the selection
            // itself carries no id), so the map kept the pre-drag
            // entries and the next rebuild silently un-dragged the
            // whole group (2026-11-04).
            if (obj && obj.type === 'activeSelection' && typeof obj.getObjects === 'function') {
                const updates: Record<string, { left: number; top: number; scaleX: number; scaleY: number; angle: number }> = {};
                obj.getObjects().forEach((child: any) => {
                    if (!child || !child.id) return;
                    const abs = absoluteSelectionChildPosition(child, obj);
                    if (abs) updates[child.id] = abs;
                });
                if (Object.keys(updates).length) {
                    backPositionsRef.current = { ...backPositionsRef.current, ...updates };
                    setObjectPositions((prev: any) => ({ ...prev, ...updates }));
                }
                return;
            }
            if (obj && (obj as any).id) {
                const newPos = {
                    left: obj.left ?? 0,
                    top: obj.top ?? 0,
                    scaleX: obj.scaleX ?? 1,
                    scaleY: obj.scaleY ?? 1,
                    angle: obj.angle ?? 0
                };
                // Copy-assign, never mutate in place: history
                // snapshots alias this same positions map (the
                // snapshot builder returns state without cloning),
                // so an in-place write silently rewrites snapshots
                // that were already recorded — Undo then "restores"
                // the position the user just dragged to (2026-11-04
                // back-position Repro A).
                backPositionsRef.current = { ...backPositionsRef.current, [(obj as any).id]: newPos };
                setObjectPositions((prev: any) => ({
                    ...prev,
                    [(obj as any).id]: newPos
                }));
            }
        };

        canvas.on('object:modified', (e) => updatePosition(e.target));

        // KEYBOARD CONTROLS FOR BACK SIDE
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!canvas || (canvas as any).__isDisposed) return;
            const activeObj = canvas.getActiveObject();
            // Delete / Backspace
            if (e.key === 'Delete' || e.key === 'Backspace') {
                if (activeObj) {
                    e.preventDefault();
                    // Pin the live arrangement first (see
                    // harvestLivePositions).
                    harvestBackPositions(canvas);
                    if ((activeObj as any).type === 'activeSelection') {
                        (activeObj as any).forEachObject((obj: any) => {
                            if (obj.id) setDeletedIds(prev => [...prev, obj.id]);
                            canvas.remove(obj);
                        });
                        canvas.discardActiveObject();
                    } else {
                        if ((activeObj as any).id) setDeletedIds(prev => [...prev, (activeObj as any).id]);
                        canvas.remove(activeObj);
                    }
                    canvas.requestRenderAll();
                }
            }
            // Arrow keys
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) && activeObj) {
                e.preventDefault();
                const step = e.shiftKey ? 10 : 1;
                if (e.key === 'ArrowUp') activeObj.set('top', (activeObj.top ?? 0) - step);
                if (e.key === 'ArrowDown') activeObj.set('top', (activeObj.top ?? 0) + step);
                if (e.key === 'ArrowLeft') activeObj.set('left', (activeObj.left ?? 0) - step);
                if (e.key === 'ArrowRight') activeObj.set('left', (activeObj.left ?? 0) + step);
                activeObj.setCoords();
                updatePosition(activeObj);
                canvas.requestRenderAll();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        // Mobile selection: counts for the selection action bar +
        // tap-to-toggle multi-select (see attachTouchSelection).
        const detachTouchSelection = attachTouchSelection(canvas, {
            isMulti: () => multiRef.current,
            onSelectionChange: (n) => onSelChangeRef.current?.(n),
            onContextAction: (info) => onCtxRef.current?.(info),
            camera: cameraOpsRef.current
        });

        // Parent-owned extras on this canvas: the shared pinch-zoom
        // wiring, and drag start/end reports for chrome dimming.
        const detachExtra = onCanvasInitRef.current?.(canvas);
        const interactOn = () => onInteractRef.current?.(true);
        const interactOff = () => onInteractRef.current?.(false);
        canvas.on('object:moving', interactOn);
        canvas.on('object:scaling', interactOn);
        canvas.on('object:rotating', interactOn);
        canvas.on('object:modified', interactOff);
        canvas.on('mouse:up', interactOff);

        return () => {
            // A back canvas disposed mid-drag must not orphan the
            // dim either: report the interaction over before the
            // listeners and canvas die.
            interactOff();
            detachTouchSelection();
            if (detachExtra) detachExtra();
            canvas.off('object:moving', interactOn);
            canvas.off('object:scaling', interactOn);
            canvas.off('object:rotating', interactOn);
            canvas.off('object:modified', interactOff);
            canvas.off('mouse:up', interactOff);
            document.removeEventListener('keydown', handleKeyDown);
            if (fabRef.current) {
                fabRef.current.dispose();
                fabRef.current = null;
            }
        };
    }, [width, height, setObjectPositions]); // Initialize once


    // The arrangement this canvas currently PAINTS, mirrored so the
    // position-application effect below can tell a position REMOVAL
    // (an entry the canvas still honours but state no longer holds)
    // from a plain move.
    const appliedBackPositionsRef = useRef<Record<string, any>>({});

    // Full regeneration of the back from the current design state.
    // Positions are read through backPositionsRef (kept in sync with
    // the objectPositions prop by the effect above), so a rebuild
    // always paints the latest recorded arrangement.
    const regenerateBack = () => {
        const canvas = fabRef.current;
        if (!canvas) return;

        // Save selection
        const activeObj = canvas.getActiveObject();
        const activeId = activeObj ? (activeObj as any).id : null;

        canvas.clear();
        canvas.setBackgroundColor(getCurrentPaperColor(), () => canvas.renderAll());

        try {
            // The back belongs to the selected design: its display name
            // (bank template, premium template, or the bank name on the
            // check) and a pale tint of its primary colour.
            const backBankTemplate = selectedBank ? (bankSpecificTemplates as any)[selectedBank] : null;
            const backPremiumTemplate = selectedBank ? PREMIUM_TEMPLATES.find((p: any) => p.id === selectedBank) : null;
            const designName = backBankTemplate?.name || backPremiumTemplate?.name || checkData.bankName || '';
            const designPrimary = backBankTemplate?.specs?.colors?.primary || backPremiumTemplate?.colors?.primary || null;
            const designAccent = backBankTemplate?.specs?.colors?.accent || backPremiumTemplate?.colors?.accent || null;
            // Per-design back (2026-11-03): the resolved variant for
            // this front design — its tint, panel lines (sourced only
            // from the enabled front features or the template's own
            // specs), watermark, frame and extras — built by the ONE
            // shared builder the export path uses too, so the on-screen
            // back and the exported back can never drift apart. The
            // backSideStyle prop arriving here is already the EFFECTIVE
            // deposit style (user override or variant default).
            const { config: backConfig, depositConfig: backDeposit } = buildBackEngineConfig({
                checkStyle,
                selectedBank,
                securityFeatures,
                designName,
                designPrimary,
                designAccent,
                effectiveStyle: backSideStyle,
                accountNumber: checkData.accountNumber,
                accountHolderName: checkData.accountHolderName,
                routingNumber: checkData.routingNumber,
                checkNumber: checkData.checkNumber
            });
            const backObjects = CheckBackEngine.generateCheckBack(width, height, backConfig, 72, backDeposit, signatureImage);

            backObjects.forEach(obj => {
                const id = (obj as any).id;
                if (id) {
                    const pos = backPositionFor(backPositionsRef.current, id, height);
                    if (pos) {
                        obj.set({
                            left: pos.left,
                            top: pos.top,
                            scaleX: pos.scaleX ?? 1,
                            scaleY: pos.scaleY ?? 1,
                            // A saved arrangement moves/scales the
                            // object, but a specimen text's DIRECTION
                            // is the engine's +90 invariant — a stale
                            // saved angle must never turn it backwards
                            // (see specimenAngleFor).
                            angle: specimenAngleFor(id, pos.angle) ?? 0
                        });
                        obj.setCoords();
                    }
                }

                if (id) {
                    obj.set({
                        selectable: true,
                        evented: true,
                        hasControls: true,
                        hasBorders: true
                    });
                }
                canvas.add(obj);
            });

            // FILTER OUT DELETED OBJECTS
            if (deletedIds && deletedIds.length > 0) {
                const currentObjects = canvas.getObjects();
                currentObjects.forEach((obj: any) => {
                    if (!obj.id) return;
                    // The mobile-deposit block is one group now: a
                    // deletion recorded against either of its pieces
                    // while they were siblings (or against the group
                    // itself) deletes the whole block — a piece must
                    // not resurrect inside the group.
                    const blockDeleted = obj.id === MOBILE_DEPOSIT_BLOCK_ID
                        && (deletedIds.includes(MOBILE_CHECKBOX_ID) || deletedIds.includes(MOBILE_INSTRUCTION_ID));
                    if (deletedIds.includes(obj.id) || blockDeleted) {
                        canvas.remove(obj);
                    }
                });
            }

            if (activeId) {
                const newActive = canvas.getObjects().find(o => (o as any).id === activeId);
                if (newActive) {
                    canvas.setActiveObject(newActive);
                }
            }

            canvas.requestRenderAll();
            // The redraw may have restored (or dropped) the selection:
            // keep the mobile selection bar's count truthful.
            onSelChangeRef.current?.(canvas.getActiveObjects().length);
        } catch (err) {
            console.error('Check Back Render Error:', err);
        }
        appliedBackPositionsRef.current = { ...(objectPositions || {}) };
    };

    // Effect for content update: design/data changes regenerate the
    // back wholesale.
    useEffect(() => {
        regenerateBack();
    }, [securityFeatures, paperColor, selectedBank, checkStyle, width, height, checkData, handwritingColor, signatureStyle, signatureImage, backSideStyle, deletedIds]);

    // Position-only state changes (2026-11-04 back-position Repros
    // A/B/C): Undo/Redo restores, the saved-default-back load-apply,
    // and clearing the default back all change ONLY the shared
    // positions map — no content dep above fires, and when the
    // effective deposit-style string is unchanged no rebuild happens
    // at all, so the canvas kept painting the old arrangement
    // (dragged panel surviving Undo; the saved default invisible
    // after a reload until a design switch forced a rebuild). This
    // effect makes position application deterministic: entries that
    // moved are re-applied to the live canvas objects in place
    // (selection and z-order preserved), and if an entry the canvas
    // currently honours has been REMOVED from state, the back
    // regenerates so the object returns to its design placement.
    useEffect(() => {
        const canvas = fabRef.current;
        if (!canvas) return;
        const positions = objectPositions || {};
        const applied = appliedBackPositionsRef.current;
        const anyRemoved = Object.keys(applied).some(id => !(id in positions));
        if (anyRemoved) {
            regenerateBack();
            return;
        }
        let moved = false;
        canvas.getObjects().forEach((obj: any) => {
            if (!obj || !obj.id) return;
            // A child of a live ActiveSelection carries
            // GROUP-RELATIVE left/top/scale (fabric sets obj.group
            // on it, and getObjects() still lists it). The recorded
            // positions are ABSOLUTE: writing them into the child's
            // relative slots makes fabric's discardActiveObject()
            // compose the selection transform over them a second
            // time, throwing the child off the check — the
            // mobile-deposit block (checkbox + its rotated label,
            // the natural pair to multi-select and enlarge
            // together) landed at x≈1416 after a +20 drag and at
            // x≈1910 with scale 3.06 (= 1.75²) after a group scale,
            // i.e. it "disappeared" (owner report, 2026-11-04).
            // Grouped children are skipped here: fabric's own
            // discard decomposes them to exactly the recorded
            // absolute transforms (probe-verified against real
            // fabric 5.5.2), so the canvas and the map agree the
            // moment the selection ends.
            // (The guard tests the group's SHAPE — a real fabric
            // group/selection object carrying its children — not
            // mere property presence: test doubles answer every
            // property with a stub function, and a truthiness check
            // would skip every object there.)
            const grp = (obj as any).group;
            if (grp && typeof grp === 'object' && Array.isArray((grp as any)._objects)) return;
            const pos = backPositionFor(positions, obj.id, height);
            if (!pos) return;
            // Same specimen-direction invariant as the rebuild: a
            // specimen text's target angle is always +90, whatever a
            // stale saved entry says (see specimenAngleFor).
            const targetAngle = specimenAngleFor(obj.id, pos.angle) ?? 0;
            if (obj.left !== pos.left || obj.top !== pos.top
                || (obj.scaleX || 1) !== (pos.scaleX ?? 1)
                || (obj.scaleY || 1) !== (pos.scaleY ?? 1)
                || (obj.angle || 0) !== targetAngle) {
                obj.set({ left: pos.left, top: pos.top, scaleX: pos.scaleX ?? 1, scaleY: pos.scaleY ?? 1, angle: targetAngle });
                if (typeof obj.setCoords === 'function') obj.setCoords();
                moved = true;
            }
        });
        if (moved) canvas.requestRenderAll();
        appliedBackPositionsRef.current = { ...positions };
    }, [objectPositions]);

    return (
        <div style={{
            width,
            height,
            background: getCurrentPaperColor(),
            border: '1px solid #ddd',
            borderRadius: '4px',
            overflow: 'hidden'
        }}>
            <canvas ref={backCanvasRef} />
        </div>
    );
});

// --- SIGNATURE INK (Princewill, 2026-11-04) ---
// "Make sure the signature upload has different color selection
// to match the exact color not just black." The placed (uploaded)
// signature has its OWN ink: preset pen inks, a custom colour for
// an exact match, or "Keep original" - the colour the signature
// was written in, preserved through the clean. This ink is
// deliberately separate from handwritingColor, which drives the
// typed handwriting fields; coupling them meant an uploaded
// signature could only ever come out in the fields' ink.
export const SIGNATURE_INK_PRESETS: Array<{ name: string; val: string }> = [
    { name: 'Black', val: '#111111' },
    { name: 'Ball-pen blue-black', val: '#1b2440' },
    { name: 'Blue', val: '#0047ab' },
    { name: 'Navy', val: '#003366' },
    { name: 'Red', val: '#cc0000' },
    { name: 'Green', val: '#006400' }
];
export const DEFAULT_SIGNATURE_INK = '#1b2440';
/** Snapshot/custom values land here untrusted: only a plain
 * #rrggbb hex is a usable ink; anything else falls back to the
 * default ball-pen ink rather than breaking the colour input. */
export const normalizeSignatureInk = (v: any): string =>
    typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v) ? v.toLowerCase() : DEFAULT_SIGNATURE_INK;

// --- SIGNATURE LAB COMPONENT ---
interface SignatureLabProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (dataUrl: string) => void;
    initialColor?: string;
    /** The studio signature ink's Keep-original mode: the Lab's
     * isolate/paste cleaning preserves the written colour too. */
    inkOriginal?: boolean;
    /** Report ink picks back so the Lab and the signature panel
     * share ONE ink control (the studio's signature ink state). */
    onInkChange?: (color: string, original: boolean) => void;
    signatureImage: string | null;
    setSignatureImage: (url: string | null) => void;
    rawSignatureImage: string | null;
    setRawSignatureImage: (url: string | null) => void;
}

const SignatureLab: React.FC<SignatureLabProps> = ({
    isOpen,
    onClose,
    onSave,
    initialColor = '#000080',
    inkOriginal = false,
    onInkChange,
    signatureImage,
    setSignatureImage,
    rawSignatureImage,
    setRawSignatureImage
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const engineRef = useRef<SignatureCaptureEngine | null>(null);
    const [penStyle, setPenStyle] = useState<'ballpoint' | 'fountain' | 'felt-tip' | 'marker'>('ballpoint');
    const [inkColor, setInkColor] = useState(initialColor);
    // The Lab draws with this ink and reports every pick back to
    // the studio's signature ink, so the pad and the upload panel
    // share one control. Its cleaning (isolate / paste) honours
    // the same mode the panel set: a chosen colour tints, Keep
    // original preserves the written colour.
    const pickLabInk = (c: string) => {
        setInkColor(c);
        engineRef.current?.setInkColor(c);
        onInkChange?.(c, false);
    };
    const labCleanOptions = () => inkOriginal ? { keepOriginalColor: true } : { tintColor: initialColor };

    useEffect(() => {
        if (isOpen && canvasRef.current && !engineRef.current) {
            engineRef.current = new SignatureCaptureEngine(canvasRef.current, 500, 200);
            engineRef.current.setInkColor(inkColor);
            engineRef.current.setPenStyle(penStyle);
        }

        return () => {
            if (engineRef.current) {
                engineRef.current.dispose();
                engineRef.current = null;
            }
        };
    }, [isOpen]);

    const handleSave = () => {
        if (engineRef.current) {
            const dataUrl = engineRef.current.toDataURL({ multiplier: 2 });
            onSave(dataUrl);
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
        }}>
            <div style={{
                backgroundColor: 'white',
                padding: '24px',
                borderRadius: '12px',
                width: '550px',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', color: '#1a3a5c' }}>Signature Lab</h3>
                    <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '20px', color: '#666' }}>&times;</button>
                </div>

                <div style={{ marginBottom: '16px', display: 'flex', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                        <label style={labelStyle}>Pen Style</label>
                        <select
                            value={penStyle}
                            onChange={(e) => {
                                const style = e.target.value as any;
                                setPenStyle(style);
                                engineRef.current?.setPenStyle(style);
                            }}
                            style={inputStyle}
                        >
                            <option value="ballpoint">Ballpoint Pen</option>
                            <option value="fountain">Fountain Pen</option>
                            <option value="felt-tip">Felt Tip</option>
                            <option value="marker">Marker</option>
                        </select>
                    </div>
                    <div style={{ flex: 1 }}>
                        <label style={labelStyle}>Ink Color</label>
                        <div style={{ display: 'flex', gap: '6px', marginTop: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                            {SIGNATURE_INK_PRESETS.map(ink => (
                                <div
                                    key={ink.val}
                                    title={ink.name}
                                    onClick={() => pickLabInk(ink.val)}
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '50%',
                                        backgroundColor: ink.val,
                                        cursor: 'pointer',
                                        border: inkColor.toLowerCase() === ink.val ? '2px solid #3498db' : '2px solid transparent'
                                    }}
                                />
                            ))}
                            <input
                                type="color"
                                aria-label="Custom ink colour"
                                value={normalizeSignatureInk(inkColor)}
                                onChange={(e) => pickLabInk(e.target.value)}
                                style={{ width: '28px', height: '24px', padding: 0, border: '1px solid #cbd5e1', borderRadius: '6px', background: 'none', cursor: 'pointer' }}
                            />
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                    <button
                        onClick={async () => {
                            try {
                                const text = await navigator.clipboard.readText();
                                if (text.startsWith('data:image')) {
                                    setRawSignatureImage(text);
                                    const cleaned = await SignatureExtractorEngine.processSignature(text, labCleanOptions());
                                    setSignatureImage(cleaned);
                                } else {
                                    alert('Clipboard does not contain a valid image data URL. Please copy a signature image first.');
                                }
                            } catch (err) {
                                alert('Clipboard access denied or failed.');
                            }
                        }}
                        style={{ ...actionBtnStyle, backgroundColor: '#3498db', flex: 1, gap: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        📋 Paste from Clipboard
                    </button>
                    <button
                        onClick={async () => {
                            const source = rawSignatureImage || signatureImage;
                            if (source) {
                                const cleaned = await SignatureExtractorEngine.processSignature(source, labCleanOptions());
                                setSignatureImage(cleaned);
                            }
                        }}
                        style={{ ...actionBtnStyle, backgroundColor: '#f39c12', flex: 1, gap: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        ✨ Isolate Signature (AI)
                    </button>
                </div>

                <div style={{
                    border: '2px dashed #cbd5e1',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    marginBottom: '16px',
                    position: 'relative',
                    cursor: 'crosshair'
                }}>
                    <canvas ref={canvasRef} />
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                        onClick={() => engineRef.current?.clear()}
                        style={{ ...actionBtnStyle, backgroundColor: '#64748b', flex: 1 }}
                    >
                        Clear
                    </button>
                    <button
                        onClick={() => engineRef.current?.undo()}
                        style={{ ...actionBtnStyle, backgroundColor: '#94a3b8', flex: 1 }}
                    >
                        Undo
                    </button>
                    <button
                        onClick={handleSave}
                        style={{ ...actionBtnStyle, flex: 2 }}
                    >
                        Save Signature
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============== MAIN COMPONENT ==============
/**
 * One ADDITIONAL logo placed on the check, beyond the primary
 * header logo (MULTIPLE LOGOS, Princewill, voice, 2026-11-04:
 * "most of the check designs I sent have a lot of logo on them. I
 * can choose to add different logos in a check — not when I upload
 * one or want to upload the second one, the second one will erase
 * the first one."). Real designs carry several marks at once, so a
 * check carries a LIST of placed logos: the primary (the header
 * logo, on its long-standing machinery — logoImage / customLogo)
 * plus any number of these instances. Each instance is its own
 * canvas object (id = instanceId, positions in the positions map
 * like any custom object), processed from its library entry with
 * the logo options in force when it was placed — the options are
 * stored on the instance so an account save (which strips images)
 * can re-derive the identical placed image from the entry.
 */
export interface PlacedLogoInstance {
    instanceId: string;
    entryId: string | null;
    name: string;
    /** The processed image as placed. Null only transiently in a
     * restored account save, until it is re-derived from the entry. */
    src: string | null;
    removeBackground: boolean;
    includeWriteups: boolean;
    cropPercent: number | null;
    /** Default seat, used until the positions map has an entry. */
    left: number;
    top: number;
}

const CheckBuilder: React.FC = () => {
    const { isPaid, canExport, useCheckExport, currentPlan, checksRemaining, activatePlan, isEnterprise } = useLicense();
    const { isAuthenticated, openAuthModal } = useAuth();
    const [isExportingServerPdf, setIsExportingServerPdf] = useState(false);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const checkBackRef = useRef<{ getDataURL: () => string | null; captureForExport: () => Promise<string | null>; deleteSelection: () => number; clearSelection: () => void; getArrangement: () => Record<string, { left: number; top: number; scaleX: number; scaleY: number; angle: number }> | null }>(null);
    const [fabricCanvas, setFabricCanvas] = useState<any | null>(null);
    const [borderConfig, setBorderConfig] = useState<BorderConfig>(factoryBorderConfig);

    // Style Settings State
    const [checkStyle, setCheckStyle] = useState('personal');
    const [selectedFont, setSelectedFont] = useState('times');
    const [selectedTexture, setSelectedTexture] = useState('linen');
    const [paperColor, setPaperColor] = useState('#f8f4ec');
    const [selectedBank, setSelectedBank] = useState('');

    // Phase 43: Dimensional Accuracy - All checks use same canvas size
    const [checkDimensions, setCheckDimensions] = useState({ width: DEFAULT_BUSINESS_WIDTH, height: DEFAULT_BUSINESS_HEIGHT });

    // All check styles use the same 825x300 canvas for consistent rendering
    useEffect(() => {
        setCheckDimensions({ width: DEFAULT_BUSINESS_WIDTH, height: DEFAULT_BUSINESS_HEIGHT });
    }, [checkStyle]);

    const getLayout = (style: string, W: number, H: number) => {
        // Standard check arrangement. The previous values were tuned for an
        // 825x300 counter check and collided at the default 850x350 size:
        // the bank block spans ~42px (name + 2-line address/routing), so the
        // holder block at top 40 printed INTO it, the date row crossed the
        // fractional routing, and memo/signature floated mid-check because
        // they were fixed at 220 instead of anchored above the MICR band.
        // Top-right stack rhythm: check number 10, fractional 31, date 52
        // (even 21-unit steps; the date previously sat at 64, level with
        // the holder block, which read as a stagger). Amount group at 94:
        // the box is 38 tall and the payee rule sits at payTo.top + 28 =
        // 144; on specimens the courtesy box floats in its own field at
        // the right end of the payee row, touching nothing - so the
        // box's bottom edge (94 + 38 = 132) holds a clear 12-unit gap
        // above the payee rule and 22 above the legal-amount text
        // (writtenAmount.top 154). The earlier seat (top 106) put the
        // box's bottom exactly ON the payee rule at 144 - Princewill,
        // 2026-11-04: "It's not supposed to be touching."
        // Amount group left is W - 200 (owner fix, 2026-11-04): the box
        // is 160 wide, so at W - 150 its right edge landed at W + 10 -
        // past the check's right edge, visibly jammed against the side.
        // W - 200 puts the right edge at W - 40, the same 40-unit margin
        // the left-hand blocks use. The $ sign sits INSIDE the box's
        // left edge (the commonest specimen seat) and the amount is
        // left-justified from it (ANSI X9.100-110 scan-field rule).
        const baseLayout = {
            checkNumber: { left: W - 100, top: 10 },
            fractionalRouting: { left: W - 100, top: 31 },
            bankDetails: { left: 40, top: 12 },
            accHolder: { left: 40, top: 62 },
            dateGroup: { left: W - 220, top: 52 },
            amountGroup: { left: W - 200, top: 94 },
            payTo: { left: 40, top: 116 },
            writtenAmount: { left: 40, top: 154 },
            memoGroup: { left: 40, top: H - 88 },
            signatureGroup: { left: W - 350, top: H - 88 },
            // payeeAddress is part of the BASE contract, not a
            // per-style extra: the Classic/Karen payee-address block
            // in renderCheck runs whenever the Karen TEMPLATE is
            // selected (isKarenPayroll keys off selectedBank too),
            // whatever layout style is active, and it dereferences
            // layout.payeeAddress unconditionally. When only the
            // payroll_classic / payroll_karen layouts carried the
            // key, selecting any other style with the Karen template
            // still applied crashed the render (TypeError reading
            // 'left', 2026-11-04 — the owner's "Payroll" crash:
            // Karen template selected, then the Payroll style tapped).
            // The classic seat is the base default; the two payroll
            // layouts keep their own overrides below.
            payeeAddress: { left: 40, top: 198 }
        };

        const layouts: any = {
            personal: { ...baseLayout },
            business: { ...baseLayout },
            cashier: { ...baseLayout, signatureGroup2: { left: W - 350, top: H - 154 } },
            payroll: { ...baseLayout, earningsGroup: { left: 40, top: 196 } },
            payroll_classic: { ...baseLayout, payeeAddress: { left: 40, top: 198 } },
            // Karen's face stack, de-overlapped within its own layout:
            // the bank block drops below the blue banner/border (it sat
            // at top 12, straddling both), the four-line studio header
            // gets the band under it, and the pay-to / written-amount /
            // payee-address rows move down clear of the header's last
            // lines (the payee script used to print across them). Memo
            // and signature come from baseLayout with the CHECK height
            // (bandH), so they sit on the check, not the stub.
            payroll_karen: { ...baseLayout, bankDetails: { left: 40, top: 26 }, accHolder: { left: 40, top: 68 }, payTo: { left: 40, top: 156 }, writtenAmount: { left: 40, top: 192 }, payeeAddress: { left: 40, top: 220 }, earningsGroup: { left: 40, top: 350 } },
            echeck: { ...baseLayout },
            money_order: { ...baseLayout },
            certified: { ...baseLayout },
            counter: { ...baseLayout },
            bofa_classic: { ...baseLayout },
            bofa_v2: { ...baseLayout, hologram: { left: W - 340, top: 186 } },
            // Specimen designs (2026-11-04 layout registration): the
            // standard blocks ride on baseLayout — on their specimens
            // the holder, fraction + date, pay-to, words, memo and
            // signature geometry IS the standard personal geometry, so
            // there is no honest standard-block divergence to record.
            // What each layout adds is the seat of its own printed
            // furniture (drawn only when the matching template is
            // selected, and the template now selects this style via
            // TEMPLATE_STYLE): ANB's wordmark block and USAA's bank
            // block bottom-left; USAA's Photo Safe mark at the right
            // end of the written-amount row (2026-11-04 close-look
            // pass: on the specimen the mark hugs the words line,
            // its block centred on that row — the earlier seat at
            // the signature area sat ~35 units too low), and USAA's
            // pale-blue microprint advisory line directly under the
            // words rule. The
            // two bottom-left blocks sit CLEAR of the memo lane: the
            // memo anchor clamps to bandH - MICR_CLEAR_BAND - 51 =
            // 234 (label 236, rule 251), and the seats below end
            // their blocks at ~232 — the furniture's original
            // hardcoded seats (blocks at bandH - 134) let the block
            // boxes graze the clamped memo label, which the layout
            // seats correct. The furniture renderers read these seats
            // through `layout`, with the historical constants as
            // fallback for snapshots restored onto other styles.
            //
            // GreenFi's seats are photo-anchored (owner's specimen,
            // image-30.png; owner corrections 2026-11-04 11:26-11:27
            // EST — the first build "did not get the actual design"):
            //  - securitySquare: GreenFi's own security mark, a ~30
            //    unit square zone at the LEFT of the pay-to row,
            //    row-centred on the pay-to label, before "PAY TO THE
            //    ORDER OF".
            //  - templateWordmark: the dominant mark on the face — a
            //    zone sized for a LARGE bold wordmark (~260 x 55),
            //    low mid-left under the written-amount rule (176):
            //    the zone (178..233) clears the memo label (236) and
            //    the memo rule (251) below it.
            //  - heatReactive: the heat-reactive ink mark at the
            //    right end of the written-amount line — a PALE PINK
            //    PADLOCK (~17 across, its centre on the DOLLARS row
            //    154; close-look at the specimen, 2026-11-04: the
            //    pink shape is a padlock silhouette with a white
            //    keyhole, not a plain circle) + a bold left arrow;
            //    the strip from its right edge (729) to the right
            //    margin (810) is the room for the stacked "Heat
            //    Reactive Ink" label. It is NOT a badge and NOT
            //    the medallion.
            //  - NO circular medallion / seal seat exists on this
            //    design (owner's rule): the greenfi entry deliberately
            //    carries no thermoSeal seat.
            //  - greenfiStatement: the printed bottom line's lane.
            greenfi: { ...baseLayout, securitySquare: { left: 40, top: 108 }, templateWordmark: { left: 40, top: 178 }, heatReactive: { left: W - 138, top: 146 }, greenfiStatement: { left: W / 2, top: H - 22 } },
            // ANB EXACT rebuild (2026-11-04): five blocks diverge
            // from baseLayout on the specimen (image-31.png, photo-
            // measured) — the wordmark block sits lower-left on the
            // field (32, H-162; ends ~244, clear of the memo lane);
            // the DATE group sits ~75 units LEFT of the standard
            // seat with its rule lower (label 481, rule at top+16 =
            // 93); the fraction prints top-centre-right (548, 2),
            // NOT stacked over the check number; the amount box
            // rides lower (top 110) straddling the horizon; and the
            // holder block rides HIGH (seat 8; the bank-block shift
            // lands it at ~36, where the stock prints it) — the
            // standard seat would drop the holder's last line into
            // the pay-to label's tall swash P; and the
            // memo lane rides LOW (rule at 285), where the stock
            // prints it — the standard lane would collide with the
            // rebuilt wordmark block above it.
            anb: { ...baseLayout, templateWordmark: { left: 32, top: H - 162 }, dateGroup: { left: W - 369, top: 77 }, fractionalRouting: { left: W - 302, top: 2 }, amountGroup: { left: W - 200, top: 110 }, accHolder: { left: 40, top: 8 }, memoGroup: { left: 40, top: 268 } },
            usaa: { ...baseLayout, templateBankBlock: { left: 40, top: H - 158 }, photoSafeMark: { left: W - 145, top: H - 163 }, usaaAdvisory: { left: 235, top: 186 } },
            us_treasury: { ...baseLayout }
        };
        // TOTALITY CONTRACT (the class fix for the 2026-11-04 Payroll
        // crash): renderCheck dereferences layout position entries
        // whose BLOCK gates key partly off the selected TEMPLATE
        // (isKarenPayroll follows selectedBank), not only off this
        // style — so the returned layout must carry every base entry
        // no matter how any individual style literal above is
        // written. Merging the selection over baseLayout guarantees
        // it: a missing entry resolves to the base default and can
        // never surface as an undefined dereference ('payeeAddress
        // .left' was the proven instance). Every current literal
        // already spreads baseLayout, so this merge changes no
        // rendered geometry today; it makes the guarantee structural
        // instead of per-literal.
        return { ...baseLayout, ...(layouts[style] || {}) };
    };

    // Form Data State - seeded from the factory builder (the single
    // source of truth for the out-of-box state; "Start a blank
    // check" clears back to the same values, and Default Clean
    // Style resets the design slice derived from them).
    const [checkData, setCheckData] = useState(factoryCheckData);

    const [logoImage, setLogoImage] = useState<string | null>(null);
    // Logo upload options (Princewill's spec): the RAW upload is kept so
    // the two options can be re-applied without re-uploading -
    //   background: remove it (default; the filtered logo blends onto
    //     the check) or keep it (the upload is placed as-is);
    //   write-ups: include the full block (logo + bank name/address
    //     text) or "logo only" (auto-crop to the left logo cluster,
    //     split auto-detected, with a manual crop slider override).
    const [rawLogoImage, setRawLogoImage] = useState<string | null>(null);
    const [logoRemoveBackground, setLogoRemoveBackground] = useState(true);
    const [logoIncludeWriteups, setLogoIncludeWriteups] = useState(true);
    const [logoCropPercent, setLogoCropPercent] = useState<number | null>(null); // null = auto split
    const [logoDetectedSplit, setLogoDetectedSplit] = useState<number | null>(null);
    const logoProcessSeqRef = useRef(0);
    // The logo's file name is APP state (shown by our own label):
    // the native file input's caption cannot be trusted - it kept
    // showing a removed logo's name until reload, and said "No file
    // chosen" while an Undo-restored logo was rendering.
    const [logoFileName, setLogoFileName] = useState<string | null>(null);
    const logoFileInputRef = useRef<HTMLInputElement | null>(null);
    const applyLogoProcessing = async (raw: string, opts: { removeBackground: boolean; includeWriteups: boolean; cropPercent: number | null }) => {
        const seq = ++logoProcessSeqRef.current;
        const res = await processLogoImage(raw, {
            removeBackground: opts.removeBackground,
            logoOnly: !opts.includeWriteups,
            cropPercent: opts.cropPercent
        });
        if (logoProcessSeqRef.current !== seq) return; // superseded by a newer change
        setLogoImage(res.url);
        if (res.detectedSplitPercent != null) setLogoDetectedSplit(res.detectedSplitPercent);
    };
    // Upload Check Design actions. Applying requires the attestation
    // AND the writing gate: the extraction brings over ONLY the
    // framed pattern swatch and the design's colours (the accent
    // becomes the border colour), and a frame whose ink reads as
    // writing is refused before anything is applied.
    // Framing sliders: each edge may inset up to 88% so a large
    // clean area can be isolated, but opposite edges together may
    // not exceed 92% - a sane minimum frame (8% of the photo) always
    // remains, and the overlay can never invert.
    const updateDesignCrop = (edge: 'left' | 'top' | 'right' | 'bottom', raw: number) => {
        const v = Math.max(0, Math.min(88, Math.round(raw || 0)));
        setDesignCrop(prev => {
            const next = { ...prev, [edge]: v };
            if (edge === 'left' || edge === 'right') {
                const over = next.left + next.right - 92;
                if (over > 0) next[edge] = v - over;
            } else {
                const over = next.top + next.bottom - 92;
                if (over > 0) next[edge] = v - over;
            }
            return next;
        });
    };
    const handleDesignPhotoFile = (file: File) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            setRawDesignPhoto(event.target?.result as string);
            setDesignAttested(false);
            setDesignPatternNote(null);
            setDesignPatternNoteWarn(false);
        };
        reader.readAsDataURL(file);
    };
    const applyDesignPattern = async () => {
        if (!rawDesignPhoto || !designAttested) return;
        const res = await extractDesignPattern(rawDesignPhoto, designCrop);
        if (!res) {
            setDesignPatternNoteWarn(true);
            setDesignPatternNote('That photo could not be read - please try another image.');
            return;
        }
        // WRITING GATE (hard): the framed crop is measured before
        // anything is applied. If its ink reads as writing,
        // numbers or linework, the apply is REFUSED and nothing —
        // no pattern, no colours — crosses over. A writing-bearing
        // crop must never be tiled onto a produced check (live
        // render check 2026-11-04: the old default frame sat on a
        // real photo's amount line and tiled handwriting fragments
        // across the face while the note claimed nothing was
        // copied). The user reframes onto a clean pattern area and
        // applies again.
        if (res.writing.blocked) {
            setDesignPatternNoteWarn(true);
            setDesignPatternNote('That frame contains writing, so it was not applied. Move or shrink the frame onto a clean pattern area - no words, numbers or signature inside it - and apply again. Nothing was copied from the photo.');
            return;
        }
        setDesignPatternNoteWarn(false);
        setDesignPattern({ url: res.patternUrl, attestedAt: new Date().toISOString() });
        // Apply the WHOLE sampled palette to the layout, not just
        // a border tint (owner report 2026-11-04: the imported
        // design left the check looking generic). The extracted
        // paper colour becomes the check's paper — until now it
        // was computed and silently dropped, so the face kept the
        // previous preset's paper and the design never cohered.
        setPaperColor(res.paperColor);
        const applied: string[] = ['the background design', 'the paper colour'];
        if (res.accentColor) {
            const accent = res.accentColor;
            const secondary = res.secondaryColor || lightTone(accent);
            setBorderConfig(prev => ({
                ...prev,
                // The frame must carry the design's colours into
                // the PRINTED check, not only the screen preview:
                // legacy border styles render as an SVG overlay
                // and never reach the canvas export, so unless the
                // current frame is already a canvas-drawn style,
                // the import seats the design on the classic
                // double rule — outer rule in the design's deep
                // tone, hairlines in its secondary colour.
                style: isNewBorderStyle(prev?.style) ? prev.style : 'DOUBLE_RULE',
                color: deepTone(accent),
                secondaryColor: secondary
            }));
            // The medallion is the face's accent element: it takes
            // the design's accent so the layout reads as designed
            // in the photo's colours, not stamped on afterwards.
            setSealColor(accent);
            applied.push('a frame in the design\u2019s colours', 'the seal colour');
        }
        // The note claims only what the gate enforces: the frame
        // was measured and passed the writing check, so what came
        // over is the pattern texture and the sampled colours —
        // no absolute "nothing was copied" beyond that.
        setDesignPatternNote(`Design applied: ${applied.join(', ')} came from your photo. Your frame passed the writing check, so only the pattern and colours were taken - your own details print on top.`);
    };
    const removeDesignPattern = () => {
        setDesignPattern(null);
        setRawDesignPhoto(null);
        setDesignAttested(false);
        setDesignPatternNote(null);
        setDesignPatternNoteWarn(false);
        setDesignFrameBlocked(false);
    };

    const handleLogoFile = (file: File) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            const dataUrl = event.target?.result as string;
            addLogoUpload(file.name, dataUrl);
        };
        reader.readAsDataURL(file);
    };
    // Forget where a previous logo was dragged to, so a fresh upload
    // takes the default header placement (see handleImageUpload).
    const clearLogoPosition = () => {
        if (objectPositionsRef.current.customLogo) {
            const nextPositions = { ...objectPositionsRef.current };
            delete nextPositions.customLogo;
            objectPositionsRef.current = nextPositions;
            setObjectPositions(nextPositions);
        }
    };

    // =====================================================================
    // ASSET LIBRARIES (Princewill, 2026-11-04): uploads are KEPT, not
    // replaced. Each kind - logos, signatures, pictures - has a small
    // library persisted in this browser (utils/assetLibrary). An
    // upload ADDS an entry; tapping an entry places it; every entry
    // can be deleted on its own. An upload NEVER swaps what is
    // already placed: with a logo/signature on the check, a new
    // upload only joins the library (marked saved) and placing
    // happens on a tile tap; the very first upload, when nothing is
    // placed yet, places as uploads always have. The design snapshot
    // records only which entry is placed (activeLogoId /
    // activeSignatureId) plus the placed image itself, as before.
    // Tapping a logo tile ADDS that logo to the check — several
    // logos can be placed at once (see PlacedLogoInstance); the
    // snapshot also records that list of additional placed logos.
    // =====================================================================
    const [logoLibrary, setLogoLibrary] = useState<AssetEntry[]>(() => loadAssetLibrary('logos'));
    const [activeLogoId, setActiveLogoId] = useState<string | null>(null);
    const [logoLibraryNote, setLogoLibraryNote] = useState<string | null>(null);
    const [signatureLibrary, setSignatureLibrary] = useState<AssetEntry[]>(() => loadAssetLibrary('signatures'));
    const [activeSignatureId, setActiveSignatureId] = useState<string | null>(null);
    // Signature ink (Princewill, 2026-11-04): the placed
    // signature's own colour control - a preset/custom ink the
    // cleaned signature is tinted with, or Keep-original mode,
    // which preserves the colour it was written in. Persisted in
    // the design snapshot with the placed signature; it applies
    // to whichever library entry is placed (entries stay raw -
    // the ink is a property of the placement, not the upload).
    const [signatureInkColor, setSignatureInkColor] = useState(DEFAULT_SIGNATURE_INK);
    const [signatureInkOriginal, setSignatureInkOriginal] = useState(false);
    const signatureInkProcessOptions = () => signatureInkOriginal
        ? { keepOriginalColor: true }
        : { tintColor: signatureInkColor };
    // The ONE derivation every placement / re-ink path runs, so
    // the ink promise holds in EVERY Auto-Clean state (render
    // check, 2026-11-04: with Auto-Clean OFF a preset pick left
    // the placed signature black - the off state placed the raw
    // upload and never stored its source, so there was nothing
    // to re-derive from):
    //  - A chosen ink ALWAYS recolours: the raw source goes
    //    through the extractor, which tints the strokes and
    //    lifts them onto a transparent background. Auto-Clean
    //    ON adds the deep clean (ruled-line breaking + stray
    //    mark purge); OFF runs the isolate-and-tint pass only
    //    (removeLines: false) - the strokes are still tinted
    //    and isolated, never left in their white box.
    //  - Keep-original keeps the written colour: with
    //    Auto-Clean ON the cleaned strokes keep their own RGB;
    //    with it OFF the upload is placed exactly as it arrived.
    const signatureInkProcessOptionsFor = (autoClean: boolean) => autoClean
        ? signatureInkProcessOptions()
        : { ...signatureInkProcessOptions(), removeLines: false };
    const derivePlacedSignatureImage = async (raw: string, autoClean: boolean): Promise<string> => {
        if (signatureInkOriginal && !autoClean) return raw;
        return SignatureExtractorEngine.processSignature(raw, signatureInkProcessOptionsFor(autoClean));
    };
    const [signatureLibraryNote, setSignatureLibraryNote] = useState<string | null>(null);
    // Transient pointer at one kept tile after an upload that did
    // NOT place ("just saved" / "already saved"), so the user's eye
    // lands on the entry their upload refers to.
    const [highlightedLogo, setHighlightedLogo] = useState<{ id: string; tag: string } | null>(null);
    const [highlightedSignature, setHighlightedSignature] = useState<{ id: string; tag: string } | null>(null);
    const highlightTimers = useRef<{ logo?: ReturnType<typeof setTimeout>; signature?: ReturnType<typeof setTimeout> }>({});
    const flashHighlight = (kind: 'logo' | 'signature', id: string, tag: string) => {
        if (kind === 'logo') setHighlightedLogo({ id, tag }); else setHighlightedSignature({ id, tag });
        if (highlightTimers.current[kind]) clearTimeout(highlightTimers.current[kind]);
        highlightTimers.current[kind] = setTimeout(() => {
            if (kind === 'logo') setHighlightedLogo(cur => (cur && cur.id === id ? null : cur));
            else setHighlightedSignature(cur => (cur && cur.id === id ? null : cur));
        }, 5000);
    };
    const [pictureLibrary, setPictureLibrary] = useState<AssetEntry[]>(() => loadAssetLibrary('pictures'));
    const [pictureLibraryNote, setPictureLibraryNote] = useState<string | null>(null);
    // Additional logos placed on this check, beyond the primary
    // header logo (see PlacedLogoInstance). Part of the design
    // snapshot, like customObjects.
    const [placedLogos, setPlacedLogos] = useState<PlacedLogoInstance[]>([]);
    useEffect(() => { saveAssetLibrary('logos', logoLibrary); }, [logoLibrary]);
    useEffect(() => { saveAssetLibrary('signatures', signatureLibrary); }, [signatureLibrary]);
    useEffect(() => { saveAssetLibrary('pictures', pictureLibrary); }, [pictureLibrary]);

    // Place a kept logo on the check: the raw upload becomes the
    // active logo and the placed image is derived from it by the
    // logo options, exactly as a fresh upload was. A fresh upload
    // resets the placed position (the shipped rule: a new logo
    // starts at the header anchor); re-selecting a kept logo swaps
    // it into the position the current logo already occupies.
    const activateLogoEntry = (entry: AssetEntry, opts?: { resetPosition?: boolean }) => {
        setActiveLogoId(entry.id);
        setLogoFileName(entry.name);
        setRawLogoImage(entry.src);
        if (opts?.resetPosition) clearLogoPosition();
        applyLogoProcessing(entry.src, { removeBackground: logoRemoveBackground, includeWriteups: logoIncludeWriteups, cropPercent: logoCropPercent });
    };
    // Take the logo off this check. The library entry is KEPT -
    // removing from the check and deleting from the library are
    // separate actions now.
    const deactivateLogo = () => {
        setLogoImage(null);
        setRawLogoImage(null);
        setLogoCropPercent(null);
        setLogoDetectedSplit(null);
        setLogoFileName(null);
        setActiveLogoId(null);
        if (logoFileInputRef.current) logoFileInputRef.current.value = '';
        clearLogoPosition();
    };
    const addLogoUpload = (name: string, dataUrl: string) => {
        const res = addAssetEntry(logoLibrary, makeAssetEntry(name, dataUrl), ASSET_LIBRARY_CAPS.logos);
        if (!res.added && res.reason === 'cap') {
            setLogoLibraryNote(`Your logo library is full (${ASSET_LIBRARY_CAPS.logos} logos). Delete one below to add another.`);
            return;
        }
        // What is on the check right now decides what an upload may
        // do (Princewill, 2026-11-04): with a logo already placed,
        // auto-placing the new upload swaps the placed logo - its
        // image, position and settings - which reads as replacing
        // it. So a placed logo stays untouched and the upload only
        // joins the library; placing is a tile tap. Only when
        // nothing is placed does an upload place, as before. "Placed"
        // includes additional logo instances (placedLogos): with any
        // logo on the check at all, an upload only saves.
        const logoPlaced = !!logoImage || !!rawLogoImage || placedLogos.length > 0;
        if (!res.added && res.reason === 'duplicate') {
            if (!logoPlaced || res.existing.src === rawLogoImage) {
                // Nothing placed, or the upload IS the placed logo:
                // (re)placing the kept copy changes nothing visible.
                setLogoLibraryNote(null);
                setHighlightedLogo(null);
                activateLogoEntry(res.existing);
            } else {
                // A different logo is placed: leave it exactly as
                // it is and point at the copy already kept.
                flashHighlight('logo', res.existing.id, 'ALREADY SAVED');
                setLogoLibraryNote(`"${res.existing.name}" is already in your library - highlighted below. The logo on your check hasn't changed.`);
            }
            return;
        }
        if (res.added) {
            setLogoLibrary(res.entries);
            if (!logoPlaced) {
                setLogoLibraryNote(null);
                setHighlightedLogo(null);
                activateLogoEntry(res.entry, { resetPosition: true });
            } else {
                flashHighlight('logo', res.entry.id, 'JUST SAVED');
                setLogoLibraryNote(`"${res.entry.name}" saved to your library - the logo on your check hasn't changed. Tap the new one below to place it.`);
            }
        }
    };
    // Deleting a library entry also removes its additionally placed
    // copies from this check: the library tile is the only handle a
    // placed copy has in the panel, so leaving copies behind would
    // strand them with no way to manage them.
    const deleteLogoEntry = (id: string) => {
        setLogoLibrary(prev => removeAssetEntry(prev, id));
        setHighlightedLogo(cur => (cur && cur.id === id ? null : cur));
        const doomed = placedLogos.filter(p => p.entryId === id).map(p => p.instanceId);
        if (doomed.length) {
            setPlacedLogos(prev => prev.filter(p => p.entryId !== id));
            setObjectPositions(prev => {
                const next = { ...prev };
                doomed.forEach(instId => { delete next[instId]; });
                return next;
            });
        }
        if (activeLogoId === id) deactivateLogo();
    };

    // Place a kept signature image: the shared derivation a
    // fresh upload gets (see derivePlacedSignatureImage). The
    // raw source is stored in EVERY state - without it a later
    // ink pick has nothing to re-derive from, which is exactly
    // what made ink picks dead with Auto-Clean off.
    const activateSignatureEntry = async (entry: AssetEntry) => {
        setActiveSignatureId(entry.id);
        setRawSignatureImage(entry.src); // Store source for re-inking
        const derived = await derivePlacedSignatureImage(entry.src, !!(checkData as any).autoCleanSignature);
        setSignatureImage(derived);
    };
    const deactivateSignature = () => {
        setSignatureImage(null);
        setRawSignatureImage(null);
        setActiveSignatureId(null);
    };
    // Toggling Auto-Clean re-derives the placed signature the way
    // a fresh placement would - the shared derivation under the
    // NEW toggle state (deep clean when on, isolate-and-tint
    // when off; the raw upload only when Keep-original meets
    // Auto-Clean off) - so the toggle (and the ink choice under
    // it) is never a dead control that only takes effect on the
    // next upload.
    const rederivePlacedSignature = async (autoClean: boolean) => {
        const entry = activeSignatureId ? signatureLibrary.find(en => en.id === activeSignatureId) : null;
        const raw = rawSignatureImage ?? entry?.src ?? null;
        if (!raw) return;
        setRawSignatureImage(raw);
        const derived = await derivePlacedSignatureImage(raw, autoClean);
        setSignatureImage(derived);
    };
    const addSignatureUpload = (name: string, dataUrl: string) => {
        const res = addAssetEntry(signatureLibrary, makeAssetEntry(name || 'Signature', dataUrl), ASSET_LIBRARY_CAPS.signatures);
        if (!res.added && res.reason === 'cap') {
            setSignatureLibraryNote(`Your signature library is full (${ASSET_LIBRARY_CAPS.signatures}). Delete one below to add another.`);
            return;
        }
        // Same rule as logos: with a signature already on the check
        // an upload only joins the library - it never swaps the
        // placed signature. Placing is a tile tap; only the first
        // upload, with nothing placed, places directly.
        const signaturePlaced = activeSignatureId !== null || !!signatureImage;
        if (!res.added && res.reason === 'duplicate') {
            if (!signaturePlaced || res.existing.id === activeSignatureId || (!!rawSignatureImage && res.existing.src === rawSignatureImage)) {
                setSignatureLibraryNote(null);
                setHighlightedSignature(null);
                activateSignatureEntry(res.existing);
            } else {
                flashHighlight('signature', res.existing.id, 'ALREADY SAVED');
                setSignatureLibraryNote(`"${res.existing.name}" is already in your library - highlighted below. The signature on your check hasn't changed.`);
            }
            return;
        }
        if (res.added) {
            setSignatureLibrary(res.entries);
            if (!signaturePlaced) {
                setSignatureLibraryNote(null);
                setHighlightedSignature(null);
                activateSignatureEntry(res.entry);
            } else {
                flashHighlight('signature', res.entry.id, 'JUST SAVED');
                setSignatureLibraryNote(`"${res.entry.name}" saved to your library - the signature on your check hasn't changed. Tap the new one below to use it.`);
            }
        }
    };
    const deleteSignatureEntry = (id: string) => {
        setSignatureLibrary(prev => removeAssetEntry(prev, id));
        setHighlightedSignature(cur => (cur && cur.id === id ? null : cur));
        if (activeSignatureId === id) deactivateSignature();
    };

    // Pictures & stamps: an upload is kept in the library AND placed
    // on the check (the attach behaviour the studio already had);
    // a kept picture can be placed again from the library without
    // re-uploading. Placed objects carry their own copy of the
    // image, so deleting a library entry never reaches onto the
    // canvas and tears a placed picture off a design.
    const registerPictureUpload = (name: string, src: string): AssetEntry => {
        const res = addAssetEntry(pictureLibrary, makeAssetEntry(name || 'Picture', src), ASSET_LIBRARY_CAPS.pictures);
        if (!res.added && res.reason === 'cap') {
            setPictureLibraryNote(`Your picture library is full (${ASSET_LIBRARY_CAPS.pictures}). Delete one below to add another - this picture was placed but not kept.`);
            return makeAssetEntry(name || 'Picture', src);
        }
        if (!res.added && res.reason === 'duplicate') {
            setPictureLibraryNote(null);
            return res.existing;
        }
        setPictureLibrary(res.entries);
        setPictureLibraryNote(null);
        return res.entry;
    };
    const placePictureEntry = (entry: AssetEntry) => {
        setCustomObjects(prev => [...prev, { id: `uimg-${Date.now()}`, kind: 'image', src: entry.src, left: 90, top: 90 }]);
    };
    const deletePictureEntry = (id: string) => {
        setPictureLibrary(prev => removeAssetEntry(prev, id));
    };
    const pictureFileInputRef = useRef<HTMLInputElement | null>(null);
    const signatureFileInputRef = useRef<HTMLInputElement | null>(null);

    const [signatureImage, setSignatureImage] = useState<string | null>(null);
    const [rawSignatureImage, setRawSignatureImage] = useState<string | null>(null);
    const [isSignatureLabOpen, setIsSignatureLabOpen] = useState(false);
    const [customObjects, setCustomObjects] = useState<Array<{ id: string; kind: 'text' | 'image'; text?: string; src?: string; left?: number; top?: number }>>([]);

    const [showMicrGuide, setShowMicrGuide] = useState(false);
    // Whether the selected MICR typeface actually loaded. A MICR line in a
    // fallback font looks plausible but is not bank-readable, so a failed
    // load must be surfaced, never silent.
    const [micrFontLoaded, setMicrFontLoaded] = useState<boolean | null>(null);
    // Webfont-canvas race repair: the front canvas paints on mount and on
    // state changes, which can beat the MICR webfont's arrival - the MICR
    // line then STAYS in a fallback face (rounded digits, literal A/C
    // letters) even though the font loads moments later, because the load
    // check below used to only update the warning state and nothing ever
    // repainted. Once the selected face is actually ready, the canvas is
    // repainted once so the MICR line is drawn with the real typeface.
    // The guard is keyed by family and is only consumed when a live
    // canvas actually repaints, so it fires exactly once per font
    // selection and can never loop (render -> ready -> render).
    const micrRepaintedForRef = useRef<string>('');
    // Render sequence: every renderCheck pass takes the next number.
    // Image loads (fabric.Image.fromURL) resolve ASYNCHRONOUSLY, so a
    // callback from an earlier pass can land after a later pass has
    // cleared and rebuilt the canvas - adding a STALE image on top of
    // the current scene. That race is what left the full-block logo's
    // write-ups ghosting under the header after switching to Logo
    // only. Async adds check the sequence and drop themselves when a
    // newer render has superseded theirs.
    const renderSeqRef = useRef(0);
    const frontCanvasRef = useRef<any>(null);
    const backFabricRef = useRef<any>(null);
    const renderCheckRef = useRef<any>(null);
    useEffect(() => {
        let live = true;
        const fam = (MICR_FONTS.find(f => f.id === checkData.micrFont) || MICR_FONTS[0]).family;
        const primary = fam.split(',')[0].trim();
        const fontsApi: any = (document as any).fonts;
        const repaintWithFont = () => {
            if (!live) return;
            if (micrRepaintedForRef.current === primary) return;
            const c = frontCanvasRef.current;
            if (!c || (c as any).__isDisposed) return;
            micrRepaintedForRef.current = primary;
            if (renderCheckRef.current) renderCheckRef.current(c);
        };
        if (fontsApi?.load) {
            fontsApi.load(`20px ${primary}`).then((res: any[]) => { if (live) { setMicrFontLoaded(res.length > 0); if (res.length > 0) repaintWithFont(); } }).catch(() => { if (live) setMicrFontLoaded(false); });
            // Backstop for the first paint: fonts.ready settles once the
            // faces in use at load time are done. Only repaint from it
            // when THIS family is verifiably loaded, so switching to a
            // not-yet-loaded face cannot consume the one repaint early -
            // the load() promise above remains the guarantee there.
            if (fontsApi.ready) {
                fontsApi.ready.then(() => { if (fontsApi.check && fontsApi.check(`20px ${primary}`)) repaintWithFont(); }).catch(() => { /* FontFaceSet unavailable */ });
            }
        }
        return () => { live = false; };
    }, [checkData.micrFont]);
    const [showComplianceDetails, setShowComplianceDetails] = useState(false);

    // Mod-10 Routing Validation
    const isValidRouting = (routing: string) => {
        const digits = routing.replace(/\D/g, '');
        if (digits.length !== 9) return false;
        let checksum = 0;
        const weights = [3, 7, 1, 3, 7, 1, 3, 7, 1];
        for (let i = 0; i < 9; i++) {
            checksum += parseInt(digits[i]) * weights[i];
        }
        return checksum % 10 === 0;
    };

    // Persistent positions for manual draggability
    const [objectPositions, setObjectPositions] = useState<Record<string, { left: number, top: number, scaleX?: number, scaleY?: number, angle?: number }>>({});
    const objectPositionsRef = useRef(objectPositions);

    // Keep ref in sync for structural renders
    useEffect(() => {
        objectPositionsRef.current = objectPositions;
    }, [objectPositions]);

    const [handwritingStyle, setHandwritingStyle] = useState('cursive');
    const [signatureStyle, setSignatureStyle] = useState('ballpoint'); // Different default style
    const [backSideStyle, setBackSideStyle] = useState('mobile'); // Default to Mobile Deposit
    // Per-design backs (2026-11-03): every front design resolves to its
    // own back variant with its own deposit-block default. The user's
    // explicit back-style choice (picker, Ask AI, snapshot restore)
    // sets backStyleTouched and always wins over that default; picking
    // a different front design clears the flag so the new design gets
    // its own back. While untouched, backSideStyle state is ignored in
    // favour of the variant default (see effectiveBackStyle below).
    const [backStyleTouched, setBackStyleTouched] = useState(false);
    const backRestoreGuardRef = useRef(false);
    const prevBackDesignKeyRef = useRef<string | null>(null);
    // Border colour auto-sync guard: when a snapshot is restored, the
    // restored borderConfig is authoritative (accuracy rule) - the
    // theme auto-sync effect must skip the pass for the restored
    // design key instead of overwriting the user's border colours.
    const borderSyncGuardKeyRef = useRef<string | null>(null);
    const borderSyncMountRef = useRef(true);

    // --- Mobile selection (front + back canvases) ---
    // Counts drive the floating selection action bar (visible only at
    // phone widths via CSS); selSide says which canvas the bar acts
    // on. multiSelect switches tap behaviour to tap-to-toggle.
    const [frontSelCount, setFrontSelCount] = useState(0);
    const [backSelCount, setBackSelCount] = useState(0);
    const [selSide, setSelSide] = useState<'front' | 'back'>('front');
    // Long-press / right-click object menu (front + back canvases):
    // the object is already selected when the menu opens; the menu is
    // an addition to the selection bar, not a replacement.
    const [objMenu, setObjMenu] = useState<null | { side: 'front' | 'back'; objectId: string; x: number; y: number }>(null);
    const [multiSelect, setMultiSelect] = useState(false);
    const multiSelectRef = useRef(false);
    useEffect(() => { multiSelectRef.current = multiSelect; }, [multiSelect]);

    // --- Default check back (owner's saved arrangement) ---
    // The arrangement (deposit style + back object positions) the
    // user saved as his default. Stored locally (BACK_DEFAULT_KEY)
    // and mirrored to the account as a 'backdefault' design when
    // signed in, so it follows him across devices.
    const [backDefault, setBackDefault] = useState<BackDefault | null>(() => {
        try { return parseBackDefault(localStorage.getItem(BACK_DEFAULT_KEY)); }
        catch { return null; }
    });
    const backDefaultRef = useRef<BackDefault | null>(backDefault);
    useEffect(() => { backDefaultRef.current = backDefault; }, [backDefault]);
    const [backDefaultId, setBackDefaultId] = useState<string | null>(null);
    const [backNote, setBackNote] = useState<string | null>(null);
    const backTouchedRef = useRef(false);
    useEffect(() => { backTouchedRef.current = backStyleTouched; }, [backStyleTouched]);
    const backDefaultLoadAppliedRef = useRef(false);
    // Apply a saved default back as a user-touched override: its
    // deposit style + its object positions become the current back.
    const applyBackDefault = useCallback((d: BackDefault) => {
        setObjectPositions(prev => mergeArrangementPositions(prev as any, d.positions) as any);
        setBackSideStyle(d.style);
        setBackStyleTouched(true);
    }, []);
    const [handwritingRotation, setHandwritingRotation] = useState(-1.2);
    const [handwritingVariation, setHandwritingVariation] = useState(0.5);
    const [handwritingColor, setHandwritingColor] = useState('#1b2440'); // Ball-pen blue-black (research: real pen ink is deep blue-black, not screen blue)
    const [textureOpacity, setTextureOpacity] = useState(1.0);
    const [securityFeatures, setSecurityFeatures] = useState(SECURITY_FEATURES);
    // Security medallion (the round SAFE disc): colour + finish are
    // per-design state, defaulting to the ash-grey flat disc on the
    // owner's reference check. The disc renders while the
    // Thermochromic Ink feature is enabled.
    const [sealColor, setSealColor] = useState<string>(FACTORY_SEAL_COLOR);
    const [sealFinish, setSealFinish] = useState<SealFinish>(FACTORY_SEAL_FINISH as SealFinish);
    // Upload Check Design (pattern-only import): the applied pattern
    // is per-design state (swatch URL + when the user attested); the
    // raw photo and the crop framing are session-only working state.
    const [designPattern, setDesignPattern] = useState<{ url: string; attestedAt: string } | null>(null);
    const [rawDesignPhoto, setRawDesignPhoto] = useState<string | null>(null);
    const [designCrop, setDesignCrop] = useState<CropRegion>(DEFAULT_CROP);
    const [designAttested, setDesignAttested] = useState(false);
    const [designPatternNote, setDesignPatternNote] = useState<string | null>(null);
    // The note's tone: a writing-gate refusal must not render in
    // the success green — it is a refusal, and it says so.
    const [designPatternNoteWarn, setDesignPatternNoteWarn] = useState(false);
    // Live writing-gate reading of the frame as the user adjusts
    // it (debounced in the effect below): true when the framed
    // area reads as writing and an apply would be refused.
    const [designFrameBlocked, setDesignFrameBlocked] = useState(false);
    const designFrameSeqRef = useRef(0);
    const designPatternImgRef = useRef<HTMLImageElement | null>(null);
    const designPatternLoadedForRef = useRef<string>('');
    // Load the applied swatch for the canvas; repaint once it arrives
    // (same pattern as the MICR webfont repaint).
    useEffect(() => {
        const url = designPattern?.url || '';
        if (!url) { designPatternImgRef.current = null; designPatternLoadedForRef.current = ''; return; }
        if (designPatternLoadedForRef.current === url) return;
        const img = new Image();
        img.onload = () => {
            designPatternImgRef.current = img;
            designPatternLoadedForRef.current = url;
            const c = frontCanvasRef.current;
            if (c && renderCheckRef.current) renderCheckRef.current(c);
        };
        img.src = url;
    }, [designPattern]);
    // Writing gate, live: while the user frames a pattern area,
    // measure the framed crop (debounced — extraction decodes the
    // photo) so the panel can say BEFORE an apply that the frame
    // contains writing. The apply itself re-measures and refuses;
    // this reading only drives the warning under the sliders. (The
    // earlier "tighten your frame" hint was driven by a near-black
    // share that is ~0 on real scanned ink, so it never appeared —
    // the gate's analyzeInk measures are the ones that see scans.)
    useEffect(() => {
        if (!rawDesignPhoto) { setDesignFrameBlocked(false); return; }
        const seq = ++designFrameSeqRef.current;
        const t = setTimeout(async () => {
            const res = await extractDesignPattern(rawDesignPhoto, designCrop);
            if (designFrameSeqRef.current !== seq) return; // superseded
            setDesignFrameBlocked(!!res?.writing.blocked);
        }, 350);
        return () => clearTimeout(t);
    }, [rawDesignPhoto, designCrop]);
    // Fractional routing auto-derives from the routing number until the user edits it by hand
    const fractionalAutoRef = useRef(true);
    // An explicit AI border-color instruction must survive the template
    // color auto-sync that runs when the AI also selects a bank template.
    const aiBorderOverrideRef = useRef(false);
    const [mobileBackground, setMobileBackground] = useState<'dark' | 'wood' | 'granite' | 'office'>('dark');
    const [showMicrBand, setShowMicrBand] = useState(true); // Default: show white band
    const [showAmountBorder, setShowAmountBorder] = useState(true); // Default: show amount box border

    // Amount field editing text: while the Amount field is focused it
    // shows the user's raw in-progress text (a plain string state), so
    // intermediate states like "555." or a long pasted decimal are
    // never pushed through the browser's number-input machinery or
    // React's numeric value coercion - either of which used to mangle
    // the amount mid-typing (a typed 555.55 could land as 55 in the
    // field, and the committed value drifted when panels remounted).
    // The committed checkData.amount stays cents-exact via parseMoney
    // on every keystroke; on blur the field re-mirrors the committed
    // amount formatted to cents.
    const [amountDraft, setAmountDraft] = useState<string | null>(null);

    // UI State
    const [activeSection, setActiveSection] = useState<string | null>('check_info');
    // Phone workspace: the tools panel is a slide-in sheet over the
    // check (Photoshop-style docked tools, adapted to a small screen),
    // not a long panel under the preview the user must scroll down to.
    const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [viewSide, setViewSide] = useState<'front' | 'back'>('front');
    const [activeObjectId, setActiveObjectId] = useState<string | null>(null);
    const [deletedIds, setDeletedIds] = useState<string[]>([]); // Front deleted objects
    const [deletedBackIds, setDeletedBackIds] = useState<string[]>([]); // Back deleted objects

    // =====================================================================
    // MULTIPLE PLACED LOGOS (Princewill, voice, 2026-11-04). The
    // placed instances actually on the check right now: an instance
    // deleted on the canvas is tombstoned in deletedIds (the house
    // pattern — Undo restores it because snapshots carry placedLogos
    // and deletedIds together), so it stops counting as placed
    // everywhere the user looks.
    // =====================================================================
    const visiblePlacedLogos = placedLogos.filter(p => !deletedIds.includes(p.instanceId));
    const logoInstanceCounterRef = useRef(0);

    // Add one placed instance of a library logo. The instance lands
    // IMMEDIATELY with the raw upload (placement never waits on
    // image processing), then its image is swapped for the processed
    // derivation — made with the logo options in force now, and
    // stored on the instance — when processing lands. The instance
    // is seated clear of the header logo, cascading per instance so
    // several marks never stack invisibly on the same spot.
    const placeAdditionalLogo = (entry: AssetEntry) => {
        const opts = {
            removeBackground: logoRemoveBackground,
            includeWriteups: logoIncludeWriteups,
            cropPercent: logoCropPercent
        };
        logoInstanceCounterRef.current += 1;
        const instanceId = `logoInst-${Date.now().toString(36)}-${logoInstanceCounterRef.current.toString(36)}`;
        setPlacedLogos(prev => {
            const live = prev.filter(p => !deletedIds.includes(p.instanceId));
            const slot = live.length % 3;
            const inst: PlacedLogoInstance = {
                instanceId,
                entryId: entry.id,
                name: entry.name,
                src: entry.src,
                ...opts,
                left: 440 + slot * 36,
                top: 24 + slot * 18
            };
            // Tombstoned instances are purged as a fresh one lands,
            // so deleted copies cannot accumulate invisibly in state.
            return [...live, inst];
        });
        processLogoImage(entry.src, {
            removeBackground: opts.removeBackground,
            logoOnly: !opts.includeWriteups,
            cropPercent: opts.cropPercent
        }).then(res => {
            if (res?.url) {
                setPlacedLogos(prev => prev.map(p => (p.instanceId === instanceId ? { ...p, src: res.url } : p)));
            }
        }).catch(() => { /* processing failed: the raw upload stays placed */ });
    };

    // A tap on a library tile ADDS that logo to the check — it never
    // swaps or removes a logo already placed. Only when the header
    // slot itself is empty does the tap fill it (the first placement
    // is the header logo, with its anchor and options panel).
    const handleLogoTileTap = (entry: AssetEntry) => {
        if (!activeLogoId) {
            activateLogoEntry(entry);
            return;
        }
        placeAdditionalLogo(entry);
    };

    // Take one placed instance off the check. The library entry is
    // kept; only this copy goes, with its recorded position.
    const removePlacedLogo = (instanceId: string) => {
        setPlacedLogos(prev => prev.filter(p => p.instanceId !== instanceId));
        setObjectPositions(prev => {
            if (!(instanceId in prev)) return prev;
            const next = { ...prev };
            delete next[instanceId];
            return next;
        });
    };

    // AI Bar Draggability State
    const [aiBarPosition, setAiBarPosition] = useState({ x: window.innerWidth / 2 - 300, y: window.innerHeight - 80 });
    // The bar is click-to-open: it must never sit over the check design
    // (on phones it covered Page 2 while scrolling). Starts open on wide
    // screens, closed on phones.
    const [aiBarOpen, setAiBarOpen] = useState(false);
    const [aiFeedback, setAiFeedback] = useState('');
    const [panelRetracted, setPanelRetracted] = useState<boolean>(() => { try { return localStorage.getItem('oc-panel-retracted') === '1'; } catch (err) { return false; } });
    const togglePanel = (retract: boolean) => { setPanelRetracted(retract); try { localStorage.setItem('oc-panel-retracted', retract ? '1' : '0'); } catch (err) { /* storage unavailable */ } };
    const [isDraggingAiBar, setIsDraggingAiBar] = useState(false);

    // --- Design history: Save / Undo / Redo (owner's requirement) ---
    // The whole editable design - fields, template, paper, security
    // toggles, dragged positions on both sides, deletions, signature
    // and handwriting settings - is one snapshot. Save persists it in
    // the browser so the design never snaps back to the old style on
    // reload; Undo/Redo walk the recorded steps.
    // Restore a design snapshot into the editor state. This is the
    // history hook's apply() (undo/redo/save/restore all flow through
    // it), and the reset actions call it directly with their snapshot
    // (Default Clean Style's design-only reset; Start a blank check's
    // factory snapshot) - called directly, not via the hook's
    // restore(), so the recorder treats a reset as a normal edit:
    // it lands on the undo stack and is persisted as the working
    // state exactly like any other change.
    const applyDesignSnapshot = (s: any) => {
        if (!s) return;
        // Amounts are cents-exact: a state saved before the rounding
        // fix can carry float dust (777.77001953125); normalize on
        // the way in so a restore can never resurrect it.
        if ('checkData' in s) setCheckData(s.checkData ? { ...s.checkData, amount: parseMoney(s.checkData.amount) } : s.checkData);
        if ('selectedBank' in s) setSelectedBank(s.selectedBank);
        if ('paperColor' in s) setPaperColor(s.paperColor);
        if ('selectedTexture' in s) setSelectedTexture(s.selectedTexture);
        if ('selectedFont' in s) setSelectedFont(s.selectedFont);
        if ('checkStyle' in s) setCheckStyle(s.checkStyle);
        if ('checkDimensions' in s) setCheckDimensions(s.checkDimensions);
        if ('borderConfig' in s) {
            setBorderConfig(s.borderConfig);
            // Accuracy: the restored border is authoritative. The
            // theme auto-sync effect skips its pass for exactly
            // this design key (guard consumed there), so a restore
            // can never repaint the user's border theme colours.
            borderSyncGuardKeyRef.current = `${'checkStyle' in s ? s.checkStyle : checkStyle}|${'selectedBank' in s ? (s.selectedBank || '') : (selectedBank || '')}`;
        }
        if ('securityFeatures' in s) setSecurityFeatures(s.securityFeatures);
        if ('sealColor' in s) setSealColor(s.sealColor);
        if ('sealFinish' in s) setSealFinish(s.sealFinish === 'metallic' ? 'metallic' : 'flat');
        // Retired back objects never re-enter state from a saved
        // snapshot: a design saved while the standalone open-field
        // wash group existed can carry its arrangement entry, and
        // the entry is stripped here (the wash box is composed
        // inside the security panel by the engine now). Every other
        // entry — including the panel's own — applies exactly as
        // saved: this is also the Undo/Redo path, and live
        // arrangements must round-trip untouched. The single
        // exception is the specimen-direction heal: a snapshot
        // recorded while the backwards (-90) back composition was
        // live carries that angle for the specimen texts, and the
        // corrected direction (+90) is a product invariant, not
        // arrangement data — positions and scales still round-trip
        // exactly (see normalizeBackTextDirections).
        if ('objectPositions' in s) setObjectPositions(normalizeBackTextDirections(stripLegacyBackObjectPositions(s.objectPositions)));
        if ('deletedIds' in s) setDeletedIds(s.deletedIds);
        if ('deletedBackIds' in s) setDeletedBackIds(s.deletedBackIds);
        if ('backSideStyle' in s) {
            setBackSideStyle(s.backSideStyle);
            // A restored back style is an explicit user choice.
            // Saves made before the flag existed carry only the
            // style, so they count as touched too. The guard stops
            // the design-change reset below from clearing the flag
            // when the same restore also changes the design.
            backRestoreGuardRef.current = true;
            setBackStyleTouched('backStyleTouched' in s ? !!s.backStyleTouched : true);
        }
        if ('signatureStyle' in s) setSignatureStyle(s.signatureStyle);
        if ('handwritingStyle' in s) setHandwritingStyle(s.handwritingStyle);
        if ('handwritingColor' in s) setHandwritingColor(s.handwritingColor);
        if ('signatureInkColor' in s) setSignatureInkColor(normalizeSignatureInk(s.signatureInkColor));
        if ('signatureInkOriginal' in s) setSignatureInkOriginal(!!s.signatureInkOriginal);
        if ('handwritingRotation' in s) setHandwritingRotation(s.handwritingRotation);
        if ('handwritingVariation' in s) setHandwritingVariation(s.handwritingVariation);
        if ('textureOpacity' in s) setTextureOpacity(s.textureOpacity);
        if ('showMicrBand' in s) setShowMicrBand(s.showMicrBand);
        if ('showAmountBorder' in s) setShowAmountBorder(s.showAmountBorder);
        if ('customObjects' in s) setCustomObjects(s.customObjects);
        if ('logoImage' in s) setLogoImage(s.logoImage);
        // A working state saved under quota may carry the placed
        // logo without the raw upload; seed the raw from the placed
        // image so the logo options keep working after a restore
        // (re-filtering a processed image is a safe no-op).
        if ('rawLogoImage' in s) setRawLogoImage(s.rawLogoImage ?? (s.logoImage ?? null));
        // Accuracy: whether the fractional routing auto-derives is
        // part of the user's editing state (a hand-edited fraction
        // must stay hand-edited after a refresh).
        if ('fractionalAuto' in s) fractionalAutoRef.current = !!s.fractionalAuto;
        if ('logoRemoveBackground' in s) setLogoRemoveBackground(s.logoRemoveBackground);
        if ('logoIncludeWriteups' in s) setLogoIncludeWriteups(s.logoIncludeWriteups);
        if ('logoCropPercent' in s) setLogoCropPercent(s.logoCropPercent);
        if ('logoDetectedSplit' in s) setLogoDetectedSplit(s.logoDetectedSplit);
        if ('logoFileName' in s) setLogoFileName(s.logoFileName ?? null);
        // Asset libraries (2026-11-04): the snapshot links the
        // placed asset to its library entry. A snapshot saved before
        // libraries existed carries the asset directly - adopt it
        // into the library (migration) so the upgrade keeps it. A
        // snapshot from the explicit Save carries the link but not
        // the image (Save strips images to fit storage) - re-place
        // the asset from the library kept in this browser.
        if ('activeLogoId' in s) setActiveLogoId(s.activeLogoId ?? null);
        if (!s.activeLogoId && (s.rawLogoImage || s.logoImage)) {
            const legacySrc = (s.rawLogoImage ?? s.logoImage) as string;
            const adopted = migrateLegacyAsset(logoLibrary, { name: s.logoFileName ?? 'My logo', src: legacySrc }, ASSET_LIBRARY_CAPS.logos);
            if (adopted.entries !== logoLibrary) setLogoLibrary(adopted.entries);
            if (adopted.id) setActiveLogoId(adopted.id);
        } else if (s.activeLogoId && !s.logoImage && !s.rawLogoImage) {
            const entry = logoLibrary.find(en => en.id === s.activeLogoId);
            if (entry) {
                setLogoFileName(entry.name);
                setRawLogoImage(entry.src);
                applyLogoProcessing(entry.src, {
                    removeBackground: 'logoRemoveBackground' in s ? s.logoRemoveBackground : logoRemoveBackground,
                    includeWriteups: 'logoIncludeWriteups' in s ? s.logoIncludeWriteups : logoIncludeWriteups,
                    cropPercent: 'logoCropPercent' in s ? s.logoCropPercent : logoCropPercent
                });
            }
        }
        // Additional placed logos (2026-11-04): restored as a list.
        // Snapshots saved before they existed simply have no field.
        // An account save strips each instance's image (storage) —
        // re-derive it from the instance's library entry with the
        // instance's own options, as the primary is re-placed above;
        // an entry that no longer exists leaves the instance out
        // rather than breaking the restore.
        if ('placedLogos' in s) {
            const list: PlacedLogoInstance[] = (Array.isArray(s.placedLogos) ? s.placedLogos : [])
                .filter((p: any) => p && typeof p.instanceId === 'string')
                .map((p: any) => ({
                    instanceId: p.instanceId,
                    entryId: typeof p.entryId === 'string' ? p.entryId : null,
                    name: typeof p.name === 'string' && p.name ? p.name : 'Logo',
                    src: typeof p.src === 'string' && p.src ? p.src : null,
                    removeBackground: p.removeBackground !== false,
                    includeWriteups: p.includeWriteups !== false,
                    cropPercent: typeof p.cropPercent === 'number' ? p.cropPercent : null,
                    left: typeof p.left === 'number' ? p.left : 440,
                    top: typeof p.top === 'number' ? p.top : 24
                }));
            setPlacedLogos(list);
            list.filter(p => !p.src && p.entryId).forEach(p => {
                const entry = logoLibrary.find(en => en.id === p.entryId);
                if (!entry) return;
                processLogoImage(entry.src, {
                    removeBackground: p.removeBackground,
                    logoOnly: !p.includeWriteups,
                    cropPercent: p.cropPercent
                }).then(res => {
                    if (res?.url) {
                        setPlacedLogos(prev => prev.map(q => (q.instanceId === p.instanceId && !q.src) ? { ...q, src: res.url } : q));
                    }
                }).catch(() => { /* a failed re-derive leaves that one instance unplaced */ });
            });
        }
        if ('designPattern' in s) setDesignPattern(s.designPattern ?? null);
        if ('signatureImage' in s) setSignatureImage(s.signatureImage);
        if ('activeSignatureId' in s) setActiveSignatureId(s.activeSignatureId ?? null);
        if (!s.activeSignatureId && s.signatureImage) {
            const adopted = migrateLegacyAsset(signatureLibrary, { name: 'My signature', src: s.signatureImage }, ASSET_LIBRARY_CAPS.signatures);
            if (adopted.entries !== signatureLibrary) setSignatureLibrary(adopted.entries);
            if (adopted.id) setActiveSignatureId(adopted.id);
        }
        if (s.activeSignatureId && !s.signatureImage) {
            // An explicit Save strips the placed signature image
            // (storage); re-derive it from the kept library entry
            // with the design's saved signature ink, exactly as
            // the primary logo is re-placed above. Without this a
            // restored account design silently lost its signature.
            const entry = signatureLibrary.find(en => en.id === s.activeSignatureId);
            if (entry) {
                const autoClean = !!((s.checkData as any)?.autoCleanSignature ?? (checkData as any).autoCleanSignature);
                const inkOriginal = !!s.signatureInkOriginal;
                // The raw source is kept in BOTH states now, so
                // an ink pick after a restore re-derives too -
                // and a chosen ink tints even with Auto-Clean
                // off (isolate-and-tint), exactly like a fresh
                // placement. Only Keep-original + Auto-Clean off
                // places the upload untouched.
                setRawSignatureImage(entry.src);
                if (inkOriginal && !autoClean) {
                    setSignatureImage(entry.src);
                } else {
                    const inkOpts = inkOriginal
                        ? { keepOriginalColor: true }
                        : { tintColor: normalizeSignatureInk(s.signatureInkColor) };
                    SignatureExtractorEngine.processSignature(entry.src, autoClean ? inkOpts : { ...inkOpts, removeLines: false })
                        .then(derived => setSignatureImage(derived))
                        .catch(() => { /* a failed re-derive leaves the signature unplaced */ });
                }
            }
        }
    };

    // The current design as a history snapshot - the exact shape
    // the hook persists/restores. One builder, shared by the hook's
    // snapshot() and by the reset actions below (Default Clean
    // Style's design-only reset, Start a blank check), so they all
    // read the same state the recorder sees.
    const buildCurrentSnapshot = () => ({
        checkData, selectedBank, paperColor, selectedTexture, selectedFont,
        checkStyle, checkDimensions, borderConfig, securityFeatures, sealColor, sealFinish,
        objectPositions, deletedIds, deletedBackIds, backSideStyle, backStyleTouched,
        signatureStyle, handwritingStyle, handwritingColor, signatureInkColor, signatureInkOriginal,
        handwritingRotation, handwritingVariation, textureOpacity,
        showMicrBand, showAmountBorder, customObjects, placedLogos, logoImage, signatureImage,
        rawLogoImage, logoRemoveBackground, logoIncludeWriteups, logoCropPercent, logoDetectedSplit, logoFileName, designPattern,
        activeLogoId, activeSignatureId,
        fractionalAuto: fractionalAutoRef.current
    });

    const designHistory = useHistorySnapshots({
        storageKey: 'oc-check-design',
        designKind: 'check',
        // Working-state persistence: the live design is kept
        // continuously (debounced) and restored verbatim on load, so
        // a refresh continues the check exactly as left - values are
        // restored from the snapshot itself, never re-derived from
        // templates or presets. The local copy keeps the placed logo
        // (only the re-derivable raw upload is dropped on quota).
        autoPersistWork: true,
        // Start options (Princewill, 2026-11-04): the studio lands on
        // the BLANK factory check; a previous check is preserved in
        // its own slot and offered as an explicit choice ("Start with
        // my previous check"), never silently restored over the blank
        // start. See useHistorySnapshots for the slot rotation rules.
        deferRestore: true,
        isBlankState: isBlankCheckSnapshot,
        stripForWorkSave: (s: any) => ({ ...s, rawLogoImage: null }),
        designName: () => `${(checkData as any).bankName || selectedBank || 'Check design'} · #${checkData.checkNumber}`,
        snapshot: buildCurrentSnapshot,
        apply: applyDesignSnapshot,
        stripForSave: (s: any) => ({
            ...s, logoImage: null, rawLogoImage: null, signatureImage: null,
            // Placed extra logos: the explicit Save strips their
            // images too; each instance keeps its library link and
            // its own options, and is re-derived on restore exactly
            // like the primary logo (see applyDesignSnapshot).
            placedLogos: Array.isArray(s.placedLogos) ? s.placedLogos.map((p: any) => ({ ...p, src: null })) : s.placedLogos
        }),
        watch: [checkData, selectedBank, paperColor, selectedTexture, selectedFont,
            checkStyle, checkDimensions, borderConfig, securityFeatures, sealColor, sealFinish,
            objectPositions, deletedIds, deletedBackIds, backSideStyle, backStyleTouched,
            signatureStyle, handwritingStyle, handwritingColor, signatureInkColor, signatureInkOriginal,
            handwritingRotation, handwritingVariation, textureOpacity,
            showMicrBand, showAmountBorder, customObjects, placedLogos, logoImage, signatureImage,
            rawLogoImage, logoRemoveBackground, logoIncludeWriteups, logoCropPercent, logoDetectedSplit, logoFileName, designPattern,
            activeLogoId, activeSignatureId]
    });
    // Start-choice "My saved designs": the designs picker lives in
    // the HistoryBar (Save / My Designs live there). Ask the bar to
    // surface it - it expands the picker, scrolls itself into view
    // and focuses it - with a direct scroll as a fallback. (The old
    // version poked for the picker's <select>, which only exists
    // for signed-in users: for a guest the click did nothing.)
    const openMyDesigns = () => {
        dispatchOpenMyDesigns();
        const bar = document.querySelector('[data-history-bar]');
        if (bar) window.setTimeout(() => { try { bar.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch { /* the bar scrolls itself too */ } }, 80);
    };
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

    // =========================================================================
    // COLOR THEME ENGINE (Lifted for Global Access)
    // =========================================================================
    const bankTemplate = selectedBank ? (bankSpecificTemplates[selectedBank] as any) : null;
    const premiumTemplate = selectedBank ? PREMIUM_TEMPLATES.find((p: any) => p.id === selectedBank) : null;

    // --- Per-design back resolution (2026-11-03) ---
    // Changing the front design (style or template) resets the user's
    // back-style override, so the new design opens with its own back —
    // except during a snapshot restore, which restores the override
    // deliberately (guard ref set in the history apply above).
    useEffect(() => {
        const key = `${checkStyle}|${selectedBank || ''}`;
        if (prevBackDesignKeyRef.current === null) {
            prevBackDesignKeyRef.current = key;
            return;
        }
        if (prevBackDesignKeyRef.current !== key) {
            prevBackDesignKeyRef.current = key;
            if (backRestoreGuardRef.current) {
                backRestoreGuardRef.current = false;
                return;
            }
            if (backDefaultRef.current) {
                // The owner's saved default back wins over
                // design-matching on design switches, applied as a
                // user-touched override (style + positions). Manual
                // back changes after this point are left alone - the
                // default only re-applies on the NEXT switch.
                applyBackDefault(backDefaultRef.current);
                return;
            }
            setBackStyleTouched(false);
        }
    }, [checkStyle, selectedBank]);
    const backVariant = resolveBackVariant(checkStyle, selectedBank);
    const effectiveBackStyle = backStyleTouched ? backSideStyle : backVariant.depositBlockDefault;

    // --- Default back: load-time application ---
    // Once per studio load, if a default back exists and the user had
    // NOT already arranged this check's back himself (a restored
    // working state with a touched back wins), the default opens the
    // check. The 900ms settle lets the history restore land first.
    useEffect(() => {
        if (!backDefault || backDefaultLoadAppliedRef.current) return;
        const t = setTimeout(() => {
            if (backDefaultLoadAppliedRef.current) return;
            backDefaultLoadAppliedRef.current = true;
            if (!backTouchedRef.current) applyBackDefault(backDefault);
        }, 900);
        return () => clearTimeout(t);
    }, [backDefault, applyBackDefault]);

    // --- Default back: account copy (cross-device when signed in) ---
    // The default lives in the account as a design of kind
    // 'backdefault' (same /api/designs store as My Designs). Newest
    // savedAt wins between device and account; the other copy is
    // refreshed on the next explicit save.
    useEffect(() => {
        let token: string | null = null;
        try { token = localStorage.getItem('token'); } catch { /* ignore */ }
        if (!token) return;
        let cancelled = false;
        (async () => {
            try {
                const headers = { 'Authorization': `Bearer ${token}` };
                const res = await fetch('/api/designs', { headers });
                if (isAuthFailureStatus(res.status)) {
                    // Dead session token: reconcile the app to
                    // signed-out instead of silently skipping the
                    // account copy (the local default still stands).
                    notifyAuthExpired();
                    return;
                }
                const json = await res.json().catch(() => null);
                if (cancelled || !res.ok || !json?.success || !Array.isArray(json.designs)) return;
                const entry = json.designs.find((d: any) => d.kind === 'backdefault');
                if (!entry) return;
                setBackDefaultId(entry.id);
                const fullRes = await fetch(`/api/designs/${entry.id}`, { headers });
                const fullJson = await fullRes.json().catch(() => null);
                const remote = normalizeBackDefault(fullJson?.design?.data);
                if (cancelled || !remote) return;
                const local = backDefaultRef.current;
                if (!local || remote.savedAt > local.savedAt) {
                    setBackDefault(remote);
                    backDefaultRef.current = remote;
                    try { localStorage.setItem(BACK_DEFAULT_KEY, serializeBackDefault(remote)); } catch { /* ignore */ }
                }
            } catch { /* offline or server down: the local default stands */ }
        })();
        return () => { cancelled = true; };
    }, []);

    // Save the CURRENT back (the live canvas's arrangement + the
    // effective deposit style) as the default back.
    const saveBackAsDefault = async () => {
        const positions = checkBackRef.current?.getArrangement?.() ?? null;
        if (!positions || Object.keys(positions).length === 0) {
            setBackNote('Nothing to save yet — the back has no objects.');
            return;
        }
        const d: BackDefault = { style: effectiveBackStyle, positions: positions as BackDefault['positions'], savedAt: Date.now() };
        setBackDefault(d);
        backDefaultRef.current = d;
        // The current back is now an explicit arrangement.
        setBackSideStyle(effectiveBackStyle);
        setBackStyleTouched(true);
        let storedLocally = true;
        try { localStorage.setItem(BACK_DEFAULT_KEY, serializeBackDefault(d)); }
        catch { storedLocally = false; }
        let token: string | null = null;
        try { token = localStorage.getItem('token'); } catch { /* ignore */ }
        if (!token) {
            setBackNote(storedLocally ? 'Saved as your default back on this device.' : 'Could not store the default (browser storage full).');
            return;
        }
        try {
            const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
            let res: Response;
            if (backDefaultId) {
                res = await fetch(`/api/designs/${backDefaultId}`, { method: 'PUT', headers, body: JSON.stringify({ name: 'Default check back', data: d }) });
                if (res.status === 404) {
                    res = await fetch('/api/designs', { method: 'POST', headers, body: JSON.stringify({ name: 'Default check back', kind: 'backdefault', data: d }) });
                }
            } else {
                res = await fetch('/api/designs', { method: 'POST', headers, body: JSON.stringify({ name: 'Default check back', kind: 'backdefault', data: d }) });
            }
            const json = await res.json().catch(() => null);
            if (res.ok && json?.success) {
                if (json.design?.id) setBackDefaultId(json.design.id);
                setBackNote('Saved as your default back — on this device and in your account.');
            } else if (isAuthFailureStatus(res.status)) {
                // The local copy above is safe; the account leg died
                // with the session. Say so, and reconcile sign-in state.
                notifyAuthExpired();
                setBackNote(storedLocally
                    ? "Saved as your default back on this device — you're signed out, so it isn't in your account yet."
                    : 'Could not store the default (browser storage full), and you are signed out.');
            } else {
                setBackNote('Saved as your default back on this device (account sync failed).');
            }
        } catch {
            setBackNote('Saved as your default back on this device (account sync failed).');
        }
    };

    // Clear the default back: deposit style returns to the
    // design-matched variant immediately, and the arrangement entries
    // the default contributed (and the user has not since moved) are
    // released so those objects return to their design placement.
    const clearBackDefault = async () => {
        const d = backDefaultRef.current;
        if (d) {
            setObjectPositions(prev => stripArrangementPositions(prev as any, d.positions) as any);
        }
        setBackDefault(null);
        backDefaultRef.current = null;
        try { localStorage.removeItem(BACK_DEFAULT_KEY); } catch { /* ignore */ }
        setBackStyleTouched(false);
        let token: string | null = null;
        try { token = localStorage.getItem('token'); } catch { /* ignore */ }
        if (token && backDefaultId) {
            try {
                await fetch(`/api/designs/${backDefaultId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
            } catch { /* the local default is cleared regardless */ }
            setBackDefaultId(null);
        }
        setBackNote('Default back cleared — backs are matched to each check design again.');
    };

    // Pin the front canvas's live arrangement into the positions
    // map (ref synchronously — the rebuild reads it — and state for
    // snapshots/persistence). See harvestLivePositions.
    const harvestFrontPositions = (canvas: any) => {
        harvestLivePositions(canvas, (entries) => {
            objectPositionsRef.current = { ...objectPositionsRef.current, ...entries };
            setObjectPositions(prev => ({ ...prev, ...entries }));
        });
    };

    // --- Mobile selection bar actions ---
    const handleSelectionDelete = () => {
        if (selSide === 'back' && backSelCount > 0) {
            checkBackRef.current?.deleteSelection();
            setBackSelCount(0);
        } else {
            deleteCanvasSelection(frontCanvasRef.current, (ids) => setDeletedIds(prev => [...prev, ...ids]), harvestFrontPositions);
            setFrontSelCount(0);
        }
    };
    const handleSelectionDone = () => {
        if (selSide === 'back' && backSelCount > 0) {
            checkBackRef.current?.clearSelection();
            setBackSelCount(0);
        } else {
            const c = frontCanvasRef.current;
            if (c) {
                c.discardActiveObject();
                c.requestRenderAll();
            }
            setFrontSelCount(0);
        }
    };

    // --- Object context menu (long-press / right-click) actions ---
    // Delete routes through the same deleted-ids flow as the
    // selection bar and the keyboard, so Undo sees it identically.
    const handleObjectMenuDelete = () => {
        if (!objMenu) return;
        if (objMenu.side === 'back') {
            checkBackRef.current?.deleteSelection();
            setBackSelCount(0);
        } else {
            deleteCanvasSelection(frontCanvasRef.current, (ids) => setDeletedIds(prev => [...prev, ...ids]), harvestFrontPositions);
            setFrontSelCount(0);
        }
        setObjMenu(null);
    };
    // Edit goes to where the object is actually edited: its form
    // field / panel section - or, for the user's own text objects,
    // straight into in-canvas text editing (their edits sync back
    // to state on exit; see the text:editing:exited handler).
    const handleObjectMenuEdit = () => {
        if (!objMenu) return;
        const { side, objectId } = objMenu;
        const target = editTargetForObject(objectId, side);
        setObjMenu(null);
        if (!target) return;
        if (target.canvasText) {
            const c = frontCanvasRef.current;
            const obj: any = c?.getObjects?.().find((o: any) => o.id === objectId);
            if (obj && typeof obj.enterEditing === 'function') {
                c.setActiveObject(obj);
                c.requestRenderAll();
                obj.enterEditing();
                if (typeof obj.selectAll === 'function') obj.selectAll();
            }
            return;
        }
        openToolSection(target.section);
        if (target.fieldId) {
            window.setTimeout(() => {
                const el = document.getElementById(target.fieldId!);
                if (!el) return;
                el.scrollIntoView({ block: 'center' });
                (el as HTMLElement).focus({ preventScroll: true });
                if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) el.select();
            }, 400);
        }
    };
    // The menu dismisses on outside tap, scroll, resize or Escape.
    useEffect(() => {
        if (!objMenu) return;
        const onDown = (e: Event) => {
            const t = e.target as HTMLElement | null;
            if (!t?.closest?.('[data-object-menu]')) setObjMenu(null);
        };
        const onScrollOrResize = () => setObjMenu(null);
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setObjMenu(null); };
        document.addEventListener('pointerdown', onDown, true);
        document.addEventListener('scroll', onScrollOrResize, true);
        window.addEventListener('resize', onScrollOrResize);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('pointerdown', onDown, true);
            document.removeEventListener('scroll', onScrollOrResize, true);
            window.removeEventListener('resize', onScrollOrResize);
            document.removeEventListener('keydown', onKey);
        };
    }, [objMenu]);

    const isPayroll = checkStyle === 'payroll';

    const primaryColor = isPayroll ? '#000000' : (bankTemplate?.specs?.colors?.primary || premiumTemplate?.colors?.primary || '#1a3a5c');
    const secondaryColor = isPayroll ? '#333333' : (bankTemplate?.specs?.colors?.secondary || premiumTemplate?.colors?.secondary || '#666666');

    // Auto-sync Border Colors with Theme
    useEffect(() => {
        const designKey = `${checkStyle}|${selectedBank || ''}`;
        if (borderSyncMountRef.current) {
            borderSyncMountRef.current = false;
            // Mount pass during a snapshot restore: the restored
            // border is authoritative - skip, and keep the guard set
            // for the restore commit's own pass below.
            if (borderSyncGuardKeyRef.current) return;
        } else if (borderSyncGuardKeyRef.current) {
            const guardKey = borderSyncGuardKeyRef.current;
            borderSyncGuardKeyRef.current = null;
            if (guardKey === designKey) return; // restored border wins
            // Stale guard (superseded design): fall through and sync.
        }
        if (aiBorderOverrideRef.current) {
            aiBorderOverrideRef.current = false;
            return;
        }
        if (borderConfig && borderConfig.style !== 'NONE') {
            setBorderConfig(prev => ({
                ...prev!,
                color: primaryColor,
                secondaryColor: secondaryColor
            }));
        }
    }, [selectedBank, checkStyle, primaryColor, secondaryColor]);

    // --- HELPER FUNCTIONS ---

    // Number to words conversion
    const numberToWords = (num: number): string => {
        const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
        const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

        const dollars = Math.floor(num);
        const cents = Math.round((num - dollars) * 100);

        let result = '';
        if (dollars === 0) {
            result = 'Zero';
        } else {
            if (dollars >= 1000) {
                result += ones[Math.floor(dollars / 1000)] + ' Thousand ';
            }
            if (dollars >= 100) {
                const hundreds = Math.floor((dollars % 1000) / 100);
                if (hundreds > 0) result += ones[hundreds] + ' Hundred ';
            }
            const remainder = dollars % 100;
            if (remainder >= 20) {
                result += tens[Math.floor(remainder / 10)] + ' ' + ones[remainder % 10];
            } else if (remainder >= 10) {
                result += teens[remainder - 10];
            } else if (remainder > 0) {
                result += ones[remainder];
            }
        }

        return `***${result.trim()} and ${cents.toString().padStart(2, '0')}/100***`;
    };

    // Get current paper color
    const getCurrentPaperColor = () => (typeof paperColor === 'string' && paperColor.startsWith('#')) ? paperColor : (PAPER_COLORS.find(c => c.id === paperColor)?.color || '#f8f4ec');

    // Check if security feature is enabled
    const isSecurityEnabled = (id: string) => securityFeatures.find(f => f.id === id)?.enabled || false;

    // Toggle security feature
    const toggleSecurityFeature = (id: string) => {
        setSecurityFeatures(prev => prev.map(f =>
            f.id === id ? { ...f, enabled: !f.enabled } : f
        ));
    };

    // Render check with PREMIUM details - BANK TEMPLATE AWARE
    const renderCheck = useCallback((c: fabric.Canvas) => {
        if (!c || (c as any).__isDisposed) return;
        // The design's write-ups are built as REAL objects below
        // (solid, selectable, draggable) and PRINT with the design.
        // Transaction fields render only what is typed - an unwritten
        // field's object carries empty text, so the face shows just
        // the design's printed labels and lines. There is NO ghost
        // layer: no ghost date, no dummy MICR, no sample texts, and
        // nothing for an export capture to hide (Princewill,
        // 2026-11-04: faint placeholders abolished as a concept).
        const renderSeq = ++renderSeqRef.current;

        const W = checkDimensions.width;
        // Dynamic Height for Full Page Payroll
        const isKarenCheck = checkStyle === 'payroll_karen' || selectedBank === 'KAREN_ART_STUDIO_PAYROLL';
        const H = isKarenCheck ? 900 : checkDimensions.height; // must match dynamicCanvasHeight (the real canvas height) or bottom-anchored elements render off-canvas
        // The height of the CHECK itself, as opposed to the page. On the
        // tall Karen page the canvas is 900 units but the check occupies
        // only the top checkH units (the rest is the voucher/stub area);
        // every bottom-of-check anchor — the MICR clear band, memo,
        // signature, warning band, chemical tag — belongs to the check,
        // so those anchor to bandH. They used to anchor to the page
        // bottom (H), which put Karen's MICR line down in the stub zone,
        // outside the ANSI X9.27 band the compliance gate measures
        // against checkH — the gate failed and no MICR was visible on
        // the check face. Page-level decoration (border, textures) still
        // spans the full page via H / textureBottom.
        const checkH = checkDimensions.height;
        const bandH = isKarenCheck ? checkH : H;
        const MICR_CLEAR_BAND = 65; // Strict compliance
        const textureBottom = showMicrBand ? H - MICR_CLEAR_BAND : H;

        const bankTemplate = selectedBank ? (bankSpecificTemplates[selectedBank] as any) : null;
        const premiumTemplate = selectedBank ? PREMIUM_TEMPLATES.find((p: any) => p.id === selectedBank) : null;
        const isTreasury = checkStyle === 'us_treasury' || selectedBank === 'US_TREASURY_OFFICIAL';
        const isPayroll = checkStyle === 'payroll';
        const isPayrollClassic = checkStyle === 'payroll_classic';
        const isKarenPayroll = isKarenCheck;
        const isBofaV2 = checkStyle === 'bofa_v2' || selectedBank === 'BOFA_BUSINESS_V2';
        // USAA close-look pass (2026-11-04): the specimen's field
        // labels are italic serif ("Pay to the / Order of", "Date",
        // "Dollars", "For") where the standard face prints bold sans
        // caps, and its signature zone carries no AUTHORIZED
        // SIGNATURE caption or two-signatures warning (the words
        // live in its microprint signature line instead).
        const isUsaa = selectedBank === 'USAA';
        // ANB Bank EXACT rebuild (2026-11-04, owner specimen
        // image-31.png top check): the face is the Deluxe scenic —
        // its printed labels carry a large SWASH SCRIPT initial with
        // letterspaced serif caps after it (Date / Pay to the order
        // of / Dollars / Memo), all in a warm medium-grey ink (the
        // owner's programme rule: write-ups keep their own colours,
        // never all black); its holder block prints in a chancery
        // italic serif; its wordmark prints BLACK with the striped
        // flag mark and the phone line; its CPSA padlock is an
        // OUTLINE icon with a three-line microprint note beside it;
        // and its signature zone carries NO caption or warning (the
        // MP marker is its microprint signature line). The script
        // face is the bundled Alex Brush (loaded in index.css for
        // the handwriting faces) with device-script fallbacks —
        // an honest substitution in the Trade Gothic mould: no
        // copperplate typeface is licensed for bundling.
        const isAnb = selectedBank === 'ANB';
        const ANB_SCRIPT = "'Alex Brush', 'Snell Roundhand', 'Apple Chancery', 'Segoe Script', 'Brush Script MT', cursive";
        const ANB_SERIF = "'Times New Roman', 'Liberation Serif', Times, serif";
        const ANB_CHANCERY = "'Apple Chancery', 'Lucida Calligraphy', 'Alex Brush', 'Snell Roundhand', cursive";
        // The labels' warm medium-grey ink, sampled from the
        // specimen (stroke cores of all four labels agree).
        const ANB_LABEL_INK = '#6e6359';
        const isAnyPayroll = isPayroll || isPayrollClassic || isKarenPayroll;
        const isCashier = checkStyle === 'cashier';
        const isMoneyOrder = checkStyle === 'money_order';
        const isCertified = checkStyle === 'certified';

        const primaryColor = isPayroll ? '#000000' : (bankTemplate?.specs?.colors?.primary || premiumTemplate?.colors?.primary || '#1a3a5c');
        const secondaryColor = isPayroll ? '#333333' : (bankTemplate?.specs?.colors?.secondary || premiumTemplate?.colors?.secondary || '#666666');
        const accentColor = isPayroll ? '#000000' : (bankTemplate?.specs?.colors?.accent || premiumTemplate?.colors?.accent || '#d4a574');
        const fontFamily = (isPayroll || isKarenPayroll) ? (isKarenPayroll ? 'Times New Roman, serif' : 'Arial, sans-serif') : (bankTemplate?.specs?.fontFamily || (FONTS.find((f: any) => f.id === selectedFont)?.family || 'Arial, sans-serif'));

        // Check again before clearing
        if ((c as any).__isDisposed) return;

        // PRESERVE SELECTION
        const activeObj = c.getActiveObject();
        const activeId = activeObj ? (activeObj as any).id : null;

        c.clear();

        c.setBackgroundColor(getCurrentPaperColor(), () => {
            if (!c || (c as any).__isDisposed) return;
            // A superseded render pass must not rebuild its (stale)
            // scene over the current one.
            if (renderSeq !== renderSeqRef.current) return;
            const offscreen = document.createElement('canvas');
            offscreen.width = W;
            offscreen.height = H;
            const ctx = offscreen.getContext('2d')!;

            // 1. Procedural Texture
            ctx.save();
            ctx.globalAlpha = textureOpacity;

            if (selectedTexture === 'blue_wave') {
                ctx.fillStyle = '#f0f8ff';
                ctx.fillRect(0, 0, W, H);
                ctx.strokeStyle = '#0055a450'; // Higher visibility for safety blue
                ctx.lineWidth = 0.6;
                for (let i = 0; i < H; i += 4) {
                    ctx.beginPath();
                    for (let x = 0; x < W; x += 10) {
                        ctx.lineTo(x, i + Math.sin(x * 0.02) * 3);
                    }
                    ctx.stroke();
                }
            } else if (selectedTexture === 'security') {
                // SECURITY CROSSHATCH - Ultra Premium visibility
                // The weave is stroked in the template's primary
                // UNLESS the design carries a weave-ink override
                // (designPresets TEMPLATE_WEAVE_INK): CITIZENS'
                // evidenced paper is untinted white safety stock,
                // and its saturated brand green otherwise paints
                // the whole face mint (parent render check,
                // 2026-11-05 — the paper token was already white;
                // the weave ink was the tint).
                ctx.strokeStyle = `${templateWeaveInk(selectedBank) ?? primaryColor}60`; // Increased from 40
                ctx.lineWidth = 0.7; // Thicker lines
                for (let i = -H; i < W + H; i += 5) {
                    ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i + H, H); ctx.stroke();
                    ctx.beginPath(); ctx.moveTo(i + H, 0); ctx.lineTo(i, H); ctx.stroke();
                }
            }

            // 2. Procedural Fluorescent Fibers (New Feature)
            if (isSecurityEnabled('fluorescent_fibers') || selectedTexture === 'fluorescent_fibers') {
                const colors = ['#ff00ff20', '#00ffff20', '#ffff0020', '#00ff0020'];
                for (let i = 0; i < 200; i++) {
                    ctx.strokeStyle = colors[Math.floor(Math.random() * colors.length)];
                    ctx.lineWidth = 0.5 + Math.random();
                    const x = Math.random() * W;
                    const y = Math.random() * H;
                    ctx.beginPath();
                    ctx.moveTo(x, y);
                    ctx.bezierCurveTo(x + 5, y - 5, x + 10, y + 5, x + 15, y);
                    ctx.stroke();
                }
            } else if (selectedTexture === 'parchment') {
                // Aged paper effect with spots
                ctx.fillStyle = 'rgba(139,119,101,0.28)'; // SIGNIFICANT visibility boost
                for (let i = 0; i < 800; i++) {
                    const size = Math.random() * 4 + 1;
                    ctx.beginPath();
                    ctx.arc(Math.random() * W, Math.random() * (textureBottom), size, 0, Math.PI * 2);
                    ctx.fill();
                }
            } else if (selectedTexture === 'marble') {
                // Swirling marble veins
                ctx.strokeStyle = 'rgba(80,100,120,0.32)'; // Ultra High Precision visibility
                ctx.lineWidth = 2.5; // Heavier premium veins
                ctx.stroke();
            } else if (selectedTexture === 'cloud_marble') {
                // CLOUD MARBLE (BofA Style)
                ctx.fillStyle = paperColor === '#f8f4ec' ? '#f0f4f8' : paperColor;
                ctx.fillRect(0, 0, W, H);
                // Soft clouds
                for (let i = 0; i < 15; i++) {
                    const x = Math.random() * W;
                    const y = Math.random() * H;
                    const r = 100 + Math.random() * 200;
                    const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
                    grad.addColorStop(0, 'rgba(210, 220, 230, 0.4)');
                    grad.addColorStop(1, 'rgba(240, 244, 248, 0)');
                    ctx.fillStyle = grad;
                    ctx.beginPath(); ctx.ellipse(x, y, r, r * 0.4, Math.random() * Math.PI, 0, Math.PI * 2); ctx.fill();
                }
                // veins
                ctx.strokeStyle = 'rgba(120,135,150,0.1)';
                ctx.lineWidth = 0.5;
                for (let i = 0; i < 20; i++) {
                    ctx.beginPath();
                    let curX = Math.random() * W; let curY = Math.random() * H;
                    ctx.moveTo(curX, curY);
                    for (let j = 0; j < 5; j++) {
                        curX += (Math.random() - 0.5) * 200; curY += (Math.random() - 0.5) * 100;
                        ctx.lineTo(curX, curY);
                    }
                    ctx.stroke();
                }
            } else if (selectedTexture === 'cloud_band') {
                // SKY CLOUD BAND (ANB specimen, 2026-11-04): a blue
                // cloud wash across the middle of the face over the
                // cream paper, at specimen strength - the sky band
                // must actually read as sky. (The first version
                // peaked at 0.20 alpha with 0.35 puffs and rendered
                // effectively invisible on the cream paper.) The
                // cloud layout is seeded, so the band is identical
                // on every render instead of reshuffling whenever
                // the canvas repaints; the whole band is clipped to
                // textureBottom like every other texture, so no
                // cloud ever enters the MICR clear band. Text stays
                // legible: the wash tops out under 0.5 alpha and the
                // cloud cores are soft-edged.
                ctx.save();
                ctx.beginPath(); ctx.rect(0, 0, W, textureBottom); ctx.clip();
                const bandGrad = ctx.createLinearGradient(0, H * 0.26, 0, H * 0.74);
                bandGrad.addColorStop(0, 'rgba(110,165,230,0)');
                bandGrad.addColorStop(0.22, 'rgba(110,165,230,0.30)');
                bandGrad.addColorStop(0.5, 'rgba(110,165,230,0.48)');
                bandGrad.addColorStop(0.78, 'rgba(110,165,230,0.30)');
                bandGrad.addColorStop(1, 'rgba(110,165,230,0)');
                ctx.fillStyle = bandGrad;
                ctx.fillRect(0, H * 0.26, W, H * 0.48);
                let cloudSeed = 20261104;
                const cloudRnd = () => { cloudSeed = (cloudSeed * 1103515245 + 12345) & 0x7fffffff; return cloudSeed / 0x7fffffff; };
                for (let i = 0; i < 14; i++) {
                    const cx = cloudRnd() * W;
                    const cy = H * 0.34 + cloudRnd() * H * 0.28;
                    const r = 46 + cloudRnd() * 84;
                    // Soft blue shadow under the cloud gives the
                    // white puffs definition against the band.
                    const shadow = ctx.createRadialGradient(cx, cy + r * 0.18, 0, cx, cy + r * 0.18, r);
                    shadow.addColorStop(0, 'rgba(90,140,210,0.20)');
                    shadow.addColorStop(1, 'rgba(90,140,210,0)');
                    ctx.fillStyle = shadow;
                    ctx.beginPath(); ctx.ellipse(cx, cy + r * 0.18, r, r * 0.42, 0, 0, Math.PI * 2); ctx.fill();
                    // The cloud itself: overlapping soft white puffs.
                    for (let j = 0; j < 3; j++) {
                        const px = cx + (cloudRnd() - 0.5) * r * 0.9;
                        const py = cy + (cloudRnd() - 0.5) * r * 0.22;
                        const pr = r * (0.55 + cloudRnd() * 0.3);
                        const grad = ctx.createRadialGradient(px, py, 0, px, py, pr);
                        grad.addColorStop(0, 'rgba(255,255,255,0.55)');
                        grad.addColorStop(0.7, 'rgba(255,255,255,0.28)');
                        grad.addColorStop(1, 'rgba(255,255,255,0)');
                        ctx.fillStyle = grad;
                        ctx.beginPath(); ctx.ellipse(px, py, pr, pr * 0.42, 0, 0, Math.PI * 2); ctx.fill();
                    }
                }
                ctx.restore();
            } else if (selectedTexture === 'cotton') {
                // Dense fiber threads
                ctx.strokeStyle = 'rgba(120,110,100,0.25)';
                ctx.lineWidth = 0.5;
                for (let i = 0; i < 400; i++) {
                    const x = Math.random() * W;
                    const y = Math.random() * (textureBottom);
                    ctx.beginPath();
                    ctx.moveTo(x, y);
                    ctx.lineTo(x + (Math.random() - 0.5) * 40, y + (Math.random() - 0.5) * 15);
                    ctx.stroke();
                }
            } else if (selectedTexture === 'particles') {
                // PREMIUM PARTICLES - Security dots and sparkles
                for (let i = 0; i < 300; i++) {
                    const x = Math.random() * W;
                    const y = Math.random() * (textureBottom);
                    const size = Math.random() * 2 + 0.5;
                    const hue = Math.random() * 60 + 30; // Gold-ish range
                    ctx.fillStyle = `hsla(${hue}, 50%, 50%, 0.15)`;
                    ctx.beginPath();
                    ctx.arc(x, y, size, 0, Math.PI * 2);
                    ctx.fill();
                }
                // Add metallic flecks
                ctx.fillStyle = `${accentColor}25`;
                for (let i = 0; i < 150; i++) {
                    ctx.fillRect(Math.random() * W, Math.random() * (textureBottom), 1.5, 1.5);
                }
            } else if (selectedTexture === 'rough') {
                // ROUGH PAPER - visible grain
                for (let y = 0; y < textureBottom; y += 2) {
                    for (let x = 0; x < W; x += 3) {
                        const brightness = Math.random() * 20 - 10;
                        ctx.fillStyle = `rgba(${128 + brightness},${120 + brightness},${110 + brightness},0.2)`;
                        ctx.fillRect(x, y, 3, 2);
                    }
                }
                // Add texture lines
                ctx.strokeStyle = 'rgba(100,90,80,0.08)';
                ctx.lineWidth = 0.5;
                for (let i = 0; i < 100; i++) {
                    ctx.beginPath();
                    ctx.moveTo(Math.random() * W, Math.random() * (textureBottom));
                    ctx.lineTo(Math.random() * W, Math.random() * (textureBottom));
                    ctx.stroke();
                }
            } else if (selectedTexture === 'guilloche_bg') {
                // GUILLOCHÉ WAVE background - elegant spirograph pattern
                ctx.strokeStyle = `${primaryColor}30`; // Boosted for premium feel
                ctx.lineWidth = 0.5;
                for (let centerY = 50; centerY < textureBottom; centerY += 80) {
                    for (let centerX = 50; centerX < W; centerX += 100) {
                        for (let ring = 0; ring < 4; ring++) {
                            ctx.beginPath();
                            for (let angle = 0; angle < Math.PI * 2; angle += 0.03) {
                                const r = 20 + ring * 10 + Math.sin(angle * 6) * 6;
                                const x = centerX + r * Math.cos(angle);
                                const y = centerY + r * Math.sin(angle);
                                if (angle === 0) ctx.moveTo(x, y);
                                else ctx.lineTo(x, y);
                            }
                            ctx.closePath();
                            ctx.stroke();
                        }
                    }
                }
            } else if (selectedTexture === 'prismatic') {
                // PRISMATIC SECURITY - Rainbow color bands
                for (let band = 0; band < W; band += 15) {
                    const hue = (band / W) * 360;
                    ctx.fillStyle = `hsla(${hue}, 40%, 80%, 0.08)`;
                    ctx.fillRect(band, 0, 15, textureBottom);
                }
                // Add security crosshatch over it
                ctx.strokeStyle = 'rgba(100,100,100,0.1)';
                ctx.lineWidth = 0.3;
                for (let i = -H; i < W + H; i += 8) {
                    ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i + H, textureBottom); ctx.stroke();
                }
            } else if (selectedTexture === 'laser_security') {
                // LASER SECURITY - Fine-line moiré pattern
                ctx.strokeStyle = `${primaryColor}15`;
                ctx.lineWidth = 0.4;
                for (let i = 0; i < W; i += 4) {
                    ctx.beginPath();
                    ctx.moveTo(i, 0);
                    ctx.bezierCurveTo(i + 20, H / 3, i - 20, (2 * H) / 3, i, textureBottom);
                    ctx.stroke();
                }
            } else if (selectedTexture === 'fluorescent_fibers') {
                // FLUORESCENT FIBERS - scattered colored threads
                const fiberColors = ['#ff000030', '#00ff0030', '#0000ff30', '#ffff0030'];
                for (let i = 0; i < 600; i++) {
                    ctx.strokeStyle = fiberColors[Math.floor(Math.random() * fiberColors.length)];
                    ctx.lineWidth = 0.6;
                    const x = Math.random() * W;
                    const y = Math.random() * (textureBottom);
                    ctx.beginPath();
                    ctx.moveTo(x, y);
                    ctx.quadraticCurveTo(x + 10, y + 5, x + 5, y + 15);
                    ctx.stroke();
                }
            } else if (selectedTexture === 'blue_wave') {
                // BLUE WAVE SAFETY PATTERN - Complex Bezier Curves
                ctx.lineWidth = 0.8;
                const colors = ['rgba(0, 100, 200, 0.15)', 'rgba(0, 150, 255, 0.1)', 'rgba(0, 50, 150, 0.08)'];

                // Multiple overlapping wave layers
                for (let layer = 0; layer < 3; layer++) {
                    ctx.strokeStyle = colors[layer];
                    const phaseShift = layer * 150;
                    const freq = 0.01 + (layer * 0.005);

                    for (let y = -50; y < H; y += 15) {
                        ctx.beginPath();
                        for (let x = 0; x <= W; x += 10) {
                            // Complex wave modulation
                            const yOffset = Math.sin((x + phaseShift) * freq) * 40 * Math.sin(x * 0.002);
                            const yMod = Math.cos((x - phaseShift) * (freq * 1.5)) * 20;

                            if (x === 0) ctx.moveTo(x, y + yOffset + yMod);
                            else ctx.lineTo(x, y + yOffset + yMod);
                        }
                        ctx.stroke();
                    }
                }

                // Add subtle vertical safety lines
                ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
                ctx.lineWidth = 0.5;
                for (let x = 0; x < W; x += 4) {
                    if (x % 40 === 0) continue; // Create "columns"
                    ctx.beginPath();
                    ctx.moveTo(x, 0);
                    ctx.lineTo(x, H);
                    ctx.stroke();
                }
            } else if (selectedTexture === 'toner_adhesion') {
                // TONER ADHESION - granular micro-texture
                for (let i = 0; i < 2000; i++) {
                    ctx.fillStyle = 'rgba(0,0,0,0.03)';
                    ctx.fillRect(Math.random() * W, Math.random() * (textureBottom), 1, 1);
                }
            } else if (selectedTexture === 'nano_optical') {
                // NANO-OPTICAL SCREEN - very fine hexagonal grid
                ctx.strokeStyle = `${primaryColor}15`;
                ctx.lineWidth = 0.2;
                const size = 6;
                for (let y = 0; y < H; y += size * 1.5) {
                    for (let x = 0; x < W + size; x += size * Math.sqrt(3)) {
                        const offsetX = (Math.floor(y / (size * 1.5)) % 2 === 0) ? 0 : (size * Math.sqrt(3)) / 2;
                        ctx.beginPath();
                        for (let i = 0; i < 6; i++) {
                            const angle = (Math.PI / 3) * i;
                            const hX = x + offsetX + size * Math.cos(angle);
                            const hY = y + size * Math.sin(angle);
                            if (i === 0) ctx.moveTo(hX, hY);
                            else ctx.lineTo(hX, hY);
                        }
                        ctx.closePath();
                        ctx.stroke();
                    }
                }
            } else if (selectedTexture === 'interference') {
                // INTERFERENCE PATTERN - Moiré-like overlapping circles
                ctx.lineWidth = 0.3;
                const centers = [{ x: 0, y: 0 }, { x: W, y: 0 }, { x: W / 2, y: H }];
                centers.forEach((center, idx) => {
                    ctx.strokeStyle = idx === 0 ? `${primaryColor}08` : idx === 1 ? `${secondaryColor}08` : `${accentColor}08`;
                    for (let r = 0; r < W * 1.2; r += 5) {
                        ctx.beginPath();
                        ctx.arc(center.x, center.y, r, 0, Math.PI * 2);
                        ctx.stroke();
                    }
                });
            } else if (selectedTexture === 'multi_tone') {
                // MULTI-TONE DEPTH - Topographic-like lines
                ctx.strokeStyle = `${primaryColor}12`;
                ctx.lineWidth = 0.5;
                for (let i = 0; i < 15; i++) {
                    ctx.beginPath();
                    const startY = (i / 15) * H;
                    ctx.moveTo(0, startY);
                    for (let x = 0; x <= W; x += 20) {
                        const noise = Math.sin(x * 0.01 + i) * 30 + Math.cos(x * 0.005) * 20;
                        ctx.lineTo(x, startY + noise);
                    }
                    ctx.stroke();
                }
            } else if (selectedTexture === 'fiber_optic') {
                // FIBER OPTIC - bright, scattered thin lines
                const opticColors = ['#00ffff40', '#ff00ff40', '#ffff0040', '#00ff0040'];
                for (let i = 0; i < 400; i++) {
                    ctx.strokeStyle = opticColors[Math.floor(Math.random() * opticColors.length)];
                    ctx.lineWidth = 0.8;
                    const x = Math.random() * W;
                    const y = Math.random() * (textureBottom);
                    ctx.beginPath();
                    ctx.moveTo(x, y);
                    ctx.lineTo(x + (Math.random() - 0.5) * 30, y + (Math.random() - 0.5) * 10);
                    ctx.stroke();
                }
            } else if (selectedTexture === 'micro_embossed') {
                // MICRO-EMBOSSED - very fine raised dots
                for (let y = 0; y < textureBottom; y += 4) {
                    for (let x = 0; x < W; x += 4) {
                        const shade = Math.random() * 20 - 10;
                        ctx.fillStyle = `rgba(0,0,0,${0.05 + shade / 100})`;
                        ctx.beginPath();
                        ctx.arc(x, y, 0.5, 0, Math.PI * 2);
                        ctx.fill();

                        ctx.fillStyle = `rgba(255,255,255,${0.05 - shade / 100})`;
                        ctx.beginPath();
                        ctx.arc(x + 1, y + 1, 0.5, 0, Math.PI * 2);
                        ctx.fill();
                    }

                }
            }

            // --- AI GENERATIVE PATTERN ENGINE (Phase 72) ---
            if (checkData.isAiActive && checkData.aiPrompt) {
                const prompt = checkData.aiPrompt.toLowerCase();
                ctx.save();

                // 1. Determine Colors (NLP)
                let strokeColor = 'rgba(0,0,0,0.1)';
                if (prompt.includes('blue')) strokeColor = 'rgba(0,0,255,0.15)';
                if (prompt.includes('red')) strokeColor = 'rgba(255,0,0,0.15)';
                if (prompt.includes('green')) strokeColor = 'rgba(0,128,0,0.15)';
                if (prompt.includes('gold')) strokeColor = 'rgba(218,165,32,0.2)';
                if (prompt.includes('purple')) strokeColor = 'rgba(128,0,128,0.15)';

                // 2. Determine Style (NLP)
                const isComplex = prompt.includes('complex') || prompt.includes('intricate') || prompt.includes('high detail');
                const isTech = prompt.includes('tech') || prompt.includes('cyber') || prompt.includes('circuit') || prompt.includes('digital');
                const isOrganic = prompt.includes('organic') || prompt.includes('natural') || prompt.includes('flow');

                // 3. Shape Engine
                ctx.strokeStyle = strokeColor;
                ctx.lineWidth = isComplex ? 0.3 : 0.8;

                if (prompt.includes('hex') || prompt.includes('honeycomb')) {
                    // HEXAGONAL GRID
                    const r = isComplex ? 8 : 15;
                    const h = r * Math.sin(Math.PI / 3);
                    for (let y = 0, i = 0; y < H + r; y += h, i++) {
                        for (let x = 0; x < W + r; x += r * 1.5) {
                            const cx = x + (i % 2 === 0 ? 0 : r * 0.75);
                            ctx.beginPath();
                            for (let a = 0; a < 6; a++) {
                                const angle = a * Math.PI / 3;
                                const px = cx + r * Math.cos(angle);
                                const py = y + r * Math.sin(angle);
                                if (a === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
                            }
                            ctx.closePath(); ctx.stroke();
                        }
                    }
                } else if (prompt.includes('wave') || prompt.includes('ocean')) {
                    // COMPLEX WAVES
                    const layers = isComplex ? 10 : 5;
                    for (let i = 0; i < layers; i++) {
                        ctx.beginPath();
                        for (let x = 0; x <= W; x += 10) {
                            const y = (i * (H / layers)) + Math.sin(x * 0.01 + i) * 20;
                            if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
                        }
                        ctx.stroke();
                    }
                } else if (prompt.includes('circle') || prompt.includes('dot') || prompt.includes('bubble')) {
                    // CIRCLES / DOTS
                    const count = isComplex ? 500 : 100;
                    for (let i = 0; i < count; i++) {
                        const x = Math.random() * W;
                        const y = Math.random() * textureBottom;
                        ctx.beginPath();
                        ctx.arc(x, y, Math.random() * (isComplex ? 5 : 15), 0, Math.PI * 2);
                        ctx.stroke();
                    }
                } else if (isTech) {
                    // CIRCUITRY
                    for (let i = 0; i < (isComplex ? 100 : 40); i++) {
                        const x = Math.random() * W;
                        const y = Math.random() * textureBottom;
                        ctx.beginPath();
                        ctx.moveTo(x, y);
                        ctx.lineTo(x + (Math.random() > 0.5 ? 50 : -50), y);
                        ctx.lineTo(x + (Math.random() > 0.5 ? 50 : -50), y + (Math.random() > 0.5 ? 20 : -20));
                        ctx.stroke();
                        ctx.beginPath(); ctx.arc(x, y, 2, 0, Math.PI * 2); ctx.fill();
                    }
                } else {
                    // DEFAULT: CHAOTIC LINES (if no specific shape recognized)
                    for (let i = 0; i < (isComplex ? 300 : 100); i++) {
                        ctx.beginPath();
                        ctx.moveTo(Math.random() * W, Math.random() * textureBottom);
                        ctx.lineTo(Math.random() * W, Math.random() * textureBottom);
                        ctx.stroke();
                    }
                }

                ctx.restore();
            }

            // --- INSTITUTION-SPECIFIC PROCEDURAL RENDERING (Universal Realism) ---
            if (selectedBank === 'us_treasury') {
                // U.S. TREASURY SPECIALIZED BACKGROUND
                ctx.save();
                // 1. Pale Green Microprint Pattern
                ctx.fillStyle = 'rgba(60, 90, 60, 0.08)';
                ctx.font = 'bold 3.5px "Arial", sans-serif';
                const treasuryText = ' UNITED STATES TREASURY FISCAL SERVICE • ';
                for (let y = 0; y < textureBottom; y += 5) {
                    ctx.fillText(treasuryText.repeat(10), (y % 10 === 0 ? 0 : -30), y);
                }
                // 2. Multi-Tone Security Waves (IRIS PRINTING)
                ctx.lineWidth = 0.5;
                for (let i = 0; i < 12; i++) {
                    ctx.strokeStyle = i % 2 === 0 ? '#1e393210' : '#d4af3710';
                    ctx.beginPath();
                    const startY = (i / 12) * H;
                    ctx.moveTo(0, startY);
                    for (let x = 0; x <= W; x += 15) {
                        ctx.lineTo(x, startY + Math.sin(x * 0.008 + i) * 25);
                    }
                    ctx.stroke();
                }
                // 3. Official Red/Blue Security Fibers
                for (let i = 0; i < 250; i++) {
                    ctx.strokeStyle = Math.random() > 0.5 ? '#e74c3c35' : '#3498db35';
                    ctx.lineWidth = 0.7;
                    const fx = Math.random() * W;
                    const fy = Math.random() * (textureBottom);
                    ctx.beginPath();
                    ctx.moveTo(fx, fy);
                    ctx.lineTo(fx + (Math.random() - 0.5) * 12, fy + (Math.random() - 0.5) * 8);
                    ctx.stroke();
                }
                ctx.restore();
            } else if (selectedBank === 'chime_pro' || selectedBank === 'varo_style') {
                // MODERN DIGITAL BANK GRID
                ctx.save();
                ctx.strokeStyle = `${primaryColor}15`;
                ctx.lineWidth = 0.4;
                for (let x = 0; x < W; x += 12) {
                    ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, textureBottom); ctx.stroke();
                }
                for (let y = 0; y < textureBottom; y += 12) {
                    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
                }
                // Subtle central radial glow
                const glow = ctx.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, W / 2);
                glow.addColorStop(0, `${primaryColor}05`);
                glow.addColorStop(1, 'transparent');
                ctx.fillStyle = glow;
                ctx.fillRect(0, 0, W, textureBottom);
                ctx.restore();
            } else if (selectedTexture === 'marble') {
                // MARBLE: Organic swirls
                ctx.save();
                for (let i = 0; i < 200; i++) {
                    ctx.beginPath();
                    const x = Math.random() * W;
                    const y = Math.random() * H;
                    ctx.arc(x, y, Math.random() * 40 + 10, 0, Math.PI * 2);
                    ctx.fillStyle = `rgba(0,0,0,${Math.random() * 0.03})`;
                    ctx.fill();
                }
                ctx.restore();
            } else if (selectedTexture === 'linen') {
                // LINEN: Bank-grade security paper with embedded fibers
                ctx.save();
                // Base weave pattern
                ctx.strokeStyle = 'rgba(0,0,0,0.12)';
                ctx.lineWidth = 0.35;
                for (let i = 0; i < W; i += 2.5) {
                    ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i, H); ctx.stroke();
                }
                for (let i = 0; i < H; i += 2.5) {
                    ctx.beginPath(); ctx.moveTo(0, i); ctx.lineTo(W, i); ctx.stroke();
                }
                // Cross-weave variation (diagonal threads)
                ctx.strokeStyle = 'rgba(0,0,0,0.04)';
                ctx.lineWidth = 0.3;
                for (let i = -H; i < W + H; i += 8) {
                    ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i + H, H); ctx.stroke();
                }
                // Security fibers (red and blue - standard bank check paper)
                const fiberSeed = 42;
                const seededRandom = (n: number) => {
                    const x = Math.sin(n * 9301 + fiberSeed) * 10000;
                    return x - Math.floor(x);
                };
                for (let i = 0; i < 60; i++) {
                    const fx = seededRandom(i) * W;
                    const fy = seededRandom(i + 100) * (H - 55);
                    const angle = seededRandom(i + 200) * Math.PI;
                    const len = 8 + seededRandom(i + 300) * 12;
                    ctx.strokeStyle = i % 3 === 0 ? 'rgba(180,30,30,0.18)' : 'rgba(30,60,180,0.15)';
                    ctx.lineWidth = 0.4 + seededRandom(i + 400) * 0.3;
                    ctx.beginPath();
                    ctx.moveTo(fx, fy);
                    ctx.lineTo(fx + Math.cos(angle) * len, fy + Math.sin(angle) * len);
                    ctx.stroke();
                }
                ctx.restore();
            } else if (selectedTexture === 'geometric_diamond') {
                // GEOMETRIC DIAMOND: Precision grid
                ctx.strokeStyle = `${primaryColor}40`; // Significantly boosted
                ctx.lineWidth = 0.5;
                const size = 20;
                for (let x = 0; x < W + size; x += size) {
                    for (let y = 0; y < H + size; y += size) {
                        ctx.beginPath();
                        ctx.moveTo(x, y - size / 2);
                        ctx.lineTo(x + size / 2, y);
                        ctx.lineTo(x, y + size / 2);
                        ctx.lineTo(x - size / 2, y);
                        ctx.closePath();
                        ctx.stroke();
                    }
                }
            } else if (selectedTexture === 'circuit_board') {
                // CIRCUIT BOARD: Tech security
                ctx.strokeStyle = `${primaryColor}35`; // Tech-forward visibility
                ctx.lineWidth = 0.8;
                for (let i = 0; i < 50; i++) {
                    const x = Math.random() * W;
                    const y = Math.random() * H;
                    ctx.beginPath();
                    ctx.moveTo(x, y);
                    ctx.lineTo(x + 20, y);
                    ctx.lineTo(x + 30, y + (Math.random() > 0.5 ? 10 : -10));
                    ctx.lineTo(x + 50, y + (Math.random() > 0.5 ? 20 : -20));
                    ctx.stroke();
                    // Nodes
                    ctx.fillStyle = `${primaryColor}30`;
                    ctx.beginPath(); ctx.arc(x, y, 1.5, 0, Math.PI * 2); ctx.fill();
                    ctx.beginPath(); ctx.arc(x + 50, y + (Math.random() > 0.5 ? 20 : -20), 1.5, 0, Math.PI * 2); ctx.fill();
                }
            } else if (selectedTexture === 'royal_damask') {
                // ROYAL DAMASK: Elegant swirls
                ctx.strokeStyle = `${primaryColor}28`; // High precision boost
                ctx.lineWidth = 1.1;
                for (let y = 0; y < H; y += 40) {
                    for (let x = 0; x < W; x += 40) {
                        ctx.beginPath();
                        ctx.arc(x, y, 15, 0, Math.PI, true);
                        ctx.bezierCurveTo(x - 5, y - 20, x + 5, y - 20, x + 20, y);
                        ctx.stroke();
                    }
                }
            } else if (selectedTexture === 'digital_hive') {
                // DIGITAL HIVE: Hexagonal depth
                ctx.strokeStyle = `${primaryColor}30`; // Matrix visibility boost
                ctx.lineWidth = 0.7;
                const r = 12;
                const h = r * Math.sin(Math.PI / 3);
                for (let y = 0, i = 0; y < H + r; y += h, i++) {
                    for (let x = 0; x < W + r; x += r * 1.5) {
                        const cx = x + (i % 2 === 0 ? 0 : r * 0.75);
                        ctx.beginPath();
                        for (let a = 0; a < 6; a++) {
                            const angle = a * Math.PI / 3;
                            const px = cx + r * Math.cos(angle);
                            const py = y + r * Math.sin(angle);
                            if (a === 0) ctx.moveTo(px, py);
                            else ctx.lineTo(px, py);
                        }
                        ctx.closePath();
                        ctx.stroke();
                    }
                }
            } else if (isNewBackgroundDesign(selectedTexture)) {
                // DESIGN-LIBRARY EXPANSION (2026-11-04): the new
                // patterns and paper textures draw from their own
                // module — same offscreen pass, same colourway, and
                // the module clips itself at textureBottom so no
                // design ever enters the MICR clear band.
                drawBackgroundDesign(ctx, selectedTexture, {
                    width: W, height: H, textureBottom,
                    primaryColor, secondaryColor, accentColor
                });
            }
            ctx.restore();

            // DYNAMIC BANK PATTERNS (Overlay on top of texture)
            if (selectedBank) {
                // === DYNAMIC BANK PATTERN ENGINES ===
                // This ensures each bank type has a visually distinct background strategy
                const isTechBank = ['chime_pro', 'varo_style', 'ally_bank', 'sofi_money', 'cash_app'].some(b => selectedBank.includes(b));
                const isGov = ['us_treasury', 'state_comptroller'].some(b => selectedBank.includes(b));
                const isCreditUnion = ['credit_union_classic', 'navy_federal', 'penfed'].some(b => selectedBank.includes(b));
                const isPremium = ['midnight_premium', 'ultra_obsidian', 'chase_sapphire'].some(b => selectedBank.includes(b));

                if (isTechBank) {
                    // ENGINE: DIGITAL GRID (Clean, Geometric, Tech-forward)
                    ctx.save();
                    ctx.globalAlpha = textureOpacity;
                    ctx.strokeStyle = `${primaryColor}25`; // Boosted
                    ctx.lineWidth = 0.5;
                    // Vertical tech lines
                    for (let x = 0; x < W; x += 15) {
                        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, textureBottom); ctx.stroke();
                        // Tech nodes
                        if (x % 45 === 0) {
                            ctx.fillStyle = `${primaryColor}30`;
                            ctx.beginPath(); ctx.arc(x, (x * 1.5) % (textureBottom), 1.5, 0, Math.PI * 2); ctx.fill();
                        }
                    }
                    // Horizontal tech lines
                    for (let y = 0; y < textureBottom; y += 15) {
                        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
                    }
                    ctx.restore();

                } else if (isGov) {
                    // ENGINE: FEDERAL GUILLOCHE (Complex, Detailed, Authoritative)
                    ctx.save();
                    ctx.globalAlpha = textureOpacity;
                    ctx.strokeStyle = `${primaryColor}20`; // Boosted
                    ctx.lineWidth = 0.4;
                    for (let x = 0; x < W; x += 3) {
                        ctx.beginPath();
                        ctx.moveTo(x, 0);
                        // Complex sine wave interference
                        for (let y = 0; y < textureBottom; y += 5) {
                            ctx.lineTo(x + Math.sin(y * 0.1) * 10, y);
                        }
                        ctx.stroke();
                    }
                    ctx.restore();

                } else if (isCreditUnion) {
                    // ENGINE: RADIAL COMMUNITY (Warm, Centralized, Flowing)
                    ctx.save();
                    ctx.globalAlpha = textureOpacity;
                    const cx = W / 2;
                    const cy = H / 2;
                    ctx.strokeStyle = `${primaryColor}15`; // Boosted
                    ctx.lineWidth = 0.8;
                    for (let i = 0; i < 360; i += 5) {
                        ctx.beginPath();
                        ctx.moveTo(cx, cy);
                        const angle = (i * Math.PI) / 180;
                        ctx.lineTo(cx + Math.cos(angle) * W, cy + Math.sin(angle) * H);
                        ctx.stroke();
                    }
                    // Soft circular waves
                    for (let r = 20; r < W; r += 20) {
                        ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
                    }
                    ctx.restore();

                } else if (isPremium) {
                    // ENGINE: LUXURY DARK MODE (Minimalist, Metallic)
                    ctx.save();
                    ctx.globalAlpha = textureOpacity;
                    const grad = ctx.createLinearGradient(0, 0, W, H);
                    grad.addColorStop(0, '#111');
                    grad.addColorStop(0.5, '#222');
                    grad.addColorStop(1, '#111');
                    ctx.fillStyle = grad;
                    ctx.fillRect(0, 0, W, textureBottom);
                    ctx.restore();
                    for (let y = 0; y < textureBottom; y += 25) {
                        ctx.beginPath();
                        for (let x = 0; x < W; x += 5) {
                            ctx.lineTo(x, y + Math.sin(x * 0.04) * 12 + Math.cos(x * 0.02) * 5);
                        }
                        ctx.stroke();
                    }
                } else if (isBofaV2 && (!borderConfig || borderConfig.style === 'NONE')) {
                    // ENGINE: BOFA V2 REPLICA - HYPER-FIDELITY FINE RADIAL GUILLOCHE
                    ctx.save();

                    // Center of the "Starburst" (Using Hologram position as anchor)
                    const cx = W - 340;
                    const cy = 210;
                    const maxR = Math.sqrt(W * W + H * H);

                    // 1. Fine Radial Starburst (High Frequency Moiré)
                    ctx.lineWidth = 0.3; // Slightly thicker for better visibility

                    // Layer A: Primary Dense Blue Radial
                    ctx.strokeStyle = '#d9eefcCC'; // 0.8 opacity approx (CC in hex)
                    for (let r = 10; r < maxR; r += 4) { // Higher density
                        ctx.beginPath();
                        for (let theta = 0; theta < Math.PI * 2; theta += 0.01) {
                            const rPrime = r + (r * 0.035) * Math.sin(64 * theta); // Higher frequency
                            const x = cx + rPrime * Math.cos(theta);
                            const y = cy + rPrime * Math.sin(theta);
                            if (theta === 0) ctx.moveTo(x, y);
                            else ctx.lineTo(x, y);
                        }
                        ctx.stroke();
                    }

                    // Layer B: Interference Weave (Teal/Greenish tint)
                    ctx.strokeStyle = 'rgba(203, 221, 232, 0.6)';
                    ctx.lineWidth = 0.2;
                    for (let r = 12; r < maxR; r += 6) {
                        ctx.beginPath();
                        for (let theta = 0; theta < Math.PI * 2; theta += 0.015) {
                            const rPrime = r + (r * 0.025) * Math.sin(72 * theta + Math.PI);
                            const x = cx + rPrime * Math.cos(theta);
                            const y = cy + rPrime * Math.sin(theta);
                            if (theta === 0) ctx.moveTo(x, y);
                            else ctx.lineTo(x, y);
                        }
                        ctx.stroke();
                    }

                    // 2. Removed V-Dot Grid Pattern (Per user request)
                    ctx.restore();
                } else {
                    // ENGINE: STANDARD COMMERCIAL (Standard Linen/Security Weave)
                    ctx.save();
                    ctx.globalAlpha = textureOpacity;
                    ctx.strokeStyle = `${primaryColor}15`;
                    ctx.lineWidth = 0.5;

                    // Classic Step-and-Repeat or Crosshatch
                    const spacing = 4;
                    for (let x = 0; x < W; x += spacing) {
                        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, textureBottom); ctx.stroke();
                    }
                    for (let y = 0; y < textureBottom; y += spacing) {
                        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
                    }

                    // Add subtle logo-like repetition if possible, or just a diamond pattern
                    ctx.fillStyle = `${primaryColor}08`;
                    for (let y = 0; y < textureBottom; y += 40) {
                        for (let x = 0; x < W; x += 40) {
                            if ((x + y) % 80 === 0) {
                                ctx.beginPath();
                                ctx.arc(x, y, 2, 0, Math.PI * 2);
                                ctx.fill();
                            }
                        }
                    }
                    ctx.restore();

                    // 3b-special. THEMATIC UNDERLAYS (Money Order/Cashier/Certified)
                    if (isMoneyOrder || isCashier || isCertified) {
                        ctx.save();
                        ctx.globalAlpha = 0.04;
                        ctx.fillStyle = primaryColor;
                        const patternText = isMoneyOrder ? 'MONEY ORDER' : (isCashier ? 'OFFICIAL INSTRUMENT' : 'CERTIFIED');

                        if (isCertified) {
                            // Guilloché-like Waves for Certified
                            ctx.strokeStyle = '#c9a227';
                            ctx.lineWidth = 0.5;
                            ctx.globalAlpha = 0.15;
                            for (let y = 0; y < textureBottom; y += 25) {
                                ctx.beginPath();
                                for (let x = 0; x < W; x += 5) {
                                    ctx.lineTo(x, y + Math.sin(x * 0.04) * 12 + Math.cos(x * 0.02) * 5);
                                }
                                ctx.stroke();
                            }
                        } else {
                            // Diagonal Repeating Text for MO/Cashier
                            ctx.font = 'bold 10px Arial';
                            for (let y = -W; y < H; y += 80) {
                                for (let x = 0; x < W; x += 150) {
                                    ctx.save();
                                    ctx.translate(x, y);
                                    ctx.rotate(-Math.PI / 4);
                                    ctx.fillText(patternText, 0, 0);
                                    ctx.restore();
                                }
                            }
                        }
                        ctx.restore();
                    }
                }
            }
            // 3c. SECURITY FIBER INJECTION (Red/Blue Silk Fibers)
            for (let i = 0; i < 400; i++) {
                const fx = Math.random() * W;
                const fy = Math.random() * (textureBottom);
                const length = 2 + Math.random() * 4;
                const fAngle = Math.random() * Math.PI * 2;
                ctx.strokeStyle = Math.random() > 0.5 ? 'rgba(180, 20, 20, 0.4)' : 'rgba(20, 20, 180, 0.4)';
                ctx.lineWidth = 0.3;
                ctx.beginPath();
                ctx.moveTo(fx, fy);
                ctx.quadraticCurveTo(fx + Math.cos(fAngle) * length, fy + Math.sin(fAngle) * length, fx + Math.cos(fAngle + 0.2) * (length + 1), fy + Math.sin(fAngle + 0.2) * (length + 1));
                ctx.stroke();
            }

            // 3d. GOLD/SILVER DUST
            for (let i = 0; i < 500; i++) {
                ctx.fillStyle = Math.random() > 0.5 ? `${accentColor}40` : '#ffffff20';
                ctx.beginPath(); ctx.arc(Math.random() * W, Math.random() * (textureBottom), 0.5, 0, Math.PI * 2); ctx.fill();
            }

            // 3e. MICR ISOLATION ZONE (Ensure clean band is pure white/noise-free)
            // Anchored to the CHECK's bottom edge (bandH): on the tall
            // Karen page the band belongs to the check at the top of the
            // page, not to the page bottom.
            if (showMicrBand) {
                ctx.fillStyle = 'white';
                ctx.fillRect(0, bandH - MICR_CLEAR_BAND, W, MICR_CLEAR_BAND);
            }

            // 4. PRISMATIC NOISE for security paper effect (FULL HEIGHT)
            if (!isBofaV2) { // Skip noise for BofA v2 to keep guilloché sharp
                const imageData = ctx.getImageData(0, 0, W, H);
                const data = imageData.data;
                for (let i = 0; i < data.length; i += 4) {
                    const noise = (Math.random() - 0.5) * 8;
                    data[i] = Math.min(255, Math.max(0, data[i] + noise));
                    data[i + 1] = Math.min(255, Math.max(0, data[i + 1] + noise));
                    data[i + 2] = Math.min(255, Math.max(0, data[i + 2] + noise));
                }
                ctx.putImageData(imageData, 0, 0);
            }

            // 4e. IMPORTED DESIGN PATTERN (Upload Check Design): the
            // user's framed pattern swatch drawn as the face's paper
            // design. It covers the procedural texture (the pattern IS
            // the texture now) but is drawn only down to textureBottom,
            // so it never enters the MICR clear band; borders, warning
            // text, microprint frame and every field still draw on top
            // from the studio's own state. Nothing from the source
            // photo's write-ups exists in this layer - the swatch is a
            // background the user framed, attested as theirs to use.
            if (designPattern && designPatternImgRef.current && designPatternLoadedForRef.current === designPattern.url) {
                // TILE the swatch - never stretch it. A stretched
                // swatch distorts the design's aspect (a photo's
                // guilloche band smears across the face); a single
                // face-spanning copy reads as a pasted picture, not
                // a paper design. The tile takes the natural width
                // for the swatch's own aspect (patternTileWidthFor:
                // motif swatches at the quarter-face base, wide
                // design bands wider, closer to their printed
                // scale), aspect always preserved. Tiles are placed
                // with MIRROR parity (patternTilePlacements): a
                // photo crop is not a seamless tile, and plain
                // repetition cut every wave and gradient at the
                // tile edge, banding the face with hard seams —
                // mirrored neighbours share their edge pixels, so
                // the design flows across the whole face. The tiles
                // sit at PATTERN_TILE_ALPHA over the check's paper
                // (the design's own sampled paper colour): at the
                // earlier 0.94 the paper underneath was structurally
                // invisible — 94% of every face pixel was photo —
                // so applying the sampled paper read as "not
                // applied" on the live build; at 0.86 the pattern
                // stays dominant while the paper genuinely tints
                // the face, and the printed fields on top stay
                // legible the way a real check's pale background
                // design allows. This same renderCheck pass feeds
                // the on-screen canvas and every canvas export, so
                // screen and export cannot drift apart.
                const img = designPatternImgRef.current;
                const { tileW, tileH } = patternTileSize(img.width, img.height);
                ctx.save();
                ctx.beginPath();
                ctx.rect(0, 0, W, textureBottom);
                ctx.clip();
                ctx.globalAlpha = PATTERN_TILE_ALPHA;
                for (const p of patternTilePlacements(W, textureBottom, tileW, tileH)) {
                    ctx.save();
                    ctx.translate(p.x + (p.flipX ? tileW : 0), p.y + (p.flipY ? tileH : 0));
                    ctx.scale(p.flipX ? -1 : 1, p.flipY ? -1 : 1);
                    ctx.drawImage(img, 0, 0, tileW, tileH);
                    ctx.restore();
                }
                ctx.restore();
            }

            // 5. DECORATIVE BORDER - DYNAMIC STYLES based on Check Style
            const isModern = ['echeck', 'payroll', 'clean_modern', 'counter'].includes(checkStyle);
            const isClassic = ['personal', 'business', 'certified'].includes(checkStyle);
            const isFormal = ['cashier', 'money_order'].includes(checkStyle);

            // DESIGN-LIBRARY BORDERS (2026-11-04): the five new
            // border styles are drawn onto this offscreen layer as
            // well as the SVG preview overlay, so the design a user
            // picks is the design the export carries. The fallback
            // frames below only fire for NONE borders, so they can
            // never double-draw over this.
            if (borderConfig && isNewBorderStyle(borderConfig.style)) {
                drawCanvasBorder(ctx, borderConfig as any, W, H);
            }

            if (isClassic && (!borderConfig || borderConfig.style === 'NONE')) {
                // CLASSIC DOUBLE BORDER
                ctx.strokeStyle = primaryColor;
                ctx.lineWidth = 3;
                ctx.strokeRect(6, 6, W - 12, H - 12);
                ctx.strokeStyle = secondaryColor + '40';
                ctx.lineWidth = 1;
                ctx.strokeRect(12, 12, W - 24, H - 24);
            } else if (isFormal && (!borderConfig || borderConfig.style === 'NONE')) {
                // FORMAL HEAVY BORDER
                ctx.strokeStyle = primaryColor;
                ctx.lineWidth = 6;
                ctx.strokeRect(4, 4, W - 8, H - 8);
                ctx.strokeStyle = 'white';
                ctx.lineWidth = 2;
                ctx.strokeRect(6, 6, W - 12, H - 12);
            } else if (isModern && (!borderConfig || borderConfig.style === 'NONE')) {
                // MODERN MINIMALIST BORDER
                if (checkStyle !== 'echeck') { // eChecks are borderless by default
                    ctx.strokeStyle = `${primaryColor}60`;
                    ctx.lineWidth = 1;
                    ctx.strokeRect(2, 2, W - 4, H - 4);
                }
            } else if (checkStyle === 'bofa_classic' && (!borderConfig || borderConfig.style === 'NONE')) {
                // BOFA CLASSIC DOUBLE BORDER
                ctx.strokeStyle = primaryColor;
                ctx.lineWidth = 1;
                ctx.strokeRect(10, 10, W - 20, H - 20);
                ctx.strokeStyle = '#5d728d60';
                ctx.lineWidth = 0.5;
                ctx.strokeRect(13, 13, W - 26, H - 26);
            } else if (isBofaV2 && (!borderConfig || borderConfig.style === 'NONE')) {
                // ENGINE: BOFA V2 REPLICA - HYPER-FIDELITY BRAIDED RIBBON BORDER
                // Concept: A solid blue band with interlaced white sine waves ("Chain Link" effect)

                const m = 15; // Margin
                const bandWidth = 5;

                ctx.save();

                // 1. Draw the Solid Blue Band (Background of the braid)
                ctx.strokeStyle = '#3b5a9a'; // BofA Blue
                ctx.lineWidth = bandWidth;
                ctx.lineJoin = 'round';
                ctx.strokeRect(m, m, W - m * 2, H - m * 2);

                // 2. Draw Interlaced White Braid (The "Chain")
                ctx.strokeStyle = '#ffffff';
                ctx.lineWidth = 1.2;
                ctx.lineCap = 'round';

                // Optimized Braid Function
                const drawBraidSide = (x1: number, y1: number, x2: number, y2: number) => {
                    const dx = x2 - x1;
                    const dy = y2 - y1;
                    const len = Math.sqrt(dx * dx + dy * dy);
                    const angle = Math.atan2(dy, dx);

                    ctx.save();
                    ctx.translate(x1, y1);
                    ctx.rotate(angle);

                    ctx.beginPath();
                    // Draw two out-of-phase sine waves along the band
                    for (let t = 0; t <= len; t += 1) {
                        const freq = 0.5;
                        const amp = bandWidth * 0.35;

                        // Wave A
                        ctx.moveTo(t, Math.sin(t * freq) * amp);
                        ctx.lineTo(t + 1, Math.sin((t + 1) * freq) * amp);

                        // Wave B (Phase shifted PI)
                        ctx.moveTo(t, Math.sin(t * freq + Math.PI) * amp);
                        ctx.lineTo(t + 1, Math.sin((t + 1) * freq + Math.PI) * amp);
                    }
                    ctx.stroke();
                    ctx.restore();
                };

                // Apply Braid to all 4 sides
                drawBraidSide(m, m, W - m, m);      // Top
                drawBraidSide(W - m, m, W - m, H - m); // Right
                drawBraidSide(W - m, H - m, m, H - m); // Bottom
                drawBraidSide(m, H - m, m, m);      // Left

                // 3. Corner Medallions (Ornate Rosettes)
                const drawCornerMedallion = (cx: number, cy: number) => {
                    ctx.save();
                    ctx.translate(cx, cy);
                    ctx.fillStyle = '#3b5a9a';
                    // Solid circle backing
                    ctx.beginPath();
                    ctx.arc(0, 0, bandWidth * 1.5, 0, Math.PI * 2);
                    ctx.fill();
                    // White starburst center
                    ctx.strokeStyle = 'white';
                    ctx.lineWidth = 1;
                    for (let i = 0; i < 8; i++) {
                        ctx.rotate(Math.PI / 4);
                        ctx.beginPath();
                        ctx.moveTo(-4, 0);
                        ctx.lineTo(4, 0);
                        ctx.stroke();
                    }
                    ctx.restore();
                };

                drawCornerMedallion(m, m);
                drawCornerMedallion(W - m, m);
                drawCornerMedallion(W - m, H - m);
                drawCornerMedallion(m, H - m);

                // 4. Integrated Security Text (Right Side - White on Blue for Contrast)
                ctx.save();
                ctx.translate(W - m, H / 2);
                ctx.rotate(Math.PI / 2);
                ctx.font = 'bold 5px Arial';
                ctx.fillStyle = '#3b5a9a'; // Dark blue text OUTSIDE the band slightly or on it? Reference shows ON text line usually.
                // Adjusting to sit just inside the line for legibility
                ctx.fillText("ORIGINAL DOCUMENT HAS A COLORED BACKGROUND", 0, 10);
                ctx.restore();

                ctx.restore();
            }

            // 4b. SECURITY STATEMENT STRIP (TOP OF CHECK): NO LONGER
            // drawn here. It was baked into this raster texture at 1x,
            // so on the live canvas it went BLURRY when zoomed
            // (Princewill, 2026-11-04: "No matter how you zoom it, it
            // needs to be sharp"). It is now a native fabric Text
            // object ('topStatement', created in the object pass
            // below) - crisp at any zoom, same words, same gate,
            // same seat. Exports were and stay x6 captures of the
            // live canvas, so they carry the native text at 600 DPI.

            // 4c. INDUSTRIAL SPECIALTY HEADERS — now rendered as Fabric objects (see below, after c.setBackgroundImage)

            // 4d. CLASSIC PAYROLL HEADER (Karen Art Studio Style)
            if (isKarenPayroll) {
                ctx.save();
                // TOP BLUE BANNER
                ctx.fillStyle = '#0055a4';
                ctx.fillRect(0, 0, W, 18);
                // The banner's white sentence is a native fabric
                // object now ('karenBannerText', object pass below) -
                // baked into this raster it blurred on zoom like the
                // top statement strip did (item 11 audit).

                // BLUE BORDER LINE
                ctx.strokeStyle = '#0055a4';
                ctx.lineWidth = 1.5;
                ctx.strokeRect(5, 20, W - 10, textureBottom - 25);

                // Blue Safety Band at the bottom of the check part
                ctx.fillStyle = '#dae9f7';
                ctx.fillRect(40, textureBottom - 15, W - 80, 12);
                ctx.strokeStyle = '#0055a4';
                ctx.lineWidth = 0.5;
                ctx.strokeRect(40, textureBottom - 15, W - 80, 12);

                // The band's sentence is a native fabric object
                // now ('securityBandText', object pass below).
                ctx.restore();
            } else if (isPayrollClassic) {
                // ... (Existing logic for payroll_classic if different)
                ctx.save();
                ctx.fillStyle = '#dae9f7';
                ctx.fillRect(40, textureBottom - 15, W - 80, 12);
                ctx.strokeStyle = '#0055a4';
                ctx.lineWidth = 0.5;
                ctx.strokeRect(40, textureBottom - 15, W - 80, 12);
                // The band's sentence is a native fabric object
                // now ('securityBandText', object pass below).
                ctx.restore();
            }

            // 6. MICROPRINT FRAME (High-Fidelity)
            if (isSecurityEnabled('microprint')) {
                ctx.save();
                ctx.font = '3px Arial';
                ctx.fillStyle = `${primaryColor}60`;
                const microText = 'SAFE DOCUMENT • ORIGINAL DOCUMENT • '.repeat(20);

                // Top border
                ctx.fillText(microText, 10, 8);
                // Bottom border: this segment sits inside the MICR clear
                // band (ANSI X9.27 - the bottom 5/8 inch carries MICR
                // characters only), so it prints only when the owner
                // turns MICR Clear Band Protection off.
                if (!isSecurityEnabled('micr_clear_band')) {
                    ctx.fillText(microText, 10, H - 10); // Edge to edge at bottom
                }

                // Left Vertical
                ctx.save();
                ctx.translate(8, H - 10);
                ctx.rotate(-Math.PI / 2);
                ctx.fillText(microText, 0, 0);
                ctx.restore();

                // Right Vertical
                ctx.save();
                ctx.translate(W - 8, 8);
                ctx.rotate(Math.PI / 2);
                ctx.fillText(microText, 0, 0);
                ctx.restore();

                ctx.restore();
            }

            // 6a. UV SECURITY MARKERS (design-layer representation of UV-reactive marks)
            if (isSecurityEnabled('uv_markers')) {
                ctx.save();
                const uvSeed = 7;
                const uvRand = (n: number) => { const x = Math.sin(n * 4177 + uvSeed) * 10000; return x - Math.floor(x); };
                ctx.fillStyle = 'rgba(140, 255, 120, 0.28)';
                ctx.strokeStyle = 'rgba(120, 255, 110, 0.35)';
                for (let i = 0; i < 16; i++) {
                    const ux = 30 + uvRand(i) * (W - 60);
                    const uy = 30 + uvRand(i + 50) * (textureBottom - 90);
                    const r = 1.6 + uvRand(i + 90) * 2.2;
                    ctx.beginPath(); ctx.arc(ux, uy, r, 0, Math.PI * 2); ctx.fill();
                }
                ctx.font = 'bold 7px Arial';
                ctx.fillStyle = 'rgba(120, 255, 110, 0.4)';
                ctx.fillText('UV', W - 42, 34);
                ctx.beginPath(); ctx.arc(W - 34, 31, 10, 0, Math.PI * 2); ctx.stroke();
                ctx.restore();
            }

            // 6b. PRISMATIC SECURITY COLUMN (Right Margin - Authentic Position)
            if (isSecurityEnabled('prismatic_column')) {
                ctx.save();
                // Positioned on the right margin, spanning full height between borders
                const colX = W - 30;
                const colW = 15;
                const prismGrad = ctx.createLinearGradient(colX, 0, colX + colW, 0);
                prismGrad.addColorStop(0, 'rgba(255,0,0,0.12)');
                prismGrad.addColorStop(0.5, 'rgba(0,255,0,0.12)');
                prismGrad.addColorStop(1, 'rgba(0,0,255,0.12)');

                ctx.fillStyle = prismGrad;
                ctx.fillRect(colX, 10, colW, H - 20); // Spans full height

                // Vertical text in column
                ctx.fillStyle = 'rgba(0,0,0,0.2)';
                ctx.font = 'bold 5px Arial'; ctx.textAlign = 'center';
                for (let y = 30; y < H - 30; y += 40) {
                    ctx.save();
                    ctx.translate(colX + (colW / 2), y);
                    ctx.rotate(Math.PI / 2);
                    ctx.fillText('SECURITY', 0, 0);
                    ctx.restore();
                }
                ctx.restore();
            }

            // 6c. BOFA VERTICAL SECURITY STRIPE
            if (micrVariantFor(checkStyle, selectedBank) === 'bofa') {
                ctx.fillStyle = isBofaV2 ? '#3b5a9a' : '#012169'; // Slightly brighter blue for v2
                const barWidth = isBofaV2 ? 18 : 22;
                const barX = W - barWidth - 12;
                const barY = 12;
                const barH = H - 65;
                ctx.fillRect(barX, barY, barWidth, barH);

                ctx.save();
                ctx.fillStyle = 'white';
                ctx.font = isBofaV2 ? 'bold 6px Arial' : 'bold 6.5px Arial';
                ctx.textAlign = 'center';
                ctx.translate(barX + barWidth / 2 + 2, barY + barH / 2);
                ctx.rotate(Math.PI / 2);
                const barText = isBofaV2
                    ? "SECURITY FEATURES INCLUDE TRUE WATERMARK PAPER, MICROPRINT BORDER, HEAT SENSITIVE ICON, AND FOIL HOLOGRAM"
                    : "THIS DOCUMENT HAS A COLORED BACKGROUND AND MICROPRINTING. THE REVERSE SIDE INCLUDES AN ARTIFICIAL WATERMARK.";
                ctx.fillText(barText, 0, 0);
                ctx.restore();
            }

            // 7. WATERMARK - REMOVED from background context (Moved to Fabric Object for Draggability)
            // if (isSecurityEnabled('watermark')) { ... }

            // 8. GUILLOCHE PATTERN with IRIS PRINTING (Color Shifting)
            if (isSecurityEnabled('guilloche')) {
                const irisGrad = ctx.createLinearGradient(W - 120, 30, W - 40, 110);
                irisGrad.addColorStop(0, `${primaryColor}20`);
                irisGrad.addColorStop(0.5, `${secondaryColor}15`);
                irisGrad.addColorStop(1, `${accentColor}20`);

                ctx.strokeStyle = irisGrad;
                ctx.lineWidth = 0.6;
                for (let ring = 0; ring < 8; ring++) { // Increased complexity
                    ctx.beginPath();
                    for (let angle = 0; angle < Math.PI * 2; angle += 0.04) {
                        const r = 25 + ring * 6 + Math.sin(angle * 12) * 6;
                        const x = W - 80 + r * Math.cos(angle);
                        const y = 70 + r * Math.sin(angle);
                        if (angle === 0) ctx.moveTo(x, y);
                        else ctx.lineTo(x, y);
                    }
                    ctx.closePath();
                    ctx.stroke();
                }
            }

            // 20. ANTI-COPY V-DOT (highly sophisticated dotted pattern)
            if (isSecurityEnabled('anti_copy_vdot')) {
                ctx.fillStyle = `${secondaryColor}10`;
                for (let y = 30; y < textureBottom - 30; y += 15) {
                    for (let x = 30; x < W - 30; x += 15) {
                        ctx.beginPath();
                        ctx.arc(x, y, 0.4, 0, Math.PI * 2);
                        ctx.fill();
                    }
                }
            }

            // --- SYNCHRONOUS FABRIC INTEGRATION ---
            const bgImg = new fabric.Image(offscreen, {
                left: 0, top: 0, selectable: false, evented: false
            });
            c.add(bgImg);
            c.sendToBack(bgImg);

            // 9. HOLOGRAPHIC STRIP on LEFT (correct placement)
            if (isSecurityEnabled('holographic')) {
                const prismGradient = new fabric.Gradient({
                    type: 'linear',
                    coords: { x1: 0, y1: 0, x2: 0, y2: textureBottom },
                    colorStops: [
                        { offset: 0, color: `${primaryColor}40` },
                        { offset: 0.25, color: `${secondaryColor}40` },
                        { offset: 0.5, color: `${accentColor}40` },
                        { offset: 0.75, color: `${primaryColor}40` },
                        { offset: 1, color: `${secondaryColor}40` }
                    ]
                });

                const holoRect = new fabric.Rect({
                    width: 25, height: textureBottom - 80,
                    fill: prismGradient, stroke: 'rgba(255,255,255,0.2)', strokeWidth: 0.5
                });

                // Micro-Logo Pattern Overlay for Hologram
                const logoPattern = [];
                for (let py = 10; py < textureBottom - 100; py += 30) {
                    logoPattern.push(new fabric.Text('★', {
                        left: 12, top: py, fontSize: 8, fill: 'rgba(255,255,255,0.3)', originX: 'center'
                    }));
                }

                const holoGroup = new fabric.Group([holoRect, ...logoPattern], {
                    left: W - 45, top: 40, selectable: true, hasControls: true
                });
                c.add(holoGroup);
            }

            // 10. EMBEDDED SECURITY THREAD (High-Fidelity)
            // Logic: A vertical thread with repeating micro-text
            const threadX = W * 0.7; // Positioned around 70% width
            const threadGroup = new fabric.Group([
                new fabric.Rect({
                    left: 0, top: 5, width: 8, height: textureBottom - 10,
                    fill: 'rgba(0,0,0,0.05)', stroke: 'rgba(0,0,0,0.03)', strokeWidth: 0.3
                }),
                ...Array.from({ length: 20 }).map((_, i) => new fabric.Text("OMNICHECK SECURE", {
                    left: 4, top: 10 + i * 15, fontSize: 3, angle: -90, fill: 'rgba(0,0,0,0.3)', originX: 'center'
                }))
            ], { left: threadX, top: 0, selectable: true, hasControls: true });
            c.add(threadGroup);

            // 11. SIMULATED PERFORATION (Realistic Tear Edge)
            c.add(new fabric.Line([0, 2, W, 2], {
                stroke: 'rgba(0,0,0,0.1)', strokeWidth: 1, strokeDashArray: [1, 3], selectable: false
            }));
            c.add(new fabric.Line([2, 0, 2, H], {
                stroke: 'rgba(0,0,0,0.1)', strokeWidth: 1, strokeDashArray: [1, 3], selectable: false
            }));

            // 12. LATENT IMAGE SECURITY (Micro-Line Pattern)
            // Logic: A region where "SAFE" is visible only via scanning/tilting
            const latentText = 'SAFE';
            const latentX = W - 150;
            const latentY = 40;
            // Background for latent image (fine vertical lines)
            for (let i = 0; i < 40; i++) {
                ctx.strokeStyle = 'rgba(0,0,0,0.03)';
                ctx.lineWidth = 0.2;
                ctx.beginPath(); ctx.moveTo(latentX + i * 1, latentY); ctx.lineTo(latentX + i * 1, latentY + 20); ctx.stroke();
            }
            // The text itself in very faint horizontal lines
            ctx.strokeStyle = 'rgba(0,0,0,0.05)';
            ctx.font = 'bold 12px Arial';
            ctx.lineWidth = 0.5;
            ctx.strokeText(latentText, latentX + 5, latentY + 15);


            // The active layout, resolved once for the whole object
            // pass below (the seal seat is the first consumer; the
            // block pass further down reads the same object).
            const layout = getLayout(checkStyle, W, bandH);

            // THERMOCHROMIC INK SEAL (ULTRA-REALISTIC METALLIC SHINE)
            // GREENFI EXCEPTION (exact rebuild, 2026-11-04): the
            // specimen carries NO circular seal/medallion — its
            // heat-reactive identity is the pale pink padlock mark
            // at the heatReactive layout seat (drawn with the
            // template furniture below), and its security identity
            // is the four-cell security square. The generic round
            // medallion never paints on this design.
            if (isSecurityEnabled('thermochromic') && selectedBank !== 'GREENFI') {
                // The medallion's colour + finish are the design's own
                // (sealColor / sealFinish state — ash-grey flat by
                // default, like the owner's reference disc). Flat uses
                // the translucent ink recipe; metallic renders the
                // bright foil-style gradient artwork.
                const shinyGradient = new fabric.Gradient({
                    type: 'radial',
                    coords: { x1: 18, y1: 18, x2: 18, y2: 18, r1: 0, r2: 18 },
                    colorStops: sealGradientStops(sealColor, sealFinish)
                });

                const thermoSealGroup = new fabric.Group([
                    // Main Seal Body
                    new fabric.Circle({ radius: 18, fill: shinyGradient, stroke: sealStroke(sealColor, sealFinish), strokeWidth: 0.8 }),
                    // Specular Highlight (The "Shine")
                    new fabric.Circle({
                        radius: 12, left: 4, top: 4,
                        fill: new fabric.Gradient({
                            type: 'radial',
                            coords: { x1: 6, y1: 6, x2: 6, y2: 6, r1: 0, r2: 12 },
                            colorStops: [
                                { offset: 0, color: 'rgba(255,255,255,0.4)' },
                                { offset: 1, color: 'rgba(255,255,255,0)' }
                            ]
                        })
                    }),
                    new fabric.Text('SAFE', { left: 18, top: 11, fontSize: 8, fontWeight: 'bold', fill: '#FFF', originX: 'center' }),
                    new fabric.Text('TOUCH TO VERIFY', { left: 18, top: 21, fontSize: 4.5, fill: '#EEE', originX: 'center', fontWeight: 'bold' }),
                    new fabric.Text('REACTS TO HEAT', { left: 18, top: 28, fontSize: 3.5, fill: '#DDD', originX: 'center' })
                ], {
                    id: 'thermoSeal',
                    left: objectPositionsRef.current.thermoSeal?.left ?? W - 70,
                    top: Math.min(objectPositionsRef.current.thermoSeal?.top ?? 175, textureBottom - 35), // MICR GUARD
                    scaleX: objectPositionsRef.current.thermoSeal?.scaleX ?? 1,
                    scaleY: objectPositionsRef.current.thermoSeal?.scaleY ?? 1,
                    selectable: true, hasControls: true
                } as any);
                c.add(thermoSealGroup);
            }

            // SECURITY WARNING BAND - BOTTOM STRIP (DRAGGABLE)
            // Princewill, 2026-11-04: this long tiny write-up
            // belongs DOWN BELOW - under the MICR line, seated on
            // the bottom border. The bottom stack (Personal, bandH
            // 350) is: MICR line 300..~325.5 / warning strip
            // 328..~334 / field captions 337..343.5 / bottom edge
            // 350 - the strip sits BETWEEN the MICR line and the
            // captions, touching neither (they compete for the same
            // strip of space, so the strip takes the middle seat).
            // This replaces the above-memo lane (y 244-254) entirely.
            // The band is a single full-strength text line - its old
            // near-transparent backing rect (rgba(0,0,0,0.02)) is
            // gone; nothing on the face prints faint.
            // EXCEPTION SEAT: a bottom-heavy border frame (the USAA
            // specimen's DOUBLE_RULE_BOLD bottom rules reach 24 in
            // from the edge, y 326..333 - exactly this strip's seat)
            // leaves no room in the bottom strip; when the active
            // border's bottom extent eats the seat, the strip takes
            // the lane just above the MICR clear band instead
            // (bandH - 70: below the memo rule, above the band,
            // clear of the signature warning's bottom) rather than
            // print across the frame.
            // GreenFi never carries this strip either (exact
            // rebuild): its own printed bottom statement replaces
            // every warning strip, whatever style route or saved
            // feature state the face was reached by.
            if (isSecurityEnabled('warning_band') && selectedBank !== 'GREENFI') {
                const stripBottomExtent = borderInnerExtents(borderConfig?.style, borderConfig?.thickness ?? 3).bottom;
                const stripTopDefault = stripBottomExtent > 15 ? bandH - 70 : bandH - 22;
                c.add(new fabric.Text("THE FRONT OF THIS DOCUMENT HAS A MICROPRINT BORDER, THE PADLOCK ICON, A MICROPRINT SIGNATURE LINE, AND THE SECURITY WARNING BOX. THE REVERSE HAS AN ARTIFICIAL WATERMARK AND A LIST OF SECURITY FEATURES.", {
                    id: 'warning_band',
                    left: objectPositionsRef.current.warning_band?.left ?? 45,
                    top: Math.min(objectPositionsRef.current.warning_band?.top ?? stripTopDefault, bandH - 16),
                    scaleX: objectPositionsRef.current.warning_band?.scaleX ?? 1,
                    scaleY: objectPositionsRef.current.warning_band?.scaleY ?? 1,
                    fontSize: 5.2, fontWeight: 'bold', fill: '#444', fontFamily: 'Arial',
                    selectable: true, hasControls: true
                } as any));
            }


            // MICR CLEAR BAND discipline (ANSI X9.100-20): the bottom
            // 0.625" of the check holds ONLY the MICR line. The whole
            // signature block (X, rule, AUTHORIZED SIGNATURE caption,
            // two-signatures warning) stacks 43 units below its group
            // anchor, so the anchor is clamped to end the block 8
            // units above the band; the memo lane shares the anchor so
            // the two lines stay on one row. User-dragged positions
            // still win per object - this clamp governs the DEFAULT
            // layout only (Princewill, 2026-11-04: his screenshot had
            // the signature stack sitting at the MICR line's level).
            const sigAnchorTop = Math.min(layout.signatureGroup.top, bandH - MICR_CLEAR_BAND - 8 - 43);
            // ANB EXACT rebuild exception: the specimen prints its
            // memo lane LOW (rule at 285, MICR glyphs at ~317) —
            // the standard clamp would haul it up into the rebuilt
            // wordmark block. ANB's layout seat (268) already
            // encodes the specimen's clearance, so it is honoured
            // directly; the clamp governs every other design.
            const memoAnchorTop = isAnb ? layout.memoGroup.top : Math.min(layout.memoGroup.top, bandH - MICR_CLEAR_BAND - 8 - 43);
            // BORDER DISCIPLINE (Princewill, 2026-11-04): a border
            // must never cover a write-up. The active border's
            // innermost stroke reach (borderInnerExtents, mirroring
            // the border engines' geometry) clamps the DEFAULT
            // positions of the header write-ups - the bank block
            // (with the holder stack following it) and the top
            // security statement - clear of the frame on every edge.
            // User-dragged positions still win per object.
            const bExt = borderInnerExtents(borderConfig?.style, borderConfig?.thickness ?? 3);
            // STRIP CLEARANCE (MAJOR rebuild wave, 2026-11-05): the
            // top security statement is horizontally centred at the
            // same height the bank block starts, so on centred-long
            // bank names the strip's text ran THROUGH the wordmark
            // (parent render check of the fidelity close-out:
            // WELLS / BOFA / CITI faces). When the strip prints, the
            // bank block's DEFAULT seat now starts a full strip
            // height + clearance below the strip's own default seat,
            // so the two never collide; the holder stack follows via
            // headerBorderShift. Saved/dragged positions still win —
            // this governs defaults only. The strip's gate and seat
            // are computed once here and shared with the strip's own
            // creation below so the two can never drift apart.
            const stripPrints = isSecurityEnabled('microprint') && selectedBank !== 'GREENFI' && ['personal', 'business', 'certified', 'cashier', 'money_order', 'payroll', 'bofa_classic'].includes(checkStyle);
            const stripTopDefault = Math.max(16, bExt.top + 3);
            const bankTopDefault = Math.max(layout.bankDetails.top, bExt.top + 4, stripPrints ? stripTopDefault + 12 : 0);
            const bankLeftDefault = Math.max(layout.bankDetails.left, bExt.left + 6);
            const headerBorderShift = bankTopDefault - layout.bankDetails.top;

            // TOP SECURITY STATEMENT - native fabric text (item 11:
            // it was raster-baked and blurred on zoom). Same words,
            // same gate (microprint feature; classic/formal/payroll/
            // BofA-classic faces), same centred seat - now clamped
            // below the border's inner extent like every header
            // write-up. The owner's reference (a BofA check) carries
            // this line at the top of the face, so bofa_classic
            // prints it too.
            // GreenFi is bank-gated OUT under any style route
            // (exact rebuild): the specimen has NO top warning
            // strip, and a restored snapshot can pair the GREENFI
            // template with a classic style that would otherwise
            // print this line.
            if (stripPrints) {
                c.add(new fabric.Text(checkStyle === 'payroll'
                    ? 'THIS PAYROLL CHECK HAS A SECURITY BACKGROUND, AN ARTIFICIAL WATERMARK ON THE BACK, AND MICROPRINTING IN THE BORDERS.'
                    : 'THE FACE OF THIS DOCUMENT HAS A COLORED BACKGROUND ON WHITE PAPER AND CONTAINS MICROPRINTING AND A VOID PANTOGRAPH.', {
                    id: 'topStatement',
                    left: objectPositionsRef.current.topStatement?.left ?? W / 2,
                    top: objectPositionsRef.current.topStatement?.top ?? stripTopDefault,
                    scaleX: objectPositionsRef.current.topStatement?.scaleX ?? 1,
                    scaleY: objectPositionsRef.current.topStatement?.scaleY ?? 1,
                    fontSize: 5.5, fontWeight: 'bold', fill: '#444', fontFamily: 'Arial',
                    originX: 'center',
                    selectable: true, hasControls: true
                } as any));
            }

            // KAREN BANNER SENTENCE - native fabric text on the
            // design's blue banner (item 11 audit: it was baked into
            // the texture with the banner). The banner band and the
            // design's own blue frame stay in the texture; the words
            // ride the canvas as an object, crisp at any zoom.
            if (isKarenPayroll) {
                c.add(new fabric.Text('FOR SECURITY PURPOSES, THE FACE OF THIS DOCUMENT CONTAINS A COLORED BACKGROUND AND MICROPRINTING IN THE BORDER', {
                    id: 'karenBannerText',
                    left: objectPositionsRef.current.karenBannerText?.left ?? W / 2,
                    top: objectPositionsRef.current.karenBannerText?.top ?? 4,
                    scaleX: objectPositionsRef.current.karenBannerText?.scaleX ?? 1,
                    scaleY: objectPositionsRef.current.karenBannerText?.scaleY ?? 1,
                    fontSize: 8, fontWeight: 'bold', fill: '#ffffff', fontFamily: 'Arial',
                    originX: 'center',
                    selectable: true, hasControls: true
                } as any));
            }

            // BLUE SAFETY-BAND SENTENCE (Karen + payroll classic) -
            // native fabric text on its (still textured) blue band,
            // same seat the raster version held (baseline bandH - 6).
            if (isKarenPayroll || isPayrollClassic) {
                c.add(new fabric.Text('🔒 SECURITY FEATURES INCLUDED. DETAILS ON BACK 🔒', {
                    id: 'securityBandText',
                    left: objectPositionsRef.current.securityBandText?.left ?? W / 2,
                    top: objectPositionsRef.current.securityBandText?.top ?? bandH - 13,
                    scaleX: objectPositionsRef.current.securityBandText?.scaleX ?? 1,
                    scaleY: objectPositionsRef.current.securityBandText?.scaleY ?? 1,
                    fontSize: 7, fontWeight: 'bold', fill: '#0055a4', fontFamily: 'Arial',
                    originX: 'center',
                    selectable: true, hasControls: true
                } as any));
            }
            // "No Lines" style for modern digital/e-checks
            const showLines = !['echeck', 'payroll', 'clean_modern'].includes(checkStyle);
            const lineStroke = showLines ? '#333' : 'transparent';

            // GREENFI LABEL INK (owner correction, 2026-11-04):
            // "pay attention to the color of the text — the
            // write-ups have different colors, not just black."
            // Pixel-sampled from the specimen: DATE, PAY TO THE
            // ORDER OF, DOLLARS and MEMO all print in the SAME
            // muted sage green (stroke cores #889d97-#879f99 on a
            // faded scan), and the payee / words / memo rules print
            // in it too. Every other design keeps the generic dark
            // label ink. Holder text, fraction, check number and
            // the heat label stay black — the specimen prints those
            // black.
            const isGreenfi = selectedBank === 'GREENFI';
            const greenfiLabelInk = '#5f8577';
            const labelInk = (fallback: string) => isGreenfi ? greenfiLabelInk : fallback;
            const greenfiLineStroke = isGreenfi && showLines ? greenfiLabelInk : lineStroke;

            // CPSA PADLOCK ICON (STANDARD POSITION - RIGHT OF "DOLLARS")
            // Per the CPSA Guideline (rev. Jan 2025) and ANSI
            // X9.100-170: on the front, the padlock goes immediately
            // to the RIGHT of the word "DOLLARS" at the right end of
            // the legal-amount line, in the right-margin zone — never
            // inside the courtesy box, which holds only the amount.
            // The icon is DRAWN (shackle + body + keyhole), not an
            // emoji: print-legible at check scale, one ink, no colour
            // surprises. Its descriptive statement stands separately
            // by the signature line (below). The interim left-of-box
            // placement (W - 280, owner fix earlier on 2026-11-04)
            // is superseded by this researched position; a position
            // the user saved themselves still wins, as everywhere.
            if (isSecurityEnabled('cpsa_padlock')) {
                const padlockGroup = new fabric.Group(isAnb ? [
                    // ANB specimen: the padlock prints as a thin
                    // OUTLINE icon — hollow body, line shackle, a
                    // single keyhole dot — in the labels' warm-grey
                    // ink, not the filled standard glyph (owner
                    // specimen, image-31.png).
                    new fabric.Circle({ left: 8.5, top: 7.5, radius: 5.5, fill: 'transparent', stroke: ANB_LABEL_INK, strokeWidth: 1.2, originX: 'center', originY: 'center' }),
                    new fabric.Rect({ left: 0, top: 7.5, width: 17, height: 12.5, rx: 2, ry: 2, fill: 'transparent', stroke: ANB_LABEL_INK, strokeWidth: 1.1 }),
                    new fabric.Circle({ left: 8.5, top: 12.8, radius: 1.6, fill: ANB_LABEL_INK, originX: 'center', originY: 'center' }),
                    new fabric.Rect({ left: 7.9, top: 12.8, width: 1.2, height: 3.2, fill: ANB_LABEL_INK })
                ] : [
                    // Shackle: a ring whose lower half the body covers.
                    new fabric.Circle({ left: 8.5, top: 7.5, radius: 5.5, fill: 'transparent', stroke: '#222', strokeWidth: 1.8, originX: 'center', originY: 'center' }),
                    // Body.
                    new fabric.Rect({ left: 0, top: 7.5, width: 17, height: 12.5, rx: 2, ry: 2, fill: '#222' }),
                    // Keyhole.
                    new fabric.Circle({ left: 8.5, top: 13, radius: 1.8, fill: '#fff', originX: 'center', originY: 'center' }),
                    new fabric.Rect({ left: 7.9, top: 13, width: 1.2, height: 3.4, fill: '#fff' })
                ], {
                    id: 'padlockGroup',
                    // The DOLLARS suffix sits at writtenAmount.left +
                    // (W - 200) with ~48 units of word; the icon
                    // anchors 10 units past the word's end, on the
                    // legal line's row. ANB's cluster rides ~33 units
                    // further left with its suffix (see 16c).
                    left: objectPositionsRef.current.padlockGroup?.left ?? (layout.writtenAmount.left + (W - 200) + (isAnb ? 25 : 58)),
                    top: Math.min(objectPositionsRef.current.padlockGroup?.top ?? (layout.writtenAmount.top - 1), textureBottom - 20), // MICR GUARD
                    scaleX: objectPositionsRef.current.padlockGroup?.scaleX ?? 1,
                    scaleY: objectPositionsRef.current.padlockGroup?.scaleY ?? 1,
                    selectable: true, hasControls: true
                } as any);
                c.add(padlockGroup);

                if (isAnb) {
                    // ANB specimen: the padlock's descriptive text
                    // stands BESIDE the icon at the words line, as
                    // three lines of microprint — "Security Features
                    // / Included / Details on Back." — and nothing
                    // prints by the signature line on this design.
                    // Print furniture, like the GreenFi statement.
                    c.add(new fabric.Text('Security Features\nIncluded\nDetails on Back.', {
                        id: 'anbPadlockNote',
                        left: (objectPositionsRef.current.padlockGroup?.left ?? (layout.writtenAmount.left + (W - 200) + 25)) + 33,
                        top: (objectPositionsRef.current.padlockGroup?.top ?? (layout.writtenAmount.top - 1)) - 3,
                        fontSize: 5, lineHeight: 1.25, fill: ANB_LABEL_INK, fontFamily: 'Arial',
                        selectable: false, evented: false
                    } as any));
                } else {
                    // The CPSA descriptive statement, in the industry
                    // form, in small type by the signature line — the
                    // centre-bottom lane between the memo rule's end and
                    // the signature X, above the MICR clear band.
                    c.add(new fabric.Text('Security Features Included — Details on Back.', {
                        id: 'padlockStatement',
                        left: objectPositionsRef.current.padlockStatement?.left ?? (layout.signatureGroup.left - 196),
                        top: Math.min(objectPositionsRef.current.padlockStatement?.top ?? bandH - 80, bandH - MICR_CLEAR_BAND - 14), // MICR GUARD: never inside the clear band
                        scaleX: objectPositionsRef.current.padlockStatement?.scaleX ?? 1,
                        scaleY: objectPositionsRef.current.padlockStatement?.scaleY ?? 1,
                        fontSize: 6.5, fill: '#444', fontFamily: 'Arial',
                        selectable: true, hasControls: true
                    } as any));
                }
            }

            // TEMPLATE IDENTITY FURNITURE (2026-11-04 specimen
            // designs): printed design furniture, not user data -
            // it prints with the design like the bank header does.
            if (selectedBank === 'GREENFI') {
                // THE SECURITY SQUARE (exact rebuild, 2026-11-04):
                // this design's security identity, at the left of
                // the pay-to row (layout.securitySquare seat) — a
                // rounded square of four tinted cells: fingerprint
                // rings on salmon, a white padlock on cream, a dark
                // padlock on pale blue, and the PURPLE cell with
                // the white KEY and its wording around it
                // (SECURITY / SQUARE — the feature's own name, as
                // the face's bottom statement names it). It
                // replaces the circular medallion on this design;
                // the pay-to row shifts right for it (see the
                // payToShiftX at the pay-to block).
                const sqParts: any[] = [
                    new fabric.Rect({ left: 0, top: 0, width: 30, height: 30, rx: 4, ry: 4, fill: 'rgba(255,255,255,0.9)', stroke: '#9db38f', strokeWidth: 0.8 }),
                    new fabric.Rect({ left: 2, top: 2, width: 12.5, height: 12.5, fill: '#e9bda6' }),
                    new fabric.Rect({ left: 15.5, top: 2, width: 12.5, height: 12.5, fill: '#f4eedd' }),
                    new fabric.Rect({ left: 2, top: 15.5, width: 12.5, height: 12.5, fill: '#cfdfec' }),
                    new fabric.Rect({ left: 15.5, top: 15.5, width: 12.5, height: 12.5, fill: '#5f3696' }),
                    // Fingerprint rings (top-left cell).
                    new fabric.Circle({ left: 4.9, top: 4.9, radius: 3.3, fill: 'transparent', stroke: '#7a4a35', strokeWidth: 0.9 }),
                    new fabric.Circle({ left: 6.6, top: 6.6, radius: 1.6, fill: 'transparent', stroke: '#7a4a35', strokeWidth: 0.8 }),
                    // White padlock (top-right cell).
                    new fabric.Circle({ left: 19.9, top: 4.6, radius: 1.8, fill: 'transparent', stroke: '#ffffff', strokeWidth: 1 }),
                    new fabric.Rect({ left: 19.2, top: 7.6, width: 5.6, height: 4.6, rx: 1, ry: 1, fill: '#ffffff' }),
                    // Dark padlock (bottom-left cell).
                    new fabric.Circle({ left: 6.4, top: 17.9, radius: 1.9, fill: 'transparent', stroke: '#33506e', strokeWidth: 1.1 }),
                    new fabric.Rect({ left: 5.4, top: 20.9, width: 5.8, height: 4.8, rx: 1, ry: 1, fill: '#33506e' }),
                    new fabric.Circle({ left: 7.9, top: 22.6, radius: 0.8, fill: '#ffffff' }),
                    // The PURPLE KEY cell: wording around the key.
                    new fabric.Text('SECURITY', { left: 21.75, top: 16.1, fontSize: 2.3, fontWeight: 'bold', fill: '#ffffff', fontFamily: 'Arial', originX: 'center' }),
                    new fabric.Circle({ left: 17.9, top: 20.6, radius: 2.1, fill: 'transparent', stroke: '#ffffff', strokeWidth: 1.1 }),
                    new fabric.Line([20, 22.7, 26.4, 22.7], { stroke: '#ffffff', strokeWidth: 1.1 }),
                    new fabric.Line([24.6, 22.7, 24.6, 24.6], { stroke: '#ffffff', strokeWidth: 1.1 }),
                    new fabric.Line([26.4, 22.7, 26.4, 24.2], { stroke: '#ffffff', strokeWidth: 1.1 }),
                    new fabric.Text('SQUARE', { left: 21.75, top: 25.6, fontSize: 2.3, fontWeight: 'bold', fill: '#ffffff', fontFamily: 'Arial', originX: 'center' })
                ];
                c.add(new fabric.Group(sqParts, {
                    id: 'securitySquare',
                    left: objectPositionsRef.current.securitySquare?.left ?? layout.securitySquare?.left ?? 40,
                    top: objectPositionsRef.current.securitySquare?.top ?? layout.securitySquare?.top ?? 108,
                    scaleX: objectPositionsRef.current.securitySquare?.scaleX ?? 1,
                    scaleY: objectPositionsRef.current.securitySquare?.scaleY ?? 1,
                    selectable: true, hasControls: true
                } as any));

                // The GreenFi wordmark (exact rebuild): LARGE,
                // heavy ROUNDED letterforms (Baloo 2), two-tone as
                // printed — "Green" in leaf green, "Fi" in charcoal
                // with a green leaf for the dot of the i — low-left
                // under the words line (layout.templateWordmark).
                const wmGreen = new fabric.Text('Green', { left: 0, top: 0, fontSize: 40, fontWeight: '800', fontFamily: "'Baloo 2', 'Arial Rounded MT Bold', Arial, sans-serif", fill: '#69a83b' });
                const wmFi = new fabric.Text('Fi', { left: (wmGreen as any).width ?? 128, top: 0, fontSize: 40, fontWeight: '800', fontFamily: "'Baloo 2', 'Arial Rounded MT Bold', Arial, sans-serif", fill: '#33383b' });
                const wmWidth = ((wmGreen as any).width ?? 128) + ((wmFi as any).width ?? 42);
                c.add(new fabric.Group([
                    wmGreen,
                    wmFi,
                    // The leaf over the i: a paper dot first lifts
                    // the printed dot away, then the leaf lands.
                    new fabric.Circle({ left: wmWidth - 11.4, top: 1.4, radius: 3.1, fill: '#f4faf0' }),
                    new fabric.Ellipse({ left: wmWidth - 12.6, top: -0.6, rx: 4.6, ry: 2.3, angle: -32, fill: '#69a83b' })
                ], {
                    id: 'templateWordmark',
                    left: objectPositionsRef.current.templateWordmark?.left ?? layout.templateWordmark?.left ?? 40,
                    top: objectPositionsRef.current.templateWordmark?.top ?? layout.templateWordmark?.top ?? 198,
                    scaleX: objectPositionsRef.current.templateWordmark?.scaleX ?? 1,
                    scaleY: objectPositionsRef.current.templateWordmark?.scaleY ?? 1,
                    selectable: true, hasControls: true
                } as any));

                // THE HEAT-REACTIVE MARK (exact rebuild): on the
                // specimen the thermochromic ink prints as a PALE
                // PINK PADLOCK right of DOLLARS — not a badge, not
                // the round medallion — with a bold left arrow and
                // the stacked "Heat / Reactive / Ink" label in the
                // strip to its right (layout.heatReactive seat;
                // label room runs to the right margin). Gated on
                // the same thermochromic feature the medallion
                // answers to, so the design's own security set
                // (which names heat-reactive) controls it.
                if (isSecurityEnabled('thermochromic')) {
                    c.add(new fabric.Group([
                        new fabric.Circle({ left: 3.2, top: 0, radius: 5.3, fill: 'transparent', stroke: '#f0bfd4', strokeWidth: 3.4 }),
                        new fabric.Rect({ left: 0, top: 7, width: 17, height: 15, rx: 3, ry: 3, fill: '#f0bfd4' }),
                        new fabric.Circle({ left: 6.8, top: 11.8, radius: 1.7, fill: '#ffffff' }),
                        new fabric.Rect({ left: 7.9, top: 11.8, width: 1.2, height: 3.8, fill: '#ffffff' }),
                        new fabric.Text('←', { left: 23, top: 3, fontSize: 15, fontWeight: 'bold', fill: '#222222', fontFamily: 'Arial' }),
                        new fabric.Text('Heat', { left: 44, top: 0, fontSize: 9, fontWeight: 'bold', fill: '#1a1a1a', fontFamily: 'Arial' }),
                        new fabric.Text('Reactive', { left: 44, top: 10, fontSize: 9, fontWeight: 'bold', fill: '#1a1a1a', fontFamily: 'Arial' }),
                        new fabric.Text('Ink', { left: 44, top: 20, fontSize: 9, fontWeight: 'bold', fill: '#1a1a1a', fontFamily: 'Arial' })
                    ], {
                        id: 'heatReactiveMark',
                        left: objectPositionsRef.current.heatReactiveMark?.left ?? layout.heatReactive?.left ?? W - 138,
                        top: objectPositionsRef.current.heatReactiveMark?.top ?? layout.heatReactive?.top ?? 146,
                        scaleX: objectPositionsRef.current.heatReactiveMark?.scaleX ?? 1,
                        scaleY: objectPositionsRef.current.heatReactiveMark?.scaleY ?? 1,
                        selectable: true, hasControls: true
                    } as any));
                }
                // This design carries its security statement as a
                // printed bottom line INSTEAD of the top warning
                // strip (forced off when the template applies):
                // centred small caps below the MICR row, in the
                // same clear lane the warning band uses (bandH - 22).
                // The original seat (bandH - 11) sat inside the FSC
                // frame's own bottom margin band - the frame's outer
                // rule (bandH - 9.5) cut through the glyphs and the
                // frame's bottom microprint row (bandH - 4.5)
                // overprinted them, so the line never read on the
                // face. Print furniture - not draggable.
                c.add(new fabric.Text('LOOK FOR FRAUD-DETERRING FEATURES INCLUDING THE SECURITY SQUARE AND HEAT-REACTIVE INK. DETAILS ON BACK.', {
                    id: 'greenfiStatement',
                    left: layout.greenfiStatement?.left ?? W / 2, top: layout.greenfiStatement?.top ?? bandH - 22, originX: 'center',
                    fontSize: 5.5, fontFamily: 'Arial', fill: '#2e7d32', charSpacing: 30,
                    selectable: false, evented: false
                } as any));
            }
            if (selectedBank === 'ANB') {
                // The ANB wordmark block, bottom-left above the memo
                // line ON THE FIELD — EXACT rebuild (2026-11-04) to
                // the specimen: the bank's striped FLAG MARK (a left
                // post with a small left-pointing tip at its top and
                // three curved ribbon bands sweeping down-right to
                // points — drawn geometry, not a traced logo),
                // "ANB Bank." in large bold BLACK grotesque with its
                // (R), and under it the full contact line the stock
                // prints: web | phone (dot-separated) | Member FDIC.
                // The first build printed navy 23px text with no
                // mark and no phone — none of which is on the
                // original. Printed furniture.
                const anbMark = 'M 1 9 L 15 5.5 L 15 12 Z '
                    + 'M 6 4 L 15 4 L 15 38 L 6 38 Z '
                    + 'M 16 2 L 24 2 C 26 9, 29 18, 34 27 C 27.5 21.5, 21.5 12.5, 14 7.5 C 15 5, 15.5 3.2, 16 2 Z '
                    + 'M 29 4.5 L 37 4.5 C 39 11, 42 19, 47 27.5 C 40.5 22, 34.5 13.5, 27 9.5 C 28 7, 28.5 5.5, 29 4.5 Z '
                    + 'M 42 7 L 50 7 C 52 13.5, 55 20.5, 60 28 C 53.5 23, 47.5 15, 40 11.5 C 41 9, 41.5 7.8, 42 7 Z';
                c.add(new fabric.Group([
                    new fabric.Path(anbMark, { left: 0, top: 0, fill: '#141414' }),
                    new fabric.Text('ANB Bank.', { left: 70, top: -3, fontSize: 44, fontWeight: 'bold', fill: '#141414', fontFamily: 'Arial, Helvetica, sans-serif' }),
                    new fabric.Text('®', { left: 297, top: 4, fontSize: 10, fill: '#141414', fontFamily: 'Arial' }),
                    new fabric.Text('anbbank.com  |  866•433•0282  |  Member FDIC', { left: 71, top: 46, fontSize: 9.5, charSpacing: 30, fill: '#232323', fontFamily: 'Arial' })
                ], {
                    id: 'templateWordmark',
                    left: objectPositionsRef.current.templateWordmark?.left ?? layout.templateWordmark?.left ?? 40,
                    top: objectPositionsRef.current.templateWordmark?.top ?? layout.templateWordmark?.top ?? bandH - 134,
                    scaleX: objectPositionsRef.current.templateWordmark?.scaleX ?? 1,
                    scaleY: objectPositionsRef.current.templateWordmark?.scaleY ?? 1,
                    selectable: true, hasControls: true
                } as any));
            }
            if (selectedBank === 'USAA') {
                // USAA face furniture (specimen close-look pass,
                // 2026-11-04; specimen image-31.png bottom check +
                // the bank's logo lockup IMG_7832.jpeg). The face's
                // print is a bold condensed grotesque; the labels are
                // italic serif (handled at their own render sites via
                // isUsaa); the ONE pale-blue print on the face is the
                // microprint advisory under the words rule.
                const USAA_CONDENSED = "'Trade Gothic', 'Arial Narrow', 'Liberation Sans Narrow', Arial, sans-serif";
                // The bank block, bottom-left above the memo lane:
                // the eagle-wing lockup at the left (a restrained
                // geometric rendering of the bank's striped eagle +
                // USAA wordmark — drawn shapes, not a traced logo),
                // then the four printed lines from the lockup: name
                // (follows the design's bank-name field), street,
                // city/state/ZIP, phone pair — all bold condensed,
                // flush left as they print on the specimen. The block
                // is compact (ends ~234) so it stays clear of the
                // clamped memo label at 236, like the specimen's own
                // tight stack.
                c.add(new fabric.Group([
                    new fabric.Rect({ left: 0, top: 1, width: 26, height: 2.6, fill: '#1a1a1a' }),
                    new fabric.Rect({ left: 0, top: 5.5, width: 22, height: 2.6, fill: '#1a1a1a' }),
                    new fabric.Rect({ left: 0, top: 10, width: 18, height: 2.6, fill: '#1a1a1a' }),
                    new fabric.Rect({ left: 0, top: 14.5, width: 14, height: 2.6, fill: '#1a1a1a' }),
                    new fabric.Text('USAA', { left: 0, top: 20, fontSize: 9, fontWeight: 'bold', fill: '#1a1a1a', fontFamily: USAA_CONDENSED }),
                    new fabric.Text((checkData.bankName || 'USAA FEDERAL SAVINGS BANK').toUpperCase(), { left: 36, top: 0, fontSize: 11, fontWeight: 'bold', fill: '#0a2d5c', fontFamily: USAA_CONDENSED }),
                    new fabric.Text('10750 McDERMOTT FWY', { left: 36, top: 14, fontSize: 7.5, fontWeight: 'bold', fill: '#0a2d5c', fontFamily: USAA_CONDENSED }),
                    new fabric.Text('SAN ANTONIO, TEXAS 78288-0544', { left: 36, top: 24.5, fontSize: 7.5, fontWeight: 'bold', fill: '#0a2d5c', fontFamily: USAA_CONDENSED }),
                    new fabric.Text('(210) 456-8000  1-800-832-3724', { left: 36, top: 35, fontSize: 7.5, fontWeight: 'bold', fill: '#0a2d5c', fontFamily: USAA_CONDENSED })
                ], {
                    id: 'templateBankBlock',
                    left: objectPositionsRef.current.templateBankBlock?.left ?? layout.templateBankBlock?.left ?? 40,
                    top: objectPositionsRef.current.templateBankBlock?.top ?? layout.templateBankBlock?.top ?? bandH - 158,
                    scaleX: objectPositionsRef.current.templateBankBlock?.scaleX ?? 1,
                    scaleY: objectPositionsRef.current.templateBankBlock?.scaleY ?? 1,
                    selectable: true, hasControls: true
                } as any));
                // The "Photo Safe Deposit" mark at the right end of
                // the written-amount row — this design's named
                // feature, drawn so the back panel's honesty rule
                // holds. On the specimen it is a SOLID dark camera
                // (filled body, white lens) beside the three stacked
                // bold words and the "Details on back" line, all in
                // dark neutral ink — not the earlier navy outline
                // camera beside a one-line caption.
                c.add(new fabric.Group([
                    new fabric.Rect({ left: 8, top: 0, width: 10, height: 5, rx: 2, ry: 2, fill: '#232323' }),
                    new fabric.Rect({ left: 0, top: 4, width: 26, height: 19, rx: 3, ry: 3, fill: '#232323' }),
                    new fabric.Circle({ left: 7.5, top: 8, radius: 5.5, fill: '#ffffff' }),
                    new fabric.Circle({ left: 10.2, top: 10.7, radius: 2.8, fill: '#232323' }),
                    new fabric.Text('Photo', { left: 32, top: 0, fontSize: 8, fontWeight: 'bold', fill: '#1c1c1c', fontFamily: USAA_CONDENSED }),
                    new fabric.Text('Safe', { left: 32, top: 10, fontSize: 8, fontWeight: 'bold', fill: '#1c1c1c', fontFamily: USAA_CONDENSED }),
                    new fabric.Text('Deposit®', { left: 32, top: 20, fontSize: 8, fontWeight: 'bold', fill: '#1c1c1c', fontFamily: USAA_CONDENSED }),
                    new fabric.Text('Details on back', { left: 32, top: 31, fontSize: 6, fill: '#333333', fontFamily: USAA_CONDENSED })
                ], {
                    id: 'photoSafeMark',
                    left: objectPositionsRef.current.photoSafeMark?.left ?? layout.photoSafeMark?.left ?? W - 145,
                    top: objectPositionsRef.current.photoSafeMark?.top ?? layout.photoSafeMark?.top ?? bandH - 163,
                    scaleX: objectPositionsRef.current.photoSafeMark?.scaleX ?? 1,
                    scaleY: objectPositionsRef.current.photoSafeMark?.scaleY ?? 1,
                    selectable: true, hasControls: true
                } as any));
                // THE tiny blue write-up (the owner's close look,
                // 2026-11-04): the microprint advisory printed in
                // pale security blue directly under the amount-words
                // rule, running from under the words to just before
                // the Photo Safe mark — italic serif fine print,
                // legible at zoom, unobtrusive at fit, exactly as
                // the specimen carries it. Print furniture of the
                // design (not user data, not selectable), USAA only.
                // The render colour is a photo estimate: the print's
                // anti-aliased cores sample ≈ RGB(150,200,205) over
                // white; #64A6AD is the solid-ink equivalent.
                c.add(new fabric.Text('Look for: Microprint signature line and grey border on front. If not present, do not cash.', {
                    id: 'usaaAdvisory',
                    left: objectPositionsRef.current.usaaAdvisory?.left ?? layout.usaaAdvisory?.left ?? 235,
                    top: objectPositionsRef.current.usaaAdvisory?.top ?? layout.usaaAdvisory?.top ?? 186,
                    scaleX: objectPositionsRef.current.usaaAdvisory?.scaleX ?? 1,
                    scaleY: objectPositionsRef.current.usaaAdvisory?.scaleY ?? 1,
                    fontSize: 7, fontStyle: 'italic', fill: '#64A6AD', fontFamily: 'Times New Roman, serif',
                    selectable: false, evented: false
                } as any));
            }


            // 11. CHECK NUMBER (DRAGGABLE) (Modified for Treasury "B" prefix and Karen "No." prefix)
            // BLANK CHECK: with no check number written, nothing of the
            // number prints - no BofA zero-padding ("00000000"), no
            // Treasury "B" prefix, no Karen "No." fragment. The padded /
            // prefixed forms apply only to a number the user wrote.
            const rawCheckNum = checkData.checkNumber || '';
            const displayCheckNum = !rawCheckNum.trim() ? '' : (isTreasury && !rawCheckNum.startsWith('B') ? 'B ' + rawCheckNum : (isKarenPayroll ? `No. ${rawCheckNum}` : (isBofaV2 ? rawCheckNum.padStart(8, '0') : rawCheckNum)));
            c.add(new fabric.Text(displayCheckNum, {
                id: 'checkNumber',
                left: objectPositionsRef.current.checkNumber?.left ?? layout.checkNumber.left,
                top: objectPositionsRef.current.checkNumber?.top ?? layout.checkNumber.top,
                scaleX: objectPositionsRef.current.checkNumber?.scaleX ?? 1,
                scaleY: objectPositionsRef.current.checkNumber?.scaleY ?? 1,
                fontSize: isTreasury ? 28 : (isKarenPayroll ? 18 : (isBofaV2 ? 28 : 24)),
                fontWeight: 'bold',
                fill: '#000',
                fontFamily: (isTreasury || isKarenPayroll || isBofaV2) ? (isKarenPayroll ? 'Times New Roman, serif' : 'Arial') : fontFamily,
                selectable: true, hasControls: true
            } as any));

            if (!isTreasury) {
                c.add(new fabric.Text(checkData.fractionalRouting, {
                    id: 'fractionalRouting',
                    left: objectPositionsRef.current.fractionalRouting?.left ?? layout.fractionalRouting.left,
                    top: objectPositionsRef.current.fractionalRouting?.top ?? layout.fractionalRouting.top,
                    scaleX: objectPositionsRef.current.fractionalRouting?.scaleX ?? 1,
                    scaleY: objectPositionsRef.current.fractionalRouting?.scaleY ?? 1,
                    fontSize: isKarenPayroll ? 9 : 11, fill: '#666',
                    fontFamily: isKarenPayroll ? 'Times New Roman, serif' : fontFamily,
                    selectable: true, hasControls: true
                } as any));
            }

            // 11b. INDUSTRIAL SPECIALTY HEADERS (Fabric objects — selectable/deletable)
            if (isPayroll || isCashier || isMoneyOrder) {
                const headerText = isPayroll ? 'PAYROLL CHECK' : (isCashier ? 'CASHIER\'S CHECK' : 'MONEY ORDER');
                const subHeaderText = isPayroll ? 'NON-NEGOTIABLE ADVICE' : (isCashier ? 'OFFICIAL INSTRUMENT' : 'PERSONAL MONEY ORDER');

                c.add(new fabric.Text(headerText, {
                    id: 'specialtyHeader',
                    left: objectPositionsRef.current.specialtyHeader?.left ?? (W / 2),
                    top: objectPositionsRef.current.specialtyHeader?.top ?? 28,
                    scaleX: objectPositionsRef.current.specialtyHeader?.scaleX ?? 1,
                    scaleY: objectPositionsRef.current.specialtyHeader?.scaleY ?? 1,
                    fontSize: 16, fontWeight: 'bold', fill: primaryColor,
                    fontFamily: 'Arial Black, Arial, sans-serif',
                    originX: 'center',
                    selectable: true, hasControls: true
                } as any));

                c.add(new fabric.Text(subHeaderText, {
                    id: 'specialtySubHeader',
                    left: objectPositionsRef.current.specialtySubHeader?.left ?? (W / 2),
                    top: objectPositionsRef.current.specialtySubHeader?.top ?? 48,
                    scaleX: objectPositionsRef.current.specialtySubHeader?.scaleX ?? 1,
                    scaleY: objectPositionsRef.current.specialtySubHeader?.scaleY ?? 1,
                    fontSize: 9, fontWeight: 'bold', fill: '#333',
                    fontFamily: 'Arial, sans-serif',
                    originX: 'center',
                    selectable: true, hasControls: true
                } as any));
            }

            // Multi-line bank headers grow downward, like real
            // checks: when the bank block's address lines would reach
            // the account-holder block, the holder block yields by
            // the overflow (bankBlockShiftPx). Two protections: the
            // shift applies only while the holder sits at its layout
            // position (a position the user dragged to is never
            // overridden) and only when the built-in bank header is
            // actually drawn (not suppressed by a full-block logo,
            // not the Treasury/BofA-v2 variants that print no
            // address text).
            const bankHeaderDrawn = !logoSuppressesBankHeader(!!logoImage, logoIncludeWriteups) && !isTreasury && !isBofaV2;
            const holderSavedTop = objectPositionsRef.current.accHolderName?.top;
            let holderShift = 0;
            if (bankHeaderDrawn && holderSavedTop == null) {
                const holderHeightPx = isAnyPayroll
                    ? (1 + lineCount(checkData.accountHolderAddress)) * 14 * 1.2
                    : 18 + Math.max(1, lineCount(checkData.accountHolderAddress)) * 12 * 1.16;
                holderShift = bankBlockShiftPx({
                    bankTop: objectPositionsRef.current.bankDetails?.top ?? bankTopDefault,
                    holderTop: layout.accHolder.top,
                    maxShift: Math.max(0, layout.payTo.top - layout.accHolder.top - holderHeightPx - 2),
                    bankAddress: (checkData as any).bankAddress,
                    hasRouting: !!(checkData.routingNumber || '').trim(),
                    addrFontSize: isKarenPayroll ? 9 : 10
                });
            }

            // 12. ACCOUNT HOLDER / COMPANY HEADER (DRAGGABLE)
            // The design's own write-up stands on the REAL object
            // while the holder fields are empty: solid ink, selectable
            // and draggable through the arrangement system (the
            // object's position is registered under its own id).
            // Typed holder data replaces it on this same object.
            const holderNameText = (checkData.accountHolderName || '').trim()
                ? checkData.accountHolderName : DESIGN_WRITEUP_TEXT.holderName;
            const holderAddressText = (checkData.accountHolderAddress || '').trim()
                ? checkData.accountHolderAddress : DESIGN_WRITEUP_TEXT.holderAddress;
            const companyHeader = (isAnyPayroll)
                ? `${holderNameText}\n${holderAddressText}`
                : holderNameText;

            c.add(new fabric.IText(companyHeader, {
                id: 'accHolderName',
                designWriteup: true,
                left: objectPositionsRef.current.accHolderName?.left ?? layout.accHolder.left,
                top: objectPositionsRef.current.accHolderName?.top ?? (layout.accHolder.top + holderShift + headerBorderShift),
                scaleX: objectPositionsRef.current.accHolderName?.scaleX ?? 1,
                scaleY: objectPositionsRef.current.accHolderName?.scaleY ?? 1,
                fontSize: isKarenPayroll ? 17 : (isAnb ? 15 : 14),
                fontWeight: isAnb ? 'normal' : 'bold', fill: isAnb ? '#333d45' : '#000',
                fontFamily: isKarenPayroll ? 'Times New Roman, serif' : (isAnb ? ANB_CHANCERY : fontFamily),
                selectable: true, hasControls: true,
                lineHeight: 1.2
            } as any));

            // 12b. EIN (Separate editable/deletable object — Payroll only)
            if (isPayroll) {
                c.add(new fabric.IText(`EIN: ${checkData.ein}`, {
                    id: 'einText',
                    left: objectPositionsRef.current.einText?.left ?? layout.accHolder.left,
                    top: objectPositionsRef.current.einText?.top ?? (layout.accHolder.top + (isKarenPayroll ? 50 : 42) + holderShift + headerBorderShift),
                    scaleX: objectPositionsRef.current.einText?.scaleX ?? 1,
                    scaleY: objectPositionsRef.current.einText?.scaleY ?? 1,
                    fontSize: 12, fontWeight: 'bold', fill: '#000',
                    fontFamily: isKarenPayroll ? 'Times New Roman, serif' : fontFamily,
                    selectable: true, hasControls: true
                } as any));
            }

            if (!isAnyPayroll) {
                c.add(new fabric.IText(holderAddressText, {
                    id: 'accHolderAddr',
                    designWriteup: true,
                    left: objectPositionsRef.current.accHolderAddr?.left ?? layout.accHolder.left,
                    top: objectPositionsRef.current.accHolderAddr?.top ?? (layout.accHolder.top + 18 + holderShift + headerBorderShift),
                    scaleX: objectPositionsRef.current.accHolderAddr?.scaleX ?? 1,
                    scaleY: objectPositionsRef.current.accHolderAddr?.scaleY ?? 1,
                    fontSize: isAnb ? 11.5 : 12, fill: isAnb ? '#333d45' : '#444',
                    fontFamily: isAnb ? ANB_CHANCERY : fontFamily,
                    selectable: true, hasControls: true
                } as any));
            }

            // 13. DATE (DRAGGABLE GROUP)
            // 13. DATE (UNGROUPED)
            if (!isTreasury) {
                // 13a. Date Label
                c.add(new fabric.Text(isBofaV2 ? 'DATE : ' : (isKarenPayroll ? '' : (isUsaa ? 'Date' : 'DATE')), {
                    id: 'dateLabel',
                    left: objectPositionsRef.current.dateLabel?.left ?? layout.dateGroup.left,
                    // ANB: the label group rides higher — the swash
                    // D's line box starts above the caps so the
                    // letterforms' baseline lands on the date rule.
                    top: objectPositionsRef.current.dateLabel?.top ?? (layout.dateGroup.top + (isAnb ? -18 : 3)),
                    fontSize: isBofaV2 ? 14 : 10,
                    fontWeight: isAnb ? 'normal' : 'bold', fill: isBofaV2 ? '#000' : (isUsaa ? '#333' : (isAnb ? ANB_LABEL_INK : labelInk('#555'))),
                    fontFamily: isBofaV2 ? 'Arial' : (isUsaa ? 'Times New Roman, serif' : (isAnb ? ANB_SERIF : fontFamily)),
                    fontStyle: isUsaa ? 'italic' : 'normal',
                    charSpacing: isAnb ? 90 : 0,
                    // ANB: swash script D + "ATE" serif caps, in
                    // the stock's warm-grey label ink.
                    styles: isAnb ? { 0: { 0: { fontFamily: ANB_SCRIPT, fontSize: 40, charSpacing: 650, fill: ANB_LABEL_INK } } } : undefined,
                    selectable: true, hasControls: true
                } as any));

                if (isAnb) {
                    // ANB specimen: a tiny PINK flourish — two
                    // petals and a heart dot — floats just above
                    // the date rule's left end, the one spot of
                    // colour in the label row. Print furniture.
                    c.add(new fabric.Group([
                        new fabric.Ellipse({ left: 0, top: 2.5, rx: 3.6, ry: 1.8, angle: -28, fill: '#d6336c' }),
                        new fabric.Ellipse({ left: 5.5, top: 0, rx: 3.6, ry: 1.8, angle: 24, fill: '#d6336c' }),
                        new fabric.Circle({ left: 4.6, top: 3.4, radius: 1.15, fill: '#d6336c' })
                    ], {
                        id: 'anbDateFlourish',
                        left: layout.dateGroup.left + 93,
                        top: layout.dateGroup.top - 16,
                        selectable: false, evented: false
                    } as any));
                }

                // 13b. Date Value (IText) - typed text only.
                // Princewill named the faint format date directly:
                // it "is not supposed to be there". Unwritten, the
                // object is empty beside the printed DATE label.
                const dateIText = new fabric.IText(checkData.date || '', {
                    id: 'dateValue',
                    left: objectPositionsRef.current.dateValue?.left ?? (layout.dateGroup.left + (isBofaV2 ? 60 : (isKarenPayroll ? 0 : (isAnb ? 71 : 55)))),
                    top: objectPositionsRef.current.dateValue?.top ?? (layout.dateGroup.top + (isBofaV2 ? 1.5 : -2)),
                    fontSize: isKarenPayroll ? 17 : 22,
                    fill: (handwritingStyle !== 'none' && !isBofaV2) ? handwritingColor : '#000',
                    fontFamily: isKarenPayroll ? 'Times New Roman' : (isBofaV2 ? 'Arial' : getHandwritingFont(handwritingStyle)),
                    fontStyle: (handwritingStyle !== 'none' && !isBofaV2) ? 'normal' : (isBofaV2 ? 'normal' : 'italic'),
                    shadow: (handwritingStyle !== 'none' && !isBofaV2) ? new fabric.Shadow({
                        color: `${handwritingColor}40`, blur: 1.5, offsetX: 0, offsetY: 0
                    }) : undefined,
                    selectable: true, hasControls: true, lockScalingY: false, lockScalingX: false
                } as any);

                // Auto-scale Date
                const maxDateWidth = isKarenPayroll ? 80 : (isBofaV2 ? 120 : 80); // Approx width
                if (dateIText.width && dateIText.width > maxDateWidth) {
                    const scale = maxDateWidth / dateIText.width;
                    dateIText.set({ scaleX: scale, scaleY: scale });
                }
                c.add(dateIText);

                // 13c. Date Line
                if (!isBofaV2) {
                    const lineCoords = isKarenPayroll ? [-5, 0, 80, 0] : (isAnb ? [0, 0, 144, 0] : [0, 0, 100, 0]);
                    c.add(new fabric.Line(lineCoords, {
                        id: 'dateLine',
                        left: objectPositionsRef.current.dateLine?.left ?? (layout.dateGroup.left + (isKarenPayroll ? -5 : (isAnb ? 75 : 35))),
                        top: objectPositionsRef.current.dateLine?.top ?? (layout.dateGroup.top + (isKarenPayroll ? 12 : 16)),
                        stroke: lineStroke, strokeWidth: isKarenPayroll ? 0.5 : 1,
                        selectable: true, hasControls: true
                    } as any));
                }
            }

            // 14. AMOUNT BOX (DRAGGABLE GROUP)
            // 14. AMOUNT BOX (UNGROUPED)
            // No amount written yet (empty / 0) means a BLANK check: the amount
            // box and the written-amount line stay empty for handwriting -
            // never print 0.00 or "Zero" on the face.
            const amountNumValue = parseMoney(checkData.amount);
            const hasAmount = amountNumValue > 0;
            // 14a. Amount Box (Deletable)
            c.add(new fabric.Rect({
                id: 'amountBox',
                left: objectPositionsRef.current.amountBox?.left ?? layout.amountGroup.left,
                top: objectPositionsRef.current.amountBox?.top ?? layout.amountGroup.top,
                width: 160, height: 38,
                fill: isKarenPayroll ? 'transparent' : (isAnb ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.6)'),
                // GreenFi (exact rebuild): the specimen's amount
                // box is a THIN GREEN outline, not the standard
                // dark rule. ANB (exact rebuild): a WHITE
                // translucent box with a THIN WARM-GREY outline —
                // the standard heavy black rule is not on the
                // original.
                stroke: (!showAmountBorder || isTreasury || isKarenPayroll) ? 'transparent' : (selectedBank === 'GREENFI' ? '#7cb350' : (isAnb ? 'rgba(110,100,70,0.5)' : '#222')),
                strokeWidth: selectedBank === 'GREENFI' || isAnb ? 1 : 1.5,
                selectable: true, hasControls: true, lockScalingY: false, lockScalingX: false
            } as any));

            // 14b. Dollar Sign - ONE $, at the specimen seat:
            // immediately INSIDE the box's left edge, vertically
            // centred in the box, clear of the payee rule below and
            // the words line beneath it. The amount is left-justified
            // from it (ANSI X9.100-110: the scan field begins with
            // the dollar sign; nothing else belongs in the field).
            c.add(new fabric.Text('$', {
                id: 'amountSign',
                // ANB specimen: the $ stands OUTSIDE the courtesy
                // box, just left of its edge (the standard seat is
                // inside the box's left edge).
                left: objectPositionsRef.current.amountSign?.left ?? (layout.amountGroup.left + (isAnb ? -16 : 6)),
                top: objectPositionsRef.current.amountSign?.top ?? (layout.amountGroup.top + 9),
                fontSize: 20, fontWeight: 'bold', fill: '#333',
                fontFamily: isKarenPayroll ? 'Times New Roman' : fontFamily,
                selectable: true, hasControls: true
            } as any));

            // 14c. Amount Value (IText) - typed amount only. With
            // no amount written the object carries EMPTY text: the
            // box shows just the $ and its border, on screen and in
            // print (nothing written = nothing printed; no samples).
            c.add(new fabric.IText(!hasAmount ? '' : ((isTreasury || isKarenPayroll || isCashier) ? `**${(isTreasury || isKarenPayroll) ? '**' : ''} ${amountNumValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : amountNumValue.toLocaleString(undefined, { minimumFractionDigits: 2 })), {
                id: 'amountValue',
                left: objectPositionsRef.current.amountValue?.left ?? (layout.amountGroup.left + 30),
                top: objectPositionsRef.current.amountValue?.top ?? (layout.amountGroup.top + (handwritingStyle !== 'none' ? 4 : 8)),
                fontSize: handwritingStyle !== 'none' ? 24 : 20,
                fontWeight: handwritingStyle !== 'none' ? 'normal' : 'bold',
                fill: handwritingStyle !== 'none' ? handwritingColor : '#000',
                fontFamily: (isKarenPayroll && handwritingStyle === 'none') ? 'Times New Roman' : getHandwritingFont(handwritingStyle),
                shadow: handwritingStyle !== 'none' ? new fabric.Shadow({
                    color: `${handwritingColor}40`, blur: 1.5, offsetX: 0, offsetY: 0
                }) : undefined,
                selectable: true, hasControls: true, lockScalingY: false, lockScalingX: false
            } as any));

            // 15. PAY TO THE ORDER OF (UNGROUPED)
            const payToLabel1 = isPayroll ? 'Pay to the' : (isTreasury ? 'Pay to' : (isUsaa ? 'Pay to the' : 'PAY TO THE'));
            const payToLabel2 = isPayroll ? 'order of' : (isTreasury ? 'the order of' : (isUsaa ? 'Order of' : 'ORDER OF'));
            // GreenFi's security square owns the left of this row
            // (exact rebuild): the whole pay-to block — labels,
            // payee name, rule — shifts right past the square.
            // Defaults only; user-dragged positions still win.
            const payToShiftX = selectedBank === 'GREENFI' ? 38 : 0;

            // 15a. Label 1
            c.add(new fabric.Text(isBofaV2 ? 'Pay To :' : payToLabel1, {
                id: 'payToLabel1',
                // ANB: the label starts at the far left like the
                // stock's (its swash P stands at the check's edge).
                left: objectPositionsRef.current.payToLabel1?.left ?? (layout.payTo.left + payToShiftX + (isAnb ? -30 : 0)),
                // ANB rides higher: the swash P's line box starts
                // above the caps so the letterforms' baseline lands
                // on the payee rule (see the DATE label note).
                top: objectPositionsRef.current.payToLabel1?.top ?? (layout.payTo.top + (isAnb ? -22 : 0)),
                fontSize: isBofaV2 ? 15 : (isAnb ? 10.5 : 11),
                fontWeight: isBofaV2 ? '900' : (isAnb ? 'normal' : 'bold'),
                fill: isBofaV2 ? '#000' : (isUsaa ? '#333' : (isAnb ? ANB_LABEL_INK : labelInk('#555'))),
                fontFamily: (isTreasury || isBofaV2) ? (isBofaV2 ? 'Arial Black' : 'Times New Roman') : (isUsaa ? 'Times New Roman, serif' : (isAnb ? ANB_SERIF : fontFamily)),
                fontStyle: isUsaa ? 'italic' : 'normal',
                charSpacing: isAnb ? 60 : 0,
                // ANB: swash script P + "AY TO THE" serif caps, in
                // the stock's warm-grey label ink.
                styles: isAnb ? { 0: { 0: { fontFamily: ANB_SCRIPT, fontSize: 44, charSpacing: 400, fill: ANB_LABEL_INK } } } : undefined,
                selectable: true, hasControls: true, lockScalingY: true, lockScalingX: true
            } as any));

            // 15b. Label 2
            if (!isBofaV2) {
                c.add(new fabric.Text(payToLabel2, {
                    id: 'payToLabel2',
                    // ANB: "ORDER OF" indents under "AY TO THE",
                    // as the stock prints it.
                    left: objectPositionsRef.current.payToLabel2?.left ?? (layout.payTo.left + payToShiftX + (isAnb ? 52 : 0)),
                    top: objectPositionsRef.current.payToLabel2?.top ?? (layout.payTo.top + 16),
                    fontSize: 11, fontWeight: isAnb ? 'normal' : 'bold', fill: isUsaa ? '#333' : (isAnb ? ANB_LABEL_INK : labelInk('#555')),
                    fontFamily: isTreasury ? 'Times New Roman' : (isUsaa ? 'Times New Roman, serif' : (isAnb ? ANB_SERIF : fontFamily)),
                    fontStyle: isUsaa ? 'italic' : 'normal',
                    charSpacing: isAnb ? 60 : 0,
                    selectable: true, hasControls: true, lockScalingY: true, lockScalingX: true
                } as any));
            }

            // 15c. Payee Name (IText - Editable) - typed text
            // only; unwritten, the object is empty and the face
            // shows just the printed PAY TO THE ORDER OF label and
            // its rule.
            const payeeNameIText = new fabric.IText(checkData.payeeName || '', {
                id: 'payeeNameVal',
                left: objectPositionsRef.current.payeeNameVal?.left ?? (layout.payTo.left + payToShiftX + (isTreasury ? 70 : 70)),
                top: objectPositionsRef.current.payeeNameVal?.top ?? (layout.payTo.top + (handwritingStyle !== 'none' ? 2 : 5)),
                fontSize: checkData.payeeFontSize,
                fill: handwritingStyle !== 'none' ? handwritingColor : '#000',
                fontFamily: getHandwritingFont(handwritingStyle),
                angle: handwritingRotation + (Math.random() - 0.5) * handwritingVariation,
                shadow: (handwritingStyle !== 'none' && !isBofaV2) ? new fabric.Shadow({
                    color: `${handwritingColor}40`, blur: 1.5, offsetX: 0, offsetY: 0
                }) : undefined,
                selectable: true, hasControls: true, lockScalingY: false, lockScalingX: false
            } as any);

            // Auto-scale logic for Payee (Initial render)
            const maxPayeeWidth = (W - 230) - 70;
            if (payeeNameIText.width && payeeNameIText.width > maxPayeeWidth) {
                const scale = maxPayeeWidth / payeeNameIText.width;
                payeeNameIText.set({ scaleX: scale, scaleY: scale });
            }
            c.add(payeeNameIText);

            // 15d. Payee Line (Deletable) - the rule STOPS clear
            // of the courtesy box: its right end holds a 10-unit
            // gap from the box's left edge, measured off the box's
            // effective position, so the rule never runs into the
            // box even after the box is dragged (Princewill,
            // 2026-11-04: the box "is colliding with that line").
            if (!isTreasury && !isBofaV2) {
                const payeeRuleLen = Math.max(120, ((objectPositionsRef.current.amountBox?.left ?? layout.amountGroup.left) - 10) - (layout.payTo.left + payToShiftX + 65));
                c.add(new fabric.Line([0, 0, payeeRuleLen, 0], {
                    id: 'payeeLine',
                    left: objectPositionsRef.current.payeeLine?.left ?? (layout.payTo.left + payToShiftX + 65),
                    top: objectPositionsRef.current.payeeLine?.top ?? (layout.payTo.top + 28),
                    stroke: greenfiLineStroke, strokeWidth: 1,
                    selectable: true, hasControls: true, lockScalingY: true
                } as any));
            }

            // 16. WRITTEN AMOUNT (UNGROUPED)
            if (!isTreasury) {
                // Auto-scale logic for long amounts. While no
                // amount is written the object carries EMPTY text -
                // the legal line stays blank until an amount exists
                // (nothing written = nothing printed; no samples).
                const amountString = !hasAmount ? '' : (isBofaV2 ? `***${numberToWords(amountNumValue).toUpperCase()}***` : numberToWords(amountNumValue).toUpperCase());
                const maxAmountWidth = (W - 220) - (isBofaV2 ? 90 : 0);

                // 16a. Amount Words Value (IText)
                const amountWordsIText = new fabric.IText(amountString, {
                    id: 'amountWordsValue',
                    left: objectPositionsRef.current.amountWordsValue?.left ?? (layout.writtenAmount.left + (isBofaV2 ? 90 : 0)),
                    top: objectPositionsRef.current.amountWordsValue?.top ?? (layout.writtenAmount.top + ((handwritingStyle !== 'none' && !isBofaV2) ? -3 : 0)),
                    fontSize: (handwritingStyle !== 'none' || isPayroll || isBofaV2) ? 19 : 16,
                    fontWeight: 'bold',
                    fill: (handwritingStyle !== 'none' && !isPayroll && !isBofaV2) ? handwritingColor : '#000',
                    fontFamily: (handwritingStyle !== 'none' && !isPayroll && !isBofaV2) ? getHandwritingFont(handwritingStyle) : (isBofaV2 ? 'Arial' : 'Times New Roman, serif'),
                    shadow: (handwritingStyle !== 'none' && !isBofaV2) ? new fabric.Shadow({
                        color: `${handwritingColor}40`, blur: 1.5, offsetX: 0, offsetY: 0
                    }) : undefined,
                    selectable: true, hasControls: true, lockScalingY: false, lockScalingX: false
                } as any);

                // Check text width and scale if needed
                if (amountWordsIText.width && amountWordsIText.width > maxAmountWidth) {
                    const scaleFactor = maxAmountWidth / amountWordsIText.width;
                    amountWordsIText.set({ scaleX: scaleFactor, scaleY: scaleFactor });
                }
                c.add(amountWordsIText);

                // 16b. BofA Label
                if (isBofaV2) {
                    c.add(new fabric.Text('Amount :', {
                        id: 'amountWordsLabel',
                        left: objectPositionsRef.current.amountWordsLabel?.left ?? layout.writtenAmount.left,
                        top: objectPositionsRef.current.amountWordsLabel?.top ?? layout.writtenAmount.top,
                        fontSize: 15, fontWeight: '900',
                        fill: '#000', fontFamily: 'Arial Black',
                        selectable: true, hasControls: true
                    } as any));
                }

                // 16c. Dollars Suffix
                if (!isPayroll && !isBofaV2) {
                    c.add(new fabric.Text(isUsaa ? 'Dollars' : 'DOLLARS', {
                        id: 'amountWordsSuffix',
                        // ANB: the whole DOLLARS + padlock + note
                        // cluster sits ~88 units LEFT of the standard
                        // seat on the specimen (photo-measured), and
                        // the label rides higher for its swash D.
                        left: objectPositionsRef.current.amountWordsSuffix?.left ?? (layout.writtenAmount.left + (W - 200) + (isAnb ? -88 : 0)),
                        top: objectPositionsRef.current.amountWordsSuffix?.top ?? (layout.writtenAmount.top + (isAnb ? -13 : 2)),
                        fontSize: 10, fontWeight: isAnb ? 'normal' : 'bold', fill: isUsaa ? '#333' : (isAnb ? ANB_LABEL_INK : labelInk('#222')), fontFamily: isUsaa ? 'Times New Roman, serif' : (isAnb ? ANB_SERIF : fontFamily),
                        fontStyle: isUsaa ? 'italic' : 'normal',
                        charSpacing: isAnb ? 90 : 0,
                        // ANB: swash script D + "OLLARS" serif caps,
                        // in the stock's warm-grey label ink.
                        styles: isAnb ? { 0: { 0: { fontFamily: ANB_SCRIPT, fontSize: 32, charSpacing: 300, fill: ANB_LABEL_INK } } } : undefined,
                        selectable: true, hasControls: true
                    } as any));
                }

                // 16d. Line (Deletable)
                if (!isPayroll && !isBofaV2) {
                    c.add(new fabric.Line([0, 0, (W - 110 + 5), 0], {
                        id: 'amountWordsLine',
                        left: objectPositionsRef.current.amountWordsLine?.left ?? (layout.writtenAmount.left - 5),
                        top: objectPositionsRef.current.amountWordsLine?.top ?? (layout.writtenAmount.top + 22),
                        stroke: greenfiLineStroke, strokeWidth: 1,
                        selectable: true, hasControls: true, lockScalingY: true
                    } as any));
                }
            }

            // 16b. PAYEE ADDRESS BLOCK (Classic Style)
            // 16b. PAYEE ADDRESS BLOCK (Classic Style & Karen Style)
            if (isPayrollClassic || isKarenPayroll) {
                const payeeAddrGroup = new fabric.Group([
                    new fabric.Text(checkData.payeeName || '', { left: 0, top: 0, fontSize: 13, fontWeight: 'bold', fill: '#000', fontFamily: isKarenPayroll ? 'Times New Roman, serif' : fontFamily }),
                    new fabric.Text(checkData.payeeAddress || '', { left: 0, top: 16, fontSize: 11, fill: '#333', fontFamily: isKarenPayroll ? 'Times New Roman, serif' : fontFamily, lineHeight: 1.1 })
                ], {
                    id: 'payeeAddress',
                    left: objectPositionsRef.current.payeeAddress?.left ?? (layout as any).payeeAddress.left,
                    top: objectPositionsRef.current.payeeAddress?.top ?? (layout as any).payeeAddress.top,
                    selectable: true, hasControls: true
                } as any);
                c.add(payeeAddrGroup);
            }

            // No bank name written, no 🏦 glyph either: a blank check
            // must not print a lone bank emoji as its header. The
            // design's own bank write-up stands on the REAL
            // bankDetails group instead (typed name wins, with the
            // glyph; the design name carries none).
            const designBank = designBankBlock({
                bankName: checkData.bankName,
                bankAddress: (checkData as any).bankAddress,
                routingNumber: checkData.routingNumber
            });
            const bankNameText = isTreasury ? "United States Treasury" : ((isPayrollClassic || isKarenPayroll || isBofaV2) ? designBank.nameText : ((checkData.bankName || '').trim() ? '🏦 ' + checkData.bankName : designBank.nameText));

            // A placed logo carrying its own write-ups IS the bank
            // header: the built-in header block (bank name + address
            // lines) is suppressed so two bank identities never print
            // on one check (the uploaded block over "Bank of Omni").
            // Logo-only keeps the built-in header - the mark
            // complements it. The rule derives from the logo state
            // itself, and preview + export share this render path, so
            // they always agree.
            // Wordmark/block designs (GreenFi, ANB, USAA) carry
            // their printed bank identity as design furniture (the
            // templateWordmark / templateBankBlock groups below), so
            // the top-left bank header does not print for them - two
            // bank identities never print on one check.
            const wordmarkTemplate = selectedBank === 'GREENFI' || selectedBank === 'ANB' || selectedBank === 'USAA';
            if (!wordmarkTemplate && !logoSuppressesBankHeader(!!logoImage, logoIncludeWriteups)) {
                const bankDetailsGroup = new fabric.Group([
                    isBofaV2 ? new fabric.Text('BANK OF AMERICA', { left: 0, top: 0, fontSize: 13, fontWeight: 'bold', fill: '#012169', fontFamily: 'Arial' }) :
                        new fabric.Text(bankNameText, {
                            left: 0, top: 0,
                            fontSize: isTreasury ? 28 : (isKarenPayroll ? 13 : 14),
                            fontWeight: 'bold',
                            // Wordmark ink (fidelity programme,
                            // 2026-11-05): a template whose published
                            // identity splits mark colour from wordmark
                            // ink (KEYBANK: red key, BLACK wordmark)
                            // paints its name from
                            // TEMPLATE_WORDMARK_INK; every other
                            // template keeps the primary colour.
                            fill: templateWordmarkInk(selectedBank) ?? primaryColor,
                            fontFamily: (isTreasury || isKarenPayroll) ? (isKarenPayroll ? 'Times New Roman, serif' : 'Garamond, serif') : fontFamily,
                            shadow: (!isBofaV2) ? new fabric.Shadow({ color: 'rgba(0,0,0,0.5)', blur: 0.5, offsetX: 0.2, offsetY: 0.2 }) : undefined
                        }),
                    isBofaV2 ? new fabric.Rect({ left: 135, top: 0, width: 20, height: 12, fill: '#da291c' }) : // BofA Red Flag
                        // Multi-line bank address: the lines print
                        // stacked (fabric Text breaks on \n), and the
                        // Routing line appears only when a routing
                        // number exists - never a dangling label on a
                        // blank check. Empty parts fall back to the
                        // design's own address write-up (designBank).
                        new fabric.Text(isTreasury ? '' : designBank.addressText, { left: 0, top: 18, fontSize: isKarenPayroll ? 9 : 10, fill: secondaryColor, fontFamily: isKarenPayroll ? 'Times New Roman, serif' : fontFamily })
                ], {
                    id: 'bankDetails',
                    designWriteup: true,
                    left: objectPositionsRef.current.bankDetails?.left ?? bankLeftDefault,
                    top: objectPositionsRef.current.bankDetails?.top ?? bankTopDefault,
                    scaleX: objectPositionsRef.current.bankDetails?.scaleX ?? 1,
                    scaleY: objectPositionsRef.current.bankDetails?.scaleY ?? 1,
                    selectable: true, hasControls: true
                } as any);
                c.add(bankDetailsGroup);
            }

            // US TREASURY SPECIFIC DATA BLOCKS
            if (isTreasury) {
                // Disbursing Center & Date Block
                const dateParts = checkData.date.replace(/\//g, ' ').split(' ');
                // Simple spaced date if 3 parts, else literal
                const dateStr = dateParts.length === 3 ? `${dateParts[0]} ${dateParts[1]} ${dateParts[2].slice(-2)}` : checkData.date;

                const disburseGroup = new fabric.Group([
                    new fabric.Text(`${dateStr}   20090900   KANSAS CITY, MO`, {
                        left: 0, top: 0, fontSize: 11, fontFamily: 'Courier New', fontWeight: 'bold'
                    }),
                    new fabric.Text('000791312102   4045  09201401  I', {
                        left: 0, top: 14, fontSize: 11, fontFamily: 'Courier New', fontWeight: 'bold'
                    })
                ], {
                    left: 280, top: 80, selectable: true
                });
                c.add(disburseGroup);

                // Check No. Label
                c.add(new fabric.Text('Check No.', {
                    left: W - 180, top: 60, fontSize: 8, fill: '#666', fontFamily: 'Arial'
                }));
                // Fractional Routing Stack
                c.add(new fabric.Text('15-51\n 000', {
                    left: 560, top: 35, fontSize: 9, fontFamily: 'Arial', fontWeight: 'bold', textAlign: 'center'
                }));
            }

            // 18. MEMO (DRAGGABLE GROUP)
            // 18. MEMO (UNGROUPED)
            if (!isTreasury) {
                // 18a. Memo Label
                c.add(new fabric.Text(isBofaV2 ? 'MEMO :' : (isUsaa ? 'For' : 'MEMO'), {
                    id: 'memoLabel',
                    left: objectPositionsRef.current.memoLabel?.left ?? layout.memoGroup.left,
                    // ANB rides higher: swash-M line box (see the
                    // DATE label note).
                    top: objectPositionsRef.current.memoLabel?.top ?? (memoAnchorTop + (isAnb ? -13 : 2)),
                    fontSize: isBofaV2 ? 10 : 8, fontWeight: isAnb ? 'normal' : 'bold', fill: isBofaV2 ? '#000' : (isUsaa ? '#333' : (isAnb ? ANB_LABEL_INK : labelInk('#555'))),
                    fontFamily: isBofaV2 ? 'Arial' : (isUsaa ? 'Times New Roman, serif' : (isAnb ? ANB_SERIF : fontFamily)),
                    fontStyle: isUsaa ? 'italic' : 'normal',
                    charSpacing: isAnb ? 70 : 0,
                    // ANB: swash script M + "EMO" serif caps, in
                    // the stock's warm-grey label ink.
                    styles: isAnb ? { 0: { 0: { fontFamily: ANB_SCRIPT, fontSize: 28, charSpacing: 260, fill: ANB_LABEL_INK } } } : undefined,
                    selectable: true, hasControls: true
                } as any));

                // 18b. Memo Value (IText) - typed text only;
                // unwritten, the object is empty above its rule.
                const memoIText = new fabric.IText(checkData.memo || '', {
                    id: 'memoValue',
                    left: objectPositionsRef.current.memoValue?.left ?? (layout.memoGroup.left + (isBofaV2 ? 60 : 50)),
                    top: objectPositionsRef.current.memoValue?.top ?? memoAnchorTop,
                    fontSize: checkData.memoFontSize,
                    fill: handwritingStyle !== 'none' ? handwritingColor : '#000',
                    fontFamily: (isBofaV2 && handwritingStyle === 'none') ? 'Arial' : getHandwritingFont(handwritingStyle),
                    angle: handwritingRotation + (Math.random() - 0.5) * handwritingVariation,
                    shadow: (handwritingStyle !== 'none' && !isBofaV2) ? new fabric.Shadow({
                        color: `${handwritingColor}40`, blur: 1.5, offsetX: 0, offsetY: 0
                    }) : undefined,
                    selectable: true, hasControls: true, lockScalingY: false, lockScalingX: false
                } as any);

                // Auto-scale Memo
                const maxMemoWidth = 260 - 50;
                if (memoIText.width && memoIText.width > maxMemoWidth) {
                    const scale = maxMemoWidth / memoIText.width;
                    memoIText.set({ scaleX: scale, scaleY: scale });
                }
                c.add(memoIText);

                // 18c. Memo Line
                if (!isBofaV2) {
                    c.add(new fabric.Line([0, 0, (260 - 45), 0], {
                        id: 'memoLine',
                        left: objectPositionsRef.current.memoLine?.left ?? (layout.memoGroup.left + 45),
                        top: objectPositionsRef.current.memoLine?.top ?? (memoAnchorTop + 17),
                        stroke: greenfiLineStroke, strokeWidth: 1,
                        selectable: true, hasControls: true, lockScalingY: true
                    } as any));
                }
            }

            // 19. SIGNATURE AREA (UNGROUPED)
            // 19a. Signature Line
            if (!isBofaV2) {
                c.add(new fabric.Line([0, 0, 300, 0], {
                    id: 'signatureLine',
                    left: objectPositionsRef.current.signatureLine?.left ?? layout.signatureGroup.left,
                    top: objectPositionsRef.current.signatureLine?.top ?? (sigAnchorTop + 20),
                    stroke: lineStroke, strokeWidth: 1.2,
                    selectable: true, hasControls: true, lockScalingY: true
                } as any));
            } else {
                c.add(new fabric.Line([0, 0, 260, 0], {
                    id: 'signatureLine',
                    left: objectPositionsRef.current.signatureLine?.left ?? layout.signatureGroup.left,
                    top: objectPositionsRef.current.signatureLine?.top ?? (sigAnchorTop + 20),
                    stroke: '#333', strokeWidth: 0.5,
                    selectable: true, hasControls: true, lockScalingY: true
                } as any));
            }

            // 19a-ii. MICROPRINT SIGNATURE LINE (micro text carried on the signature rule - standard bank practice)
            if (isSecurityEnabled('microprint_sig') && showLines) {
                c.add(new fabric.Text('SECURE DOCUMENT • AUTHORIZED SIGNATURE • '.repeat(4), {
                    id: 'microprintSigLine',
                    left: objectPositionsRef.current.signatureLine?.left ?? layout.signatureGroup.left,
                    top: (objectPositionsRef.current.signatureLine?.top ?? (sigAnchorTop + 20)) - 3,
                    fontSize: 3.4, fill: '#666', fontFamily: 'Arial',
                    selectable: false
                } as any));
            }

            // 19b. X Mark
            c.add(new fabric.Polyline([
                { x: 0, y: 0 },
                { x: 0, y: 20 }
            ], {
                id: 'signatureX',
                left: objectPositionsRef.current.signatureX?.left ?? layout.signatureGroup.left,
                top: objectPositionsRef.current.signatureX?.top ?? sigAnchorTop,
                stroke: isPayroll ? '#555' : lineStroke, strokeWidth: 1.5, fill: 'transparent',
                selectable: true, hasControls: true
            } as any));

            // 19c. Signature Label (Authorized Signature)
            // The owner's reference check (a BofA check) carries the
            // AUTHORIZED SIGNATURE caption under the line, so the
            // bofa_classic design does too - it follows the standard
            // path below rather than suppressing the caption.
            c.add(new fabric.Text((isPayroll || isKarenPayroll || isUsaa || isAnb) ? '' : (isCashier ? 'OFFICER SIGNATURE' : 'AUTHORIZED SIGNATURE'), {
                id: 'signatureLabel',
                left: objectPositionsRef.current.signatureLabel?.left ?? layout.signatureGroup.left,
                top: objectPositionsRef.current.signatureLabel?.top ?? (sigAnchorTop + 25),
                fontSize: 7, fontWeight: 'bold', fill: '#444', fontFamily: 'Arial',
                selectable: true, hasControls: true
            } as any));

            // 19d. Void / Warning Text
            c.add(new fabric.Text((isUsaa || isAnb) ? '' : (isTreasury ? 'VOID AFTER ONE YEAR' : (isPayroll ? '' : (isCashier ? 'NOT VALID WITHOUT TWO SIGNATURES' : (isMoneyOrder ? 'REPORT FRAUD - CALL 1-800-BANK' : 'NOT VALID WITHOUT TWO SIGNATURES IF OVER $10,000')))), {
                id: 'signatureWarning',
                left: objectPositionsRef.current.signatureWarning?.left ?? layout.signatureGroup.left,
                // +37 (was +35): a clean gap under the AUTHORIZED
                // SIGNATURE caption (its bounding box ends at group
                // top + 34) - the two lines of small print under the
                // signature rule must never touch (owner fix,
                // 2026-11-04).
                top: objectPositionsRef.current.signatureWarning?.top ?? (sigAnchorTop + 37),
                fontSize: 6, fill: '#444', fontFamily: 'Arial', fontWeight: isTreasury ? 'bold' : 'normal',
                selectable: true, hasControls: true
            } as any));

            // 19e. Treasury Regional Officer Label
            if (isTreasury) {
                c.add(new fabric.Text('REGIONAL DISBURSING OFFICER', {
                    id: 'treasuryOfficerLabel',
                    left: objectPositionsRef.current.treasuryOfficerLabel?.left ?? layout.signatureGroup.left,
                    top: objectPositionsRef.current.treasuryOfficerLabel?.top ?? (sigAnchorTop - 10),
                    fontSize: 6, fill: '#444', fontFamily: 'Arial', fontWeight: 'bold',
                    selectable: true, hasControls: true
                } as any));
            }

            // 19f. Microprint Indicator (MP)
            if (!isKarenPayroll && !isBofaV2) {
                c.add(new fabric.Text('MP', {
                    id: 'mpIndicator',
                    left: objectPositionsRef.current.mpIndicator?.left ?? (layout.signatureGroup.left + 255),
                    top: objectPositionsRef.current.mpIndicator?.top ?? (sigAnchorTop + 20),
                    fontSize: 7, fontWeight: 'bold', fill: '#444', fontFamily: 'Arial',
                    selectable: true, hasControls: true
                } as any));
            }

            // SPECIAL: BofA Business v2 Octagonal Hologram (SHINING GLOWING SPOT)
            if (isBofaV2) {
                const sideLen = 16;
                const points = [];
                for (let i = 0; i < 8; i++) {
                    const angle = (i * Math.PI) / 4;
                    points.push({ x: 25 + sideLen * Math.cos(angle), y: 25 + sideLen * Math.sin(angle) });
                }

                // 1. Bloom Layer (Soft Focus Light Emission)
                const bloom = new fabric.Circle({
                    radius: 28, left: 25, top: 25, originX: 'center', originY: 'center',
                    fill: new fabric.Gradient({
                        type: 'radial',
                        coords: { x1: 28, y1: 28, x2: 28, y2: 28, r1: 0, r2: 28 },
                        colorStops: [
                            { offset: 0, color: 'rgba(255, 215, 0, 0.4)' },
                            { offset: 1, color: 'rgba(255, 215, 0, 0)' }
                        ]
                    }),
                    selectable: false, evented: false
                });

                // 2. Main Iridescent Octagon
                const holoPolygon = new fabric.Polygon(points, {
                    left: 25, top: 25, originX: 'center', originY: 'center',
                    fill: new fabric.Gradient({
                        type: 'linear',
                        coords: { x1: 0, y1: 0, x2: 40, y2: 40 },
                        colorStops: [
                            { offset: 0, color: '#ffd700cc' },
                            { offset: 0.25, color: '#ffffff' },
                            { offset: 0.5, color: '#c0c0c0' },
                            { offset: 0.75, color: '#ffd70099' },
                            { offset: 1, color: '#ffffffcc' }
                        ]
                    }),
                    stroke: '#ffd700', strokeWidth: 1.2,
                    shadow: new fabric.Shadow({ color: 'rgba(255,215,0,0.6)', blur: 15, offsetX: 0, offsetY: 0 })
                });

                // 3. Central Shining Spot (Specular Highlight)
                const shine = new fabric.Circle({
                    radius: 5, left: 20, top: 20, originX: 'center', originY: 'center',
                    fill: 'rgba(255, 255, 255, 0.9)', blur: 4
                } as any);

                const holoText = new fabric.Text('🔒', { left: 25, top: 25, fontSize: 18, originX: 'center', originY: 'center', fill: '#8b6914', shadow: '0 0 5px rgba(255,255,255,0.8)' });

                const holoGroup = new fabric.Group([bloom, holoPolygon, shine, holoText], {
                    id: 'bofa_hologram',
                    left: objectPositionsRef.current.bofa_hologram?.left ?? (layout.hologram?.left || (W - 340)),
                    top: objectPositionsRef.current.bofa_hologram?.top ?? (layout.hologram?.top || 210),
                    scaleX: objectPositionsRef.current.bofa_hologram?.scaleX ?? 1,
                    scaleY: objectPositionsRef.current.bofa_hologram?.scaleY ?? 1,
                    selectable: true, hasControls: true
                } as any);
                c.add(holoGroup);
            }

            // SPECIAL: Second Signature Line for Cashier/Official Checks
            if ((layout as any).signatureGroup2) {
                // Create a fresh group for the second signature since signatureGroup is now ungrouped
                const sig2 = new fabric.Group([
                    new fabric.Line([0, 0, 300, 0], { stroke: lineStroke, strokeWidth: 1.2 }),
                    new fabric.Polyline([{ x: 0, y: 0 }, { x: 0, y: 20 }], { stroke: lineStroke, strokeWidth: 1.5, fill: 'transparent' }),
                    new fabric.Text('COUNTERSIGNATURE', { left: 0, top: 25, fontSize: 7, fontWeight: 'bold', fill: '#444', fontFamily: 'Arial' })
                ], {
                    id: 'signatureGroup2',
                    left: objectPositionsRef.current.signatureGroup2?.left ?? (layout as any).signatureGroup2.left,
                    top: objectPositionsRef.current.signatureGroup2?.top ?? (layout as any).signatureGroup2.top,
                    selectable: true, hasControls: true
                } as any);
                c.add(sig2);
            }

            // SPECIAL: Certified / Official Stamp
            if (isCertified) {
                const certifiedStamp = new fabric.Group([
                    new fabric.Rect({ width: 140, height: 60, rx: 10, ry: 10, fill: 'transparent', stroke: '#c9a227', strokeWidth: 3, opacity: 0.7 }),
                    new fabric.Text('CERTIFIED', { left: 70, top: 15, fontSize: 16, fontWeight: 'bold', fill: '#c9a227', originX: 'center', opacity: 0.8 }),
                    new fabric.Text('OFFICIAL INSTRUMENT', { left: 70, top: 38, fontSize: 8, fontWeight: 'bold', fill: '#c9a227', originX: 'center', opacity: 0.8 })
                ], {
                    left: W / 2 - 70, top: 110, angle: -5, selectable: true
                });
                c.add(certifiedStamp);
            }
            if (isSecurityEnabled('void_pantograph')) {
                for (let y = 55; y < textureBottom - 30; y += 45) {
                    for (let x = 70; x < W - 70; x += 90) {
                        c.add(new fabric.Text('VOID', {
                            left: x, top: y, fontSize: 9, fill: `${primaryColor}06`,
                            fontWeight: 'bold', angle: -25, fontFamily: 'Arial', selectable: false
                        }));
                    }
                }
            }

            // 20b. US TREASURY MAILING NOTICE / E-CHECK STAMP
            if (isTreasury) {
                c.add(new fabric.Text('PER ENCLOSED MAILING NOTICE', {
                    left: layout.payTo.left || 310,
                    top: (layout.payTo.top || 100) + 130,
                    fontSize: 8, fontWeight: 'bold', fill: '#222', fontFamily: 'Arial', selectable: true
                } as any));
            } else if (checkStyle === 'echeck') {
                const echeckStamp = new fabric.Group([
                    new fabric.Rect({ width: 140, height: 35, rx: 5, ry: 5, fill: 'transparent', stroke: `${primaryColor}60`, strokeWidth: 2 }),
                    new fabric.Text('ELECTRONICALLY', { left: 70, top: 8, fontSize: 9, fontWeight: 'bold', fill: primaryColor, originX: 'center' }),
                    new fabric.Text('PROCESSED', { left: 70, top: 22, fontSize: 9, fontWeight: 'bold', fill: primaryColor, originX: 'center' })
                ], {
                    left: W - 180, top: 60, angle: -10, opacity: 0.6, selectable: true
                });
                c.add(echeckStamp);
            }

            // 21. CHEMICAL-REACTIVE: NO FRONT ELEMENT (Phase 2, per
            // the banking-standard research). Chemical reactivity is
            // a property of the paper; no standard or specimen puts a
            // chemical indicator on the front's bottom row. The
            // feature's furniture is the Chemical Wash Detection Box
            // in the BACK security panel (CheckBackEngine, driven by
            // config.chemicalWash in backVariants) plus the panel's
            // "CHEMICAL SENSITIVE PAPER STOCK" line — both follow
            // this same enabled flag, so the feature stays honest in
            // the design's lists with nothing drawn on the face.

            // 22. RAINBOW STRIPE REMOVED - per user request for realism
            // (Feature disabled - uncomment to re-enable)
            // if (isSecurityEnabled('rainbow_stripe')) { ... }

            // 23. MICR LINE: PROFESSIONAL BANKING STANDARD
            const isBusinessStyle = ['business', 'payroll', 'cashier', 'certified'].includes(checkStyle);
            const selectedMicrFontObj = MICR_FONTS.find(f => f.id === checkData.micrFont) || MICR_FONTS[0];
            const micrFontFamily = selectedMicrFontObj.family;

            let micrLine = '';
            let accNum = checkData.accountNumber.replace(/\s/g, '');
            let routing = checkData.routingNumber.replace(/\s/g, '');
            let checkNum = checkData.checkNumber.padStart(4, '0');
            // BLANK CHECK: with no numbers written there is no MICR
            // line to print - nothing written, nothing printed (the
            // blank-amount rule applied to the band; without this the
            // band would show bare control symbols and a padded
            // "0000"). The field captions belong to the line and go
            // with it; the empty band itself stays in place.
            const hasMicrData = routing.length > 0 || accNum.length > 0 || checkData.checkNumber.trim().length > 0;

            // Symbol mapping helper (E-13B vs CMC-7)
            const getMicrSymbol = (symbol: 'transit' | 'onus' | 'dash') => {
                if (checkData.micrFont === 'cmc7') {
                    if (symbol === 'transit') return '⑇'; // CMC-7 S1
                    if (symbol === 'onus') return '⑊';    // CMC-7 S3
                    if (symbol === 'dash') return '⑉';    // CMC-7 S2
                }
                if (checkData.micrFont === 'e13b') {
                    // GnuMICR (the self-hosted primary E-13B face) carries
                    // the four control symbols at ASCII, per the E-13B
                    // font-encoding convention: A = transit, B = amount,
                    // C = on-us, D = dash. It has NO glyphs at U+2446-2449,
                    // so the canvas string must use the letters or the
                    // symbols would render as blanks.
                    if (symbol === 'transit') return 'A';
                    if (symbol === 'onus') return 'C';
                    if (symbol === 'dash') return 'D';
                }
                // Other MICR faces keep the Unicode OCR-symbol codepoints.
                if (symbol === 'transit') return '⑆';
                if (symbol === 'onus') return '⑈';
                if (symbol === 'dash') return '⑉';
                return '';
            };

            const t = getMicrSymbol('transit');
            const o = getMicrSymbol('onus');
            const d = getMicrSymbol('dash');
            const epc = (checkData as any).epcDigit || '';

            // PERFECT READ Sequence: Routing -> Account -> Check Number.
            // The variant gate is the shared micrVariantFor resolver —
            // the BofA pattern belongs to the BofA Classic design only.
            const isBofAClassic = micrVariantFor(checkStyle, selectedBank) === 'bofa';

            if (isBofAClassic) {
                // Precision calibrated for BofA Mobile Deposit
                micrLine = `${t}${routing}${t} ${accNum}${o} ${checkNum}`;
            } else {
                // Bank-standard sequence (matches the owner's reference
                // check): transit symbol brackets the routing number, the
                // on-us symbol LEADS the account field, and an on-us symbol
                // closes the check-number field.
                micrLine = `${t}${routing}${t} ${o}${accNum} ${checkNum}${o}`;
            }
            if (epc) micrLine = `${epc} ${micrLine}`;

            // MEASUREMENT GUARD: fabric caches per-character widths
            // globally, keyed by the font-family stack, the first time
            // a character is measured (fabric.charWidthsCache). If that
            // first measurement happens before the MICR webfont has
            // arrived, the widths cached are the fallback face's
            // (narrower), and every later MICR object - including the
            // font-ready repaint's fresh one - inherits them: the line
            // is rasterized into a too-narrow cache canvas and its tail
            // (the check-number segment) is clipped off the check.
            // Drop the cached widths for this stack so the line below
            // is always measured against the face that will draw it.
            (fabric.util as any).clearFabricFontCache?.(micrFontFamily);

            const micrObject = new fabric.Text(micrLine, {
                id: 'micrLine',
                // Bank Standard: 0.625" (5/8") from Left | 0.5" from Bottom implied aligned
                left: objectPositionsRef.current.micrLine?.left ?? (isBusinessStyle ? 62.5 : 60),
                top: objectPositionsRef.current.micrLine?.top ?? bandH - 50, // 0.5" from the CHECK's bottom edge approx
                fontSize: checkData.micrFontSize,
                fontFamily: micrFontFamily,
                fill: '#1a1a1a',
                // MICR typefaces are fixed-pitch by design: added tracking
                // breaks the standard character pitch, so none is applied.
                charSpacing: 0,
                // Flat print at normal weight: a shadow or a bold face
                // thickens the glyph strokes - a known machine-read
                // reject cause. Real MICR print is flat and sharp.
                fontWeight: 'normal',
                selectable: true,
                hasControls: true,
                lockRotation: true
            } as any);
            // STANDARD ENFORCEMENT: the MICR line stays draggable, but never out of
            // the ANSI clear band - out-of-band MICR is the top machine-reject cause.
            // The band is measured from the CHECK's bottom edge (bandH),
            // which on the Karen page is the top portion's bottom edge.
            micrObject.top = Math.min(Math.max(micrObject.top ?? (bandH - 50), bandH - 62), bandH - 30);
            micrObject.left = Math.min(Math.max(micrObject.left ?? 60, 30), 150);
            if (hasMicrData) c.add(micrObject);

            // FIELD LABELS (bank-standard stock furniture, as on the
            // owner's reference check): small captions under the routing
            // and account fields at the bottom edge of the band. US E-13B
            // designs only; they are print furniture, not part of the
            // machine-read line, and they are not draggable.
            if (checkData.micrFont !== 'cmc7' && !isBofAClassic && !epc && hasMicrData && selectedBank !== 'GREENFI') {
                const micrLabelStyle = { fontSize: 6.5, fontFamily: 'Arial', fill: '#555', charSpacing: 60, selectable: false, evented: false } as any;
                c.add(new fabric.Text('TRANSIT ROUTING NUMBER', { ...micrLabelStyle, id: 'micrLabelRouting', left: micrObject.left, top: bandH - 13 }));
                const micrPrefix = new fabric.Text(`${t}${routing}${t} ${o}`, { fontSize: checkData.micrFontSize, fontFamily: micrFontFamily, charSpacing: 0 });
                c.add(new fabric.Text('ACCOUNT NUMBER', { ...micrLabelStyle, id: 'micrLabelAccount', left: (micrObject.left ?? 60) + (micrPrefix.width ?? 0) + 1, top: bandH - 13 }));
            }

            // WATERMARK: High-Fidelity & Draggable
            if (isSecurityEnabled('watermark') && !isBofaV2) {
                const watermarkText = (checkData as any).watermarkText || getWatermarkText();
                const watermark = new fabric.Text(watermarkText, {
                    id: 'watermark',
                    left: objectPositionsRef.current.watermark?.left ?? W / 2,
                    top: objectPositionsRef.current.watermark?.top ?? H / 2,
                    scaleX: objectPositionsRef.current.watermark?.scaleX ?? 1,
                    scaleY: objectPositionsRef.current.watermark?.scaleY ?? 1,
                    fontSize: 55, fontFamily: 'Arial Black',
                    fill: 'rgba(0,0,0,0.04)', angle: -25, originX: 'center', originY: 'center',
                    selectable: true, hasControls: true
                } as any);
                c.add(watermark);
                c.sendToBack(watermark);
            }

            // 9. HOLOGRAPHIC STRIP on LEFT (RESTORED & GROUPED)
            if (isSecurityEnabled('holographic')) {
                const prismGradient = new fabric.Gradient({
                    type: 'linear',
                    coords: { x1: 0, y1: 0, x2: 0, y2: textureBottom },
                    colorStops: [
                        { offset: 0, color: 'rgba(255,0,0,0.15)' },
                        { offset: 0.2, color: 'rgba(255,255,0,0.15)' },
                        { offset: 0.4, color: 'rgba(0,255,0,0.15)' },
                        { offset: 0.6, color: 'rgba(0,0,255,0.15)' },
                        { offset: 0.8, color: 'rgba(75,0,130,0.15)' },
                        { offset: 1, color: 'rgba(148,0,211,0.15)' }
                    ]
                });

                const holoGroup = new fabric.Group([
                    new fabric.Rect({
                        left: 0, top: 0, width: 35, height: textureBottom - 40,
                        fill: prismGradient, stroke: 'rgba(0,0,0,0.1)', strokeWidth: 0.5
                    }),
                    ...Array.from({ length: 15 }).map((_, i) => new fabric.Text("SECURE", {
                        left: 17.5, top: 20 + i * 40, fontSize: 5,
                        angle: -90, fill: 'rgba(0,0,0,0.3)', originX: 'center'
                    }))
                ], {
                    id: 'holoGroup',
                    left: objectPositionsRef.current.holoGroup?.left ?? W - 60,
                    top: objectPositionsRef.current.holoGroup?.top ?? 20,
                    scaleX: objectPositionsRef.current.holoGroup?.scaleX ?? 1,
                    scaleY: objectPositionsRef.current.holoGroup?.scaleY ?? 1,
                    selectable: true, hasControls: true
                } as any);
                c.add(holoGroup);
            }



            // 24. MICR CLEAR ZONE GUIDE: intentionally NOT drawn here.
            // The guide is an HTML overlay in the studio JSX, so it can
            // never be baked into an exported or printed check (it used
            // to be a canvas object and exported with the design).

            // 25. CUSTOM ASSETS: LOGO & SIGNATURE
            if (logoImage) {
                fabric.Image.fromURL(logoImage, (img) => {
                    // Stale-load guard (see renderSeqRef): a logo image
                    // from a superseded render - e.g. the full-block
                    // processing landing after the user switched to
                    // Logo only - must never be added over the current
                    // scene, and exactly one customLogo may exist.
                    if (renderSeq !== renderSeqRef.current || (c as any).__isDisposed) return;
                    c.getObjects().filter((o: any) => o.id === 'customLogo').forEach((o: any) => c.remove(o));
                    if (objectPositionsRef.current.customLogo) {
                        // The user has placed this logo themselves (drag /
                        // resize, persisted): honour their position, and
                        // their scale too — EXCEPT that a scale saved for
                        // a different crop of the mark must never render
                        // the logo down over the account-holder block
                        // (Phase 2 live trace: a tall logo-only mark
                        // restored at a full-block scale covered the
                        // holder name). clampLogoScaleToZone only ever
                        // shrinks, and only inside the header band.
                        const savedLogo = objectPositionsRef.current.customLogo;
                        img.set({
                            id: 'customLogo',
                            left: savedLogo.left,
                            top: savedLogo.top,
                            selectable: true,
                            hasControls: true
                        } as any);
                        img.scale(clampLogoScaleToZone(
                            img.height || 0,
                            savedLogo.scaleX || 0.5,
                            savedLogo.top ?? 0,
                            logoZoneForLayout({ ...layout.bankDetails, left: bankLeftDefault, top: bankTopDefault }, { ...layout.accHolder, top: layout.accHolder.top + headerBorderShift })
                        ));
                    } else {
                        // Default placement: the logo IS the bank header,
                        // so it anchors on the bank block (top-left) and is
                        // fitted into the header band above the account
                        // holder - a wide write-ups block extends right
                        // from the mark, and nothing spills onto the
                        // holder name or the payee line. (The old 50,50 /
                        // 220x85 default printed across "JOHN DOE".)
                        const placement = defaultLogoPlacement(
                            img.width || 0,
                            img.height || 0,
                            logoZoneForLayout({ ...layout.bankDetails, left: bankLeftDefault, top: bankTopDefault }, { ...layout.accHolder, top: layout.accHolder.top + headerBorderShift })
                        );
                        img.set({
                            id: 'customLogo',
                            left: placement.left,
                            top: placement.top,
                            selectable: true,
                            hasControls: true
                        } as any);
                        img.scale(placement.scale);
                    }
                    c.add(img);
                    c.renderAll();
                });
            }

            if (signatureImage) {
                fabric.Image.fromURL(signatureImage, (img) => {
                    if (renderSeq !== renderSeqRef.current || (c as any).__isDisposed) return;
                    c.getObjects().filter((o: any) => o.id === 'customSignature').forEach((o: any) => c.remove(o));
                    img.set({
                        id: 'customSignature',
                        left: objectPositionsRef.current.customSignature?.left ?? layout.signatureGroup.left,
                        top: objectPositionsRef.current.customSignature?.top ?? sigAnchorTop - 20,
                        selectable: true,
                        hasControls: true
                    } as any);
                    if (objectPositionsRef.current.customSignature) {
                        img.scaleX = objectPositionsRef.current.customSignature.scaleX || 1;
                        img.scaleY = objectPositionsRef.current.customSignature.scaleY || 1;
                    } else {
                        img.scaleToWidth(150);
                    }
                    c.add(img);
                    c.renderAll();
                });
            }

            // 25-pre-b. ADDITIONAL PLACED LOGOS (2026-11-04): every
            // extra logo instance is its own canvas object, drawn
            // from state like a custom object — selectable, draggable
            // and resizable through the same machinery, positioned
            // from the positions map under its instance id, and
            // removed by the deleted-ids filter below when the user
            // deletes it on the canvas. Because they are real canvas
            // objects, every canvas export (PNG / canvas PDF) carries
            // them with no extra export code.
            placedLogos.forEach(inst => {
                if (!inst.src) return; // awaiting re-derivation from its library entry
                const instSrc = inst.src;
                fabric.Image.fromURL(instSrc, (img) => {
                    if (renderSeq !== renderSeqRef.current || (c as any).__isDisposed) return;
                    c.getObjects().filter((o: any) => o.id === inst.instanceId).forEach((o: any) => c.remove(o));
                    const saved = objectPositionsRef.current[inst.instanceId];
                    img.set({
                        id: inst.instanceId,
                        left: saved?.left ?? inst.left,
                        top: saved?.top ?? inst.top,
                        selectable: true,
                        hasControls: true
                    } as any);
                    if (saved) {
                        img.scaleX = saved.scaleX ?? 1;
                        img.scaleY = saved.scaleY ?? 1;
                        img.angle = saved.angle ?? 0;
                    } else {
                        img.scaleToWidth(140);
                    }
                    c.add(img);
                    c.renderAll();
                });
            });

            // 25a. USER OBJECTS (Add Text / Attachments toolbar tools)
            customObjects.forEach(item => {
                const saved = objectPositionsRef.current[item.id];
                if (item.kind === 'text') {
                    const t = new fabric.IText(item.text || 'New Text', {
                        id: item.id,
                        left: saved?.left ?? item.left ?? 110,
                        top: saved?.top ?? item.top ?? 110,
                        fontSize: 16,
                        fontFamily: 'Arial, sans-serif',
                        fill: '#1a3a5c',
                        selectable: true,
                        hasControls: true
                    } as any);
                    if (saved) {
                        t.scaleX = saved.scaleX ?? 1;
                        t.scaleY = saved.scaleY ?? 1;
                        t.angle = saved.angle ?? 0;
                    }
                    c.add(t);
                } else if (item.kind === 'image' && item.src) {
                    fabric.Image.fromURL(item.src, (img) => {
                        if (renderSeq !== renderSeqRef.current || (c as any).__isDisposed) return;
                        c.getObjects().filter((o: any) => o.id === item.id).forEach((o: any) => c.remove(o));
                        img.set({
                            id: item.id,
                            left: saved?.left ?? item.left ?? 90,
                            top: saved?.top ?? item.top ?? 90,
                            selectable: true,
                            hasControls: true
                        } as any);
                        if (saved) {
                            img.scaleX = saved.scaleX || 1;
                            img.scaleY = saved.scaleY || 1;
                            img.angle = saved.angle || 0;
                        } else {
                            img.scaleToWidth(120);
                        }
                        c.add(img);
                        c.renderAll();
                    });
                }
            });

            // 25b. RESTORE SELECTION
            if (activeId) {
                const newActive = c.getObjects().find(o => (o as any).id === activeId);
                if (newActive) {
                    c.setActiveObject(newActive);
                }
            }

            c.renderAll();

            // FREE USER WATERMARK — blocks screenshot/deposit usability
            if (!isPaid) {
                const wmLines = ['OMNICHECK', 'SAMPLE', 'UPGRADE TO', 'EXPORT'];
                wmLines.forEach((txt, i) => {
                    const wm = new fabric.Text(txt, {
                        left: W / 2,
                        top: 60 + i * 65,
                        fontSize: 42,
                        fontFamily: 'Arial Black, Impact, sans-serif',
                        fill: 'rgba(255, 80, 80, 0.22)',
                        fontWeight: '900',
                        angle: -35,
                        originX: 'center',
                        originY: 'center',
                        selectable: false,
                        evented: false
                    });
                    c.add(wm);
                    c.bringToFront(wm);
                });
                c.renderAll();
            }

            // FILTER OUT DELETED OBJECTS (Persistence of Deletion)
            if (deletedIds.length > 0) {
                const objects = c.getObjects();
                objects.forEach((obj: any) => {
                    if (obj.id && deletedIds.includes(obj.id)) {
                        c.remove(obj);
                    }
                });
                c.requestRenderAll();
            }

            // GHOSTS ABOLISHED (Princewill, 2026-11-04): no ghost
            // objects are created anywhere on the front. The dummy
            // MICR ghost and its captions are gone - with no numbers
            // written, the real MICR block above simply adds nothing,
            // and the band holds only its emptiness; with numbers
            // written, the real line and its real captions render.
            // A deleted object stays deleted: the deletion filter
            // above removes it and nothing re-creates a stand-in.

        }); // Close setBackgroundColor callback
    }, [checkData, checkStyle, selectedFont, selectedTexture, paperColor, selectedBank, securityFeatures, sealColor, sealFinish, designPattern, handwritingStyle, handwritingRotation, handwritingVariation, handwritingColor, textureOpacity, logoImage, logoIncludeWriteups, signatureImage, signatureStyle, deletedIds, customObjects, placedLogos, isPaid]);

    // Keep the latest render callback reachable from the MICR font-load
    // effect (declared earlier in the component) for the one-time
    // repaint once the selected MICR webfont is ready.
    useEffect(() => { renderCheckRef.current = renderCheck; }, [renderCheck]);

    const getWatermarkText = () => {
        if (checkStyle === 'cashier') return "CASHIER'S CHECK";
        if (selectedBank && bankSpecificTemplates[selectedBank]) {
            return bankSpecificTemplates[selectedBank].name.toUpperCase();
        }
        return "SECURE DOCUMENT";
    };

    // Handle form changes
    const handleInputChange = (field: string, value: string | number) => {
        setCheckData(prev => {
            const newData = { ...prev, [field]: value };

            // Auto-synchronize legal written words with numeric dollar amount (UCC § 3-114 compliance)
            if (field === 'amount') {
                // Cents-exact: the stored amount is rounded here, at
                // the single entry point every amount edit flows
                // through, so float dust can never enter the state.
                const numericVal = parseMoney(value);
                newData.amount = numericVal;
                // Empty / zero amount = blank check: no written words either.
                newData.writtenAmount = numericVal > 0 ? convertToWords(numericVal) : '';
            }

            // Auto-fill bank details if routing number is provided
            if (field === 'routingNumber') {
                const routing = value.toString().replace(/\D/g, '').trim();
                if (fractionalAutoRef.current) {
                    const derived = MICRComplianceEngine.deriveFractionalRouting(routing);
                    if (derived) newData.fractionalRouting = derived;
                }
                if (routing.length >= 9) {
                    const bankInfo: Record<string, { name: string, address: string, template?: string }> = {
                        '021000021': { name: 'JPMorgan Chase Bank, N.A.', address: '270 Park Ave, New York, NY 10017', template: 'CHASE' },
                        '121000248': { name: 'Wells Fargo Bank, N.A.', address: '420 Montgomery St, San Francisco, CA 94104', template: 'WELLS' },
                        '026009593': { name: 'Bank of America, N.A.', address: '100 N Tryon St, Charlotte, NC 28202', template: 'BOFA_CLASSIC' },
                        '021000018': { name: 'Citibank, N.A.', address: '388 Greenwich St, New York, NY 10013', template: 'CITI' },
                        '053000219': { name: 'PNC Bank, N.A.', address: '300 Fifth Ave, Pittsburgh, PA 15222', template: 'PNC' },
                        '031100209': { name: 'TD Bank, N.A.', address: '1701 Route 70 East, Cherry Hill, NJ 08034', template: 'TD' },
                        '051000017': { name: 'U.S. Bank National Association', address: '800 Nicollet Mall, Minneapolis, MN 55402', template: 'US_BANK' },
                        '053101121': { name: 'Capital One, N.A.', address: '1680 Capital One Dr, McLean, VA 22102', template: 'CAPITAL_ONE' },
                        '071000013': { name: 'BMO Harris Bank, N.A.', address: '111 W Monroe St, Chicago, IL 60603', template: 'BMO' },
                        '042000314': { name: 'Fifth Third Bank, N.A.', address: '38 Fountain Square Plaza, Cincinnati, OH 45263', template: 'FIFTH_THIRD' },
                        '054000030': { name: 'SunTrust Bank (Truist)', address: '303 Peachtree St NE, Atlanta, GA 30308', template: 'TRUIST' },
                        '061113415': { name: 'Navy Federal Credit Union', address: '820 Follin Ln, Vienna, VA 22180', template: 'NAVY_FCU' }
                    };

                    if (bankInfo[routing]) {
                        newData.bankName = bankInfo[routing].name.toUpperCase();
                        newData.bankAddress = bankInfo[routing].address.toUpperCase();
                        if (bankInfo[routing].template) {
                            // Apply template settings as well
                            setTimeout(() => applyBankTemplate(bankInfo[routing].template!), 0);
                        }
                    }
                }
            }
            return newData;
        });
    };

    // Generate/Download check
    // Guest export gate: a signed-out user who taps ANY export,
    // print or download affordance gets the same honest sign-in gate
    // the server Bank-Grade PDF uses (a plain message + the sign-in
    // modal) - never a silent no-op. Signed-in behaviour and the
    // real export paths are untouched.
    const guestExportGate = (): boolean => {
        if (isAuthenticated) return false;
        alert('⚠️ Please sign in to export, print or download your check.');
        openAuthModal('login');
        return true;
    };

    // GENERATE opens the result sheet instead of downloading a
    // front-only PNG on the spot. Owner report (2026-11-04): he
    // tapped Generate expecting the PDF, got a PNG of the front,
    // and could not find a PDF download anywhere afterwards - the
    // only PDF entries were a dropdown item mislabelled "Preview"
    // and a toolbar icon. The sheet's primary action IS the PDF
    // (front + back, with a front-only variant beside it); the
    // front PNG remains as a clearly secondary action.
    const handleGenerate = () => {
        if (guestExportGate()) return;
        if (!fabricCanvas) return;
        if (!runBankAcceptanceGate()) return;
        setShowGenerateSheet(true);
    };

    const handleDownloadFrontPng = () => {
        if (!fabricCanvas) return;
        const dataUrl = captureCanvasWithoutGhosts(fabricCanvas, { format: 'png', multiplier: EXPORT_MULTIPLIER });
        if (!dataUrl) return;
        const link = document.createElement('a');
        link.download = `check_${checkData.checkNumber}.png`;
        link.href = dataUrl;
        link.click();
        setShowGenerateSheet(false);
    };

    // Applies a validated AiDesignEngine patch to the studio state. The
    // engine starts from the current design, so this function applies only
    // the fields the prompt actually changed and reports unsupported parts
    // instead of silently resetting or inventing a design.
    const applyAiResult = (result: Awaited<ReturnType<typeof processAiPrompt>>) => {
        if (result.meta.bankDetected) {
            if (result.border.color || result.border.secondaryColor) {
                aiBorderOverrideRef.current = true;
                window.setTimeout(() => { aiBorderOverrideRef.current = false; }, 600);
            }
            applyBankTemplate(result.meta.bankDetected);
        }

        if (result.layout.resetToDefault) setObjectPositions({});
        if (result.layout.restoreDeleted) {
            setDeletedIds([]);
            setDeletedBackIds([]);
        }

        if (result.styles.checkStyle !== undefined) applyStylePreset(result.styles.checkStyle);
        if (result.styles.texture !== undefined) setSelectedTexture(result.styles.texture);
        if (result.styles.paperColor !== undefined) setPaperColor(result.styles.paperColor);
        if (result.styles.font !== undefined) setSelectedFont(result.styles.font);
        if (result.styles.textureOpacity !== undefined) setTextureOpacity(result.styles.textureOpacity);

        if (Object.keys(result.border).length > 0) {
            if (!result.meta.bankDetected) aiBorderOverrideRef.current = false;
            setBorderConfig(prev => ({ ...prev, ...result.border }));
        }

        if (Object.keys(result.fields).length > 0) {
            setCheckData(prev => {
                const next: any = { ...prev, ...result.fields, isAiActive: true };
                if (result.fields.amount !== undefined) {
                    const aiAmount = parseMoney(result.fields.amount);
                    next.amount = aiAmount;
                    next.writtenAmount = aiAmount > 0 ? convertToWords(aiAmount) : '';
                }
                if (result.fields.routingNumber !== undefined && result.fields.fractionalRouting === undefined && fractionalAutoRef.current) {
                    const derived = MICRComplianceEngine.deriveFractionalRouting(String(result.fields.routingNumber));
                    if (derived) next.fractionalRouting = derived;
                }
                return next;
            });
        } else {
            setCheckData(prev => ({ ...prev, isAiActive: true }));
        }

        if (result.controls.handwritingStyle !== undefined) setHandwritingStyle(result.controls.handwritingStyle);
        if (result.controls.signatureStyle !== undefined) setSignatureStyle(result.controls.signatureStyle);
        if (result.controls.backSideStyle !== undefined) {
            setBackSideStyle(result.controls.backSideStyle);
            setBackStyleTouched(true); // An Ask AI back choice is a user override (spec C10/C12)
        }
        if (result.controls.sealColor !== undefined) setSealColor(result.controls.sealColor);
        if (result.controls.sealFinish !== undefined) setSealFinish(result.controls.sealFinish);
        if (result.controls.handwritingColor !== undefined) setHandwritingColor(result.controls.handwritingColor);
        if (result.controls.handwritingRotation !== undefined) setHandwritingRotation(result.controls.handwritingRotation);
        if (result.controls.showMicrBand !== undefined) setShowMicrBand(result.controls.showMicrBand);
        if (result.controls.showAmountBorder !== undefined) setShowAmountBorder(result.controls.showAmountBorder);
        if (result.controls.showMicrGuide !== undefined) setShowMicrGuide(result.controls.showMicrGuide);
        if (result.controls.clearLogo) { setLogoImage(null); setActiveLogoId(null); }
        if (result.controls.clearSignature) {
            setSignatureImage(null);
            setRawSignatureImage(null);
        }

        if (result.security.enable.length > 0 || result.security.disable.length > 0) {
            setSecurityFeatures(prev => prev.map(f => ({
                ...f,
                enabled: result.security.disable.includes(f.id)
                    ? false
                    : result.security.enable.includes(f.id)
                        ? true
                        : f.enabled
            })));
        }

        if (!result.layout.resetToDefault && Object.keys(result.layout.moveMap).length > 0) {
            const canvasW = checkDimensions.width;
            const canvasH = (checkStyle === 'payroll_karen' || selectedBank === 'KAREN_ART_STUDIO_PAYROLL') ? 900 : checkDimensions.height;
            setObjectPositions(prev => {
                const next = { ...prev };
                Object.entries(result.layout.moveMap).forEach(([id, move]) => {
                    if (id === 'micrLine') return; // MICR position is compliance-locked.
                    next[id] = {
                        ...(next[id] || {}),
                        ...move,
                        left: move.left !== undefined ? Math.min(canvasW - 20, Math.max(0, move.left)) : (next[id]?.left ?? move.left),
                        top: move.top !== undefined ? Math.min(canvasH - 70, Math.max(0, move.top)) : (next[id]?.top ?? move.top)
                    };
                });
                return next;
            });
        }

        // Open the tool group that best matches the edit, so the user lands
        // exactly where the AI's change can be inspected or fine-tuned.
        if (result.fields.micrFont !== undefined || result.fields.micrFontSize !== undefined) setActiveSection('micr_config');
        else if (Object.keys(result.fields).length > 0) setActiveSection('check_info');
        else if (result.security.enable.length > 0 || result.security.disable.length > 0 || result.controls.sealColor !== undefined || result.controls.sealFinish !== undefined) setActiveSection('security');
        else if (Object.keys(result.border).length > 0) setActiveSection('borders');
        else if (result.styles.texture !== undefined || result.styles.paperColor !== undefined) setActiveSection('paper_texture');
        else if (result.styles.font !== undefined || result.controls.handwritingStyle !== undefined || result.controls.signatureStyle !== undefined) setActiveSection('typography');
        else if (result.meta.bankDetected || result.styles.checkStyle !== undefined) setActiveSection('bank_style');

        setAiFeedback((result.meta.explanation || 'Design updated.').replace(/\*\*/g, ''));
        window.setTimeout(() => setAiFeedback(''), 12000);
    };

    const runAiPrompt = async () => {
        const p = String((checkData as any).aiPrompt || '').trim();
        if (!p) {
            setAiFeedback('Type what you want changed — for example: “Chase business check, blue marble, pay Jane Doe $250, memo rent, maximum security.”');
            window.setTimeout(() => setAiFeedback(''), 9000);
            return;
        }
        const result = await processAiPrompt(p, {
            checkStyle,
            selectedTexture,
            paperColor,
            selectedFont,
            borderConfig,
            textureOpacity,
            securityFeatures,
            checkData: checkData as any,
            handwritingStyle,
            signatureStyle,
            backSideStyle: effectiveBackStyle,
            sealColor,
            sealFinish,
            handwritingColor,
            handwritingRotation,
            showMicrBand,
            showAmountBorder,
            showMicrGuide,
            objectPositions,
            width: checkDimensions.width,
            height: (checkStyle === 'payroll_karen' || selectedBank === 'KAREN_ART_STUDIO_PAYROLL') ? 900 : checkDimensions.height
        });
        applyAiResult(result);
    };

    // BANK ACCEPTANCE GATE - every export runs the same compliance audit the
    // widget shows. Anything short of 100% lists the exact decline risks and
    // asks the owner to confirm before any export credit is consumed.
    const runBankAcceptanceGate = (): boolean => {
        const H = checkDimensions.height;
        const micrObj: any = (fabricCanvas as any)?.getObjects?.().find((o: any) => o.id === 'micrLine');
        const micrTop = micrObj?.top ?? objectPositionsRef.current.micrLine?.top;
        const amountNum = parseMoney(checkData.amount);
        const compliance = MICRComplianceEngine.evaluateZeroDeclineCompliance({
            routingNumber: checkData.routingNumber,
            accountNumber: checkData.accountNumber,
            checkNumber: checkData.checkNumber,
            amount: checkData.amount,
            payeeName: checkData.payeeName,
            date: checkData.date,
            fractionalRouting: checkData.fractionalRouting,
            hasSignature: Boolean(signatureImage) || Boolean(signatureStyle && signatureStyle !== 'none'),
            amountWords: amountNum > 0 ? convertToWords(amountNum) : '',
            micrInBand: typeof micrTop === 'number' ? (micrTop >= H - 67 && micrTop <= H - 24) : undefined
        });
        if (compliance.is100PercentCompliant) return true;
        const failed = compliance.checks.filter(c => !c.passed);
        return window.confirm(
            `BANK ACCEPTANCE CHECK - ${compliance.score}%\n\nThis check may be DECLINED for:\n${failed.map(c => `- ${c.label}: ${c.message}`).join('\n')}\n\nFix these in the editor for a fully standard-compliant check. Export anyway only if you know the receiving bank accepts it.`
        );
    };

    const handleDirectPrint = async () => {
        if (guestExportGate()) return;
        if (!fabricCanvas) return;
        // Same license gate as the PDF export
        if (!isPaid) {
            alert('\u26a0\ufe0f Upgrade to a paid plan to print clean checks.\n\nGo to Pricing to choose a plan.');
            return;
        }
        if (!canExport) {
            alert(`\u26a0\ufe0f You\'ve reached your ${currentPlan.name} plan limit (${currentPlan.checksPerMonth} exports/month).\n\nUpgrade to export more.`);
            return;
        }
        if (!runBankAcceptanceGate()) return;
        const frontData = captureCanvasWithoutGhosts(fabricCanvas, { format: 'png', multiplier: EXPORT_MULTIPLIER });
        const backData = await captureBackForExport();
        if (!frontData || !backData || await isBlankCapture(backData)) {
            alert('The back of the check could not be captured. Please view Page 2 once, then print again.');
            return;
        }
        if (!useCheckExport()) {
            alert('Print failed. Please check your plan status.');
            return;
        }
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        document.body.appendChild(iframe);
        const printDoc = iframe.contentWindow?.document;
        if (!printDoc) {
            alert('Printing is not available in this browser.');
            document.body.removeChild(iframe);
            return;
        }
        printDoc.open();
        printDoc.write('<html><head><title>Check ' + checkData.checkNumber + '</title><style>@page { size: 8.5in 3.5in; margin: 0; } body { margin: 0; } img { width: 8.5in; height: 3.5in; display: block; page-break-after: always; }</style></head><body><img src="' + frontData + '" />' + (backData ? '<img src="' + backData + '" />' : '') + '</body></html>');
        printDoc.close();
        iframe.onload = () => {
            window.setTimeout(() => {
                iframe.contentWindow?.focus();
                iframe.contentWindow?.print();
                window.setTimeout(() => { if (iframe.parentNode) document.body.removeChild(iframe); }, 1500);
            }, 300);
        };
    };

    const handleEmailToPayee = (recipient?: string) => {
        const to = typeof recipient === 'string' ? recipient : '';
        const amountRaw = parseMoney(checkData.amount);
        const amount = amountRaw > 0 ? `$${amountRaw.toFixed(2)}` : 'blank (amount to be written)';
        const subjectText = `Check #${checkData.checkNumber} - ${amount}`;
        const bodyText = `Hello ${checkData.payeeName || 'Payee'},\n\nA check has been prepared for you:\n\nCheck #: ${checkData.checkNumber}\nAmount: ${amount}\nDate: ${checkData.date}\nBank: ${checkData.bankName}\n\n(Attach the exported check PDF or image to this email before sending.)`;
        try { navigator.clipboard?.writeText(`Subject: ${subjectText}\n\n${bodyText}`); } catch (err) { /* clipboard unavailable */ }
        window.location.href = `mailto:${to}?subject=${encodeURIComponent(subjectText)}&body=${encodeURIComponent(bodyText)}`;
        window.setTimeout(() => alert('Your email app should now open with the check details pre-filled.\n\nIf nothing opened, the same draft was copied to your clipboard - paste it into any email.'), 400);
    };

    // Executes Send Check delivery requests from the app shell's
    // DeliveryModal. Only channels with a real action behind them are
    // executed here (PDF download, email draft). Provider-dependent
    // channels (postal mail, ACH, SMS, fax) are explained in the modal
    // itself and rerouted to the PDF - if one somehow arrives here, say
    // so plainly instead of pretending it was sent.
    const executeDelivery = (method: string, options: any) => {
        if (method === 'download') { handleDownloadPDF(); return; }
        if (method === 'email') { handleEmailToPayee(typeof options?.email === 'string' ? options.email : undefined); return; }
        alert('That delivery channel is not connected yet - nothing was sent or charged. Download the PDF instead to send the check today.');
    };
    const executeDeliveryRef = useRef(executeDelivery);
    executeDeliveryRef.current = executeDelivery;
    useEffect(() => {
        const run = () => {
            let raw: string | null = null;
            try { raw = sessionStorage.getItem('oc_pending_delivery'); } catch { /* ignore */ }
            if (!raw) return;
            try { sessionStorage.removeItem('oc_pending_delivery'); } catch { /* ignore */ }
            try {
                const req = JSON.parse(raw);
                if (!req || Date.now() - (req.at || 0) > 60000) return;
                executeDeliveryRef.current(req.method, req.options || {});
            } catch { /* ignore malformed request */ }
        };
        run();
        window.addEventListener('oc:deliver', run);
        return () => window.removeEventListener('oc:deliver', run);
    }, []);

    const handleAddText = () => {
        setCustomObjects(prev => [...prev, { id: `uctx-${Date.now()}`, kind: 'text', text: 'New Text', left: 110, top: 110 }]);
    };

    const handleAttachImage = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            // Kept in the picture library as well as placed, so the
            // same picture can be placed again without re-uploading.
            registerPictureUpload(file.name, String(reader.result));
            setCustomObjects(prev => [...prev, { id: `uimg-${Date.now()}`, kind: 'image', src: String(reader.result), left: 90, top: 90 }]);
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const handleGenerateMobileShot = async () => {
        if (guestExportGate()) return;
        if (!fabricCanvas) return;

        // Create a temporary high-res composite canvas
        const compositeW = 1200;
        const compositeH = 1600;
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = compositeW;
        tempCanvas.height = compositeH;
        const ctx = tempCanvas.getContext('2d')!;

        // 1. Background Generation
        if (mobileBackground === 'wood') {
            // Rich Mahogany Wood
            ctx.fillStyle = '#3e2723';
            ctx.fillRect(0, 0, compositeW, compositeH);

            // Wood grain texture
            for (let i = 0; i < 2000; i++) {
                ctx.fillStyle = `rgba(0,0,0,${Math.random() * 0.1})`;
                const x = Math.random() * compositeW;
                const y = Math.random() * compositeH;
                const w = 50 + Math.random() * 150;
                const h = 2 + Math.random() * 4;
                ctx.fillRect(x, y, w, h);
            }
        } else if (mobileBackground === 'granite') {
            // Granite / Marble
            ctx.fillStyle = '#2c3e50';
            ctx.fillRect(0, 0, compositeW, compositeH);

            // Speckles
            for (let i = 0; i < 5000; i++) {
                ctx.fillStyle = Math.random() > 0.5 ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.3)';
                ctx.fillRect(Math.random() * compositeW, Math.random() * compositeH, 2, 2);
            }
        } else if (mobileBackground === 'office') {
            // Clean Office Desk (White/Beige Laminate)
            ctx.fillStyle = '#f5f5f5';
            ctx.fillRect(0, 0, compositeW, compositeH);
            // Subtle noise
            for (let i = 0; i < 1000; i++) {
                ctx.fillStyle = `rgba(0,0,0,${Math.random() * 0.03})`;
                ctx.fillRect(Math.random() * compositeW, Math.random() * compositeH, 1, 1);
            }
        } else {
            // Default: Dark Studio Table
            ctx.fillStyle = '#111';
            ctx.fillRect(0, 0, compositeW, compositeH);

            // Add subtle table grain
            for (let i = 0; i < 500; i++) {
                ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.02})`;
                ctx.fillRect(Math.random() * compositeW, Math.random() * compositeH, 2, 2);
            }
        }

        // 2. Render Front of Check
        const frontImg = new Image();
        frontImg.src = captureCanvasWithoutGhosts(fabricCanvas, { format: 'png', multiplier: 1.5 });
        await new Promise(resolve => frontImg.onload = resolve);

        // Draw front with slight shadow
        ctx.shadowColor = 'rgba(0,0,0,0.5)';
        ctx.shadowBlur = 15;
        ctx.shadowOffsetY = 10;
        ctx.drawImage(frontImg, 50, 100, compositeW - 100, 400);

        // 3. Render Back of Check (using engine). Height matches the
        // on-screen back exactly (900 for the tall payroll design, the
        // standard height otherwise) so the exported back is the back
        // the user sees.
        const backExportHeight = (checkStyle === 'payroll_karen' || selectedBank === 'KAREN_ART_STUDIO_PAYROLL') ? 900 : checkDimensions.height;
        const backFabricCanvas = new fabric.StaticCanvas(null, {
            width: checkDimensions.width,
            height: backExportHeight,
            backgroundColor: '#ffffff' // Ensure white paper background
        });
        // This call previously passed the securityFeatures ARRAY as the
        // engine config, so the chosen deposit style never reached the
        // engine and every exported back silently used the mobile style.
        // It now passes the same design config as the on-screen back —
        // built by the same shared builder (buildBackEngineConfig) with
        // the same effective deposit style, so the exported back is the
        // per-design back the user sees, never a different one.
        const exportDesignName = bankTemplate?.name || premiumTemplate?.name || checkData.bankName || '';
        const exportDesignPrimary = bankTemplate?.specs?.colors?.primary || premiumTemplate?.colors?.primary || null;
        const exportDesignAccent = bankTemplate?.specs?.colors?.accent || premiumTemplate?.colors?.accent || null;
        const { config: exportBackConfig, depositConfig: exportBackDeposit } = buildBackEngineConfig({
            checkStyle,
            selectedBank,
            securityFeatures,
            designName: exportDesignName,
            designPrimary: exportDesignPrimary,
            designAccent: exportDesignAccent,
            effectiveStyle: effectiveBackStyle,
            accountNumber: checkData.accountNumber,
            accountHolderName: checkData.accountHolderName,
            routingNumber: checkData.routingNumber,
            checkNumber: checkData.checkNumber
        });
        const backObjects = CheckBackEngine.generateCheckBack(checkDimensions.width, backExportHeight, exportBackConfig, 72, exportBackDeposit, signatureImage);
        backObjects.forEach(obj => {
            const id = (obj as any).id;
            const savedPos = id ? backPositionFor(objectPositions, id, backExportHeight) : undefined;
            if (savedPos) {
                obj.set({
                    left: savedPos.left,
                    top: savedPos.top,
                    scaleX: savedPos.scaleX ?? 1,
                    scaleY: savedPos.scaleY ?? 1,
                    // The exported back obeys the same specimen
                    // direction as the studio back (see
                    // specimenAngleFor): a stale saved angle never
                    // turns a specimen text backwards in print.
                    angle: specimenAngleFor(id, savedPos.angle) ?? obj.angle
                });
            }
            backFabricCanvas.add(obj);
        });
        backFabricCanvas.renderAll();

        const backImg = new Image();
        backImg.src = backFabricCanvas.toDataURL({ format: 'png', multiplier: 1.5 });
        await new Promise(resolve => backImg.onload = resolve);

        // Draw back with same shadow
        ctx.drawImage(backImg, 50, 600, compositeW - 100, 400);
        ctx.shadowBlur = 0; // Reset shadow

        // 4. Timestamp - 07 03 AM EST (Per user requirement)
        ctx.font = 'bold 28px "Courier New", monospace';
        ctx.fillStyle = 'rgba(255, 140, 0, 0.9)'; // Industrial orange digital
        ctx.fillText('07 03 AM EST', compositeW - 220, compositeH - 60);

        // 5. Filename formatting: CheckXXXXMMDDYY
        const lastFour = (checkData as any).lastFour || '4567';
        // Date format MM/DD/YYYY -> MMDDYY
        const dateParts = checkData.date.split('/');
        const mm = dateParts[0].padStart(2, '0');
        const dd = dateParts[1].padStart(2, '0');
        const yy = dateParts[2].slice(-2);
        const filename = `Check${lastFour}${mm}${dd}${yy}.jpg`;

        // 6. Download with 300 DPI multiplier (3x of 72dpi = 216, 4x = 288)
        // Using JPEG with 0.9 quality to stay under 5MB
        const link = document.createElement('a');
        link.download = filename;
        link.href = tempCanvas.toDataURL('image/jpeg', 0.9);
        link.click();
    };

    // Toggle dropdown state
    const [showExportMenu, setShowExportMenu] = useState(false);
    // Generate result sheet (the obvious home of the PDF download)
    const [showGenerateSheet, setShowGenerateSheet] = useState(false);
    const [zoom, setZoom] = useState(0.85);
    const stepZoom = (dir: number) => {
        const stops = [0.5, 0.75, 0.85, 1, 1.25];
        const cur = stops.reduce((best, z, i) => Math.abs(z - zoom) < Math.abs(stops[best] - zoom) ? i : best, 0);
        setZoom(stops[Math.min(stops.length - 1, Math.max(0, cur + dir))]);
    };

    // Apply bank template
    const applyBankTemplate = (bankId: string) => {
        setSelectedBank(bankId);

        // Default Clean Style: a DESIGN-ONLY reset (Princewill's
        // ruling, 2026-11-04: "Keep my typed details when I tap
        // Default Clean Style" - superseding the earlier same-day
        // full-wipe behaviour of this control). The factory DESIGN
        // slice returns: style/template back to the clean personal
        // design, paper/texture/font, border, the security-feature
        // set, the medallion, the MICR toggles, the imported design
        // pattern cleared, and the back override released
        // (backStyleTouched false, so the clean design's matched
        // variant governs - a stored "my default back" preference
        // is untouched and re-applies on the next design switch, per
        // its existing rule). Everything the user put IN survives:
        // the full checkData, the placed logo with its options and
        // filename label, signature/handwriting, custom objects, and
        // the arranged positions + deletions on both sides. Applied
        // through the same apply() as every other state (directly,
        // not via the hook's restore), so the recorder treats the
        // reset as a normal edit: persisted as the working state and
        // Undo-able within the session. The pinned previous check is
        // NOT touched - this reset clears nothing, so there is
        // nothing the start options must stop offering. A true blank
        // start lives on its own control (startBlankCheck, below).
        if (!bankId) {
            applyDesignSnapshot(buildDesignOnlyResetSnapshot(buildCurrentSnapshot()));
            setTimeout(() => designHistory.persistWorkNow(), 0);
            return;
        }

        // Find template in either bank-specific or premium lists
        const bankTemplate = bankSpecificTemplates[bankId];
        const premiumTemplate = PREMIUM_TEMPLATES.find((p: any) => p.id === bankId);

        const template = bankTemplate || premiumTemplate;

        if (template) {
            // Update bank details from template
            setCheckData(prev => ({
                ...prev,
                bankName: template.name.toUpperCase(),
                bankAddress: (template as any).specs?.address || prev.bankAddress
            }));

            // Effective design (texture / font / paper) comes from the
            // TEMPLATE_DESIGN map — the single authoritative source.
            // The old code set a per-bank texture here and then a final
            // catch-all else overwrote it with guilloche_bg for nearly
            // every template, so 43 of 48 bank templates rendered with
            // one of two (texture, font, paper) combinations.
            const design = TEMPLATE_DESIGN[bankId];
            if (design) {
                setSelectedTexture(design.texture);
                setSelectedFont(design.font);
                setPaperColor(design.paper);
                setTextureOpacity(design.opacity ?? 1.0);
            } else {
                // Fallback for any future template id not yet in the map:
                // derive from its spec strings instead of collapsing.
                if (template.specs?.fontFamily?.includes('Courier')) setSelectedFont('courier');
                else if (template.specs?.fontFamily?.includes('Garamond')) setSelectedFont('garamond');
                else if (template.specs?.fontFamily?.includes('Georgia')) setSelectedFont('georgia');
                else if (template.specs?.fontFamily?.includes('Helvetica') || template.specs?.fontFamily?.includes('Arial')) setSelectedFont('arial');
                else if (template.specs?.fontFamily) setSelectedFont('helvetica');
                setSelectedTexture('linen');
                setPaperColor('white');
                setTextureOpacity(1.0);
            }

            // The template brings its own layout style (TEMPLATE_STYLE):
            // applying a template must yield that template's complete
            // face. This setter used to exist only for the treasury /
            // BofA-classic / Karen branches, so every other template
            // inherited the previously selected style's face furniture —
            // the Money Order heading survived onto bank templates, and
            // the BofA Classic MICR variant survived onto premium
            // designs selected after it.
            setCheckStyle(resolveTemplateStyle(bankId));

            // Per-design security identity (2026-11-04): the design's
            // own spec list, mapped to the features the renderer
            // actually draws, becomes the front security-feature
            // default set — so designs no longer all carry the same
            // security mix, and the back panel (which follows the
            // enabled set) differs with them. The structural MICR
            // clear-band guard is never touched; user toggles win
            // from here and persist through snapshots as usual.
            // Premium designs carry no spec list, so their set is
            // left untouched (null). This replaces the old bespoke
            // BOFA_CLASSIC enable-list, which only ever turned
            // features on and matched no template's actual specs.
            let secSet = applyTemplateSecuritySet(securityFeatures, bankId);
            // GreenFi carries its security statement as a printed
            // bottom line on the face (renderer furniture), NOT as
            // the top warning strip - the strip is forced off for
            // this design so the two never duplicate.
            if (secSet && bankId === 'GREENFI') {
                secSet = secSet.map(f => f.id === 'warning_band' ? { ...f, enabled: false } : f);
            }
            if (secSet) setSecurityFeatures(secSet);

            // Per-template border treatment (TEMPLATE_BORDER): a
            // design whose printed frame is its identity (GreenFi's
            // FSC microprint frame, USAA's bold double rules)
            // applies its frame with the template, like its texture.
            // A template WITHOUT its own frame takes its style
            // route's standard border instead (the same value
            // applyStylePreset applies) - the border is resolved on
            // EVERY template application, so the previous template's
            // frame can never survive the switch. (Live defect
            // 2026-11-04: GreenFi -> ANB kept the FSC microprint
            // frame printing on ANB's face, because the old code
            // only ever set a frame and never cleared one.) A
            // style-only TEMPLATE_BORDER entry (ANB) sets the style
            // and leaves the current border colour untouched, like
            // the preset branch does - an entry's colour applies
            // only when it carries one.
            const tBorder = TEMPLATE_BORDER[bankId];
            if (tBorder) {
                setBorderConfig(prev => ({ ...prev, style: tBorder.style as BorderConfig['style'], ...(tBorder.color ? { color: tBorder.color } : {}) }));
            } else {
                const preset = STYLE_PRESETS[resolveTemplateStyle(bankId)];
                if (preset) setBorderConfig(prev => ({ ...prev, style: preset.border as BorderConfig['style'] }));
            }

            // GreenFi's heat-reactive ink is the pale pink
            // padlock mark the renderer draws as furniture (exact
            // rebuild) — it is NOT the thermochromic seal, so the
            // seal colour is deliberately left untouched here:
            // tinting it would leak a red medallion colour into
            // the next design the user picks.

            // Template-type side effects (sample data, security
            // enables). Design parameters are handled above.
            if (bankId === 'us_treasury' || bankId === 'US_TREASURY_OFFICIAL') {
                setCheckData(prev => ({
                    ...prev,
                    bankName: "United States Treasury",
                    accountHolderName: "JSEAN BROWN",
                    accountHolderAddress: "7622 RIVER OAKS PL\nREYNOLDSBURG, OH 43068",
                    date: "09 27 22",
                    amount: 8952.36
                }));
            } else if (bankId === 'KAREN_ART_STUDIO_PAYROLL') {
                // Bank standard: the printed fractional routing must
                // derive from the routing number on the check. The old
                // preset hardcoded "67-76890", which can never match the
                // routing number in the form, so every Karen check
                // failed the fractional-consistency compliance check
                // for a design-caused reason. Derive it instead (and
                // re-enable auto-derivation for later routing edits);
                // when the studio's real routing number is entered,
                // its true fraction derives automatically.
                fractionalAutoRef.current = true;
                setCheckData(prev => ({
                    ...prev,
                    accountHolderName: "Karen Art Studio",
                    accountHolderAddress: "1134 Main Street\nLouisville, KY 40211\n(502) 123-1234",
                    bankName: "My Bank",
                    bankAddress: "123 Bank Road",
                    fractionalRouting: MICRComplianceEngine.deriveFractionalRouting(prev.routingNumber) || prev.fractionalRouting,
                    payeeName: "John Smith",
                    payeeAddress: "1123 River Road\nLouisville, KY 42011"
                }));
            }
        }
    };

    // Start a blank check (Princewill, 2026-11-04): the FULL clear
    // to the blank factory state - blank data, logo removed,
    // positions and deletions cleared (front and back), back
    // design-matched. This was Default Clean Style's job until the
    // same-day ruling made that control design-only; it lives here
    // as its own explicit action so both behaviours stay available.
    // The check being left is pinned FIRST (pinPreviousNow), so it
    // survives the clear as the previous check: on the next studio
    // load the app menu's Start section offers "Start with my
    // previous check" and restores it exactly. The clear flows
    // through the shared apply() (Undo-able within the session) and is persisted
    // immediately after the working state is cleared, so a refresh
    // continues the blank check - never the cleared one.
    const startBlankCheck = () => {
        designHistory.pinPreviousNow();
        // Invalidate any logo processing still in flight, so a
        // late-resolving upload cannot resurrect a logo the clear
        // just removed.
        logoProcessSeqRef.current++;
        applyDesignSnapshot(buildFactoryCheckSnapshot());
        setRawSignatureImage(null);
        designHistory.clearWork();
        setTimeout(() => designHistory.persistWorkNow(), 0);
    };

    // Apply a check-layout style WITH its design preset (border,
    // texture, font, paper). Before this, the style buttons set only
    // the style id and Personal / Business / Counter / Money Order
    // rendered the identical check. Explicit AI texture/font/border
    // choices in the same prompt are applied AFTER this call in
    // applyAiResult, so they still win over the preset.
    const applyStylePreset = (styleId: string) => {
        // A style that IS a template (STYLE_TEMPLATE_ROUTES —
        // payroll_karen plus the three specimen styles greenfi /
        // anb / usaa) goes through applyBankTemplate, so the style
        // button produces exactly the state the template picker
        // produces. Setting the style id alone never set selectedBank
        // or the design's own data (account holder, bank, payee), so
        // the button left the previous design on the Karen layout and
        // read as a dead control in live verification.
        const routedTemplate = STYLE_TEMPLATE_ROUTES[styleId];
        if (routedTemplate) {
            applyBankTemplate(routedTemplate);
            return;
        }
        setCheckStyle(styleId);
        const preset = STYLE_PRESETS[styleId];
        if (!preset) return; // unreachable while styleButtons.test.ts passes: every style id has a preset or a route
        setBorderConfig(prev => ({ ...prev, style: preset.border as BorderConfig['style'] }));
        setSelectedTexture(preset.texture);
        setSelectedFont(preset.font);
        setPaperColor(preset.paper);
    };

    // A real check back has many colors; an unpainted canvas is one flat color.
    const isBlankCapture = (dataUrl: string): Promise<boolean> => new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            try {
                const c = document.createElement('canvas');
                c.width = 64; c.height = 24;
                const ctx = c.getContext('2d');
                if (!ctx) { resolve(false); return; }
                ctx.drawImage(img, 0, 0, 64, 24);
                const d = ctx.getImageData(0, 0, 64, 24).data;
                const seen = new Set<string>();
                for (let i = 0; i < d.length; i += 16) {
                    seen.add(d[i] + ',' + d[i + 1] + ',' + d[i + 2]);
                    if (seen.size > 4) break;
                }
                resolve(seen.size <= 4);
            } catch { resolve(false); }
        };
        img.onerror = () => resolve(false);
        img.src = dataUrl;
    });

    // Capture the back at print resolution; if it comes back blank (canvas
    // never painted on this device), bring Page 2 into view, let it paint,
    // and capture again.
    const captureBackForExport = async (): Promise<string | null> => {
        let data = await checkBackRef.current?.captureForExport() ?? null;
        if (data && await isBlankCapture(data)) {
            document.getElementById('oc-page-back')?.scrollIntoView({ block: 'center' });
            await new Promise(r => setTimeout(r, 800));
            data = await checkBackRef.current?.captureForExport() ?? null;
            document.getElementById('oc-page-front')?.scrollIntoView({ block: 'start' });
        }
        return data;
    };

    const scrollToPage = (side: 'front' | 'back') => {
        if (window.innerWidth <= 920) {
            document.getElementById(side === 'front' ? 'oc-page-front' : 'oc-page-back')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
        }
        const container = document.querySelector('.canvas-wrapper-scroll');
        if (container) container.scrollTop = side === 'front' ? 0 : container.scrollHeight;
    };

    // The canvas-PDF builder. includeBack=false produces the
    // front-only PDF (one page, same capture and page standard);
    // the Generate result sheet offers both, while the export
    // menu, the toolbar and the Send Check delivery route use
    // front + back via handleDownloadPDF below.
    const downloadCheckPdf = async (includeBack: boolean) => {
        if (guestExportGate()) return;
        if (!fabricCanvas) return;

        // LICENSE GATE — free users cannot export
        if (!isPaid) {
            alert('\u26a0\ufe0f Upgrade to a paid plan to export clean checks.\n\nGo to Pricing to choose a plan.');
            return;
        }
        if (!canExport) {
            alert(`\u26a0\ufe0f You\'ve reached your ${currentPlan.name} plan limit (${currentPlan.checksPerMonth} exports/month).\n\nUpgrade to export more.`);
            return;
        }
        if (!runBankAcceptanceGate()) return;
        try {
            // 1. Capture Front (600 DPI at print size, lossless PNG)
            const frontData = captureCanvasWithoutGhosts(fabricCanvas, { format: 'png', multiplier: EXPORT_MULTIPLIER });
            if (!frontData) {
                alert('The front of the check could not be captured. Please try again.');
                return;
            }

            // 2. Capture Back (same print resolution, blank-capture retry)
            const backData = includeBack ? await captureBackForExport() : null;

            if (includeBack && (!backData || await isBlankCapture(backData))) {
                alert('The back of the check could not be captured. Please view Page 2 once, then export again.');
                return;
            }

            // Consume one export credit (only once the needed sides are captured)
            if (!useCheckExport()) {
                alert('Export failed. Please check your plan status.');
                return;
            }

            // 3. Generate PDF — a print SHEET, not a bare check page:
            // the check image keeps its exact standard size at the
            // top (8.5" x 3.5" bank standard; 6.00" x 2.75" for the
            // personal layout family: the personal style itself plus
            // the three specimen styles registered 2026-11-04 —
            // greenfi, anb, usaa — whose designs shipped at this page
            // size and must not silently change physical size), and
            // the sheet grows downward with the cut line + cutting /
            // deposit instructions (utils/checkPdfSheet). Both pages
            // use the same sheet height so a duplex print aligns.
            const isPersonal = checkStyle === 'personal' || checkStyle === 'greenfi' || checkStyle === 'anb' || checkStyle === 'usaa';
            const pdfW = isPersonal ? 6.00 : 8.50; // 8.5 inches
            const pdfH = isPersonal ? 2.75 : 3.50; // 3.5 inches
            const sheetH = checkSheetLayout(pdfW, pdfH).sheetH;

            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'in',
                format: [pdfW, sheetH]
            });

            // Page 1: Front sheet
            drawCheckSheetPage(doc, frontData, pdfW, pdfH, 'front');

            // Page 2: Back sheet (front + back output only)
            if (includeBack && backData) {
                doc.addPage([pdfW, sheetH], 'landscape');
                drawCheckSheetPage(doc, backData, pdfW, pdfH, 'back');
            }

            // 4. Save
            // Deliver as a real file download through the shared
            // helper - NOT jsPDF's built-in save call: FileSaver
            // clicks its anchor inside a setTimeout after all the
            // async capture work, the click loses the user's gesture,
            // and on iOS Safari the PDF opens as an inline preview
            // instead of saving (Princewill, 2026-11-04). Same PDF
            // bytes either way (the built-in save is output('blob')
            // + FileSaver).
            const pdfFilename = includeBack ? `Check_${checkData.checkNumber}_${Date.now()}.pdf` : `Check_${checkData.checkNumber}_front_${Date.now()}.pdf`;
            await saveBlobAsDownload(doc.output('blob'), pdfFilename);

        } catch (err) {
            console.error('PDF Generation Error:', err);
            alert('Failed to generate PDF. See console for details.');
        }
    };

    const handleDownloadPDF = async () => { await downloadCheckPdf(true); };

    const handleServerPdfExport = async () => {
        if (!isAuthenticated) {
            alert('⚠️ Please sign in to generate bank-grade vector PDFs with verified MICR lines and security elements.');
            openAuthModal('login');
            return;
        }

        setIsExportingServerPdf(true);
        try {
            const routingClean = String(checkData.routingNumber || '').replace(/\D/g, '');
            const accountClean = String(checkData.accountNumber || '').replace(/\D/g, '');

            if (routingClean.length !== 9) {
                alert(`⚠️ Routing number must be exactly 9 digits (currently ${routingClean.length}).`);
                setIsExportingServerPdf(false);
                return;
            }

            const routingCheck = MICRComplianceEngine.validateRoutingNumber(routingClean);
            if (!routingCheck.valid) {
                alert(`⚠️ ${routingCheck.error || 'Invalid routing number'} - banks reject checks whose routing number fails the ABA checksum.`);
                setIsExportingServerPdf(false);
                return;
            }

            if (accountClean.length < 4 || accountClean.length > 17) {
                alert(`⚠️ Account number must be between 4 and 17 digits (currently ${accountClean.length}).`);
                setIsExportingServerPdf(false);
                return;
            }

            // BLANK CHECK RULE (Princewill, 2026-11-03): a blank
            // amount is a valid blank check and must NEVER block the
            // export - parseMoney maps blank/invalid to 0, and 0 is
            // sent as-is; the server renders the amount box and the
            // legal line empty. (This gate used to hard-block every
            // blank check with "amount must be a positive number".)
            const cleanAmount = parseMoney(checkData.amount);

            const payloadData = {
                ...checkData,
                checkNumber: checkData.checkNumber || '1001',
                payeeName: checkData.payeeName || 'Valued Payee',
                amount: cleanAmount,
                // Words exist only when an amount does - a blank
                // check must not carry "Zero and 00/100" (mirrors the
                // canvas rule in applyAiResult / the amount onChange).
                amountWords: cleanAmount > 0 ? (checkData.writtenAmount || convertToWords(cleanAmount)) : '',
                // The placed signature (uploaded or drawn, already
                // ink-tinted) rides the payload so the server PDF can
                // embed it - it used to be dropped entirely and the
                // "bank-grade" PDF printed an unsigned check.
                signatureImage: signatureImage || undefined,
                routingNumber: routingClean,
                accountNumber: accountClean,
                bankName: checkData.bankName || 'Federal Reserve Member Bank',
                memo: checkData.memo || '',
                date: checkData.date
            };

            const result = await apiClient.downloadPdf(payloadData, 'premium-1', {
                layout: 'check-on-top',
                includeBackSide: true,
                securityFeatures: securityFeatures.filter(f => f.enabled).map(f => f.id)
            });

            if (!result.success || !result.blob) {
                if (result.status !== undefined && isAuthFailureStatus(result.status)) {
                    // Dead session: the plain signed-out prompt (the
                    // save chain's wording) + the sign-in modal -
                    // never the server's raw 'Invalid token' string.
                    // The design itself is untouched and already
                    // preserved on this device by the working-state
                    // autosave. (apiClient has also raised
                    // notifyAuthExpired, so the app reconciles.)
                    alert('⚠️ You\'re signed out — your session has expired. Your design is saved on this device. Sign in to generate the bank-grade PDF.');
                    openAuthModal('login');
                } else {
                    alert(`❌ Server PDF generation failed: ${result.error}`);
                }
            } else {
                // Save through the shared delivery helper: the old
                // hand-rolled anchor revoked the blob URL in the same
                // tick as the click, and on iOS Safari the PDF opened
                // as a preview instead of saving (2026-11-04).
                const outcome = await saveBlobAsDownload(result.blob, result.filename || `check_${payloadData.checkNumber}.pdf`);
                if (outcome === 'download') {
                    alert('✅ Bank-Grade Vector PDF successfully generated and downloaded!');
                } else if (outcome === 'cancelled') {
                    alert('The PDF was generated, but sharing was cancelled so nothing was saved. Export again and choose Save to Files to keep it.');
                }
                // 'shared': the share sheet itself was the confirmation.
            }
        } catch (err: any) {
            console.error('Server PDF Error:', err);
            alert(`Error: ${err.message || 'Failed to communicate with PDF engine.'}`);
        } finally {
            setIsExportingServerPdf(false);
        }
    };

    const handleSaveTemplate = async () => {
        if (!isAuthenticated) {
            alert('⚠️ Please sign in to save templates to your cloud account.');
            openAuthModal('login');
            return;
        }

        try {
            const templateData = {
                name: `${checkData.bankName || 'Custom'} Template ${Date.now().toString().slice(-4)}`,
                checkStyle,
                selectedFont,
                selectedTexture,
                paperColor,
                borderConfig,
                securityFeatures: securityFeatures.filter(f => f.enabled).map(f => f.id),
                checkData
            };

            const res = await apiClient.post('/api/save-template', { templateData });
            if (res.success) {
                alert(`✅ Template "${templateData.name}" saved to cloud!`);
            } else if (res.status !== undefined && isAuthFailureStatus(res.status)) {
                // Dead session: the plain signed-out prompt (the
                // save chain's wording) + the sign-in modal - never
                // the server's raw 'Invalid token' string. The
                // design itself is untouched and already preserved
                // on this device by the working-state autosave
                // (the snapshot carries every field this template
                // payload does). (apiClient has also raised
                // notifyAuthExpired, so the app reconciles.)
                alert('⚠️ You\'re signed out — your session has expired. Your design is saved on this device. Sign in to save the template to your cloud account.');
                openAuthModal('login');
            } else {
                alert(`❌ Failed to save template: ${res.error}`);
            }
        } catch (err: any) {
            alert(`❌ Error saving template: ${err.message}`);
        }
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'logo' | 'signature') => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (event) => {
            const dataUrl = event.target?.result as string;
            if (type === 'logo') {
                // Libraries (2026-11-04): an upload ADDS the logo to
                // the kept library. It places only when nothing is
                // placed yet - with a logo on the check, the upload
                // is saved and the placed logo is left untouched.
                addLogoUpload(file.name, dataUrl);
            }
            else {
                addSignatureUpload(file.name, dataUrl);
            }
        };
        reader.readAsDataURL(file);
    };

    const handlePaste = useCallback(async (event?: ClipboardEvent | React.MouseEvent) => {
        let dataUrl: string | null = null;

        // 1. Handle actual Paste Event (Ctrl+V)
        if (event && 'clipboardData' in event) {
            const items = (event as ClipboardEvent).clipboardData?.items;
            if (items) {
                for (let i = 0; i < items.length; i++) {
                    if (items[i].type.indexOf('image') !== -1) {
                        const blob = items[i].getAsFile();
                        if (blob) {
                            dataUrl = await new Promise((resolve) => {
                                const reader = new FileReader();
                                reader.onload = (e) => resolve(e.target?.result as string);
                                reader.readAsDataURL(blob);
                            });
                        }
                        break;
                    }
                }
            }
        }
        // 2. Handle Manual Toolbar Click (Navigator API)
        else {
            try {
                const items = await navigator.clipboard.read();
                for (const item of items) {
                    const imageTypes = item.types.filter(type => type.startsWith('image/'));
                    if (imageTypes.length > 0) {
                        const blob = await item.getType(imageTypes[0]);
                        dataUrl = await new Promise((resolve) => {
                            const reader = new FileReader();
                            reader.onload = (e) => resolve(e.target?.result as string);
                            reader.readAsDataURL(blob);
                        });
                        break;
                    }
                }
            } catch (err) {
                // Fallback for text-based data URLs in clipboard
                const text = await navigator.clipboard.readText();
                if (text.startsWith('data:image')) dataUrl = text;
            }
        }

        if (dataUrl) {
            setRawSignatureImage(dataUrl); // Store source for re-inking
            // Automatically clean and isolate signatures on paste,
            // in the signature's own ink (or its written colour).
            const cleaned = await SignatureExtractorEngine.processSignature(dataUrl, signatureInkProcessOptions());
            setSignatureImage(cleaned);
        }
    }, [signatureInkColor, signatureInkOriginal]);

    const toggleSection = (section: string) => {
        setActiveSection(activeSection === section ? null : section);
    };

    const openToolSection = (section: string) => {
        if (panelRetracted) togglePanel(false);
        setActiveSection(section);
        if (typeof window !== 'undefined' && window.innerWidth <= 920) {
            // Phone: open the tools as a slide-in sheet over the check
            // and scroll the SHEET's own scroll area to the section -
            // the page (and the check on it) never moves, and closing
            // the sheet puts the user straight back on the design.
            // Same panel, same sections, same state: only the phone
            // presentation changes (see .oc-builder-panel in index.css).
            setMobileSheetOpen(true);
            window.setTimeout(() => {
                const scroller = document.querySelector<HTMLElement>('.oc-tool-scroll');
                const el = document.getElementById(`oc-tool-${section}`);
                if (scroller && el) {
                    scroller.scrollTop = Math.max(0, el.offsetTop - scroller.offsetTop - 8);
                }
            }, 60);
            return;
        }
        window.setTimeout(() => {
            document.getElementById(`oc-tool-${section}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 60);
    };

    // Start choices (previous check / start blank / saved designs /
    // patterns) live in the app menu now — the hamburger drawer on
    // mobile, the sidebar on desktop (StudioStartMenu) — not stacked
    // over the preview (Princewill, 2026-11-04). The studio publishes
    // whether a previous check is on offer, and menu clicks run
    // through the same handlers the old start bar called; this is
    // the single implementation both entry points share. The ref
    // mirrors executeDeliveryRef so the listener never closes over
    // stale state.
    const hasPendingPrevious = Boolean(designHistory.pendingPrevious);
    useEffect(() => {
        publishStudioStartOffer(hasPendingPrevious);
    }, [hasPendingPrevious]);
    useEffect(() => () => publishStudioStartOffer(false), []);
    const startActionRef = useRef<(action: StudioStartAction) => void>(() => {});
    startActionRef.current = (action: StudioStartAction) => {
        if (action === 'previous') designHistory.acceptPrevious();
        else if (action === 'blank') designHistory.dismissPrevious();
        else if (action === 'designs') openMyDesigns();
        else if (action === 'patterns') openToolSection('bank_style');
    };
    useEffect(() => onStudioStartAction((action) => startActionRef.current(action)), []);

    // --- EFFECTS ---

    useEffect(() => {
        const onPaste = (e: any) => handlePaste(e);
        window.addEventListener('paste', onPaste as EventListener);
        return () => window.removeEventListener('paste', onPaste as EventListener);
    }, [handlePaste]);

    // Re-ink the placed signature when its ink changes: the raw
    // source is re-derived under the current Auto-Clean state in
    // the new ink (or back to its own written colour in
    // Keep-original mode), so a colour pick recolours the
    // signature already on the check immediately - with
    // Auto-Clean on OR off. (This used to ride on
    // handwritingColor - the typed fields' ink - which is why an
    // uploaded signature's colour could only be changed from
    // the Typography panel, in field ink.)
    useEffect(() => {
        const reink = async () => {
            if (rawSignatureImage) {
                const derived = await derivePlacedSignatureImage(rawSignatureImage, !!(checkData as any).autoCleanSignature);
                setSignatureImage(derived);
            }
        };
        reink();
    }, [signatureInkColor, signatureInkOriginal]);

    // Load preset signature for Stephanie Lacey
    useEffect(() => {
        if (signatureStyle === 'stephanie_lacey' || handwritingStyle === 'stephanie_lacey') {
            const signatureUrl = 'file:///C:/Users/User/.gemini/antigravity/brain/5de6d9a4-adde-4ebb-b22c-bd7ec6188b22/uploaded_media_1770398405736.png';
            setSignatureImage(signatureUrl);
        }
    }, [signatureStyle, handwritingStyle]);

    const dynamicCanvasHeight = (checkStyle === 'payroll_karen' || selectedBank === 'KAREN_ART_STUDIO_PAYROLL') ? 900 : checkDimensions.height;
    // Where the MICR clear band starts, as a % of the page height, for
    // the screen-only guide overlay: the band hugs the CHECK's bottom
    // edge, which on the tall Karen page is partway down the page.
    const micrGuideTopPct = (((checkStyle === 'payroll_karen' || selectedBank === 'KAREN_ART_STUDIO_PAYROLL') ? checkDimensions.height : dynamicCanvasHeight) - 65) / dynamicCanvasHeight * 100;

    // Mobile fit-to-width (<=920px): the canvas is a fixed-format surface
    // (check width + ruler). Never let it exceed the viewport - scale it
    // down and shrink its layout box to match, so the page itself never
    // scrolls sideways and no blank scaled space is reserved.
    const [mobileFit, setMobileFit] = useState<number | null>(null);
    useEffect(() => {
        const compute = () => {
            if (window.innerWidth > 920) { setMobileFit(null); return; }
            const contentW = checkDimensions.width + 20;
            // Reserve the app shell's 24px of side padding plus the canvas
            // wrapper's 12px. The old 12px allowance let the scaled check
            // overflow real phone screens by roughly one padding width.
            const avail = Math.max(240, window.innerWidth - 36);
            setMobileFit(Math.min(zoom, avail / contentW));
        };
        compute();
        window.addEventListener('resize', compute);
        return () => window.removeEventListener('resize', compute);
    }, [zoom, checkDimensions.width]);
    // While the studio is open on a phone, the app bottom nav steps aside
    // (CSS: body.oc-studio-open .oc-mobile-nav) so the studio dock is the
    // ONE bottom bar - the two fixed bars used to stack and bury the
    // check under ~180px of chrome. Leaving the studio restores the nav.
    useEffect(() => {
        document.body.classList.add('oc-studio-open');
        // While the studio is open the PAGE must never pinch-zoom:
        // page zoom is what blew the selection pill and the dock up
        // over the check on Princewill's phone in landscape. Zooming
        // happens on the canvas instead (pinch view, below). The
        // viewport lock restores the app's normal meta on exit.
        const restoreViewport = lockStudioViewport();
        return () => { document.body.classList.remove('oc-studio-open'); restoreViewport(); };
    }, []);

    const effectiveZoom = mobileFit ?? zoom;
    const scaledLayoutW = Math.ceil((checkDimensions.width + 20) * effectiveZoom);
    const scaledLayoutH = Math.ceil((dynamicCanvasHeight + 60) * 2 * effectiveZoom + 16);

    // --- Pinch zoom (phones): two fingers on either check canvas
    // zoom and pan the CANVAS view - a multiplier + pan composed
    // onto the .oc-canvas-scale transform on top of the fit zoom -
    // while the bars keep their size (page zoom is locked out
    // above). The math + touch wiring live in utils/pinchZoom; both
    // canvases attach to this ONE view, so a pinch on the front and
    // a pinch on the back drive the same camera. During a gesture
    // the wrapper style is mutated directly (no re-render per move)
    // and committed to state when the gesture ends; the effect
    // after this block re-applies the ref's transform after every
    // render, so a stray re-render mid-gesture can never strand a
    // stale transform on the wrapper.
    const [pinchView, setPinchView] = useState<PinchView>(IDENTITY_PINCH_VIEW);
    const pinchViewRef = useRef<PinchView>(IDENTITY_PINCH_VIEW);
    const scaleWrapRef = useRef<HTMLDivElement>(null);
    const effectiveZoomRef = useRef(effectiveZoom);
    const scaledDimsRef = useRef({ w: scaledLayoutW, h: scaledLayoutH });
    useEffect(() => {
        effectiveZoomRef.current = effectiveZoom;
        scaledDimsRef.current = { w: scaledLayoutW, h: scaledLayoutH };
    }, [effectiveZoom, scaledLayoutW, scaledLayoutH]);
    const applyPinchToDom = useCallback((v: PinchView) => {
        const el = scaleWrapRef.current;
        if (el) el.style.transform = pinchCssTransform(v, effectiveZoomRef.current);
    }, []);
    useEffect(() => { applyPinchToDom(pinchViewRef.current); });
    // A new check format starts from the fitted view, never from a
    // leftover pinch.
    useEffect(() => {
        pinchViewRef.current = IDENTITY_PINCH_VIEW;
        setPinchView(IDENTITY_PINCH_VIEW);
    }, [checkDimensions.width, dynamicCanvasHeight]);
    // Chrome dimming: while an object is being dragged or a pinch
    // is live, the dock + selection pill fade to a low-profile
    // state (CSS: .oc-builder-root.oc-interacting) and return in
    // full the moment the interaction ends.
    const [chromeDim, setChromeDim] = useState(false);
    // Per-source dim flags (utils/interactionFade): the old single
    // boolean could be orphaned by any gesture that ended without
    // fabric's modified/mouse:up - an interrupted touch drag, a
    // canvas disposed mid-gesture - and the dock stayed faint for
    // the rest of the session. Sources now clear independently,
    // cleanups clear their own flag, and a safety effect below
    // hard-resets on blur/hide and re-evaluates on the next touch.
    const interactFlagsRef = useRef(new InteractFlags());
    const syncChromeDim = () => setChromeDim(interactFlagsRef.current.any());
    const pinchOpts: PinchZoomOptions = {
        getView: () => pinchViewRef.current,
        getOrigin: () => {
            const el = scaleWrapRef.current;
            if (!el) return { x: 0, y: 0 };
            const r = el.getBoundingClientRect();
            // The rect is post-transform; with a top-left origin the
            // corner sits at layout + (tx, ty), so subtract the pan
            // back out to get the untransformed anchor.
            return { x: r.left - pinchViewRef.current.tx, y: r.top - pinchViewRef.current.ty };
        },
        getClampBox: () => {
            const v = pinchViewRef.current;
            const { w, h } = scaledDimsRef.current;
            return { boxW: w, boxH: h, visualW: w * v.k, visualH: h * v.k };
        },
        onStart: () => {
            interactFlagsRef.current.set('pinch', true);
            syncChromeDim();
            scaleWrapRef.current?.classList.add('oc-pinching');
        },
        onMove: (v) => {
            pinchViewRef.current = v;
            applyPinchToDom(v);
        },
        onEnd: (v) => {
            pinchViewRef.current = v;
            setPinchView(v);
            interactFlagsRef.current.set('pinch', false);
            syncChromeDim();
            scaleWrapRef.current?.classList.remove('oc-pinching');
        }
    };
    // Camera control handed to the touch-selection wiring on BOTH
    // canvases (empty-area pan while zoomed + double-tap toggle).
    // Closures read refs only, so the object captured at canvas
    // init stays correct for the canvas's whole life.
    const cameraOps = {
        getView: pinchOpts.getView,
        getOrigin: pinchOpts.getOrigin,
        getClampBox: pinchOpts.getClampBox,
        applyView: (v: PinchView) => { pinchViewRef.current = v; applyPinchToDom(v); },
        commitView: (v: PinchView) => { pinchViewRef.current = v; setPinchView(v); }
    };

    // Dim-safety net (utils/interactionFade): even with per-source
    // flags and cleanup clears, guarantee the fade can never outlive
    // its gesture. Blur / page-hide hard-reset it (no gesture can be
    // live in a hidden page), and every new touchstart / pointer end
    // re-evaluates the flags against physical reality - a source
    // counts as live only while its canvas holds an in-flight
    // transform (or, for the pinch, two touches are down). A leaked
    // flag therefore self-heals at the user's next touch at worst.
    useEffect(() => {
        const flags = interactFlagsRef.current;
        const transformOf = (c: any) => !!(c && c._currentTransform);
        const reevaluate = (touches: number | null) => {
            flags.reevaluate({ frontTransform: transformOf(frontCanvasRef.current), backTransform: transformOf(backFabricRef.current), touches });
            setChromeDim(flags.any());
        };
        const hardReset = () => { flags.reset(); setChromeDim(false); };
        const onVis = () => { if (document.hidden) hardReset(); };
        const onTouchStart = (e: TouchEvent) => reevaluate(e.touches ? e.touches.length : null);
        const onPointerEnd = () => reevaluate(null);
        window.addEventListener('blur', hardReset);
        document.addEventListener('visibilitychange', onVis);
        document.addEventListener('touchstart', onTouchStart, true);
        window.addEventListener('pointerup', onPointerEnd, true);
        window.addEventListener('pointercancel', onPointerEnd, true);
        return () => {
            window.removeEventListener('blur', hardReset);
            document.removeEventListener('visibilitychange', onVis);
            document.removeEventListener('touchstart', onTouchStart, true);
            window.removeEventListener('pointerup', onPointerEnd, true);
            window.removeEventListener('pointercancel', onPointerEnd, true);
        };
    }, []);

    useEffect(() => {
        if (!canvasRef.current) return;

        const fabricCanvas = new fabric.Canvas(canvasRef.current, {
            width: checkDimensions.width,
            height: dynamicCanvasHeight,
            backgroundColor: '#f8f4ec',
            selection: true,
            preserveObjectStacking: true
        });

        setFabricCanvas(fabricCanvas);
        frontCanvasRef.current = fabricCanvas;

        const updatePosition = (obj: any) => {
            // Handle Multi-Selection (ActiveSelection): every child
            // is recorded at its ABSOLUTE canvas position via the
            // canonical decompose (see absoluteSelectionChildPosition).
            // The previous composition (calcTransformMatrix values +
            // qrDecompose translate as left/top) wrote garbage
            // positions for group members on every group drag, and
            // the next rebuild scattered them (2026-11-04).
            if (obj && obj.type === 'activeSelection' && typeof obj.getObjects === 'function') {
                const updates: Record<string, { left: number; top: number; scaleX: number; scaleY: number; angle: number }> = {};
                obj.getObjects().forEach((child: any) => {
                    if (!child || !child.id) return;
                    const abs = absoluteSelectionChildPosition(child, obj);
                    if (abs) updates[child.id] = abs;
                });
                if (Object.keys(updates).length) {
                    // Copy-assign, never mutate in place — recorded
                    // history snapshots alias this same (front+back
                    // shared) positions map, so an in-place write
                    // corrupts them (2026-11-04 back-position
                    // Repro A).
                    objectPositionsRef.current = { ...objectPositionsRef.current, ...updates };
                    setObjectPositions(prev => ({ ...prev, ...updates }));
                }
                return;
            }

            // Handle Single Object
            if (obj && (obj as any).id) {
                const newPos = {
                    left: obj.left ?? 0,
                    top: obj.top ?? 0,
                    scaleX: obj.scaleX ?? 1,
                    scaleY: obj.scaleY ?? 1,
                    angle: obj.angle ?? 0
                };
                // Copy-assign, never mutate in place — recorded
                // history snapshots alias this same (front+back
                // shared) positions map, so an in-place write
                // corrupts them (2026-11-04 back-position Repro A).
                objectPositionsRef.current = { ...objectPositionsRef.current, [(obj as any).id]: newPos };
                setObjectPositions(prev => ({
                    ...prev,
                    [(obj as any).id]: newPos
                }));
            }
        };

        fabricCanvas.on('object:modified', (e) => updatePosition(e.target));
        fabricCanvas.on('text:editing:exited', (e: any) => {
            const t = e.target;
            if (t && t.id && String(t.id).startsWith('uctx-')) {
                setCustomObjects(prev => prev.map(o => o.id === t.id ? { ...o, text: t.text } : o));
            }
        });
        // Removed continuous listeners to prevent heavy re-renders while dragging

        // KEYBOARD CONTROLS — Delete, Backspace, Ctrl+A, Escape
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!fabricCanvas || (fabricCanvas as any).__isDisposed) return;
            const activeObj = fabricCanvas.getActiveObject();

            // Don't intercept keys when editing IText inline
            if (activeObj && (activeObj as any).isEditing) return;

            // Delete / Backspace — remove selected object(s)
            if (e.key === 'Delete' || e.key === 'Backspace') {
                if (activeObj) {
                    e.preventDefault();
                    // Pin the live arrangement first (see
                    // harvestLivePositions): the rebuild this
                    // deletion triggers must repaint the survivors
                    // exactly where they are.
                    harvestFrontPositions(fabricCanvas);
                    if ((activeObj as any).type === 'activeSelection') {
                        // Multiple selection
                        (activeObj as any).forEachObject((obj: any) => {
                            if (obj.id) setDeletedIds(prev => [...prev, obj.id]);
                            fabricCanvas.remove(obj);
                        });
                        fabricCanvas.discardActiveObject();
                    } else {
                        if ((activeObj as any).id) setDeletedIds(prev => [...prev, (activeObj as any).id]);
                        fabricCanvas.remove(activeObj);
                    }
                    fabricCanvas.requestRenderAll();
                }
            }

            // Ctrl+A — select all objects
            if (e.key === 'a' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                const allObjs = fabricCanvas.getObjects();
                const sel = new fabric.ActiveSelection(allObjs, { canvas: fabricCanvas });
                fabricCanvas.setActiveObject(sel);
                fabricCanvas.requestRenderAll();
            }

            // Escape — deselect
            if (e.key === 'Escape') {
                fabricCanvas.discardActiveObject();
                fabricCanvas.requestRenderAll();
            }

            // Arrow keys — nudge selected object
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) && activeObj) {
                e.preventDefault();
                const step = e.shiftKey ? 10 : 1;
                if (e.key === 'ArrowUp') activeObj.set('top', (activeObj.top ?? 0) - step);
                if (e.key === 'ArrowDown') activeObj.set('top', (activeObj.top ?? 0) + step);
                if (e.key === 'ArrowLeft') activeObj.set('left', (activeObj.left ?? 0) - step);
                if (e.key === 'ArrowRight') activeObj.set('left', (activeObj.left ?? 0) + step);
                activeObj.setCoords();
                updatePosition(activeObj);
                fabricCanvas.requestRenderAll();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        // Mobile selection: counts for the selection action bar +
        // tap-to-toggle multi-select (see attachTouchSelection).
        setFrontSelCount(0);
        const detachTouchSelection = attachTouchSelection(fabricCanvas, {
            isMulti: () => multiSelectRef.current,
            onSelectionChange: (n) => { setFrontSelCount(n); if (n > 0) setSelSide('front'); },
            onContextAction: (info) => setObjMenu({ side: 'front', objectId: (info.object as any)?.id ?? '', x: info.clientX, y: info.clientY }),
            camera: cameraOps
        });

        // Pinch zoom on this canvas (shared view with the back) +
        // drag start/end reports for chrome dimming.
        const detachPinch = attachPinchZoom((fabricCanvas as any).upperCanvasEl, pinchOpts);
        const interactOn = () => { interactFlagsRef.current.set('front', true); syncChromeDim(); };
        const interactOff = () => { interactFlagsRef.current.set('front', false); syncChromeDim(); };
        fabricCanvas.on('object:moving', interactOn);
        fabricCanvas.on('object:scaling', interactOn);
        fabricCanvas.on('object:rotating', interactOn);
        fabricCanvas.on('object:modified', interactOff);
        fabricCanvas.on('mouse:up', interactOff);

        return () => {
            // A canvas disposed mid-drag must not orphan the dim:
            // clear this source's flag before the listeners die.
            interactOff();
            detachTouchSelection();
            detachPinch();
            fabricCanvas.off('object:moving', interactOn);
            fabricCanvas.off('object:scaling', interactOn);
            fabricCanvas.off('object:rotating', interactOn);
            fabricCanvas.off('object:modified', interactOff);
            fabricCanvas.off('mouse:up', interactOff);
            document.removeEventListener('keydown', handleKeyDown);
            (fabricCanvas as any).__isDisposed = true;
            fabricCanvas.dispose();
            if (frontCanvasRef.current === fabricCanvas) frontCanvasRef.current = null;
            setFabricCanvas(null);
        };
    }, [dynamicCanvasHeight, checkDimensions.width]); // Re-init on dimension change

    // Global mouse + touch listeners for AI Bar dragging. Touch support is
    // essential on phones: the bar used to be mouse-draggable only.
    useEffect(() => {
        const moveTo = (clientX: number, clientY: number) => {
            setAiBarPosition({
                x: Math.min(Math.max(0, clientX - dragOffset.x), Math.max(0, window.innerWidth - 80)),
                y: Math.min(Math.max(0, clientY - dragOffset.y), Math.max(0, window.innerHeight - 64))
            });
        };
        const handleMouseMove = (e: MouseEvent) => { if (isDraggingAiBar) moveTo(e.clientX, e.clientY); };
        const handleTouchMove = (e: TouchEvent) => {
            if (!isDraggingAiBar || !e.touches[0]) return;
            moveTo(e.touches[0].clientX, e.touches[0].clientY);
        };
        const handleEnd = () => setIsDraggingAiBar(false);

        if (isDraggingAiBar) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleEnd);
            window.addEventListener('touchmove', handleTouchMove, { passive: true });
            window.addEventListener('touchend', handleEnd);
            window.addEventListener('touchcancel', handleEnd);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleEnd);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleEnd);
            window.removeEventListener('touchcancel', handleEnd);
        };
    }, [isDraggingAiBar, dragOffset]);

    useEffect(() => {
        if (fabricCanvas) renderCheck(fabricCanvas);
    }, [fabricCanvas, renderCheck]);
    // ============== RENDER ==============
    return (
        <div className={`oc-builder-root${chromeDim ? ' oc-interacting' : ''}`} style={containerStyle}>
            {/* Phone tools sheet backdrop (CSS hides it above 920px;
                the sheet itself is the same docked panel below). */}
            {mobileSheetOpen && (
                <div className="oc-sheet-backdrop" onClick={() => setMobileSheetOpen(false)} aria-hidden="true" />
            )}
            {/* LEFT SIDEBAR */}
            <div className={`oc-builder-panel${panelRetracted ? ' oc-panel-retracted' : ''}${mobileSheetOpen ? ' oc-sheet-open' : ''}`} style={panelRetracted ? { ...sidebarStyle, width: '48px', minWidth: '48px', overflow: 'hidden', transition: 'width 0.28s ease, min-width 0.28s ease' } : { ...sidebarStyle, transition: 'width 0.28s ease, min-width 0.28s ease' }}>
                {panelRetracted ? (
                    <div className="oc-panel-rail" onClick={() => togglePanel(false)} title="Open design panel">
                        <button className="oc-rail-btn" onClick={() => togglePanel(false)} aria-label="Open design panel">&#8250;</button>
                        <span className="oc-rail-label">Design Studio</span>
                    </div>
                ) : (<>
                <div style={headerStyle}>
                    <span style={{ fontSize: 20 }}>🏦</span>
                    <div>
                        <div style={{ fontWeight: '700', fontSize: 15, letterSpacing: '-0.3px', fontFamily: "'Space Grotesk', sans-serif" }}>OmniCheck Pro</div>
                        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.5px', fontWeight: '500' }}>Design Studio</div>
                    </div>
                    <button onClick={() => togglePanel(true)} title="Retract panel - give the check editor full width" aria-label="Retract design panel" style={{ marginLeft: 'auto', width: 28, height: 28, borderRadius: 9, border: '1px solid rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.14)', color: '#FFFFFF', fontSize: 18, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>&#8249;</button>
                </div>

                {/* Sheet header: only rendered as a bar on phones (CSS),
                    naming the section being edited, with a thumb-size
                    close control that returns the user to the check. */}
                <div className="oc-sheet-bar">
                    <span className="oc-sheet-title">{(activeSection && SECTION_TITLES[activeSection]) || 'Design Tools'}</span>
                    <button type="button" className="oc-sheet-close" onClick={() => setMobileSheetOpen(false)} aria-label="Close design tools">✕</button>
                </div>

                <div style={{ textAlign: 'right', marginBottom: 8, paddingRight: 6 }}>
                    <span style={{ color: '#FB923C', fontSize: 10, cursor: 'pointer', fontWeight: '600' }}>💎 Marketplace</span>
                    <Link to="/admin" style={{ textDecoration: 'none' }}>
                        <span
                            style={{ color: isEnterprise ? '#10B981' : '#94A3B8', fontSize: 10, marginLeft: 8, cursor: 'pointer', fontWeight: isEnterprise ? '700' : '400' }}
                            title="Go to Admin Portal"
                        >
                            {isEnterprise ? 'Admin Active ✅' : 'Admin'}
                        </span>
                    </Link>
                </div>

                <div className="oc-tool-scroll" style={{ maxHeight: 'calc(100vh - 180px)', overflowY: 'auto', paddingRight: 4, display: 'flex', flexDirection: 'column' }}>
                    {/* Intelligent workflow order: start with the check itself,
                        then design it, sign it, secure it, and finish/export. */}
                    <nav className="oc-tool-nav" aria-label="Studio workflow" style={{ order: 0 }}>
                        <button type="button" onClick={() => openToolSection('check_info')}>1 · Details</button>
                        <button type="button" onClick={() => openToolSection('bank_style')}>2 · Design</button>
                        <button type="button" onClick={() => openToolSection('custom_assets')}>3 · Sign</button>
                        <button type="button" onClick={() => openToolSection('security')}>4 · Security</button>
                        <button type="button" onClick={() => openToolSection('micr_config')}>5 · MICR</button>
                        <button type="button" onClick={() => setAiBarOpen(true)}>✨ Ask AI</button>
                    </nav>

                    {/* BANK TEMPLATES */}
                    <div id="oc-tool-bank_style" style={{ ...sectionStyle, order: 3 }}>
                        <div style={sectionHeaderStyle} onClick={() => toggleSection('bank_style')}>
                            <span>🏢 Bank Styles & Templates</span>
                            <span>{activeSection === 'bank_style' ? '▼' : '▶'}</span>
                        </div>
                        {activeSection === 'bank_style' && (
                            <div style={{ padding: '15px' }}>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Select Template</label>
                                    <select
                                        style={inputStyle}
                                        value={selectedBank}
                                        onChange={(e) => applyBankTemplate(e.target.value)}
                                    >
                                        <option value="">Default Clean Style</option>
                                        {BANK_TEMPLATES.map(b => (
                                            <option key={b.id} value={b.id}>{b.name}</option>
                                        ))}
                                    </select>
                                    <div style={{ fontSize: '10px', color: '#94A3B8', marginTop: '4px', lineHeight: 1.5 }}>
                                        Default Clean Style resets the design only — your typed details stay.
                                    </div>
                                </div>
                                <div style={inputGroupStyle}>
                                    <button
                                        type="button"
                                        data-start-blank-check
                                        onClick={startBlankCheck}
                                        style={{ ...dropdownItemStyle, justifyContent: 'center', border: '1px solid rgba(255,255,255,0.12)', color: '#E2E8F0', fontWeight: 600 }}
                                    >
                                        Start a blank check
                                    </button>
                                    <div style={{ fontSize: '10px', color: '#94A3B8', marginTop: '4px', lineHeight: 1.5 }}>
                                        Clears everything — details, logo, layout — to a blank check. This check stays saved as your previous check, so you can pick it back up any time from the start options.
                                    </div>
                                </div>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Check Layout Type</label>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                                        {CHECK_STYLES.map(s => (
                                            <button
                                                key={s.id}
                                                onClick={() => {
                                                    applyStylePreset(s.id);
                                                    // Removed setObjectPositions({}) to prevent scattering of custom layout
                                                }}
                                                style={{
                                                    ...dropdownItemStyle,
                                                    padding: '6px',
                                                    backgroundColor: checkStyle === s.id ? 'rgba(26,35,126,0.08)' : 'transparent',
                                                    border: checkStyle === s.id ? '1px solid #F97316' : '1px solid rgba(255,255,255,0.12)',
                                                    justifyContent: 'center',
                                                    flexDirection: 'column',
                                                    gap: '3px',
                                                    color: checkStyle === s.id ? '#FB923C' : '#94A3B8'
                                                }}
                                            >
                                                <span style={{ fontSize: '18px' }}>{s.icon}</span>
                                                <span style={{ fontSize: '10px', textAlign: 'center' }}>{s.name}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* TEXTURES & COLORS */}
                    <div id="oc-tool-paper_texture" style={{ ...sectionStyle, order: 4 }}>
                        <div style={sectionHeaderStyle} onClick={() => toggleSection('paper_texture')}>
                            <span>🎨 Background & Texture Design</span>
                            <span>{activeSection === 'paper_texture' ? '▼' : '▶'}</span>
                        </div>
                        {activeSection === 'paper_texture' && (
                            <div style={{ padding: '15px' }}>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Paper Color</label>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {PAPER_COLORS.map(c => (
                                            <div
                                                key={c.id}
                                                onClick={() => setPaperColor(c.id)}
                                                title={c.name}
                                                style={{
                                                    width: '24px',
                                                    height: '24px',
                                                    borderRadius: '50%',
                                                    backgroundColor: c.color,
                                                    border: paperColor === c.id ? '2px solid #F97316' : '1px solid rgba(255,255,255,0.12)',
                                                    cursor: 'pointer',
                                                    boxShadow: paperColor === c.id ? '0 0 5px rgba(52,152,219,0.5)' : 'none'
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Security Texture Pattern</label>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                                        {TEXTURES.map(t => (
                                            <button
                                                key={t.id}
                                                onClick={() => setSelectedTexture(t.id)}
                                                style={{
                                                    ...dropdownItemStyle,
                                                    padding: '6px',
                                                    backgroundColor: selectedTexture === t.id ? 'rgba(26,35,126,0.08)' : 'transparent',
                                                    border: selectedTexture === t.id ? '1px solid #F97316' : '1px solid rgba(255,255,255,0.12)',
                                                    gap: '6px',
                                                    color: selectedTexture === t.id ? '#FB923C' : '#94A3B8'
                                                }}
                                            >
                                                <span>{t.icon}</span>
                                                <span style={{ fontSize: '11px' }}>{t.name}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div style={{ ...inputGroupStyle, marginTop: '15px' }}>
                                    <label style={labelStyle}>Texture Intensity ({Math.round(textureOpacity * 100)}%)</label>
                                    <input
                                        type="range" min="0" max="1" step="0.01"
                                        value={textureOpacity}
                                        onChange={(e) => setTextureOpacity(parseFloat(e.target.value))}
                                        style={{ width: '100%', accentColor: '#F97316' }}
                                    />
                                    {/* AI UI Moved to Floating Bar */}

                                </div>
                            </div>
                        )}
                    </div>

                    {/* CUSTOM ASSETS */}
                    <div id="oc-tool-custom_assets" style={{ ...sectionStyle, order: 5 }}>
                        <div style={sectionHeaderStyle} onClick={() => toggleSection('custom_assets')}>
                            <span>🖼️ Custom Assets (Logos & Signs)</span>
                            <span>{activeSection === 'custom_assets' ? '▼' : '▶'}</span>
                        </div>
                        {activeSection === 'custom_assets' && (
                            <div style={{ padding: '15px' }}>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Bank Logo (Top Left)</label>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        {/* The native file input is hidden: its own caption
                                            cannot be kept in sync with app state (it kept a
                                            removed logo's name until reload, and denied an
                                            Undo-restored logo existed). Our label below is
                                            the single source of truth. */}
                                        <input
                                            ref={logoFileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => { handleImageUpload(e, 'logo'); e.target.value = ''; }}
                                            style={{ display: 'none' }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => logoFileInputRef.current?.click()}
                                            style={{ padding: '5px 10px', fontSize: '10px', fontWeight: '600', backgroundColor: 'rgba(255,255,255,0.05)', color: '#E2E8F0', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', cursor: 'pointer' }}
                                        >
                                            {logoLibrary.length > 0 || logoImage ? 'Add logo image' : 'Choose logo image'}
                                        </button>
                                        {logoImage && (
                                            <button
                                                onClick={deactivateLogo}
                                                style={{ padding: '5px 10px', fontSize: '10px', fontWeight: '600', backgroundColor: 'rgba(248,113,113,0.08)', color: '#FCA5A5', border: '1px solid rgba(248,113,113,0.5)', borderRadius: '6px', cursor: 'pointer' }}
                                            >
                                                Remove from check
                                            </button>
                                        )}
                                    </div>
                                    <div style={{ fontSize: '10px', color: '#94A3B8', marginTop: '4px' }}>
                                        {(activeLogoId || visiblePlacedLogos.length > 0)
                                            ? `${(activeLogoId ? 1 : 0) + visiblePlacedLogos.length} logo${((activeLogoId ? 1 : 0) + visiblePlacedLogos.length) > 1 ? 's' : ''} on this check${activeLogoId ? ` — ${logoFileName ?? 'Logo'} in the header` : ''}`
                                            : (logoLibrary.length > 0 ? 'No logo on this check — tap one below to place it' : 'No logo placed')}
                                    </div>
                                    <p style={{ fontSize: '9px', color: '#94A3B8', marginTop: '4px', marginBottom: 0 }}>New uploads are saved to your library — the logos on your check stay as they are. Tap a logo below to add it to the check; you can place several.</p>
                                    {logoLibraryNote && (
                                        <p style={{ fontSize: '9px', color: '#FBBF24', marginTop: '4px', marginBottom: 0 }}>{logoLibraryNote}</p>
                                    )}
                                    {logoLibrary.length > 0 && (
                                        <div style={{ marginTop: '10px' }}>
                                            <span style={segGroupLabelStyle}>Your logos — tap one to add it to the check</span>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(64px, 1fr))', gap: '6px', marginTop: '4px' }}>
                                                {logoLibrary.map(entry => {
                                                    const placedCount = (entry.id === activeLogoId ? 1 : 0) + visiblePlacedLogos.filter(p => p.entryId === entry.id).length;
                                                    const isActive = placedCount > 0;
                                                    const highlight = highlightedLogo && highlightedLogo.id === entry.id ? highlightedLogo : null;
                                                    return (
                                                        <div key={entry.id} style={{ position: 'relative', border: isActive ? '1px solid #F97316' : highlight ? '2px solid #38BDF8' : '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', padding: '4px', backgroundColor: '#F8F7F2' }}>
                                                            <button type="button" title={entry.name} aria-label={`Place logo ${entry.name}`} onClick={() => { setHighlightedLogo(null); handleLogoTileTap(entry); }} style={{ display: 'block', width: '100%', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                                                                <img src={entry.src} alt={entry.name} style={{ width: '100%', height: '36px', objectFit: 'contain', display: 'block' }} />
                                                                <span style={{ display: 'block', fontSize: '8px', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: '2px' }}>{entry.name}</span>
                                                                {isActive
                                                                    ? <span style={{ display: 'block', fontSize: '8px', fontWeight: '700', color: '#EA580C' }}>{placedCount > 1 ? `ON CHECK ×${placedCount}` : 'ON CHECK'}</span>
                                                                    : highlight
                                                                        ? <span style={{ display: 'block', fontSize: '8px', fontWeight: '700', color: '#0284C7' }}>{highlight.tag}</span>
                                                                        : <span style={{ display: 'block', fontSize: '8px', fontWeight: '600', color: '#64748B' }}>SAVED - TAP TO ADD</span>}
                                                            </button>
                                                            <button type="button" title="Delete from library" aria-label={`Delete logo ${entry.name}`} onClick={() => deleteLogoEntry(entry.id)} style={{ position: 'absolute', top: '2px', right: '2px', width: '16px', height: '16px', lineHeight: '14px', textAlign: 'center', fontSize: '10px', color: '#FCA5A5', backgroundColor: 'rgba(2,6,23,0.75)', border: '1px solid rgba(248,113,113,0.5)', borderRadius: '50%', cursor: 'pointer', padding: 0 }}>×</button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <p style={{ fontSize: '9px', color: '#94A3B8', marginTop: '4px', marginBottom: 0 }}>Uploads are kept, never replaced — up to {ASSET_LIBRARY_CAPS.logos} logos, stored in this browser. You can place several logos on one check: tap a logo to add it, and each placed logo is its own object — drag, resize or delete it right on the check. Deleting a logo here also removes its placed copies from this check.</p>
                                        </div>
                                    )}
                                    {visiblePlacedLogos.length > 0 && (
                                        <div style={{ marginTop: '10px' }}>
                                            <span style={segGroupLabelStyle}>Also placed on this check</span>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                                                {visiblePlacedLogos.map(inst => (
                                                    <div key={inst.instanceId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', padding: '4px 6px' }}>
                                                        <span style={{ fontSize: '10px', color: '#E2E8F0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{inst.name}</span>
                                                        <button type="button" aria-label={`Remove this copy of ${inst.name}`} onClick={() => removePlacedLogo(inst.instanceId)} style={{ padding: '3px 8px', fontSize: '10px', fontWeight: '600', backgroundColor: 'rgba(248,113,113,0.08)', color: '#FCA5A5', border: '1px solid rgba(248,113,113,0.5)', borderRadius: '6px', cursor: 'pointer', flexShrink: 0 }}>Remove</button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {logoImage && (
                                        <div style={{ marginTop: '10px' }}>
                                            <span style={segGroupLabelStyle}>Preview — as placed on the check</span>
                                            <div style={{
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                minHeight: '52px', padding: '8px', borderRadius: '8px',
                                                border: '1px solid rgba(255,255,255,0.10)',
                                                backgroundColor: '#F8F7F2',
                                                backgroundImage: 'repeating-conic-gradient(rgba(15,23,42,0.06) 0% 25%, transparent 0% 50%)',
                                                backgroundSize: '12px 12px'
                                            }}>
                                                <img src={logoImage} alt="Processed logo preview" style={{ maxWidth: '100%', maxHeight: '64px', objectFit: 'contain' }} />
                                            </div>
                                        </div>
                                    )}
                                    <div style={{ marginTop: '10px' }}>
                                        <span style={segGroupLabelStyle}>Background</span>
                                        <div style={segRowStyle} role="group" aria-label="Logo background">
                                            <button
                                                type="button"
                                                aria-pressed={logoRemoveBackground}
                                                style={segBtnStyle(logoRemoveBackground)}
                                                onClick={() => {
                                                    setLogoRemoveBackground(true);
                                                    if (rawLogoImage) applyLogoProcessing(rawLogoImage, { removeBackground: true, includeWriteups: logoIncludeWriteups, cropPercent: logoCropPercent });
                                                }}
                                            >
                                                Remove
                                            </button>
                                            <button
                                                type="button"
                                                aria-pressed={!logoRemoveBackground}
                                                style={segBtnStyle(!logoRemoveBackground)}
                                                onClick={() => {
                                                    setLogoRemoveBackground(false);
                                                    if (rawLogoImage) applyLogoProcessing(rawLogoImage, { removeBackground: false, includeWriteups: logoIncludeWriteups, cropPercent: logoCropPercent });
                                                }}
                                            >
                                                Keep
                                            </button>
                                        </div>
                                        <p style={{ fontSize: '9px', color: '#94A3B8', marginTop: '4px', marginBottom: 0 }}>Remove filters the paper out so the logo blends onto the check; Keep places the upload as-is.</p>
                                    </div>
                                    <div style={{ marginTop: '10px' }}>
                                        <span style={segGroupLabelStyle}>Content</span>
                                        <div style={segRowStyle} role="group" aria-label="Logo content">
                                            <button
                                                type="button"
                                                aria-pressed={logoIncludeWriteups}
                                                style={segBtnStyle(logoIncludeWriteups)}
                                                onClick={() => {
                                                    setLogoIncludeWriteups(true);
                                                    if (rawLogoImage) applyLogoProcessing(rawLogoImage, { removeBackground: logoRemoveBackground, includeWriteups: true, cropPercent: logoCropPercent });
                                                }}
                                            >
                                                Full block
                                            </button>
                                            <button
                                                type="button"
                                                aria-pressed={!logoIncludeWriteups}
                                                style={segBtnStyle(!logoIncludeWriteups)}
                                                onClick={() => {
                                                    setLogoIncludeWriteups(false);
                                                    if (rawLogoImage) applyLogoProcessing(rawLogoImage, { removeBackground: logoRemoveBackground, includeWriteups: false, cropPercent: logoCropPercent });
                                                }}
                                            >
                                                Logo only
                                            </button>
                                        </div>
                                        <p style={{ fontSize: '9px', color: '#94A3B8', marginTop: '4px', marginBottom: 0 }}>Full block keeps the write-ups beside the mark (bank name, address, phone); Logo only places just the mark.</p>
                                    </div>
                                    {!logoIncludeWriteups && rawLogoImage && (
                                        <div style={{ marginTop: '10px' }}>
                                            <span style={segGroupLabelStyle}>
                                                Logo-only crop — {logoCropPercent ?? logoDetectedSplit ?? 40}%{logoCropPercent === null ? ' · auto-detected split' : ' · manual'}
                                            </span>
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                <input
                                                    type="range"
                                                    min="10"
                                                    max="90"
                                                    value={logoCropPercent ?? logoDetectedSplit ?? 40}
                                                    onChange={(e) => {
                                                        const v = parseInt(e.target.value, 10);
                                                        setLogoCropPercent(v);
                                                        applyLogoProcessing(rawLogoImage, { removeBackground: logoRemoveBackground, includeWriteups: false, cropPercent: v });
                                                    }}
                                                    style={{ flex: 1 }}
                                                    aria-label="Logo-only crop percentage"
                                                />
                                                <span style={{ minWidth: '36px', textAlign: 'center', fontSize: '11px', fontWeight: '700', color: '#E2E8F0', backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', padding: '3px 0' }}>
                                                    {logoCropPercent ?? logoDetectedSplit ?? 40}%
                                                </span>
                                                {logoCropPercent !== null && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setLogoCropPercent(null);
                                                            applyLogoProcessing(rawLogoImage, { removeBackground: logoRemoveBackground, includeWriteups: false, cropPercent: null });
                                                        }}
                                                        style={{ padding: '4px 8px', fontSize: '10px', fontWeight: '600', backgroundColor: 'rgba(255,255,255,0.03)', color: '#CBD5E1', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', cursor: 'pointer' }}
                                                    >
                                                        Auto
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Hand-written Signature</label>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        {/* Hidden input + our own label, same as the
                                            logo: the native caption cannot show
                                            which kept signature is in use. */}
                                        <input
                                            ref={signatureFileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => { handleImageUpload(e, 'signature'); e.target.value = ''; }}
                                            style={{ display: 'none' }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => signatureFileInputRef.current?.click()}
                                            style={{ padding: '5px 10px', fontSize: '10px', fontWeight: '600', backgroundColor: 'rgba(255,255,255,0.05)', color: '#E2E8F0', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', cursor: 'pointer' }}
                                        >
                                            {signatureLibrary.length > 0 || signatureImage ? 'Add signature image' : 'Choose signature image'}
                                        </button>
                                        {signatureImage && (
                                            <button
                                                onClick={deactivateSignature}
                                                style={{ padding: '5px 10px', fontSize: '10px', fontWeight: '600', backgroundColor: 'rgba(248,113,113,0.08)', color: '#FCA5A5', border: '1px solid rgba(248,113,113,0.5)', borderRadius: '6px', cursor: 'pointer' }}
                                            >
                                                Remove from check
                                            </button>
                                        )}
                                    </div>
                                    <div style={{ fontSize: '10px', color: '#94A3B8', marginTop: '4px' }}>
                                        {signatureImage ? `${signatureLibrary.find(en => en.id === activeSignatureId)?.name ?? 'Signature'} — on this check` : (signatureLibrary.length > 0 ? 'No signature on this check — tap one below to use it' : 'No signature image')}
                                    </div>
                                    <p style={{ fontSize: '9px', color: '#94A3B8', marginTop: '4px', marginBottom: 0 }}>New uploads are saved to your library — the signature on your check stays until you tap another one.</p>
                                    {signatureLibraryNote && (
                                        <p style={{ fontSize: '9px', color: '#FBBF24', marginTop: '4px', marginBottom: 0 }}>{signatureLibraryNote}</p>
                                    )}
                                    {signatureLibrary.length > 0 && (
                                        <div style={{ marginTop: '10px' }}>
                                            <span style={segGroupLabelStyle}>Your signatures — tap one to use it</span>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(64px, 1fr))', gap: '6px', marginTop: '4px' }}>
                                                {signatureLibrary.map(entry => {
                                                    const isActive = entry.id === activeSignatureId;
                                                    const highlight = highlightedSignature && highlightedSignature.id === entry.id ? highlightedSignature : null;
                                                    return (
                                                        <div key={entry.id} style={{ position: 'relative', border: isActive ? '1px solid #F97316' : highlight ? '2px solid #38BDF8' : '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', padding: '4px', backgroundColor: '#F8F7F2' }}>
                                                            <button type="button" title={entry.name} aria-label={`Use signature ${entry.name}`} onClick={() => { setHighlightedSignature(null); activateSignatureEntry(entry); }} style={{ display: 'block', width: '100%', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                                                                <img src={entry.src} alt={entry.name} style={{ width: '100%', height: '36px', objectFit: 'contain', display: 'block' }} />
                                                                <span style={{ display: 'block', fontSize: '8px', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: '2px' }}>{entry.name}</span>
                                                                {isActive
                                                                    ? <span style={{ display: 'block', fontSize: '8px', fontWeight: '700', color: '#EA580C' }}>ON CHECK</span>
                                                                    : highlight
                                                                        ? <span style={{ display: 'block', fontSize: '8px', fontWeight: '700', color: '#0284C7' }}>{highlight.tag}</span>
                                                                        : <span style={{ display: 'block', fontSize: '8px', fontWeight: '600', color: '#64748B' }}>SAVED - TAP TO USE</span>}
                                                            </button>
                                                            <button type="button" title="Delete from library" aria-label={`Delete signature ${entry.name}`} onClick={() => deleteSignatureEntry(entry.id)} style={{ position: 'absolute', top: '2px', right: '2px', width: '16px', height: '16px', lineHeight: '14px', textAlign: 'center', fontSize: '10px', color: '#FCA5A5', backgroundColor: 'rgba(2,6,23,0.75)', border: '1px solid rgba(248,113,113,0.5)', borderRadius: '50%', cursor: 'pointer', padding: 0 }}>×</button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <p style={{ fontSize: '9px', color: '#94A3B8', marginTop: '4px', marginBottom: 0 }}>Uploads are kept, never replaced — up to {ASSET_LIBRARY_CAPS.signatures} signatures, stored in this browser.</p>
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                                        <input
                                            type="checkbox"
                                            checked={(checkData as any).autoCleanSignature || false}
                                            onChange={(e) => { const on = e.target.checked; setCheckData({ ...checkData, autoCleanSignature: on } as any); rederivePlacedSignature(on); }}
                                        />
                                        <span style={{ fontSize: '11px', color: '#94A3B8' }}>✨ Auto-Clean & Isolate Signature (Removes BG/Lines)</span>
                                    </div>
                                    <p style={{ fontSize: '9px', color: '#94A3B8', marginTop: '4px' }}>Replaces simulated font signature. Draggable.</p>
                                    <div style={{ marginTop: '10px' }}>
                                        <span style={segGroupLabelStyle}>Signature ink</span>
                                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '5px', flexWrap: 'wrap' }}>
                                            {SIGNATURE_INK_PRESETS.map(ink => {
                                                const selected = !signatureInkOriginal && signatureInkColor.toLowerCase() === ink.val;
                                                return (
                                                    <button
                                                        key={ink.val}
                                                        type="button"
                                                        title={ink.name}
                                                        aria-label={`Signature ink ${ink.name}`}
                                                        aria-pressed={selected}
                                                        onClick={() => { setSignatureInkOriginal(false); setSignatureInkColor(ink.val); }}
                                                        style={{ width: '22px', height: '22px', borderRadius: '50%', backgroundColor: ink.val, cursor: 'pointer', padding: 0, border: selected ? '2px solid #F97316' : '1px solid rgba(255,255,255,0.25)', boxShadow: selected ? '0 0 5px rgba(249,115,22,0.6)' : 'none' }}
                                                    />
                                                );
                                            })}
                                            <input
                                                type="color"
                                                aria-label="Custom signature ink colour"
                                                title="Custom colour - match the exact ink"
                                                value={normalizeSignatureInk(signatureInkColor)}
                                                onChange={(e) => { setSignatureInkOriginal(false); setSignatureInkColor(e.target.value); }}
                                                style={{ width: '30px', height: '22px', padding: 0, border: '1px solid rgba(255,255,255,0.25)', borderRadius: '6px', background: 'none', cursor: 'pointer' }}
                                            />
                                            <span style={{ fontSize: '10px', color: '#CBD5E1', fontFamily: 'monospace' }}>{signatureInkOriginal ? 'original' : signatureInkColor.toLowerCase()}</span>
                                        </div>
                                        <button
                                            type="button"
                                            aria-label="Keep the signature's own colour"
                                            aria-pressed={signatureInkOriginal}
                                            onClick={() => setSignatureInkOriginal(true)}
                                            style={{ marginTop: '6px', padding: '4px 8px', fontSize: '10px', fontWeight: '600', borderRadius: '6px', cursor: 'pointer', backgroundColor: signatureInkOriginal ? 'rgba(249,115,22,0.15)' : 'rgba(255,255,255,0.03)', color: signatureInkOriginal ? '#FDBA74' : '#CBD5E1', border: signatureInkOriginal ? '1px solid #F97316' : '1px solid rgba(255,255,255,0.12)' }}
                                        >
                                            🎨 Keep original colour
                                        </button>
                                        <p style={{ fontSize: '9px', color: '#94A3B8', marginTop: '4px', marginBottom: 0 }}>
                                            {signatureInkOriginal
                                                ? 'Keeps the exact colour the signature was written in. With Auto-Clean on, the cleaned strokes keep their written colour; with it off, the upload is placed exactly as it arrived.'
                                                : 'Recolours the signature on the check to this ink right away, whether Auto-Clean is on or off - use the picker for an exact match. The strokes are lifted off their background so only the ink prints; Auto-Clean adds ruled-line and stray-mark removal on top.'}
                                        </p>
                                    </div>
                                </div>

                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Pictures & Stamps</label>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <input
                                            ref={pictureFileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handleAttachImage}
                                            style={{ display: 'none' }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => pictureFileInputRef.current?.click()}
                                            style={{ padding: '5px 10px', fontSize: '10px', fontWeight: '600', backgroundColor: 'rgba(255,255,255,0.05)', color: '#E2E8F0', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', cursor: 'pointer' }}
                                        >
                                            Add picture
                                        </button>
                                    </div>
                                    <p style={{ fontSize: '9px', color: '#94A3B8', marginTop: '4px', marginBottom: 0 }}>Adds the picture to your library and places it on the check. Tap a kept picture to place another copy — no need to upload it again.</p>
                                    {pictureLibraryNote && (
                                        <p style={{ fontSize: '9px', color: '#FBBF24', marginTop: '4px', marginBottom: 0 }}>{pictureLibraryNote}</p>
                                    )}
                                    {pictureLibrary.length > 0 && (
                                        <div style={{ marginTop: '10px' }}>
                                            <span style={segGroupLabelStyle}>Your pictures — tap one to place it</span>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(64px, 1fr))', gap: '6px', marginTop: '4px' }}>
                                                {pictureLibrary.map(entry => (
                                                    <div key={entry.id} style={{ position: 'relative', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', padding: '4px', backgroundColor: '#F8F7F2' }}>
                                                        <button type="button" title={entry.name} aria-label={`Place picture ${entry.name}`} onClick={() => placePictureEntry(entry)} style={{ display: 'block', width: '100%', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                                                            <img src={entry.src} alt={entry.name} style={{ width: '100%', height: '36px', objectFit: 'contain', display: 'block' }} />
                                                            <span style={{ display: 'block', fontSize: '8px', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: '2px' }}>{entry.name}</span>
                                                        </button>
                                                        <button type="button" title="Delete from library" aria-label={`Delete picture ${entry.name}`} onClick={() => deletePictureEntry(entry.id)} style={{ position: 'absolute', top: '2px', right: '2px', width: '16px', height: '16px', lineHeight: '14px', textAlign: 'center', fontSize: '10px', color: '#FCA5A5', backgroundColor: 'rgba(2,6,23,0.75)', border: '1px solid rgba(248,113,113,0.5)', borderRadius: '50%', cursor: 'pointer', padding: 0 }}>×</button>
                                                    </div>
                                                ))}
                                            </div>
                                            <p style={{ fontSize: '9px', color: '#94A3B8', marginTop: '4px', marginBottom: 0 }}>Up to {ASSET_LIBRARY_CAPS.pictures} pictures, stored in this browser. Deleting a picture here never removes copies already placed on a check.</p>
                                        </div>
                                    )}
                                </div>

                                {/* UPLOAD CHECK DESIGN — pattern-only import. Only the
                                    background design crosses over (a framed pattern
                                    swatch + the design's colours), and only after the
                                    frame passes the writing gate: a frame containing
                                    write-ups, numbers or a signature is refused, so
                                    none of those are ever tiled onto the check. */}
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Check Design Pattern (from a photo)</label>
                                    <p style={{ fontSize: '9px', color: '#94A3B8', marginTop: '2px' }}>
                                        Upload a photo of a check whose design you like. Only the background design — the pattern, texture and colours — is imported, as your check's paper design. Before anything is applied, the framed area is measured: if it contains writing, numbers or a signature, the import is refused and nothing is taken from it — move the frame onto a clean pattern area. Your own details print on top.
                                    </p>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleDesignPhotoFile(f); e.target.value = ''; }}
                                            style={{ fontSize: '11px', flex: 1 }}
                                        />
                                        {(designPattern || rawDesignPhoto) && (
                                            <button
                                                type="button"
                                                onClick={removeDesignPattern}
                                                style={{ padding: '5px 10px', fontSize: '10px', fontWeight: '600', backgroundColor: 'rgba(248,113,113,0.08)', color: '#FCA5A5', border: '1px solid rgba(248,113,113,0.5)', borderRadius: '6px', cursor: 'pointer' }}
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                    {rawDesignPhoto && (
                                        <div style={{ marginTop: '10px' }}>
                                            <span style={segGroupLabelStyle}>Frame a clean pattern area — no writing, numbers or signature inside it</span>
                                            <div style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.12)' }}>
                                                <img src={rawDesignPhoto} alt="Uploaded check design" style={{ display: 'block', width: '100%' }} />
                                                <div style={{
                                                    position: 'absolute',
                                                    left: `${designCrop.left}%`, top: `${designCrop.top}%`,
                                                    right: `${designCrop.right}%`, bottom: `${designCrop.bottom}%`,
                                                    border: '2px solid #F97316', borderRadius: '4px',
                                                    boxShadow: '0 0 0 999px rgba(2,6,23,0.45)', pointerEvents: 'none'
                                                }} />
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', marginTop: '8px' }}>
                                                {(['left', 'top', 'right', 'bottom'] as const).map(edge => (
                                                    <label key={edge} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', color: '#94A3B8', textTransform: 'capitalize' }}>
                                                        <span style={{ minWidth: '38px' }}>{edge}</span>
                                                        <input
                                                            type="range" min="0" max="88" value={designCrop[edge]}
                                                            onChange={(e) => updateDesignCrop(edge, parseInt(e.target.value, 10) || 0)}
                                                            style={{ flex: 1 }} aria-label={`Crop ${edge} inset`}
                                                        />
                                                        <span style={{ minWidth: '26px', textAlign: 'right' }}>{designCrop[edge]}%</span>
                                                    </label>
                                                ))}
                                            </div>
                                            {designFrameBlocked && (
                                                <p style={{ fontSize: '10px', color: '#FCA5A5', marginTop: '8px', marginBottom: 0 }}>
                                                    This frame contains writing — applying it will be refused. Move or shrink the frame onto a clean pattern area: no words, numbers or signature inside it.
                                                </p>
                                            )}
                                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '10px', cursor: 'pointer' }}>
                                                <input type="checkbox" checked={designAttested} onChange={(e) => setDesignAttested(e.target.checked)} style={{ marginTop: '2px' }} />
                                                <span style={{ fontSize: '10px', color: '#CBD5E1' }}>This is my own check, or I have the right to use this design.</span>
                                            </label>
                                            <button
                                                type="button"
                                                disabled={!designAttested}
                                                onClick={applyDesignPattern}
                                                style={{
                                                    marginTop: '8px', width: '100%', padding: '7px 10px', fontSize: '11px', fontWeight: '700',
                                                    backgroundColor: designAttested ? 'rgba(249,115,22,0.15)' : 'rgba(255,255,255,0.04)',
                                                    color: designAttested ? '#FB923C' : '#64748B',
                                                    border: `1px solid ${designAttested ? 'rgba(249,115,22,0.6)' : 'rgba(255,255,255,0.12)'}`,
                                                    borderRadius: '6px', cursor: designAttested ? 'pointer' : 'not-allowed'
                                                }}
                                            >
                                                Use this pattern as my check design
                                            </button>
                                        </div>
                                    )}
                                    {designPattern && !rawDesignPhoto && (
                                        <div style={{ fontSize: '10px', color: '#94A3B8', marginTop: '6px' }}>A design pattern from your photo is applied to this check.</div>
                                    )}
                                    {designPattern && typeof paperColor === 'string' && paperColor.startsWith('#') && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', color: '#94A3B8', marginTop: '6px' }}>
                                            <span style={{ width: '14px', height: '14px', borderRadius: '4px', backgroundColor: paperColor, border: '1px solid rgba(255,255,255,0.35)', display: 'inline-block' }} />
                                            <span>Paper colour from your photo: {paperColor}</span>
                                        </div>
                                    )}
                                    {designPatternNote && (
                                        <div style={{ fontSize: '10px', color: designPatternNoteWarn ? '#FCA5A5' : '#A7F3D0', marginTop: '6px' }}>{designPatternNote}</div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* TYPOGRAPHY */}
                    <div id="oc-tool-typography" style={{ ...sectionStyle, order: 6 }}>
                        <div style={sectionHeaderStyle} onClick={() => toggleSection('typography')}>
                            <span>🖋️ Handwriting & Typography</span>
                            <span>{activeSection === 'typography' ? '▼' : '▶'}</span>
                        </div>
                        {activeSection === 'typography' && (
                            <div style={{ padding: '15px' }}>

                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Signature Style (Front)</label>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginTop: '6px' }}>
                                        {HANDWRITING_STYLES.map(h => (
                                            <button
                                                key={h.id}
                                                onClick={() => setSignatureStyle(h.id)}
                                                style={{
                                                    ...dropdownItemStyle,
                                                    padding: '8px',
                                                    backgroundColor: signatureStyle === h.id ? 'rgba(249,115,22,0.10)' : 'transparent',
                                                    border: signatureStyle === h.id ? '1px solid #3b82f6' : '1px solid #e5e5e5',
                                                    gap: '8px'
                                                }}
                                            >
                                                <span>{h.icon}</span>
                                                <span style={{ fontSize: '11px' }}>{h.name}</span>
                                            </button>
                                        ))}
                                    </div>
                                    <p style={{ fontSize: '9px', color: '#94A3B8', marginTop: '4px' }}>Simulates signer's unique hand.</p>
                                </div>

                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Primary Font Face</label>
                                    <select
                                        style={inputStyle}
                                        value={selectedFont}
                                        onChange={(e) => setSelectedFont(e.target.value)}
                                    >
                                        {FONTS.map((f: any) => (
                                            <option key={f.id} value={f.id}>{f.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Handwriting Style (Signatures)</label>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                                        {HANDWRITING_STYLES.map(h => (
                                            <button
                                                key={h.id}
                                                onClick={() => setHandwritingStyle(h.id)}
                                                style={{
                                                    ...dropdownItemStyle,
                                                    padding: '8px',
                                                    backgroundColor: handwritingStyle === h.id ? 'rgba(249,115,22,0.10)' : 'transparent',
                                                    border: handwritingStyle === h.id ? '1px solid #F97316' : '1px solid transparent',
                                                    gap: '8px'
                                                }}
                                            >
                                                <span>{h.icon}</span>
                                                <span style={{ fontSize: '11px' }}>{h.name}</span>
                                            </button>
                                        ))}
                                    </div>
                                    <button
                                        onClick={() => setIsSignatureLabOpen(true)}
                                        style={{
                                            ...actionBtnStyle,
                                            marginTop: '12px',
                                            fontSize: '12px',
                                            padding: '8px',
                                            backgroundColor: '#2ecc71',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        ✍️ Design Custom Signature
                                    </button>
                                </div>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Ink Color</label>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        {[
                                            { name: 'Navy', val: '#003366' },
                                            { name: 'Black', val: '#111111' },
                                            { name: 'Blue', val: '#0000FF' },
                                            { name: 'Red', val: '#8B0000' },
                                            { name: 'Purple', val: '#4B0082' }
                                        ].map(ink => (
                                            <div
                                                key={ink.name}
                                                onClick={() => setHandwritingColor(ink.val)}
                                                style={{
                                                    width: '24px', height: '24px', borderRadius: '50%',
                                                    backgroundColor: ink.val,
                                                    border: handwritingColor === ink.val ? '2px solid #F97316' : '1px solid rgba(255,255,255,0.12)',
                                                    cursor: 'pointer',
                                                    boxShadow: handwritingColor === ink.val ? '0 0 5px rgba(52,152,219,0.5)' : 'none'
                                                }}
                                                title={ink.name}
                                            />
                                        ))}
                                    </div>
                                </div>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Payee Name Size ({checkData.payeeFontSize}px)</label>
                                    <input
                                        type="range" min="12" max="32" step="1"
                                        value={checkData.payeeFontSize}
                                        onChange={(e) => handleInputChange('payeeFontSize', parseInt(e.target.value))}
                                        style={{ width: '100%', accentColor: '#F97316' }}
                                    />
                                </div>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Memo Font Size ({checkData.memoFontSize}px)</label>
                                    <input
                                        type="range" min="8" max="24" step="1"
                                        value={checkData.memoFontSize}
                                        onChange={(e) => handleInputChange('memoFontSize', parseInt(e.target.value))}
                                        style={{ width: '100%', accentColor: '#F97316' }}
                                    />
                                </div>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Handwriting Angle ({handwritingRotation}°)</label>
                                    <input
                                        type="range" min="-5" max="5" step="0.1"
                                        value={handwritingRotation}
                                        onChange={(e) => setHandwritingRotation(parseFloat(e.target.value))}
                                        style={{ width: '100%', accentColor: '#F97316' }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <div id="oc-tool-check_info" style={{ ...sectionStyle, order: 1 }}>
                        <div style={sectionHeaderStyle} onClick={() => toggleSection('check_info')}>
                            <span>📝 Check Information & Bank Details</span>
                            <span>{activeSection === 'check_info' ? '▼' : '▶'}</span>
                        </div>
                        {activeSection === 'check_info' && (
                            <div style={{ padding: '15px' }}>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Bank Name</label>
                                    <input
                                        id="oc-field-bankName"
                                        style={inputStyle}
                                        value={checkData.bankName}
                                        onChange={(e) => setCheckData({ ...checkData, bankName: e.target.value })}
                                    />
                                </div>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Bank Address</label>
                                    <textarea
                                        id="oc-field-bankAddress"
                                        rows={3}
                                        placeholder={'Street address\nCity, State ZIP\nPhone'}
                                        style={{ ...inputStyle, minHeight: '64px', resize: 'vertical' }}
                                        value={(checkData as any).bankAddress}
                                        onChange={(e) => setCheckData({ ...checkData, bankAddress: e.target.value } as any)}
                                    />
                                    <div style={{ fontSize: '10px', color: '#94A3B8', marginTop: '2px' }}>Press Enter for the next line — the lines print stacked on the check, like a bank header block.</div>
                                </div>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Watermark Text</label>
                                    <input
                                        id="oc-field-watermarkText"
                                        style={inputStyle}
                                        value={checkData.watermarkText}
                                        onChange={(e) => handleInputChange('watermarkText', e.target.value)}
                                    />
                                </div>
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={labelStyle}>Check #</label>
                                        <input
                                            id="oc-field-checkNumber"
                                            style={inputStyle}
                                            value={checkData.checkNumber}
                                            onChange={(e) => setCheckData({ ...checkData, checkNumber: e.target.value })}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={labelStyle}>Date</label>
                                        <input
                                            id="oc-field-date"
                                            style={inputStyle}
                                            value={checkData.date}
                                            onChange={(e) => setCheckData({ ...checkData, date: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <div style={{ flex: 2 }}>
                                        <label style={labelStyle}>Routing Number {isValidRouting(checkData.routingNumber) ? '✅' : '⚠️'}</label>
                                        <input
                                            id="oc-field-routingNumber"
                                            style={{ ...inputStyle, borderColor: isValidRouting(checkData.routingNumber) ? '#ddd' : '#e74c3c' }}
                                            value={checkData.routingNumber}
                                            onChange={(e) => {
                                                const routingNumber = e.target.value;
                                                setCheckData(prev => ({
                                                    ...prev,
                                                    routingNumber,
                                                    fractionalRouting: fractionalAutoRef.current
                                                        ? (MICRComplianceEngine.deriveFractionalRouting(routingNumber) || prev.fractionalRouting)
                                                        : prev.fractionalRouting
                                                }));
                                            }}
                                        />
                                        {!isValidRouting(checkData.routingNumber) && (
                                            <div style={{ fontSize: '8px', color: '#e74c3c', marginTop: '2px' }}>Invalid Mod-10 Check Digit</div>
                                        )}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={labelStyle}>EPC</label>
                                        <input
                                            style={inputStyle}
                                            maxLength={1}
                                            placeholder="0-9"
                                            value={(checkData as any).epcDigit || ''}
                                            onChange={(e) => handleInputChange('epcDigit', e.target.value)}
                                        />
                                    </div>
                                    <div style={{ flex: 1.5 }}>
                                        <label style={labelStyle}>Fractional Routing</label>
                                        <input
                                            id="oc-field-fractionalRouting"
                                            style={inputStyle}
                                            placeholder="Auto from routing (e.g. 21/1220)"
                                            value={checkData.fractionalRouting || ''}
                                            onChange={(e) => { fractionalAutoRef.current = false; setCheckData({ ...checkData, fractionalRouting: e.target.value }); }}
                                        />
                                        {!fractionalAutoRef.current && (
                                            <button type="button"
                                                onClick={() => { fractionalAutoRef.current = true; setCheckData(prev => ({ ...prev, fractionalRouting: MICRComplianceEngine.deriveFractionalRouting(prev.routingNumber) || prev.fractionalRouting })); }}
                                                style={{ marginTop: 4, fontSize: 10, background: 'none', border: 'none', color: '#7dd3fc', cursor: 'pointer', padding: 0 }}>
                                                Re-derive from routing number
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Account #</label>
                                    <input
                                        id="oc-field-accountNumber"
                                        style={inputStyle}
                                        value={checkData.accountNumber}
                                        onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                                    />
                                </div>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Last 4 of Card (for Filename)</label>
                                    <input
                                        style={inputStyle}
                                        maxLength={4}
                                        placeholder="4567"
                                        value={(checkData as any).lastFour || ''}
                                        onChange={(e) => handleInputChange('lastFour', e.target.value.replace(/\D/g, ''))}
                                    />
                                </div>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Pay To (Name)</label>
                                    <input
                                        id="oc-field-payeeName"
                                        type="text"
                                        value={checkData.payeeName}
                                        onChange={e => handleInputChange('payeeName', e.target.value)}
                                        placeholder="Enter payee name"
                                        style={inputStyle}
                                    />
                                </div>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Payee Address</label>
                                    <textarea
                                        id="oc-field-payeeAddress"
                                        value={checkData.payeeAddress || ''}
                                        onChange={e => handleInputChange('payeeAddress', e.target.value)}
                                        placeholder="Line 1&#10;Line 2"
                                        style={{ ...inputStyle, minHeight: '60px', fontFamily: 'monospace' }}
                                    />
                                </div>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Amount ($)</label>
                                    <input
                                        id="oc-field-amount"
                                        type="text"
                                        inputMode="decimal"
                                        autoComplete="off"
                                        value={amountDraft !== null ? amountDraft : (checkData.amount ? roundMoney(checkData.amount).toFixed(2) : '')}
                                        placeholder="Blank until an amount is added"
                                        onFocus={() => setAmountDraft(checkData.amount ? String(roundMoney(checkData.amount)) : '')}
                                        onChange={e => { setAmountDraft(e.target.value); handleInputChange('amount', e.target.value.trim() === '' ? 0 : parseMoney(e.target.value)); }}
                                        onBlur={() => setAmountDraft(null)}
                                        style={inputStyle}
                                    />
                                </div>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Check Back Deposit Style</label>
                                    <div style={{ fontSize: '10px', color: '#94A3B8', marginTop: '2px' }}>
                                        Back design: {backVariant.variantName} ({backVariant.variant})
                                        {backStyleTouched ? ' — deposit style set by you' : ' — matched to this check design'}
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginTop: '5px' }}>
                                        {BACK_SIDE_STYLES.map(style => (
                                            <button
                                                key={style.id}
                                                onClick={() => { setBackSideStyle(style.id); setBackStyleTouched(true); }}
                                                style={{
                                                    ...dropdownItemStyle,
                                                    padding: '8px',
                                                    fontSize: '11px',
                                                    backgroundColor: effectiveBackStyle === style.id ? 'rgba(249,115,22,0.10)' : 'rgba(255,255,255,0.03)',
                                                    border: effectiveBackStyle === style.id ? '1px solid #F97316' : '1px solid rgba(255,255,255,0.12)',
                                                    borderRadius: '6px',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    fontWeight: effectiveBackStyle === style.id ? 'bold' : 'normal'
                                                }}
                                            >
                                                <span style={{ fontSize: '16px' }}>{style.icon}</span>
                                                <span>{style.name}</span>
                                            </button>
                                        ))}
                                    </div>
                                    <div style={{ marginTop: '12px', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '10px' }}>
                                        <span style={segGroupLabelStyle}>My Default Back</span>
                                        {backDefault ? (
                                            <>
                                                <div style={{ fontSize: '10px', lineHeight: 1.5, color: '#A7F3D0' }}>
                                                    ✓ Default back active — new checks and design switches open with your saved arrangement (deposit style + positions).
                                                </div>
                                                <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
                                                    <button
                                                        type="button"
                                                        onClick={saveBackAsDefault}
                                                        style={{ padding: '7px 10px', fontSize: '11px', fontWeight: '700', backgroundColor: 'rgba(249,115,22,0.12)', color: '#FDBA74', border: '1px solid #F97316', borderRadius: '8px', cursor: 'pointer' }}
                                                    >
                                                        Update my default
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={clearBackDefault}
                                                        style={{ padding: '7px 10px', fontSize: '11px', fontWeight: '600', backgroundColor: 'rgba(255,255,255,0.03)', color: '#FCA5A5', border: '1px solid rgba(248,113,113,0.5)', borderRadius: '8px', cursor: 'pointer' }}
                                                    >
                                                        Use design-matched backs
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div style={{ fontSize: '10px', lineHeight: 1.5, color: '#94A3B8' }}>
                                                    Arrange the back once, save it, and every new check opens with it — no re-arranging.
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={saveBackAsDefault}
                                                    style={{ width: '100%', marginTop: '8px', padding: '8px 10px', fontSize: '11px', fontWeight: '700', backgroundColor: 'rgba(249,115,22,0.12)', color: '#FDBA74', border: '1px solid #F97316', borderRadius: '8px', cursor: 'pointer' }}
                                                >
                                                    Save as my default back
                                                </button>
                                            </>
                                        )}
                                        {backNote && (
                                            <div style={{ fontSize: '10px', lineHeight: 1.5, color: '#94A3B8', marginTop: '6px' }}>{backNote}</div>
                                        )}
                                    </div>
                                </div>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Memo</label>
                                    <input
                                        id="oc-field-memo"
                                        type="text"
                                        value={checkData.memo}
                                        onChange={e => handleInputChange('memo', e.target.value)}
                                        placeholder="Optional memo"
                                        style={inputStyle}
                                    />
                                </div>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Account Holder Name</label>
                                    <input
                                        id="oc-field-accountHolderName"
                                        type="text"
                                        value={checkData.accountHolderName}
                                        onChange={e => handleInputChange('accountHolderName', e.target.value)}
                                        style={inputStyle}
                                    />
                                    <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input type="checkbox" checked={showMicrGuide} onChange={(e) => setShowMicrGuide(e.target.checked)} />
                                        <span style={{ fontSize: '11px', color: '#94A3B8' }}>Show 5/8" MICR Clear Zone Guide</span>
                                    </div>
                                </div>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>Account Holder Address</label>
                                    <textarea
                                        id="oc-field-accountHolderAddress"
                                        rows={2}
                                        placeholder={'Street address\nCity, State ZIP'}
                                        style={{ ...inputStyle, minHeight: '52px', resize: 'vertical' }}
                                        value={checkData.accountHolderAddress}
                                        onChange={e => handleInputChange('accountHolderAddress', e.target.value)}
                                    />
                                    <div style={{ fontSize: '10px', color: '#94A3B8', marginTop: '2px' }}>Press Enter for the next line — the lines print stacked under the holder name.</div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* PAYROLL DETAILS (Contextual) */}
                    {checkStyle === 'payroll' && (
                        <div id="oc-tool-payroll_info" style={{ ...sectionStyle, order: 2 }}>
                            <div style={sectionHeaderStyle} onClick={() => toggleSection('payroll_info')}>
                                <span>💼 Payroll & Corporate Details</span>
                                <span>{activeSection === 'payroll_info' ? '▼' : '▶'}</span>
                            </div>
                            {activeSection === 'payroll_info' && (
                                <div style={{ padding: '15px' }}>
                                    <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Company EIN</label>
                                        <input
                                            id="oc-field-ein"
                                            style={inputStyle}
                                            value={checkData.ein}
                                            onChange={(e) => setCheckData({ ...checkData, ein: e.target.value })}
                                            placeholder="12-3456789"
                                        />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                        <div style={inputGroupStyle}>
                                            <label style={labelStyle}>Hours Worked</label>
                                            <input
                                                style={inputStyle}
                                                value={checkData.hoursWorked}
                                                onChange={(e) => setCheckData({ ...checkData, hoursWorked: e.target.value })}
                                                placeholder="40.0"
                                            />
                                        </div>
                                        <div style={inputGroupStyle}>
                                            <label style={labelStyle}>Hourly Rate</label>
                                            <input
                                                style={inputStyle}
                                                value={checkData.payRate}
                                                onChange={(e) => setCheckData({ ...checkData, payRate: e.target.value })}
                                                placeholder="$25.00"
                                            />
                                        </div>
                                    </div>
                                    <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Total Payout (Gross)</label>
                                        <input
                                            style={inputStyle}
                                            value={checkData.payTotal}
                                            onChange={(e) => setCheckData({ ...checkData, payTotal: e.target.value })}
                                            placeholder="1,000.00"
                                        />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                        <div style={inputGroupStyle}>
                                            <label style={labelStyle}>YTD Gross</label>
                                            <input
                                                style={inputStyle}
                                                value={checkData.ytdGross}
                                                onChange={(e) => setCheckData({ ...checkData, ytdGross: e.target.value })}
                                                placeholder="10,000.00"
                                            />
                                        </div>
                                        <div style={inputGroupStyle}>
                                            <label style={labelStyle}>YTD Net</label>
                                            <input
                                                style={inputStyle}
                                                value={checkData.ytdNet}
                                                onChange={(e) => setCheckData({ ...checkData, ytdNet: e.target.value })}
                                                placeholder="8,500.00"
                                            />
                                        </div>
                                    </div>
                                    <div style={inputGroupStyle}>
                                        <label style={labelStyle}>Earnings Type</label>
                                        <select
                                            style={inputStyle}
                                            value={checkData.memo}
                                            onChange={(e) => setCheckData({ ...checkData, memo: e.target.value })}
                                        >
                                            <option value="REGULAR">REGULAR</option>
                                            <option value="OVERTIME">OVERTIME</option>
                                            <option value="BONUS">BONUS</option>
                                        </select>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* MICR ENGINEERING */}
                    <div id="oc-tool-micr_config" style={{ ...sectionStyle, order: 8 }}>
                        <div style={sectionHeaderStyle} onClick={() => toggleSection('micr_config')}>
                            <span>🔢 MICR Line Engineering</span>
                            <span>{activeSection === 'micr_config' ? '▼' : '▶'}</span>
                        </div>
                        {activeSection === 'micr_config' && (
                            <div style={{ padding: '15px' }}>
                                <div style={inputGroupStyle}>
                                    <label style={labelStyle}>MICR Font Standard</label>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', gap: '6px' }}>
                                        {MICR_FONTS.map(f => (
                                            <button
                                                key={f.id}
                                                onClick={() => setCheckData(prev => ({ ...prev, micrFont: f.id }))}
                                                style={{
                                                    ...dropdownItemStyle,
                                                    padding: '10px',
                                                    backgroundColor: (checkData as any).micrFont === f.id ? 'rgba(249,115,22,0.10)' : 'transparent',
                                                    border: (checkData as any).micrFont === f.id ? '1px solid #F97316' : '1px solid rgba(255,255,255,0.12)',
                                                    justifyContent: 'flex-start',
                                                    fontWeight: (checkData as any).micrFont === f.id ? 'bold' : 'normal'
                                                }}
                                            >
                                                <span style={{ fontSize: '12px' }}>{f.name}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div style={{ ...inputGroupStyle, marginTop: '15px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <label style={labelStyle}>MICR Font Size</label>
                                        <span style={{ fontSize: '11px', color: '#FB923C' }}>{(checkData as any).micrFontSize}px</span>
                                    </div>
                                    <input
                                        type="range" min="16" max="32" step="0.5"
                                        value={(checkData as any).micrFontSize}
                                        onChange={(e) => setCheckData(prev => ({ ...prev, micrFontSize: parseFloat(e.target.value) }))}
                                        style={{ width: '100%', accentColor: '#F97316' }}
                                    />
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#999', marginTop: '4px' }}>
                                        <span>Compact</span>
                                        <span>Oversized</span>
                                    </div>
                                </div>
                                {micrFontLoaded === false && (
                                    <div style={{ marginTop: '12px', padding: '10px', borderRadius: '8px', background: 'rgba(231,76,60,0.12)', border: '1px solid rgba(231,76,60,0.45)', color: '#FCA5A5', fontSize: '11px', lineHeight: 1.5 }}>
                                        ⚠️ The selected MICR font did not load, so the line below is showing a fallback typeface and will not read as bank standard. Check your connection and reload the page.
                                    </div>
                                )}
                                <div style={{ marginTop: '10px', fontSize: '10px', color: '#8A93A6', lineHeight: 1.5 }}>
                                    MICR font: GnuMICR by Eric Sandeen (GPL) — <a href="/fonts/LICENSE-GnuMICR.txt" target="_blank" rel="noreferrer" style={{ color: '#8A93A6', textDecoration: 'underline' }}>license</a>
                                </div>
                                <div style={{ marginTop: '15px' }}>
                                    <label style={checkboxStyle}>
                                        <input
                                            type="checkbox"
                                            checked={showMicrBand}
                                            onChange={(e) => setShowMicrBand(e.target.checked)}
                                        />
                                        Show MICR Clear Band (White Bottom)
                                    </label>
                                    <label style={checkboxStyle}>
                                        <input
                                            type="checkbox"
                                            checked={showAmountBorder}
                                            onChange={(e) => setShowAmountBorder(e.target.checked)}
                                        />
                                        Show Amount Box Border
                                    </label>
                                </div>
                                <div style={{ marginTop: '10px', padding: '10px', background: 'rgba(245,158,11,0.10)', borderRadius: '8px', border: '1px solid rgba(245,158,11,0.35)', fontSize: '11px', color: '#FCD34D' }}>
                                    <strong>Note:</strong> Standard checks require 22px for OCR compatibility.
                                </div>
                            </div>
                        )}
                    </div>

                    {/* SECURITY FEATURES SECTION */}
                    <div id="oc-tool-security" style={{ ...sectionStyle, order: 7 }}>
                        <div style={sectionHeaderStyle} onClick={() => toggleSection('security')}>
                            <span>🔒 Security Features</span>
                            <span>{activeSection === 'security' ? '▼' : '▶'}</span>
                        </div>
                        {activeSection === 'security' && (
                            <div style={{ padding: '15px' }}>
                                {securityFeatures.map(feature => (
                                    <label key={feature.id} style={checkboxStyle}>
                                        <input
                                            type="checkbox"
                                            checked={feature.enabled}
                                            onChange={() => toggleSecurityFeature(feature.id)}
                                        />
                                        <span style={{ marginLeft: 8 }}>{feature.name}</span>
                                    </label>
                                ))}

                                {/* Security medallion: the round SAFE disc by the
                                    signature line. Colour + finish are per-design;
                                    the disc shows while Thermochromic Ink is on. */}
                                <div style={{ marginTop: '14px', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '12px' }}>
                                    <span style={segGroupLabelStyle}>Security medallion — colour</span>
                                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                                        {SEAL_COLOR_PRESETS.map(p => (
                                            <button
                                                key={p.id}
                                                type="button"
                                                title={p.name}
                                                aria-pressed={sealColor.toLowerCase() === p.color.toLowerCase()}
                                                onClick={() => setSealColor(p.color)}
                                                style={{
                                                    width: '26px', height: '26px', borderRadius: '50%',
                                                    background: `radial-gradient(circle at 35% 35%, #ffffff88, transparent 55%), ${p.color}`,
                                                    border: sealColor.toLowerCase() === p.color.toLowerCase() ? '2px solid #F97316' : '1px solid rgba(255,255,255,0.25)',
                                                    cursor: 'pointer', padding: 0
                                                }}
                                            />
                                        ))}
                                        <input
                                            type="color"
                                            value={/^#[0-9a-fA-F]{6}$/.test(sealColor) ? sealColor : '#a7adb5'}
                                            onChange={(e) => setSealColor(e.target.value)}
                                            title="Custom colour"
                                            style={{ width: '30px', height: '26px', padding: 0, background: 'transparent', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '6px', cursor: 'pointer' }}
                                        />
                                    </div>
                                    <div style={{ marginTop: '10px' }}>
                                        <span style={segGroupLabelStyle}>Security medallion — finish</span>
                                        <div style={segRowStyle} role="group" aria-label="Medallion finish">
                                            <button type="button" aria-pressed={sealFinish === 'flat'} style={segBtnStyle(sealFinish === 'flat')} onClick={() => setSealFinish('flat')}>Flat</button>
                                            <button type="button" aria-pressed={sealFinish === 'metallic'} style={segBtnStyle(sealFinish === 'metallic')} onClick={() => setSealFinish('metallic')}>Metallic shine</button>
                                        </div>
                                        <p style={{ fontSize: '9px', color: '#94A3B8', marginTop: '4px', marginBottom: 0 }}>
                                            Metallic designs print bright and bold, and the artwork is foil-ready — but a normal printer cannot lay down real reflective foil. True foil shine needs foil printing.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* BORDERS & SECURE FRAMES (Phase 41 Integration) */}
                    <div id="oc-tool-borders" style={{ ...sectionStyle, order: 9 }}>
                        <div style={sectionHeaderStyle} onClick={() => toggleSection('borders')}>
                            <span>🖼️ Borders & Secure Frames</span>
                            <span>{activeSection === 'borders' ? '▼' : '▶'}</span>
                        </div>
                        {activeSection === 'borders' && (
                            <div style={{ padding: '15px' }}>
                                <p style={{ fontSize: '11px', color: '#94A3B8', marginBottom: '12px' }}>
                                    Select high-security border patterns for your check.
                                </p>
                                <UltimateCustomizationPanel
                                    checkData={{ ...checkData, borderConfig } as any}
                                    onUpdate={(c) => {
                                        if (c.borderConfig) setBorderConfig(c.borderConfig);
                                    }}
                                    onLogoFile={handleLogoFile}
                                />
                            </div>
                        )}
                    </div>

                </div>

                <div style={{ marginTop: 20, paddingTop: 15, borderTop: '1px solid rgba(255,255,255,0.12)', position: 'relative' }}>

                    <button
                        onClick={() => setShowExportMenu(!showExportMenu)}
                        style={{ ...actionBtnStyle, justifyContent: 'space-between', background: 'linear-gradient(135deg, #F97316 0%, #EA580C 55%, #9A3412 100%)', color: '#FFFFFF', border: 'none', boxShadow: '0 2px 8px rgba(26,35,126,0.25)' }}
                    >
                        <span>🚀 Premium Actions / Export</span>
                        <span>{showExportMenu ? '▲' : '▼'}</span>
                    </button>

                    {
                        showExportMenu && (
                            <div style={{
                                position: 'absolute', bottom: '100%', left: 0, right: 0, marginBottom: 12,
                                background: '#14161D', boxShadow: '0 16px 40px rgba(0,0,0,0.55)',
                                borderRadius: '10px', border: '1px solid rgba(255,255,255,0.12)', zIndex: 100, padding: '6px'
                            }}>
                                <div style={{ padding: '6px 10px', fontSize: 9, fontWeight: 700, color: '#FB923C', textTransform: 'uppercase', letterSpacing: 1.2 }}>Export Options</div>

                                {/* Mobile Background Selector */}
                                <div style={{ padding: '4px 12px 12px', borderBottom: '1px solid rgba(255,255,255,0.12)', marginBottom: '8px' }}>
                                    <div style={{ fontSize: '10px', color: '#94A3B8', marginBottom: '6px' }}>Mobile Shot Surface:</div>
                                    <div style={{ display: 'flex', gap: '6px' }}>
                                        {[
                                            { id: 'dark', label: 'Dark', color: '#333' },
                                            { id: 'wood', label: 'Wood', color: '#5D4037' },
                                            { id: 'granite', label: 'Stone', color: '#455A64' },
                                            { id: 'office', label: 'Desk', color: '#ddd' }
                                        ].map(bg => (
                                            <button
                                                key={bg.id}
                                                onClick={() => setMobileBackground(bg.id as any)}
                                                style={{
                                                    flex: 1, padding: '4px', fontSize: '10px', borderRadius: '4px',
                                                    border: mobileBackground === bg.id ? `2px solid ${bg.color}` : '1px solid rgba(255,255,255,0.12)',
                                                    backgroundColor: mobileBackground === bg.id ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.04)',
                                                    fontWeight: mobileBackground === bg.id ? 'bold' : 'normal',
                                                    color: mobileBackground === bg.id ? '#FFFFFF' : '#94A3B8',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {bg.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <button
                                    onClick={handleServerPdfExport}
                                    disabled={isExportingServerPdf}
                                    style={{ ...dropdownItemStyle, color: '#10B981', fontWeight: 700, background: 'rgba(16, 185, 129, 0.08)' }}
                                >
                                    {isExportingServerPdf ? '⏳ Generating Bank PDF...' : '🏦 Export Bank-Grade Vector PDF (Server)'}
                                </button>
                                <button onClick={handleDownloadPDF} style={dropdownItemStyle}>
                                    📄 Download PDF — Front + Back (600 DPI)
                                </button>
                                <button onClick={() => downloadCheckPdf(false)} style={dropdownItemStyle}>
                                    📄 Download PDF — Front Only (600 DPI)
                                </button>
                                <button onClick={() => handleEmailToPayee()} style={dropdownItemStyle}>📧 Email to Payee</button>
                                <button onClick={handleDirectPrint} style={dropdownItemStyle}>🖨️ Direct Print (Pro)</button>
                                <button onClick={handleGenerateMobileShot} style={{ ...dropdownItemStyle, color: '#F97316', fontWeight: 600 }}>
                                    📸 Mobile Deposit Shot (Final)
                                </button>
                                <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '8px 4px' }} />
                                <button onClick={handleSaveTemplate} style={{ ...dropdownItemStyle, color: '#2563EB', fontWeight: 600 }}>
                                    💾 Save Cloud Template
                                </button>
                            </div>
                        )
                    }

                    <button onClick={handleGenerate} style={generateBtnStyle}>
                        GENERATE FINAL CHECK
                    </button>
                </div>
                </>)}
            </div> {/* END LEFT SIDEBAR */}

            {/* RIGHT SIDE - PREVIEW */}
            <div className="oc-preview-pane" style={previewContainerStyle}>
                {/* No start bar stacks here any more: the start/design
                    choices live in the app menu (StudioStartMenu),
                    reached through the bridge wiring above, so the
                    studio presents the check immediately. */}
                <div className="oc-history-wrap" style={{ padding: '10px 12px 0' }}>
                    <HistoryBar history={designHistory} dark label="Design" />
                </div>
                <div className="oc-toolbar oc-preview-header" style={{ ...previewHeaderStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', position: 'relative' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ color: '#4CAF50', fontSize: '8px' }}>●</span>
                        <span style={{ color: '#E2E8F0', fontSize: '11px', fontWeight: '600' }}>
                            Live Preview
                        </span>
                        <span style={{ color: '#94A3B8', fontSize: '10px', padding: '2px 6px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', border: '1px solid rgba(255,255,255,0.08)' }}>
                            {checkDimensions.width / 100}" × {checkDimensions.height / 100}" @ 100 DPI
                        </span>
                    </div>

                    {/* Zero-Decline Bank Compliance Widget */}
                    {(() => {
                        const compliance = MICRComplianceEngine.evaluateZeroDeclineCompliance({
                            routingNumber: checkData.routingNumber,
                            accountNumber: checkData.accountNumber,
                            checkNumber: checkData.checkNumber,
                            amount: checkData.amount,
                            payeeName: checkData.payeeName,
                            date: checkData.date,
                            fractionalRouting: checkData.fractionalRouting,
                            hasSignature: Boolean(signatureImage) || Boolean(signatureStyle && signatureStyle !== 'none'),
                            amountWords: (() => { const n = parseMoney(checkData.amount); return n > 0 ? convertToWords(n) : ''; })(),
                            micrInBand: (() => {
                                const H = checkDimensions.height;
                                const obj: any = (fabricCanvas as any)?.getObjects?.().find((o: any) => o.id === 'micrLine');
                                const top = obj?.top ?? objectPositionsRef.current.micrLine?.top;
                                return typeof top === 'number' ? (top >= H - 67 && top <= H - 24) : undefined;
                            })()
                        });

                        return (
                            <div className="oc-compliance" style={{ position: 'relative' }}>
                                <button
                                    className="oc-compliance-button"
                                    onClick={() => setShowComplianceDetails(!showComplianceDetails)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '4px 10px',
                                        background: compliance.is100PercentCompliant ? 'rgba(16, 185, 129, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                                        border: `1px solid ${compliance.is100PercentCompliant ? '#10B981' : '#F59E0B'}`,
                                        borderRadius: '6px',
                                        color: compliance.is100PercentCompliant ? '#34D399' : '#FBBF24',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <span>{compliance.is100PercentCompliant ? '🛡️' : '⚠️'}</span>
                                    <span>{compliance.is100PercentCompliant ? '100% Zero-Decline Bank Guaranteed' : `Bank Acceptance Score: ${compliance.score}%`}</span>
                                    <span style={{ fontSize: '9px', opacity: 0.8 }}>{showComplianceDetails ? '▲' : '▼'}</span>
                                </button>

                                {showComplianceDetails && (
                                    <div className="oc-compliance-details" style={{
                                        position: 'absolute',
                                        top: '110%',
                                        right: 0,
                                        width: '340px',
                                        background: '#0F172A',
                                        border: '1px solid #334155',
                                        borderRadius: '10px',
                                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.5)',
                                        padding: '12px',
                                        zIndex: 200,
                                        color: '#F8FAFC'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, borderBottom: '1px solid #1E293B', paddingBottom: 6 }}>
                                            <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#38BDF8' }}>
                                                Check 21 & Fed Reserve Audit
                                            </span>
                                            <span style={{ fontSize: '11px', fontWeight: 700, color: compliance.is100PercentCompliant ? '#34D399' : '#FBBF24' }}>
                                                {compliance.score}% Score
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            {compliance.checks.map(item => (
                                                <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '11px', lineHeight: 1.3 }}>
                                                    <span style={{ color: item.passed ? '#34D399' : '#F87171' }}>{item.passed ? '✓' : '✗'}</span>
                                                    <div>
                                                        <div style={{ fontWeight: 600, color: item.passed ? '#E2E8F0' : '#FCA5A5' }}>{item.label}</div>
                                                        <div style={{ fontSize: '10px', color: '#94A3B8' }}>{item.message}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div style={{ marginTop: 8, borderTop: '1px solid #1E293B', paddingTop: 6, fontSize: '10px', color: '#94A3B8', lineHeight: 1.4 }}>
                                            Print at 100% scale. Counter/ATM deposits: MICR toner + check stock. Mobile deposit: this 400 DPI design works on plain paper too - the standard MICR line, clear band and security features are in the file.
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </div>

                <div className="oc-toolbar" style={toolbarStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ fontSize: '9px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: '1px' }}>Studio</span>
                        <span style={{ color: '#475569', margin: '0 4px', fontSize: '10px' }}>|</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <button style={toolBtnStyle} onClick={() => scrollToPage('front')}>↑ Front</button>
                        <button style={toolBtnStyle} onClick={() => scrollToPage('back')}>↓ Back</button>
                        <span style={{ color: '#475569', margin: '0 2px', fontSize: '10px' }}>|</span>
                        <button style={toolBtnStyle} onClick={() => stepZoom(-1)}>−</button>
                        <button style={toolBtnStyle} onClick={() => stepZoom(1)}>+</button>
                        <select
                            style={zoomSelectStyle}
                            value={Math.round(zoom * 100) + '%'}
                            onChange={(e) => setZoom(parseInt(e.target.value) / 100)}
                        >
                            <option value="50%">50%</option>
                            <option value="75%">75%</option>
                            <option value="85%">85% (Default)</option>
                            <option value="100%">100%</option>
                            <option value="125%">125%</option>
                        </select>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <button style={toolBtnStyle} onClick={() => handlePaste()} title="Paste Signature (Ctrl+V)">📋</button>
                        <button style={toolBtnStyle} onClick={() => setIsSignatureLabOpen(true)} title="Signature Lab">✏️</button>
                        <button style={toolBtnStyle} onClick={handleAddText} title="Add Text">T</button>
                        <button style={toolBtnStyle} onClick={async () => {
                            if (signatureImage) {
                                const cleaned = await SignatureExtractorEngine.processSignature(signatureImage, signatureInkProcessOptions());
                                setSignatureImage(cleaned);
                            } else {
                                alert('Add or draw a signature first - Auto-Isolate cleans up an existing signature.');
                            }
                        }} title="Auto-Isolate Signature">🖍️</button>
                        <label style={{ ...toolBtnStyle, cursor: 'pointer' }} title="Attachments">
                            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAttachImage} />
                            📎
                        </label>
                        <span style={{ color: '#475569', margin: '0 2px', fontSize: '10px' }}>|</span>
                        <button style={toolBtnStyle} onClick={handleDownloadPDF} title="Print / PDF">🖨️</button>
                    </div>
                </div>

                <div className="canvas-wrapper-scroll" style={{ ...canvasWrapperStyle, overflowY: 'auto', maxHeight: 'calc(100vh - 120px)', paddingBottom: '120px' }}>
                    <div className="oc-canvas-scale" ref={scaleWrapRef} style={{
                        transform: pinchCssTransform(pinchView, effectiveZoom),
                        width: scaledLayoutW ? `${scaledLayoutW}px` : undefined,
                        height: scaledLayoutH ? `${scaledLayoutH}px` : undefined,
                        flex: 'none',
                        transformOrigin: 'top left',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '10px',
                        transition: 'transform 0.2s ease-out',
                        padding: '0',
                        margin: '0 auto'
                    }}>
                        {/* FRONT SIDE WITH RULER */}
                        <div id="oc-page-front" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0px', flexShrink: 0 }}>
                            <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '6px' }}>
                                PAGE 1 — FRONT SIDE
                            </div>
                            {/* Horizontal Ruler */}
                            <div style={{ marginLeft: '20px', marginBottom: '0px' }}>
                                <svg width={checkDimensions.width} height={18} style={{ display: 'block' }}>
                                    <rect x={0} y={0} width={checkDimensions.width} height={18} fill="#2a2d35" rx={2} />
                                    {Array.from({ length: Math.ceil(checkDimensions.width / 100) + 1 }, (_, i) => (
                                        <g key={`h-${i}`}>
                                            <line x1={i * 100} y1={0} x2={i * 100} y2={14} stroke="#94A3B8" strokeWidth={1} />
                                            <text x={i * 100 + 3} y={12} fill="#94A3B8" fontSize={8} fontFamily="Inter, sans-serif">{i}"</text>
                                            {i < Math.ceil(checkDimensions.width / 100) && (
                                                <>
                                                    <line x1={i * 100 + 25} y1={10} x2={i * 100 + 25} y2={18} stroke="#64748B" strokeWidth={0.5} />
                                                    <line x1={i * 100 + 50} y1={6} x2={i * 100 + 50} y2={18} stroke="#64748B" strokeWidth={0.5} />
                                                    <line x1={i * 100 + 75} y1={10} x2={i * 100 + 75} y2={18} stroke="#64748B" strokeWidth={0.5} />
                                                </>
                                            )}
                                        </g>
                                    ))}
                                </svg>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', flexShrink: 0 }}>
                                {/* Vertical Ruler */}
                                <div style={{ marginRight: '0px' }}>
                                    <svg width={20} height={checkDimensions.height} style={{ display: 'block' }}>
                                        <rect x={0} y={0} width={20} height={checkDimensions.height} fill="#2a2d35" rx={2} />
                                        {Array.from({ length: Math.ceil(checkDimensions.height / 100) + 1 }, (_, i) => (
                                            <g key={`v-${i}`}>
                                                <line x1={0} y1={i * 100} x2={14} y2={i * 100} stroke="#94A3B8" strokeWidth={1} />
                                                <text x={3} y={i * 100 + 12} fill="#94A3B8" fontSize={7} fontFamily="Inter, sans-serif">{i}</text>
                                                {i < Math.ceil(checkDimensions.height / 100) && (
                                                    <>
                                                        <line x1={12} y1={i * 100 + 25} x2={20} y2={i * 100 + 25} stroke="#64748B" strokeWidth={0.5} />
                                                        <line x1={8} y1={i * 100 + 50} x2={20} y2={i * 100 + 50} stroke="#64748B" strokeWidth={0.5} />
                                                        <line x1={12} y1={i * 100 + 75} x2={20} y2={i * 100 + 75} stroke="#64748B" strokeWidth={0.5} />
                                                    </>
                                                )}
                                            </g>
                                        ))}
                                    </svg>
                                </div>
                                {/* Check Canvas */}
                                <div style={{
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                                    borderRadius: '2px',
                                    overflow: 'hidden',
                                    backgroundColor: '#fff',
                                    position: 'relative',
                                    flexShrink: 0,
                                    width: checkDimensions.width,
                                    height: checkDimensions.height,
                                    margin: '0',
                                    padding: '0'
                                }}>
                                    {/* Karen Art Studio Payroll carries a full-bleed blue
                                        header banner (its own frame is drawn in its texture):
                                        the generic border overlay would print across the
                                        banner and its write-up, so it stands down for that
                                        design only (Princewill, 2026-11-04: the borderline
                                        was covering the design's blue write-up). */}
                                    {(checkStyle === 'payroll_karen' || selectedBank === 'KAREN_ART_STUDIO_PAYROLL') ? null : (
                                    <svg width={checkDimensions.width} height={checkDimensions.height} style={{ position: 'absolute', top: 0, left: 0, zIndex: 10, pointerEvents: 'none' }}>
                                        <BorderlineEngine
                                            width={checkDimensions.width}
                                            height={dynamicCanvasHeight}
                                            config={{
                                                ...borderConfig,
                                                color: borderConfig?.color || '#000',
                                                secondaryColor: borderConfig?.secondaryColor || '#333'
                                            } as any} />
                                    </svg>
                                    )}
                                    <canvas ref={canvasRef} />
                                    {showMicrGuide && (
                                        <div style={{ position: 'absolute', left: 0, right: 0, top: `${micrGuideTopPct}%`, height: `${(65 / dynamicCanvasHeight) * 100}%`, background: 'rgba(231, 76, 60, 0.12)', borderTop: '1px dashed rgba(231, 76, 60, 0.5)', zIndex: 11, pointerEvents: 'none' }}>
                                            <span style={{ position: 'absolute', left: 10, top: 5, fontSize: 8, color: '#c0392b', fontWeight: 'bold' }}>MICR CLEAR ZONE (0.625") - KEEP CLEAR</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* BACK SIDE */}
                        <div id="oc-page-back" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0px', marginLeft: '20px', flexShrink: 0 }}>
                            <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '6px' }}>
                                PAGE 2 — BACK SIDE
                            </div>
                            <div style={{
                                boxShadow: '0 15px 50px rgba(0,0,0,0.18)',
                                borderRadius: '4px',
                                overflow: 'hidden',
                                backgroundColor: '#fff',
                                flexShrink: 0
                            }}>
                                <CheckBack
                                    ref={checkBackRef}
                                    securityFeatures={securityFeatures}
                                    paperColor={paperColor}
                                    selectedBank={selectedBank}
                                    checkStyle={checkStyle}
                                    width={checkDimensions.width}
                                    height={dynamicCanvasHeight}
                                    checkData={checkData}
                                    handwritingColor={handwritingColor}
                                    signatureStyle={signatureStyle}
                                    backSideStyle={effectiveBackStyle}
                                    signatureImage={signatureImage}
                                    objectPositions={objectPositions}
                                    setObjectPositions={setObjectPositions}
                                    deletedIds={deletedBackIds}
                                    setDeletedIds={setDeletedBackIds}
                                    multiSelect={multiSelect}
                                    onSelectionChange={(n) => { setBackSelCount(n); if (n > 0) setSelSide('back'); }}
                                    onContextAction={(info) => setObjMenu({ side: 'back', objectId: (info.object as any)?.id ?? '', x: info.clientX, y: info.clientY })}
                                    onCanvasInit={(canvas) => { backFabricRef.current = canvas; return attachPinchZoom(canvas.upperCanvasEl, pinchOpts); }}
                                    onInteractChange={(active) => { interactFlagsRef.current.set('back', active); syncChromeDim(); }}
                                    cameraOps={cameraOps}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {/* Mobile selection bar (phone widths only, via CSS):
                    appears while objects are selected on either canvas
                    or multi-select is on. Floats ABOVE the studio dock
                    so the studio keeps exactly one bottom bar. */}
                {(frontSelCount > 0 || backSelCount > 0 || multiSelect) && (
                    <div className="oc-selection-bar" role="toolbar" aria-label="Selection actions">
                        <span className="oc-sel-count">
                            {(() => {
                                const n = selSide === 'back' ? (backSelCount || frontSelCount) : (frontSelCount || backSelCount);
                                return n > 0 ? `${n} selected${selSide === 'back' ? ' · Back' : ''}` : 'Multi-select';
                            })()}
                        </span>
                        <button
                            type="button"
                            className={multiSelect ? 'oc-sel-toggle-on' : ''}
                            aria-pressed={multiSelect}
                            onClick={() => setMultiSelect(m => !m)}
                        >
                            Multi
                        </button>
                        {(frontSelCount > 0 || backSelCount > 0) && (
                            <button type="button" className="oc-sel-delete" onClick={handleSelectionDelete}>Delete</button>
                        )}
                        {(frontSelCount > 0 || backSelCount > 0) && (
                            <button type="button" onClick={handleSelectionDone}>Done</button>
                        )}
                    </div>
                )}
                {/* Object context menu: long-press (touch) or
                    right-click (desktop) on any canvas object. An
                    addition to the selection bar, not a replacement. */}
                {objMenu && (
                    <div
                        className="oc-object-menu"
                        data-object-menu
                        role="menu"
                        aria-label={`${objectMenuLabel(objMenu.objectId, objMenu.side)} actions`}
                        style={{
                            left: Math.max(8, Math.min(objMenu.x, (typeof window !== 'undefined' ? window.innerWidth : 360) - 188)),
                            top: Math.max(8, Math.min(objMenu.y, (typeof window !== 'undefined' ? window.innerHeight : 640) - 158))
                        }}
                    >
                        <div className="oc-object-menu-title">{objectMenuLabel(objMenu.objectId, objMenu.side)}</div>
                        {editTargetForObject(objMenu.objectId, objMenu.side) && (
                            <button type="button" className="oc-object-menu-item" onClick={handleObjectMenuEdit}>
                                Edit
                            </button>
                        )}
                        <button type="button" className="oc-object-menu-item oc-object-menu-delete" onClick={handleObjectMenuDelete}>
                            Delete
                        </button>
                    </div>
                )}
                {/* Mobile studio dock: the five actions a phone user needs
                    stay reachable without hunting through the tool panel. */}
                <nav className="oc-mobile-studio-dock" aria-label="Studio quick actions">
                    <button type="button" onClick={() => openToolSection('check_info')}>📝<span>Details</span></button>
                    <button type="button" onClick={() => openToolSection('bank_style')}>🎨<span>Design</span></button>
                    <button type="button" onClick={() => setAiBarOpen(true)}>✨<span>Ask AI</span></button>
                    <button type="button" onClick={() => scrollToPage('back')}>📄<span>Back</span></button>
                    <button type="button" className="oc-dock-primary" onClick={handleGenerate}>✓<span>Generate</span></button>
                </nav>

                {/* GENERATE RESULT SHEET — the obvious home of the PDF
                    download. Generate used to hand back a front-only
                    PNG with no PDF in sight (owner report, 2026-11-04);
                    this sheet leads with the PDF actions. */}
                {showGenerateSheet && (
                    <div
                        data-generate-sheet
                        role="dialog"
                        aria-label="Download your check"
                        style={{
                            position: 'fixed', inset: 0, zIndex: 1200,
                            background: 'rgba(4, 6, 12, 0.66)', backdropFilter: 'blur(3px)',
                            display: 'flex', alignItems: 'flex-end', justifyContent: 'center'
                        }}
                        onClick={() => setShowGenerateSheet(false)}
                    >
                        <div
                            style={{
                                width: '100%', maxWidth: '520px', margin: '0 12px 12px',
                                background: '#12141f', border: '1px solid rgba(255,255,255,0.12)',
                                borderRadius: '16px', padding: '20px 18px 16px',
                                boxShadow: '0 24px 60px rgba(0,0,0,0.6)', color: '#E2E8F0'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div style={{ fontSize: '17px', fontWeight: 700, marginBottom: '2px' }}>Your check is ready</div>
                            <div style={{ fontSize: '12px', color: '#94A3B8', marginBottom: '14px' }}>
                                Check #{checkData.checkNumber} — the check at exact size, 600 DPI, lossless, on a sheet with cutting and deposit instructions.
                            </div>
                            <button
                                type="button"
                                onClick={() => { setShowGenerateSheet(false); void downloadCheckPdf(true); }}
                                style={{
                                    display: 'block', width: '100%', padding: '15px 16px', marginBottom: '10px',
                                    border: 'none', borderRadius: '12px', cursor: 'pointer',
                                    background: 'linear-gradient(135deg, #F97316, #EA580C)', color: '#fff',
                                    fontSize: '16px', fontWeight: 700, textAlign: 'left'
                                }}
                            >
                                ⬇ Download PDF — Front + Back
                            </button>
                            <button
                                type="button"
                                onClick={() => { setShowGenerateSheet(false); void downloadCheckPdf(false); }}
                                style={{
                                    display: 'block', width: '100%', padding: '13px 16px', marginBottom: '10px',
                                    border: '1px solid rgba(255,255,255,0.18)', borderRadius: '12px', cursor: 'pointer',
                                    background: 'rgba(255,255,255,0.06)', color: '#E2E8F0',
                                    fontSize: '15px', fontWeight: 600, textAlign: 'left'
                                }}
                            >
                                ⬇ Download PDF — Front Only
                            </button>
                            <button
                                type="button"
                                onClick={handleDownloadFrontPng}
                                style={{
                                    display: 'block', width: '100%', padding: '12px 16px', marginBottom: '6px',
                                    border: '1px solid rgba(255,255,255,0.10)', borderRadius: '12px', cursor: 'pointer',
                                    background: 'transparent', color: '#94A3B8',
                                    fontSize: '14px', fontWeight: 500, textAlign: 'left'
                                }}
                            >
                                ⬇ Download Front Image (PNG)
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowGenerateSheet(false)}
                                style={{
                                    display: 'block', width: '100%', padding: '10px 16px',
                                    border: 'none', borderRadius: '12px', cursor: 'pointer',
                                    background: 'transparent', color: '#64748B',
                                    fontSize: '14px', fontWeight: 500
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {/* FLOATING AI COMMAND BAR (Phase 74 - Draggable) */}
                <div
                    className="oc-aibar"
                    onMouseDown={(e) => {
                        // Only start drag if clicking on the container or icon, not inputs
                        if ((e.target as HTMLElement).tagName !== 'INPUT') {
                            setIsDraggingAiBar(true);
                            setDragOffset({
                                x: e.clientX - aiBarPosition.x,
                                y: e.clientY - aiBarPosition.y
                            });
                        }
                    }}
                    onTouchStart={(e) => {
                        const target = e.target as HTMLElement;
                        if (target.tagName !== 'INPUT' && target.tagName !== 'BUTTON' && target.tagName !== 'LABEL' && e.touches[0]) {
                            setIsDraggingAiBar(true);
                            setDragOffset({
                                x: e.touches[0].clientX - aiBarPosition.x,
                                y: e.touches[0].clientY - aiBarPosition.y
                            });
                        }
                    }}
                    style={{
                        position: 'fixed' as any, // Changed from absolute to fixed for viewport dragging
                        left: aiBarPosition.x,
                        top: aiBarPosition.y,
                        transform: 'none', // Removed centering transform
                        width: '90%', maxWidth: '600px',
                        background: 'rgba(14, 16, 24, 0.92)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '16px',
                        boxShadow: '0 20px 50px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.10)',
                        padding: '10px 16px',
                        display: aiBarOpen ? 'flex' : 'none', alignItems: 'center', gap: '12px',
                        zIndex: 1000,
                        transition: isDraggingAiBar ? 'none' : 'all 0.3s ease', // Disable transition during drag for smoothness
                        cursor: isDraggingAiBar ? 'grabbing' : 'grab'
                    }}>
                    <div style={{
                        width: '36px', height: '36px', borderRadius: '50%',
                        background: 'linear-gradient(135deg, #7b1fa2, #4a148c)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontSize: '16px', flexShrink: 0,
                        boxShadow: '0 4px 12px rgba(123, 31, 162, 0.3)',
                        cursor: 'grab'
                    }}>✨</div>

                    <div style={{ flex: 1, position: 'relative' }}>
                        <input
                            type="text"
                            value={(checkData as any).aiPrompt}
                            onChange={(e) => {
                                setCheckData(prev => ({ ...prev, aiPrompt: e.target.value, isAiActive: true }));
                            }}
                            onKeyDown={async (e) => {
                                if (e.key === 'Enter') await runAiPrompt();
                            }}
                            placeholder="Design or edit anything: 'Chase business, blue marble, pay Jane Doe $250, memo rent, maximum security…'"
                            style={{
                                width: '100%', border: 'none', outline: 'none', background: 'transparent',
                                fontSize: '14px', color: '#F1F5F9', fontWeight: 500
                            }}
                        />
                    </div>

                    <div className="oc-ai-secondary" style={{ height: '24px', width: '1px', background: 'rgba(255,255,255,0.12)' }} />

                    {/* Actions */}
                    <label className="oc-ai-secondary" title="Upload Logo/Reference" style={{ cursor: 'pointer', padding: '8px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                        <input type="file" style={{ display: 'none' }} onChange={(e) => handleImageUpload(e, 'logo')} />
                        <span style={{ fontSize: '18px' }}>📂</span>
                    </label>

                    <button
                        className="oc-ai-secondary"
                        title="Reset Layout"
                        onClick={() => {
                            if (window.confirm('Reset layout positions?')) setObjectPositions({});
                        }}
                        style={{
                            border: 'none', background: 'transparent', cursor: 'pointer', padding: '8px', borderRadius: '8px', fontSize: '18px', transition: 'background 0.2s',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                        🔄
                    </button>

                    <button
                        title="Apply the AI design/edit prompt"
                        onClick={runAiPrompt}
                        style={{
                            border: 'none', background: 'linear-gradient(135deg, #7b1fa2, #4a148c)', color: 'white', cursor: 'pointer', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold',
                            boxShadow: '0 4px 12px rgba(123, 31, 162, 0.4)',
                            minWidth: '60px'
                        }}
                    >
                        ✨ Go
                    </button>

                    <button
                        title="Close AI bar"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => setAiBarOpen(false)}
                        style={{
                            border: 'none', background: 'transparent', cursor: 'pointer', padding: '8px', borderRadius: '8px', fontSize: '15px', color: '#666',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                        }}
                    >
                        ✕
                    </button>
                </div>

                {/* AI FEEDBACK CHIP - says what the AI just changed */}
                {aiBarOpen && aiFeedback && (
                    <div className="oc-ai-feedback" style={{
                        position: 'fixed', left: Math.max(12, aiBarPosition.x), top: aiBarPosition.y - 52,
                        width: '90%', maxWidth: '600px', zIndex: 1001,
                        background: 'rgba(14, 16, 24, 0.95)', color: '#FCD34D',
                        border: '1px solid rgba(245,158,11,0.35)', borderLeft: '3px solid #F59E0B',
                        borderRadius: '10px', padding: '8px 12px', fontSize: '12.5px', lineHeight: 1.45,
                        boxShadow: '0 12px 32px rgba(0,0,0,0.5)'
                    }}>
                        ✨ {aiFeedback}
                    </div>
                )}

                {/* No floating Ask AI launcher (Phase 2 declutter):
                    the sparkle FAB floated over the check's bottom
                    right corner. Ask AI's entry points are the studio
                    dock's Ask AI button (mobile) and the Details
                    panel's Ask AI button (desktop) — the bar itself
                    above is unchanged. */}
            </div>

            {/* Signature Lab Modal */}
            <SignatureLab
                isOpen={isSignatureLabOpen}
                onClose={() => setIsSignatureLabOpen(false)}
                onSave={(dataUrl) => { setRawSignatureImage(dataUrl); setSignatureImage(dataUrl); }}
                initialColor={signatureInkColor}
                inkOriginal={signatureInkOriginal}
                onInkChange={(color, original) => { setSignatureInkColor(normalizeSignatureInk(color)); setSignatureInkOriginal(original); }}
                signatureImage={signatureImage}
                setSignatureImage={setSignatureImage}
                rawSignatureImage={rawSignatureImage}
                setRawSignatureImage={setRawSignatureImage}
            />
        </div >
    );
};

export default CheckBuilder;
